// apiSupabase.js — Supabase-backed implementation of the api.js contract.
// ===========================================================================
// Phase 4 of the Sheets→Supabase migration. Same exported function names and
// SAME return shapes as the legacy src/lib/api.js (which talks to /exec), so
// components need zero changes. At cutover, this file's contents replace
// api.js (or api.js re-exports from here). Until then it's unused — the live
// app keeps running on the old api.js.
//
// VALUE-SHAPE FIDELITY (critical): the legacy serializers return some values in
// quirky forms the components already parse. We reproduce them exactly:
//   * booking date  -> noon-UTC ISO ("2026-04-02T12:00:00.000Z"): safe for both
//     the getUTC* helpers AND any toLocaleDateString path (no ±1 day drift).
//   * start/end time -> the 1899-epoch UTC ISO ("1899-12-30T15:00:00.000Z")
//     that the frontend's getHours() reads as NY-local (UTC-5 on that epoch).
//   * timestamps     -> ISO strings (PostgREST already returns ISO).
//   * empty -> '' for fields the legacy code defaulted to '' (not null).
//
// AUTH/RLS: supabaseClient holds the session after signInWithPassword; every
// .from()/.rpc() call is authenticated and RLS-scoped by role automatically.
//
// !! VERIFY IN A BROWSER (npm start) BEFORE CUTOVER — the value reshaping above
//    must be confirmed against real component rendering (dates, times, money).
// ===========================================================================
import { cachedRead, bustClientCache, LONG_TTL_MS as _LONG_TTL } from './apiLegacy';
import { renderEmailHtml } from './emailHtml';
import { supabase } from './supabaseClient';
import { getActiveSite, siteAddress, siteName, KINGSTON, ALBANY } from './sites';
import { composeSms } from './sms';
import { pinToPassword, roleEmail } from './pinAuth';
import { tasksForService, tasksForServiceAndVehicle } from './checklistTemplates';
import { SERVICE_DURATIONS, canonVehicleSize, BOOKING_DAY_CAP, DEAD_BOOKING_STATUSES } from './constants';
import { withBiohazardNote } from './waivers';
import { depositLines } from './depositBlock';
import { albanyPaymentLines, albanyPaymentApplies } from './albanyPayment';
import { DROPOFF_EMAIL_LINE, DROPOFF_SMS_LINE, dropoffWindowApplies, customerSeesTime } from './dropoff';

// 🚨 SERVICE_DURATIONS is in HOURS; bookings.duration_min and the end-time
// helper below are in MINUTES. This returned the raw hours, so every
// approved request whose service name IS a key in that map became a
// 3-MINUTE booking -- 14 of them live, back to 2026-08-07, each one telling
// the bay grid the car was gone by 9:03. Names NOT in the map (the website's
// 'Signature (Interior & Exterior)') fell through to the 180 default and were
// correct by accident, which is why it stayed invisible.
//
// ⚠️ Multi-day services (Ceramic 168h) come out past 1440 minutes and
// _addMinutesToTimeStr wraps them, landing end_time back on start_time --
// which is exactly the documented convention for a job that spans days.
const _serviceDuration = (svc) => (SERVICE_DURATIONS[svc] || 3) * 60;
function _addMinutesToTimeStr(t, mins) {
  const m = String(t || '').match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t;
  let tot = ((+m[1]) * 60 + (+m[2]) + mins) % 1440;
  if (tot < 0) tot += 1440;
  return String(Math.floor(tot / 60)).padStart(2, '0') + ':' + String(tot % 60).padStart(2, '0');
}

const okResp = (extra) => ({ success: true, ...extra });

// ---------------------------------------------------------------------------
// Session-expiry handling (Supabase parity with legacy _handleAuthFailure).
// ---------------------------------------------------------------------------
// The legacy /exec path bounced the user to the PIN gate whenever the server
// returned {authRequired}. The Supabase path had no equivalent: once the
// session lapsed (refresh token expired / revoked), supabase-js still returns
// a locally-stored session so the SPA kept rendering an authed shell while
// every RLS-scoped read came back empty — blank cards, a blank invoicer
// deeplink, silently-dropped writes — and NEVER prompted a re-PIN. We detect a
// genuine auth error on any read/write and force re-authentication.
function _isAuthError(e) {
  if (!e) return false;
  if (e.__isAuthError) return true;
  const code = String(e.code || '');
  const status = Number(e.status || e.statusCode || 0);
  if (code === 'PGRST301' || code === 'PGRST302') return true; // JWT expired / invalid (PostgREST)
  if (status === 401) return true;
  const msg = String((e && e.message) || '').toLowerCase();
  return /jwt (expired|is invalid)|token is expired|invalid (jwt|token|claim)|refresh token|not authenticated|no api key|missing sub claim/.test(msg);
}
let _authFailureHandled = false;
async function _handleSupabaseAuthFailure() {
  if (_authFailureHandled) return;
  _authFailureHandled = true;
  // Don't log a user out over a transient blip: a single read can race the
  // background token auto-refresh. getUser() validates the JWT server-side, so
  // if it still resolves a user the failure wasn't a real expiry — stand down.
  try {
    const { data, error } = await supabase.auth.getUser();
    if (!error && data && data.user) { _authFailureHandled = false; return; }
  } catch (_) { /* fall through to re-login */ }
  try { await supabase.auth.signOut(); } catch (_) {}
  try {
    // Clear the legacy GAS token too so a reload doesn't re-render the zombie
    // authed shell before getSession resolves (auth.jsx seeds authed from it).
    localStorage.removeItem('3c_token');
    localStorage.removeItem('3c_role');
    localStorage.removeItem('3c_auth');
    localStorage.removeItem('3c_expiry');
  } catch (_) {}
  // Bounce to the SPA login gate ('/' renders LoginGate when unauthed). A full
  // navigation also tears down any zombie in-memory component state.
  try {
    if (typeof window !== 'undefined' && window.location) {
      if (window.location.pathname !== '/') window.location.href = '/';
      else window.location.reload();
    }
  } catch (_) {}
}
const failResp = (e) => {
  if (_isAuthError(e)) { _handleSupabaseAuthFailure(); }
  return { success: false, error: (e && e.message) || String(e) };
};

// Fire the Vercel calendar-sync function (best-effort, never blocks the
// mutation). Passes the staff JWT so the function authorizes. action:
// 'upsert' | 'delete'. Same-origin /api on the deployed app; a 404 in local
// dev (CRA has no functions) is harmless.
async function _fireCalendar(bookingId, action, extra) {
  try {
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    fetch('/api/calendar-sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
      body: JSON.stringify({ bookingId, action, ...(extra || {}) }),
    }).catch(() => {});
  } catch (_) { /* never blocks the mutation */ }
}

// Trigger the website-lead poller (Vercel /api/invoicer?action=poll-leads).
// Server-gated to the signed-in owner/principal JWT. Best-effort — newly
// imported leads surface as bell notifications via the normal notification poll
// and in the Comms Inbox. Returns the poll summary {scanned,imported,...}.
export async function pollWebsiteLeads() {
  try {
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    if (!tok) return failResp('no session');
    const res = await fetch('/api/invoicer?action=poll-leads', {
      headers: { Authorization: 'Bearer ' + tok },
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp(j) : failResp((j && j.error) || 'poll failed');
  } catch (e) { return failResp(e.message); }
}

// One-time owner catch-up: the Stripe membership webhook is forward-only, so
// memberships set up before it was wired never notified. This asks the server
// (/api/invoicer?action=backfill-membership-notifs, owner-JWT-gated) to pull
// the account's active subscriptions and drop a membership_purchase bell for
// each. Idempotent by subscription id. Returns {scanned, created}.
export async function backfillMembershipNotifs() {
  try {
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    if (!tok) return failResp('no session');
    const res = await fetch('/api/invoicer?action=backfill-membership-notifs', {
      headers: { Authorization: 'Bearer ' + tok },
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp(j) : failResp((j && j.error) || 'backfill failed');
  } catch (e) { return failResp(e.message); }
}

// Principal: build/find the Winter Wash Club Payment Links on Stripe and save
// them as plan links (MEMBERSHIP.planLinks) so the tab texts the right tier.
// Idempotent server-side.
export async function ensureWinterClubLinks() {
  try {
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    if (!tok) return failResp('no session');
    const res = await fetch('/api/invoicer?action=ensure-winter-club-links', {
      headers: { Authorization: 'Bearer ' + tok },
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp(j) : failResp((j && j.error) || 'setup failed');
  } catch (e) { return failResp(e.message); }
}

// Principal: build/find the Backseat Overhaul monthly Payment Links (one per
// vehicle size) on Stripe and save them as plan links. Idempotent server-side.
export async function ensureBackseatLinks() {
  try {
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    if (!tok) return failResp('no session');
    const res = await fetch('/api/invoicer?action=ensure-backseat-links', {
      headers: { Authorization: 'Bearer ' + tok },
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp(j) : failResp((j && j.error) || 'setup failed');
  } catch (e) { return failResp(e.message); }
}

// One-time catch-up: record membership dues collected BEFORE the invoice.paid
// webhook was recording revenue. Asks the server
// (/api/invoicer?action=backfill-membership-revenue, principal-JWT-gated) to
// pull the account's paid Stripe subscription invoices and write a MEMBER-
// revenue row for each. Idempotent by Stripe invoice id. Returns {scanned, recorded}.
export async function backfillMembershipRevenue() {
  try {
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    if (!tok) return failResp('no session');
    const res = await fetch('/api/invoicer?action=backfill-membership-revenue', {
      headers: { Authorization: 'Bearer ' + tok },
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp(j) : failResp((j && j.error) || 'backfill failed');
  } catch (e) { return failResp(e.message); }
}

// Manually run the walk-around media retention purge (owner/principal). Normally
// fires nightly via the pg_cron `purge-old-media-daily` job; this is a manual
// trigger for testing/visibility. Returns { purged, removedObjects, months }.
export async function purgeOldMedia() {
  try {
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    if (!tok) return failResp('no session');
    const res = await fetch('/api/invoicer?action=purge-old-media', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + tok },
      body: '{}',
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp(j) : failResp((j && j.error) || 'purge failed');
  } catch (e) { return failResp(e.message); }
}

// Audit / repair invoice↔appointment S.O.# links (Vercel /api/invoicer
// action=audit-invoice-links). Server-gated to the owner/principal JWT. Default
// is a read-only report of mismatched links; pass { apply:true } to unlink the
// confirmed-bad ones and re-link them to the correct appointment by date.
// Returns { totalLinked, mismatches, badLinks[], unlinked?, relinked? }.
export async function auditInvoiceLinks({ apply = false, maxGapDays } = {}) {
  try {
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    if (!tok) return failResp('no session');
    const res = await fetch('/api/invoicer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + tok },
      body: JSON.stringify({ action: 'audit-invoice-links', apply, maxGapDays }),
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp(j) : failResp((j && j.error) || 'audit failed');
  } catch (e) { return failResp(e.message); }
}

// Per-card invoice-link fix (schedule cards). All three POST /api/invoicer under
// the owner/principal JWT. resync auto-matches this booking → its invoice (links
// both sides on a confident match, else returns candidates); link stamps a
// hand-picked S.O.#; unlink clears a bad link. Each returns the updated shape so
// the card can reflect it; callers reloadBookings() to fully resync UI + cache.
async function _postInvoicerAuthed(action, extra) {
  try {
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    if (!tok) return failResp('no session');
    const res = await fetch('/api/invoicer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + tok },
      body: JSON.stringify({ action, ...extra }),
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp(j) : failResp((j && j.error) || (action + ' failed'));
  } catch (e) { return failResp(e.message); }
}
export const resyncBookingLink  = (bookingId, windowDays) => _postInvoicerAuthed('resync-booking-link', { bookingId, windowDays });
export const linkBookingSo      = (bookingId, soNum)      => _postInvoicerAuthed('link-booking-so', { bookingId, soNum });
export const unlinkBookingSo    = (bookingId)            => _postInvoicerAuthed('unlink-booking-so', { bookingId });

// ---- value reshapers (legacy-faithful) ------------------------------------
const S = (v) => (v == null ? '' : String(v));            // '' default
const N = (v) => Number(v) || 0;                          // 0 default
const isoTs = (v) => (v ? new Date(v).toISOString() : '');
// numeric for DB writes: '' / null -> null (not 0/NaN), so blank form fields
// don't trip "invalid input syntax for type integer/numeric".
const num = (v) => (v === '' || v == null || Number.isNaN(Number(v)) ? null : Number(v));
// A date bound is only applied if it's a real, in-range YYYY-MM-DD. The Detailer
// calls getBookingsRange('0000-01-01','9999-12-31') as an "all" sentinel, and
// Postgres rejects 0000-01-01 (date out of range) — so skip insane bounds.
const saneDate = (d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d || '')) && String(d) >= '1900-01-01' && String(d) <= '2999-12-31';

// Postgres date "YYYY-MM-DD" -> noon-UTC ISO (drift-safe). Pass-through if the
// value already has a time component.
function dateOut(d) {
  if (!d) return '';
  const s = String(d);
  const day = s.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? `${day}T12:00:00.000Z` : s;
}

// minutes-since-midnight -> the 1899-epoch UTC ISO the frontend getHours() reads
// (NY is UTC-5 on the 1899 epoch, no DST). null/'' -> ''.
function minToIso(min) {
  if (min == null || min === '') return '';
  const h = Math.floor(min / 60), m = min % 60;
  return new Date(Date.UTC(1899, 11, 30, h + 5, m, 0)).toISOString();
}
// inverse: legacy "HH:mm" string OR 1899-ISO -> minutes (for write payloads).
function isoOrHmToMin(v) {
  if (v == null || v === '') return null;
  const s = String(v);
  const hm = s.match(/^(\d{1,2}):(\d{2})/);
  if (hm) return (+hm[1]) * 60 + (+hm[2]);
  if (/T/.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return ((d.getUTCHours() * 60 + d.getUTCMinutes()) - 300 + 1440) % 1440;
  }
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
}

// ---- row mappers (snake_case row -> legacy camelCase shape) ----------------
function mapBooking(r) {
  return {
    id: r.id, customerId: r.customer_id, customerName: r.customer_name,
    phone: S(r.phone),                       // phone joined below in callers
    vehicleId: r.vehicle_id, vehicleDesc: r.vehicle_desc,
    service: r.service, bay: r.bay,
    // general | specialty | mechanical. May be '' on rows written before the
    // column existed (and on recurring bookings, which don't set it) — read it
    // through categoryForBooking() from lib/constants, never bare, so those
    // rows still resolve to a category instead of painting no dot at all.
    serviceCategory: S(r.service_category),
    date: dateOut(r.date),
    startTime: minToIso(r.start_time), endTime: minToIso(r.end_time),
    duration: r.duration_min,
    status: r.status, notes: r.notes,
    createdAt: isoTs(r.created_at),
    checkedInAt: isoTs(r.checked_in_at), startedAt: isoTs(r.started_at),
    completedAt: isoTs(r.completed_at), cancelledAt: isoTs(r.cancelled_at),
    deliveredAt: isoTs(r.delivered_at),
    depositStatus: S(r.deposit_status), depositAmount: r.deposit_amount == null ? '' : r.deposit_amount,
    depositRequestedAt: isoTs(r.deposit_requested_at), depositPaidAt: isoTs(r.deposit_paid_at),
    depositRefundedAt: isoTs(r.deposit_refunded_at),
    // Where the deposit went (Revenue → Payments → Deposits, 2026-10-29).
    depositAppliedToSo: S(r.deposit_applied_to_so), depositAppliedAt: isoTs(r.deposit_applied_at),
    depositPaymentRef: S(r.deposit_payment_ref),
    vipCode: S(r.vip_code), vipChecklistVisible: r.vip_checklist_visible === true,
    invoiceSoNum: S(r.invoice_so_num),
    customDuration: N(r.custom_duration), invoiceTotal: N(r.invoice_total),
    balanceStatus: S(r.balance_status), balanceRequestedAt: isoTs(r.balance_requested_at),
    balanceAmount: r.balance_amount == null ? '' : r.balance_amount,
    balancePaidAt: isoTs(r.balance_paid_at), balancePaymentRef: S(r.balance_payment_ref),
    balanceRefundedAt: isoTs(r.balance_refunded_at), balanceRefundAmount: r.balance_refund_amount == null ? '' : r.balance_refund_amount,
    vehicleSize: S(r.vehicle_size),
    // Hazardous-material handling (the signed Biohazard Waiver). Carried into
    // the invoicer by Create Invoice, which adds the fee line — the AMOUNT
    // lives in the invoicer's catalog, never on the booking.
    biohazard: r.biohazard === true,
    // This visit is the one the customer's membership covers. Distinct from
    // "is this customer a member" (that lives in the `members` table): a member
    // booking a ceramic coating is NOT on their included wash. Create Invoice
    // carries this to the invoicer, which redeems at $0 when true and bills
    // normally when false — member or not.
    isMembership: r.is_membership === true,
    // Gift card the customer is bringing, decided at booking time. The CODE,
    // because gift_cards has no customer_id and the name on a card is usually
    // the buyer's — "their gift card" isn't a thing the invoicer can derive.
    // Create Invoice carries it over; the amount stays the invoicer's business.
    giftCardCode: S(r.gift_card_code),
    // Which dealership raised this job, when one did. Drives the job-alert
    // redirect: on a dealer booking the "~1 hr" and "ready" texts go to the
    // DEALERSHIP, not to their customer (enforced server-side in api/send-sms).
    dealer: S(r.dealer),
    dealerPrice: r.dealer_price == null ? '' : Number(r.dealer_price),
    // The dealership's biohazard fee currently folded INTO dealerPrice (NULL =
    // none), and when 3C told them / when they acknowledged it in 3COT.
    dealerBiohazardFee: r.dealer_biohazard_fee == null ? 0 : Number(r.dealer_biohazard_fee),
    biohazardNote: S(r.biohazard_note),
    biohazardNotifiedAt: r.biohazard_notified_at || null,
    biohazardAckAt: r.biohazard_ack_at || null,
    // Custom soil / heavy-cleaning fee folded INTO dealerPrice (COO, from the
    // appointment card — action=dealer-soil-fee, 2026-10-28). 0 = none.
    dealerSoilFee: r.dealer_soil_fee == null ? 0 : Number(r.dealer_soil_fee),
    soilNote: S(r.soil_note),
    soilNotifiedAt: r.soil_notified_at || null,
    soilAckAt: r.soil_ack_at || null,
    // Technician Approved Recommendations + the principal-review gate.
    tars: Array.isArray(r.tars) ? r.tars : [],
    reviewStatus: S(r.review_status),
    reviewRequestedAt: isoTs(r.review_requested_at), reviewRequestedBy: S(r.review_requested_by),
    reviewApprovedAt: isoTs(r.review_approved_at), reviewedBy: S(r.reviewed_by),
    // Which shop the JOB belongs to — not whoever is looking at it. The
    // walk-around deep link carries this to the capture page, which has no
    // auth context of its own and would otherwise default every API call to
    // Kingston. `select('*')` already returned the column; nothing read it.
    site: S(r.site),
    // What the DEALERSHIP said about getting the car here — "on the way" or
    // "running late", with the drop-off time they expect (minutes from
    // midnight, same unit as start_time). Written only by the dealer portal
    // through the dealer_set_arrival RPC (20260925120000); read-only here, and
    // blank on every job no dealership raised.
    arrivalState: S(r.arrival_state), arrivalEtaMin: r.arrival_eta_min == null ? null : Number(r.arrival_eta_min),
    arrivalNote: S(r.arrival_note), arrivalAt: isoTs(r.arrival_at),
    // Expected drop-off, picked at booking instead of a time (2026-10-14,
    // 20261014120000). '' reads as the normal 9–10 window via dropoffKey().
    dropoff: S(r.dropoff), dropoffMin: r.dropoff_min == null ? null : Number(r.dropoff_min),
  };
}
// ⚠️ Some vehicle reads name their columns explicitly rather than select('*').
// A field added here but not to that list arrives `undefined`, not as an error —
// it just silently renders blank. Keep the two in step.
const mapVehicle = (v) => ({
  id: v.id, year: v.year, make: v.make, model: v.model, size: S(v.size),
  // The description fields the garage tile and its editor show (color added
  // to the forms 2026-10-23; the column had been there all along).
  color: S(v.color), trim: S(v.trim), vin: S(v.vin),
  // Service facts (20260926120000). Written only by set_vehicle_service_facts —
  // the floor has no UPDATE policy on vehicles.
  oilType: S(v.oil_type), oilQty: v.oil_qty == null ? null : Number(v.oil_qty),
  oilFilter: S(v.oil_filter),
  odometer: v.odometer == null ? null : Number(v.odometer),
  odometerAt: isoTs(v.odometer_at), odometerBy: S(v.odometer_by),
});
// Display safety net: fall back to full_name when first/last are blank, so a
// customer can never render nameless just because a writer only set full_name.
// upsert_customer now splits the name itself (20260810200000), so this should
// never fire — but the invoicer's own customer list has always had this
// fallback, and the Customers tab silently didn't, which is why 6 rows read as
// blank there while looking fine in the invoicer. Keep the two in step.
// Exactly the columns mapCustomer reads. `select('*')` fetched all 25, and
// PostgREST repeats every key NAME on every row — so at 3,015 Albany
// customers the five it never looks at (services, site, review_credit_*)
// cost ~350 KB of pure key-name overhead, a fifth of the payload, for
// nothing. ⚠️ Keep this list in step with mapCustomer: a column added there
// and not here arrives undefined rather than erroring.
const CUSTOMER_COLS = [
  'id', 'first_name', 'last_name', 'full_name', 'email', 'phone',
  'visit_count', 'total_spend', 'notes', 'first_seen', 'last_seen',
  'source', 'opt_out', 'opt_out_at', 'phones', 'red_flag', 'red_flag_at',
  'red_flag_by', 'red_flag_note', 'dealer',
  'email_opt_out', 'unsub_token', 'email_bounced_at', 'email_bounce_type',
].join(',');
const _nameParts = (c) => String(c.full_name || '').trim().split(/\s+/).filter(Boolean);
const mapCustomer = (c, vehicles) => ({
  id: c.id,
  firstName: c.first_name || _nameParts(c)[0] || '',
  lastName: c.last_name || _nameParts(c).slice(1).join(' ') || '',
  email: c.email,
  phone: S(c.phone), visitCount: c.visit_count, totalSpend: c.total_spend,
  notes: c.notes, firstSeen: c.first_seen, lastSeen: c.last_seen,
  optOut: !!c.opt_out, optOutAt: c.opt_out_at || null,
  // Red Flag — perpetual retention hold on their walk-around media + a 🚩
  // identifier next to their name everywhere. See lib/redFlags.js.
  redFlag: !!c.red_flag, redFlagAt: c.red_flag_at || null,
  redFlagBy: S(c.red_flag_by), redFlagNote: S(c.red_flag_note),
  phones: (Array.isArray(c.phones) ? c.phones : []).map((p) => ({ number: S(p && p.number), label: S(p && p.label) })).filter((p) => p.number),
  // Where this customer came from. Surfaced so the blast audience can tell a
  // customer of OURS from one imported off a dealer list — the two have very
  // different consent to be marketed to.
  dealer: S(c.dealer), source: S(c.source),
  // Marketing-email suppression. 🚨 SEPARATE from optOut (SMS) by Ryder's
  // explicit call, 2026-08-26 — a text STOP stops texts and nothing else.
  // unsubToken is the credential in that customer's unsubscribe link: it goes
  // to the mailer and is never rendered anywhere in the app.
  emailOptOut: !!c.email_opt_out, unsubToken: S(c.unsub_token),
  // A dead mailbox, recorded by the Resend webhook. Only a HARD bounce or a
  // spam complaint suppresses — a soft bounce is a full mailbox or a bad
  // afternoon at their mail host, and treating that as permanent would drop a
  // real customer from every future campaign.
  emailBounced: c.email_bounce_type === 'hard' || c.email_bounce_type === 'complaint',
  emailBounceType: S(c.email_bounce_type), emailBouncedAt: c.email_bounced_at || null,
  vehicles: vehicles || [],
});
const mapStock = (r) => ({
  id: S(r.id), category: S(r.category), name: S(r.name), unit: S(r.unit) || 'units',
  // `|| 8` clobbered a legitimate 0 (fully out of stock) back to 8 containers on
  // every load — the "container amount resets to 8" bug. Preserve a real 0;
  // only default a genuinely-missing value (matches StockTab's new-item default).
  maxQty: r.max_qty == null ? 1 : (Number(r.max_qty) || 0), currentQty: Number(r.current_qty) || 0,
  lowThreshold: Number(r.low_threshold) || 2, notes: S(r.notes),
  lastUpdated: isoTs(r.last_updated),
});
const mapTask = (r) => ({
  id: S(r.id), assignedTo: S(r.assigned_to), assignedBy: S(r.assigned_by),
  title: S(r.title), notes: S(r.notes), dueDate: S(r.due_date),
  priority: S(r.priority) || 'medium', status: S(r.status) || 'open',
  createdAt: isoTs(r.created_at), completedAt: isoTs(r.completed_at),
});
const mapChecklistTask = (r) => ({
  id: r.id, bookingId: r.booking_id, task: r.task, order: r.sort_order,
  isFinal: r.is_final, completed: r.completed, completedAt: isoTs(r.completed_at),
  completedBy: r.completed_by, custom: r.custom === true,
  // Mechanical worksheets (20260926120000). `kind` is 'step' for every
  // detailing line and every row written before that migration, so read it
  // through isTaskDone() in lib/checklistState — never bare `completed`.
  kind: S(r.kind) || 'step', state: S(r.state), reading: S(r.reading),
});
const mapBlock = (r) => ({
  id: S(r.id), date: dateOut(r.date), bay: S(r.bay), reason: S(r.reason),
  createdBy: S(r.created_by), createdAt: isoTs(r.created_at), timeOfDay: S(r.time_of_day),
});
const mapTimeOff = (r) => ({
  id: S(r.id), date: dateOut(r.date), role: S(r.role), reason: S(r.reason),
  createdBy: S(r.created_by), createdAt: isoTs(r.created_at),
});
const mapGiftCard = (r) => ({
  id: r.id, code: r.code, customer: r.customer, service: r.service, value: r.value,
  issued: dateOut(r.issued), expires: dateOut(r.expires), status: r.status,
  notes: r.notes, closedAt: isoTs(r.closed_at), phone: S(r.phone),
  email: S(r.email), recipientName: S(r.recipient_name), recipientEmail: S(r.recipient_email),
  giftMessage: S(r.gift_message), deliveredAt: isoTs(r.delivered_at),
  // Which shop ISSUED it. Every shop sees every card (a gift card is honoured
  // wherever the holder walks in), so the list has to say which series a code
  // belongs to.
  site: S(r.site),
});
const mapNotification = (r) => ({
  id: r.id, type: r.type, message: r.message, forRole: r.for_role,
  bookingId: r.booking_id, customerName: r.customer_name, createdAt: isoTs(r.created_at),
  meta: r.meta || null,
  // Which shop the event came from. Notifications are deliberately NOT
  // location-scoped — Ryder wants every alert from both locations landing in
  // one bell — so the site rides along as a K/A tag instead of a filter.
  site: r.site || 'kingston',
});
const mapShopNeed = (r) => ({
  id: r.id,
  submittedBy: S(r.submitted_by),
  submittedAt: isoTs(r.submitted_at),
  title: S(r.title),
  description: S(r.description),
  category: S(r.category) || 'other',
  importance: S(r.importance) || 'medium',
  customFields: Array.isArray(r.custom_fields) ? r.custom_fields : [],
  status: S(r.status) || 'new',
  reviewNote: S(r.review_note),
  reviewedBy: S(r.reviewed_by),
  reviewedAt: isoTs(r.reviewed_at),
  // Which shop asked. The board is unified for owner/principal (buying is
  // brand-level), so without this a Kingston manager can't tell whose shelf
  // is empty.
  site: S(r.site),
});
const mapDraft = (r) => ({
  id: r.id, bookingId: r.booking_id, customerId: r.customer_id, to: S(r.to_addr),
  subject: S(r.subject), body: S(r.body), status: S(r.status),
  draftedAt: isoTs(r.drafted_at), sentAt: isoTs(r.sent_at), soNumber: S(r.so_number),
  // reply/comeback (and legacy null) kinds surface in the Post tab — matches
  // the GAS _rowToDraft coercion so Inbox-reply drafts are visible/sendable.
  // 'reschedule' is its own tab (date-change notices), so keep it distinct.
  attachmentId: S(r.attachment_id), kind: ['pre', 'post', 'balance', 'reschedule'].includes(r.kind) ? r.kind : 'post',
  careGuide: S(r.care_guide), // '' = auto (service-derived); else 'ceramic'|'1051'|'none'
  biohazardWaiver: !!r.biohazard_waiver, // pre drafts: attach the biohazard waiver PDF
  // Which shop this draft's job belongs to. The Outbox is a single unified
  // queue across both locations (Ryder sends every customer email), so the tag
  // is how a row declares which shop it's for.
  site: r.site || 'kingston',
});

// Customer phone map (legacy bookings carry a joined phone). The schedule
// re-reads bookings on every date flip / overview refresh, and each read used
// to re-download the WHOLE customers table just to attach phone digits to the
// cards. Memoize the in-flight promise with a short TTL so rapid reads share
// one fetch; phone numbers are display-only, so a few seconds of staleness is
// invisible and self-heals. Customer mutations bust it eagerly (below) so an
// edited/added phone shows immediately, not on the next TTL tick.
const _PHONE_MAP_TTL_MS = 30000;
let _phoneMapCache = { at: 0, promise: null };
function _bustPhoneMap() { _phoneMapCache = { at: 0, promise: null }; }
// One cached fetch of light customer metadata, shared by phoneMap()/memberMap()
// so the schedule's phone + membership lookups cost a single round-trip. Returns
// { phone: {id|name -> phone}, member: {id -> true for any ACTIVE member} }.
// Membership now lives in the `members` table (status 'active'), not on customers.
function _custMeta() {
  const now = Date.now();
  if (_phoneMapCache.promise && (now - _phoneMapCache.at) < _PHONE_MAP_TTL_MS) {
    return _phoneMapCache.promise;
  }
  const p = (async () => {
    const [{ data: custs }, { data: mems }] = await Promise.all([
      selectAllRows(() => supabase.from('customers').select('id,full_name,phone').order('id')),
      supabase.from('members').select('customer_id').eq('status', 'active'),
    ]);
    const phone = {}, member = {};
    (custs || []).forEach((c) => {
      if (c.phone) { phone[c.id] = String(c.phone); if (c.full_name) phone[String(c.full_name).toLowerCase()] = String(c.phone); }
    });
    (mems || []).forEach((m) => { if (m.customer_id) member[m.customer_id] = true; });
    return { phone, member };
  })();
  // Don't let a rejected fetch stick in the cache — re-fetch next call.
  p.catch(() => { if (_phoneMapCache.promise === p) _bustPhoneMap(); });
  _phoneMapCache = { at: now, promise: p };
  return p;
}
async function phoneMap() { return (await _custMeta()).phone; }
async function memberMap() { return (await _custMeta()).member; }

// ===========================================================================
// AUTH
// ===========================================================================
export async function verifyPin(role, pin) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: roleEmail(role), password: pinToPassword(pin),
    });
    if (error) return { success: false, error: 'Invalid PIN' };
    const claimed = data.user?.app_metadata?.role;
    if (claimed !== role) { await supabase.auth.signOut(); return { success: false, error: 'Role mismatch' }; }
    return okResp({ role: claimed, token: data.session?.access_token });
  } catch (e) { return failResp(e); }
}
export async function verifySecretWord() { return { success: false, error: 'Secret-word login retired in Supabase migration' }; }

// ===========================================================================
// BOOKINGS
// ===========================================================================
export async function getBookingsRange(startISO, endISO) {
  try {
    let q = supabase.from('bookings').select('*');
    if (saneDate(startISO)) q = q.gte('date', startISO);
    if (saneDate(endISO)) q = q.lte('date', endISO);
    q = q.order('date');
    const [{ data, error }, pm, mm] = await Promise.all([q, phoneMap(), memberMap()]);
    if (error) return failResp(error);
    const bookings = (data || []).map((r) => { const b = mapBooking(r); b.phone = pm[r.customer_id] || pm[String(r.customer_name || '').toLowerCase()] || ''; b.isMember = !!mm[r.customer_id]; return b; });
    return okResp({ bookings });
  } catch (e) { return failResp(e); }
}
export async function getBookings(date) {
  try {
    const [{ data, error }, pm, mm] = await Promise.all([
      supabase.from('bookings').select('*').eq('date', date).order('start_time'),
      phoneMap(), memberMap(),
    ]);
    if (error) return failResp(error);
    const bookings = (data || []).map((r) => { const b = mapBooking(r); b.phone = pm[r.customer_id] || ''; b.isMember = !!mm[r.customer_id]; return b; });
    return okResp({ bookings });
  } catch (e) { return failResp(e); }
}
// camelCase booking payload -> snake_case row (only keys present are written).
// Move an appointment to the other shop.
//
// 🚨 This does NOT update bookings.site directly, and must not be rewritten to.
// A row's site is immutable (35 triggers), and a booking is not one row — the
// checklist, the reminders, the Outbox draft, the invoice and the media all
// carry their own site. The RPC moves the whole job inside one transaction
// with the sanctioned `app.allow_site_move` opt-in, and enforces that the
// caller's own claims cover BOTH shops. See 20260822000000_move_booking_site.
export async function moveBookingSite(bookingId, site) {
  try {
    const { data, error } = await supabase.rpc('move_booking_site', {
      p_booking_id: bookingId, p_site: site,
    });
    if (error) return failResp(error);
    if (!data || data.success === false) {
      return failResp((data && data.error) || "That didn't go through.");
    }
    // Every surface that cached this job did so under the OLD shop — the
    // schedule, the overview, the Outbox and the revenue stream all key on
    // site. Leaving them is how a moved job keeps showing the old badge.
    try {
      ['getBookings', 'getBookingsRange', 'getDashboardOverview', 'getEmailDrafts',
       'getNotifications', 'getRevenueSummary'].forEach((k) => bustClientCache(k));
    } catch (_) { /* a stale cache is not worth failing a completed move */ }
    return okResp({
      moved: !!data.moved, from: data.from || null, to: data.to || site,
      counts: data.counts || {},
    });
  } catch (e) { return failResp(e); }
}

// Booking columns young enough that the live DB might not have them yet.
//
// Migrations are applied BY HAND (Supabase SQL editor / `supabase db push`), so
// a Vercel deploy can reach production minutes — or days — before the column
// does. In that window every booking write would carry a field the table
// doesn't have, and a failed insert here is the shop unable to book a car:
// never an acceptable price for a tick-box feature. Same trick api/invoicer.js
// uses for is_membership/customer_id on invoices.
//
// Drop a name from this list once its migration is definitely applied
// everywhere; leaving one here indefinitely just hides a real schema drift.
// 2026-09-15: is_membership, gift_card_code and service_category are all
// APPLIED to the live DB now. They sat in this list for two weeks, during
// which every category change on an appointment was silently dropped and
// "reverted" — exactly the drift the comment above warns about. Empty on
// purpose; add a name here only for the days between a deploy and its
// migration, and remove it the moment the migration runs.
const _NEW_BOOKING_COLS = [];
function _missingBookingCol(error) {
  const m = String((error && error.message) || '');
  if (!/column|schema cache/i.test(m)) return null;
  return _NEW_BOOKING_COLS.find((c) => new RegExp(c, 'i').test(m)) || null;
}
// Retry a booking write without whichever new column the DB rejected, one at a
// time so an unrelated failure still surfaces as itself.
async function _bookingWriteWithFallback(row, attempt) {
  let res = await attempt(row);
  let next = row;
  for (let i = 0; i < _NEW_BOOKING_COLS.length; i++) {
    const missing = _missingBookingCol(res.error);
    if (!missing) return res;
    const { [missing]: _dropped, ...safe } = next;  // eslint-disable-line no-unused-vars
    next = safe;
    res = await attempt(next);
  }
  return res;
}

function bookingToRow(d) {
  const row = {};
  const set = (k, v) => { if (v !== undefined) row[k] = v; };
  // Numeric setter: '' / null -> null, otherwise Number (NaN -> null). Prevents
  // Postgres "invalid input syntax for type integer/numeric" on blank form fields.
  const setNum = (k, v) => {
    if (v === undefined) return;
    if (v === '' || v === null) { row[k] = null; return; }
    const n = Number(v);
    row[k] = Number.isNaN(n) ? null : n;
  };
  // FK columns: '' must become null (a NOT VALID FK still rejects '' on insert).
  if (d.customerId !== undefined) row.customer_id = d.customerId || null;
  set('customer_name', d.customerName);
  if (d.vehicleId !== undefined) row.vehicle_id = d.vehicleId || null;
  set('vehicle_desc', d.vehicleDesc);
  set('service', d.service); set('bay', d.bay);
  // WHAT KIND of work this is — general | specialty | mechanical. Separate from
  // `bay` (where the car sits) because a mechanical job runs in either bay, so
  // the schedule cannot tell one from a general detail by bay alone. '' clears
  // it back to null, which readers resolve via the old bay-derived meaning.
  if (d.serviceCategory !== undefined) row.service_category = d.serviceCategory || null;
  // Empty date = "leave unchanged" (the SchedCard edit form sends date:'' since
  // it has no date picker). Writing '' to a Postgres `date` column throws
  // "invalid input syntax for type date" and fails the WHOLE update — so a
  // service/bay/time/notes edit silently saved nothing. Only set a real date.
  if (d.date !== undefined && d.date !== '' && d.date !== null) row.date = String(d.date).slice(0, 10);
  if (d.startTime !== undefined) row.start_time = isoOrHmToMin(d.startTime);
  if (d.endTime !== undefined) row.end_time = isoOrHmToMin(d.endTime);
  setNum('duration_min', d.duration); set('status', d.status); set('notes', d.notes);
  set('vehicle_size', d.vehicleSize);
  // Coerced, not passed through: the column is NOT NULL, and an edit form that
  // sends '' would otherwise fail the whole update.
  if (d.biohazard !== undefined) row.biohazard = !!d.biohazard;
  // Same coercion, same reason: NOT NULL column, and the SchedCard edit form
  // would otherwise send '' and fail the whole update.
  if (d.isMembership !== undefined) row.is_membership = !!d.isMembership;
  // '' clears the card (nullable column, unlike the two booleans above).
  if (d.giftCardCode !== undefined) row.gift_card_code = d.giftCardCode || null;
  // What 3C bills the DEALERSHIP for this job (partner rate), not the retail
  // price their customer pays. Copied off the approved request; posts to the
  // revenue stream when the booking is marked delivered (trg_sync_dealer_revenue).
  if (d.dealerPrice !== undefined) row.dealer_price = d.dealerPrice === '' || d.dealerPrice === null ? null : (Number(d.dealerPrice) || null);
  // The part of dealer_price that is the biohazard fee — set only by request
  // approval (OTTO ticked it) and the server's dealer-biohazard action.
  if (d.dealerBiohazardFee !== undefined) row.dealer_biohazard_fee = Number(d.dealerBiohazardFee) > 0 ? Number(d.dealerBiohazardFee) : null;
  // Which dealership this job came from, when it came from one. Set only from
  // a trusted source (the approved request), never from user input — the
  // notification and the schedule both read it to tell an OTTO job apart.
  if (d.dealer !== undefined) row.dealer = d.dealer || null;
  setNum('custom_duration', d.customDuration);
  // Expected drop-off (lib/dropoff.js). '' clears back to the default window.
  if (d.dropoff !== undefined) row.dropoff = d.dropoff || null;
  setNum('dropoff_min', d.dropoffMin);
  // Location. Omitted for an ordinary booking, where the `site` column default
  // (auth_site()) stamps whichever shop the writer is working in — which is
  // right, because they're creating it from that shop's schedule.
  //
  // It must be passed EXPLICITLY whenever the write is derived from an existing
  // record: approving an Albany booking request from the unified hub while the
  // toggle sits on Kingston would otherwise create the job at the wrong shop.
  // Inherit the parent's site, never the active one. See approveBookingRequest.
  set('site', d.site);
  return row;
}
// 6-char uppercase alphanumeric VIP portal code (matches the legacy format,
// e.g. "K5M6WP"). Customer enters it on /3c-vip.html to follow their job.
function _genVipCode() {
  // Unambiguous alphabet (no I/L/O/0/1) so customers can't mistype the code AND
  // it stays within what the portal input accepts. Existing codes may still
  // contain ambiguous chars — the portal now accepts the full set for those.
  const A = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 6; i++) s += A[Math.floor(Math.random() * A.length)];
  return s;
}
export async function createBooking(data) {
  try {
    // New-customer path: Booking.jsx sends customerId='' with name/phone. Create
    // or match the customer first so the booking links to a real registry row
    // (parity with GAS saveBookingCustomer).
    if (!data.customerId && (data.customerName || data.customerPhone)) {
      try {
        // Split fullName into first/last — upsert_customer otherwise sets only
        // full_name and the registry/readers (which key on first_name/last_name)
        // show a BLANK name. Same fix as /api/invoicer register-customer.
        const nm = String(data.customerName || '').trim();
        const parts = nm.split(/\s+/).filter(Boolean);
        const up = await supabase.rpc('upsert_customer', { p: {
          fullName: nm, phone: data.customerPhone, email: data.customerEmail,
          firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '',
        } });
        if (up.data && up.data.id) { data = { ...data, customerId: up.data.id }; _bustPhoneMap(); }
      } catch (_) { /* fall through — booking still carries the name */ }
    }
    // Register a brand-new vehicle to the customer's registry. Booking.jsx sends
    // addVehicle as JSON {year,make,model}; createBooking previously ignored it,
    // so the vehicle was only ever on the booking's vehicle_desc string, never
    // saved to the customer. Sparse match to avoid dupes; stamp vehicle_id.
    if (data.customerId && !data.vehicleId && data.addVehicle) {
      try {
        const v = JSON.parse(data.addVehicle) || {};
        const norm = (s) => String(s || '').trim().toLowerCase();
        const { data: veh } = await supabase.from('vehicles').select('id,year,make,model').eq('customer_id', data.customerId);
        const hit = (veh || []).find((x) => norm(x.year) === norm(v.year) && norm(x.make) === norm(v.make) && norm(x.model) === norm(v.model));
        if (hit) { data = { ...data, vehicleId: hit.id }; }
        else {
          const vid = 'V-' + genIdSuffix();
          const { error: ve } = await supabase.from('vehicles').insert({
            id: vid, customer_id: data.customerId,
            year: v.year ? (Number(v.year) || null) : null, make: v.make || '', model: v.model || '',
            vin: v.vin || '', color: v.color || '', trim: v.trim || '',
            // Register the vehicle to the shop the booking is for, not the one
            // the booker is toggled to. Undefined for the ordinary case → the
            // column default handles it.
            site: data.site || undefined,
          });
          if (!ve) data = { ...data, vehicleId: vid };
        }
      } catch (_) { /* vehicle registration best-effort */ }
    }
    const row = bookingToRow(data);
    if (!row.id) row.id = 'B-' + genIdSuffix();
    if (!row.status) row.status = 'scheduled';
    if (!row.vip_code) row.vip_code = _genVipCode(); // portal key for the pre-draft + VIP page
    row.created_by = data.createdBy || 'staff';
    row.created_at = new Date().toISOString();
    const { error } = await _bookingWriteWithFallback(row, (r) => supabase.from('bookings').insert(r));
    if (error) return failResp(error);
    // Generate the checklist from the service template (parity with the legacy
    // GAS createChecklist). Non-fatal to the BOOKING — a failed booking insert
    // already returned above — but never silent: a job that reaches the floor
    // with no checklist is the detailer's whole worklist missing, and it reads
    // as "the booking came in broken" rather than as a failed write.
    let checklistWarning = '';
    try {
      // Hand-written tasks from the booking form's "Custom checklist tasks" box
      // (the Clean Bay specialty flow and the Mechanical flow both have one).
      // 🚨 The form has always SENT these and this function never read them, so
      // every task typed there was dropped on the floor: a specialty job
      // reached the detailer with the template's tasks, or with none at all,
      // and nothing said the list had been thrown away.
      //
      // They come in as a JSON array string. A bad parse is not worth failing a
      // booking over — fall through to the template rather than throwing.
      let customTasks = [];
      try {
        const parsed = JSON.parse(data.customTasks || '[]');
        if (Array.isArray(parsed)) customTasks = parsed.map((t) => String(t || '').trim()).filter(Boolean);
      } catch (_) { /* not JSON — treat as no custom tasks */ }
      // A MECHANICAL job draws its lines from the mech_services catalog
      // (20260926120000) — the operator-owned template — because the detailing
      // templates have nothing to say about an oil change, and an unmatched
      // service name falls to DEFAULT_CHECKLIST ("Job started / Job complete"),
      // which is exactly what the one mechanical job on file ended up with.
      //
      // Unlike the detailing templates these lines carry a `kind`: a plain step
      // to tick, or a CONDITION CHECK answered pass/fail/na with a reading.
      // ⚠️ Condition check, NOT an inspection — 3C is not a NYS inspection
      // station and nothing here may be worded as one.
      let mechLines = [];
      if (String(row.service_category || '') === 'mechanical' && !customTasks.length) {
        try {
          // The service string is comma-joined like every other booking's, so
          // each part gets looked up and their lines run in order.
          const names = String(row.service || '').split(',').map((s) => s.trim()).filter(Boolean);
          for (const nm of names) {
            // ilike with no wildcards is a case-insensitive exact match, which
            // is what the unique index on lower(name) already guarantees.
            const { data: ms } = await supabase.from('mech_services')
              .select('tasks').eq('active', true).ilike('name', nm).limit(1);
            const hit = (ms || [])[0];
            if (hit && Array.isArray(hit.tasks)) {
              hit.tasks.forEach((t) => {
                const label = String((t && t.label) || '').trim();
                if (label && !mechLines.some((x) => x.label === label)) {
                  mechLines.push({ label, kind: (t && t.kind) === 'check' ? 'check' : 'step' });
                }
              });
            }
          }
        } catch (_) { /* no catalog row — fall through to the normal template */ }
      }
      // Typed tasks REPLACE the template. They're written for a job the
      // template doesn't describe (that is the whole reason the box exists);
      // appending a generic wash checklist under them is noise on the floor.
      const tasks = customTasks.length
        ? customTasks.map((t) => ({ label: t, kind: 'step' }))
        : mechLines.length
          ? mechLines
          : tasksForServiceAndVehicle(row.service, { desc: row.vehicle_desc, size: row.vehicle_size })
              .map((t) => ({ label: t, kind: 'step' }));
      if (tasks.length) {
        const clRows = tasks.map((t, i) => {
          const r = {
            id: 'CT-' + genIdSuffix(), booking_id: row.id, task: t.label, kind: t.kind,
            sort_order: i + 1, is_final: i === tasks.length - 1, completed: false, custom: false,
          };
          // Follow the BOOKING's location — but ONLY attach the key when there
          // is a value. 🚨 `site: undefined` is NOT the same as omitting it on a
          // BULK insert: supabase-js builds PostgREST's `columns` list with
          // `Object.keys()`, and `Object.keys({site: undefined})` still yields
          // 'site'. That tells PostgREST to insert the column, the JSON body
          // carries no value for it, and it writes an explicit NULL — bypassing
          // the `auth_site()` column default. `site` is NOT NULL, but RLS is
          // evaluated BEFORE constraints, so `in_hub_scope(null)` fails first
          // and the whole thing comes back as "new row violates row-level
          // security policy" — a permissions error for what is really a null.
          // (Single-object inserts are safe: no `columns` param, so JSON.stringify
          // drops the undefined key and the default applies.)
          if (row.site) r.site = row.site;
          return r;
        });
        // ⚠️ supabase-js RETURNS {error}, it does not throw — so the bare
        // `await …insert()` this replaces dropped every failure on the floor.
        // That is how bookings reached the schedule with no checklist at all
        // and nothing anywhere said why.
        let ins = await supabase.from('checklist_tasks').insert(clRows);
        // checklist_tasks INSERT is RLS'd on auth_role(); a lapsed token
        // resolves to '' and the write is refused. Refresh once and retry
        // before believing it (same pattern as the other write helpers here).
        if (ins.error && _isAuthError(ins.error)) {
          try { await supabase.auth.refreshSession(); } catch (_) { /* retry decides */ }
          ins = await supabase.from('checklist_tasks').insert(clRows);
        }
        if (ins.error) {
          checklistWarning = `The appointment saved, but its ${tasks.length}-task checklist did not (${ins.error.message || 'write refused'}). Open the appointment and re-save it to rebuild the checklist.`;
          try { console.error('[createBooking] checklist insert failed for', row.id, ins.error); } catch (_) { /* logging only */ }
        }
      }
    } catch (e) {
      checklistWarning = `The appointment saved, but its checklist did not (${(e && e.message) || 'unknown error'}). Open the appointment and re-save it to rebuild the checklist.`;
      try { console.error('[createBooking] checklist threw for', row.id, e); } catch (_) { /* logging only */ }
    }
    // Auto-build the welcome pre-draft (deposit request + portal link + access
    // code), same as the legacy autoBuildPreDraftForBooking. Fire-and-forget so
    // it never blocks the booking write; the owner reviews + sends from Outbox.
    generatePreDraft(row.id, data.preDraftNote ? { changeNote: data.preDraftNote } : undefined).catch(() => {});
    // Arm the automatic 2-day + 1-day SMS reminders (no-op when there's no phone
    // on file). Fire-and-forget so it never blocks the booking write.
    armAppointmentReminders(row.id).catch(() => {});
    _fireCalendar(row.id, 'upsert');
    return okResp({ id: row.id, checklistWarning });
  } catch (e) { return failResp(e); }
}
export async function updateBooking(bookingId, data) {
  try {
    // Capture the prior date/time BEFORE the write so the caller can offer to
    // produce an updated customer email. Only read when the update actually
    // carries a date or a start time (SchedCard's edit form may send date:''
    // for some edits — bookingToRow ignores blanks).
    const hasDate = data && data.date !== undefined && data.date !== '' && data.date !== null;
    const hasTime = data && data.startTime !== undefined && data.startTime !== '' && data.startTime !== null;
    let prevDate = '', prevStart = null;
    if (hasDate || hasTime) {
      try {
        const prev = await _bookingRow(bookingId);
        if (prev) { prevDate = String(prev.date || '').slice(0, 10); prevStart = prev.start_time == null ? null : Number(prev.start_time); }
      } catch (_) {}
    }
    const { error } = await _bookingWriteWithFallback(
      bookingToRow(data),
      (r) => supabase.from('bookings').update(r).eq('id', bookingId),
    );
    if (error) return failResp(error);
    _fireCalendar(bookingId, 'upsert');
    // Report whether the calendar date or start time actually moved, so the UI
    // can prompt the owner to produce/overwrite the "appointment moved" email.
    // Production is now explicit (see produceRescheduleEmail) rather than a
    // silent fire-and-forget, so the owner stays in control of what gets sent.
    const newDate = hasDate ? String(data.date).slice(0, 10) : prevDate;
    const newStart = hasTime ? isoOrHmToMin(data.startTime) : prevStart;
    const dateChanged = !!(hasDate && prevDate && newDate !== prevDate);
    const timeChanged = !!(hasTime && prevStart != null && newStart != null && Number(newStart) !== Number(prevStart));
    // Re-date the automatic SMS reminders when the appointment moves.
    if (dateChanged || timeChanged) armAppointmentReminders(bookingId).catch(() => {});
    let hasExistingReschedule = false;
    if (dateChanged || timeChanged) {
      try {
        const { data: ex } = await supabase.from('email_drafts')
          .select('id').eq('booking_id', bookingId).eq('kind', 'reschedule').eq('status', 'pending').limit(1);
        hasExistingReschedule = !!(ex && ex[0]);
      } catch (_) {}
    }
    return okResp({ dateChanged, timeChanged, prevDate, hasExistingReschedule });
  } catch (e) { return failResp(e); }
}
// A completed appointment's balance is settled at pickup, so any still-pending
// balance-REQUEST email draft is stale — void it (delete + principal heads-up)
// so it can't sit in the Outbox or get sent to a customer who already paid.
// Only touches pending balance drafts; already-sent ones are left alone.
// Owner/principal only (email_drafts_del RLS) — which is who completes jobs.
async function _voidPendingBalanceDrafts(bookingId) {
  // 🚨 A request raised with the before/after pick-up switch is the operator's
  // explicit decision and must survive the job being marked complete: a
  // "before pick-up" request goes out exactly when the car is finished, and
  // voiding it here deleted it unsent. Only the older un-timed drafts (the
  // pay-at-pickup Kingston card flow) are still swept.
  try {
    const { data: bk } = await supabase.from('bookings').select('balance_timing').eq('id', bookingId).maybeSingle();
    if (bk && bk.balance_timing) return;
  } catch (_) { return; }
  const { data } = await supabase.from('email_drafts')
    .select('id').eq('booking_id', bookingId).eq('kind', 'balance').eq('status', 'pending');
  const ids = (data || []).map((d) => d.id);
  if (!ids.length) return;
  const { error } = await supabase.from('email_drafts').delete().in('id', ids);
  if (error) return;
  // Dynamic import — apiLegacy owns the client cache; static import would cycle.
  try { const leg = await import('./apiLegacy'); leg.bustClientCache('getEmailDrafts'); } catch (_) {}
  let cname = '';
  try { const b = await _bookingRow(bookingId); if (b) cname = b.customer_name || ''; } catch (_) {}
  _insertNotification({
    type: 'draft_voided',
    message: '🗑 Auto-voided the pending balance email' + (cname ? ' for ' + cname : '') + ' — appointment completed.',
    forRole: 'principal', bookingId, customerName: cname || null,
  });
}

export async function updateBookingStatus(bookingId, status) {
  try {
    const { error } = await supabase.rpc('set_booking_status', { p_id: bookingId, p_status: status });
    if (error) return failResp(error);
    _fireCalendar(bookingId, status === 'cancelled' ? 'delete' : 'upsert');
    // Completed job → clear any stale pending balance-request draft. Fire-and-forget.
    if (status === 'complete') _voidPendingBalanceDrafts(bookingId).catch(() => {});
    // Auto-build the thank-you (post) draft when a job is delivered — parity
    // with the pre-draft auto-gen on booking creation, so the Outbox Post tab
    // fills as jobs are delivered instead of only via the manual "Generate"
    // button. Idempotent (generatePostDraft skips an existing one); fire-and-forget.
    if (status === 'delivered') generatePostDraft(bookingId).catch(() => {});
    // A cancelled/delivered/complete job needs no upcoming reminders — pull ours.
    if (['cancelled', 'delivered', 'complete'].includes(status)) clearAppointmentReminders(bookingId).catch(() => {});
    // NOTE: the "your car is ready" text is now sent from the interactive
    // completion popup (SchedCard/bay Close Job → Send now / +10/+20/+30),
    // not auto-fired here — see scheduleReadyText. Auto-firing here on top of
    // that popup double-texted the customer.
    return okResp({});
  } catch (e) { return failResp(e); }
}
// ── Technician Approved Recommendations (TARs) ───────────────────────────────
// tars is a jsonb array of { id, text, by, at } stored on the booking. The
// editor owns the array and overwrites the whole thing on every change.
export async function saveTars(bookingId, tars) {
  try {
    const clean = (Array.isArray(tars) ? tars : [])
      .map((t) => ({ id: S(t.id), text: S(t.text), by: S(t.by), at: S(t.at) }))
      .filter((t) => t.text.trim());
    const { error } = await supabase.from('bookings').update({ tars: clean }).eq('id', bookingId);
    if (error) return failResp(error);
    return okResp({ tars: clean });
  } catch (e) { return failResp(e); }
}
// ── Principal review gate ────────────────────────────────────────────────────
// Detailer requests review (instead of completing); principal approves; then
// the principal completes via updateBookingStatus. review_status: '' |
// 'requested' | 'approved'.
export async function requestReview(bookingId, by, customerName) {
  try {
    const { error } = await supabase.from('bookings')
      .update({ review_status: 'requested', review_requested_at: new Date().toISOString(), review_requested_by: S(by) })
      .eq('id', bookingId);
    if (error) return failResp(error);
    _insertNotification({
      type: 'review',
      message: `🔎 ${S(customerName) || 'A vehicle'} is ready for COO Review${by ? ` (by ${S(by)})` : ''}`,
      forRole: 'principal', bookingId, customerName: S(customerName),
    });
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function approveReview(bookingId, by) {
  try {
    const { error } = await supabase.from('bookings')
      .update({ review_status: 'approved', review_approved_at: new Date().toISOString(), reviewed_by: S(by) })
      .eq('id', bookingId);
    if (error) return failResp(error);
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function cancelBooking(bookingId) { return updateBookingStatus(bookingId, 'cancelled'); }
export async function deleteBooking(bookingId) {
  try {
    // Grab the calendar event id before the row is gone, then delete the row,
    // then remove the calendar event by id (no booking lookup needed).
    let calId = null;
    try {
      const { data } = await supabase.from('bookings').select('calendar_id').eq('id', bookingId).maybeSingle();
      calId = data && data.calendar_id;
    } catch (_) { /* ignore */ }
    const { error } = await supabase.from('bookings').delete().eq('id', bookingId);
    if (error) return failResp(error);
    clearAppointmentReminders(bookingId).catch(() => {});
    if (calId) _fireCalendar(null, 'delete', { calendarId: calId });
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function pushBookingToToday(bookingId) {
  const n = new Date();
  const today = n.getFullYear() + '-' + String(n.getMonth() + 1).padStart(2, '0') + '-' + String(n.getDate()).padStart(2, '0');
  const d = await updateBooking(bookingId, { date: today });
  if (d && d.success === false) throw new Error('Date update failed: ' + d.error);
  const s = await updateBookingStatus(bookingId, 'scheduled');
  if (s && s.success === false) throw new Error('Status reset failed: ' + s.error);
  return { success: true };
}

// ===========================================================================
// DEPOSIT / BALANCE — staff actions (the charge happens on the customer pay
// pages via the Vercel functions; these are the staff-side state changes).
// ===========================================================================
export async function requestDeposit(bookingId, amount) {
  try {
    const { error } = await supabase.from('bookings').update({ deposit_status: 'requested', deposit_amount: num(amount), deposit_requested_at: new Date().toISOString() }).eq('id', bookingId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function markDepositPaid(bookingId) {
  try {
    // Back-fill the amount when the booking never went through requestDeposit
    // (e.g. deposit collected on the Clover terminal, staff just logging it).
    // Mirrors gas/Deposits.js markDepositPaid — don't clobber a deliberate
    // non-zero amount; default to 100 to match the email/pay-page fallback.
    const { data: cur } = await supabase.from('bookings')
      .select('deposit_amount,deposit_status,customer_name,vehicle_desc,service').eq('id', bookingId).maybeSingle();
    if (cur && cur.deposit_status === 'paid') return okResp({}); // idempotent — don't re-write revenue
    const amount = Number(cur && cur.deposit_amount) > 0 ? Number(cur.deposit_amount) : 100;
    const { error } = await supabase.from('bookings')
      .update({ deposit_status: 'paid', deposit_paid_at: new Date().toISOString(), deposit_amount: amount }).eq('id', bookingId);
    if (error) return failResp(error);
    // Cash-basis revenue row, tagged with the customer name (mirrors the
    // deposit-charge path + gas _writeDepositRevenueRow). Idempotent by id.
    try { await _writeDepositRevenueRow(bookingId, amount, cur); } catch (_) {}
    return okResp({});
  } catch (e) { return failResp(e); }
}
// Insert/upsert the DEPOSIT-<id> revenue row. id keeps Sync Invoices' SO- orphan
// cleanup away from it; no file_id. The invoicer nets the deposit out of the
// eventual invoice (credit line), so deposit row + net invoice = full job total.
async function _writeDepositRevenueRow(bookingId, amount, b) {
  const cust = (b && b.customer_name) || '';
  const label = 'Deposit' + (cust ? ' from ' + cust : '') + (b && b.service ? ' — ' + b.service : '');
  const nowIso = new Date().toISOString();
  return supabase.from('invoices').upsert({
    id: 'DEPOSIT-' + bookingId, so_number: 'DEPOSIT-' + bookingId,
    date: nowIso.slice(0, 10), customer: cust, vehicle: (b && b.vehicle_desc) || '', service: label,
    subtotal: amount, tax: 0, total: amount, cc_fee: 0,
    file_id: null, file_name: '', booking_id: bookingId, imported_at: nowIso, is_quote: false,
  }, { onConflict: 'id' });
}
export async function markDepositRefunded(bookingId) {
  try {
    const { error } = await supabase.from('bookings').update({ deposit_status: 'refunded', deposit_refunded_at: new Date().toISOString() }).eq('id', bookingId);
    if (error) return failResp(error);
    // Back out the cash-basis revenue row — we no longer hold that money.
    try { await supabase.from('invoices').delete().eq('id', 'DEPOSIT-' + bookingId); } catch (_) {}
    return okResp({});
  } catch (e) { return failResp(e); }
}
// Owner one-tap Stripe refund of a paid deposit (server does the Stripe refund +
// flips the booking + removes the DEPOSIT- revenue row). A deposit with no Stripe
// payment on file (Clover-era / manual) comes back {noStripe:true} so the caller
// can offer to just mark it refunded instead.
export async function refundDeposit(bookingId) {
  try {
    const { data: sess } = await supabase.auth.getSession();
    const tok = sess && sess.session && sess.session.access_token;
    const res = await fetch('/api/invoicer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
      body: JSON.stringify({ action: 'refund-deposit', bookingId }),
    });
    const j = await res.json().catch(() => ({}));
    if (j && j.success) return okResp(j);
    const fr = failResp((j && j.error) || 'Refund failed');
    if (j && j.noStripe) fr.noStripe = true;
    return fr;
  } catch (e) { return failResp(e.message); }
}
export async function requestBalance(bookingId) {
  try {
    const { error } = await supabase.from('bookings').update({ balance_status: 'requested', balance_requested_at: new Date().toISOString() }).eq('id', bookingId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function markBalancePaid(bookingId, paidAmount, paymentRef) {
  try {
    const { error } = await supabase.from('bookings').update({ balance_status: 'paid', balance_paid_at: new Date().toISOString(), balance_payment_ref: paymentRef || 'manual' }).eq('id', bookingId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// CUSTOMERS + VEHICLES
// ===========================================================================
// 🚨 PostgREST caps a single response at 1000 rows (Supabase `max-rows`), and
// it does so SILENTLY — no error, no flag, just a short array. Any read that
// ENUMERATES a whole table is therefore correct only until that table passes
// 1000 rows, at which point it quietly starts lying. The OTTO import took
// customers to 3,773 and vehicles to 3,638 overnight, and the Customers tab
// began reporting exactly 1000 — the number that gives this away.
//
// `build()` must return a FRESH query each call: a PostgrestFilterBuilder is
// a thenable that cannot be re-executed once awaited.
//
// Pass a stable ORDER in build(), or paging is not guaranteed to be a
// partition — rows can repeat or vanish between pages.
async function selectAllRows(build) {
  const PAGE = 1000;
  // 🚨 The first page goes out ALONE. Almost every table this is used on is
  // well under 1,000 rows, and firing a speculative wave at all of them
  // turned a single request into four — three of them returning nothing —
  // on every paged read in the app. Only a FULL first page is evidence that
  // there is more to fetch.
  const first = await build().range(0, PAGE - 1);
  if (first.error) return { data: null, error: first.error };
  let out = first.data || [];
  if (out.length < PAGE) return { data: out, error: null };
  // Past that point the table genuinely is big, so latency dominates and
  // pages go out in concurrent waves rather than one at a time: 3,773
  // customers costs one more round trip for the rest, not three stacked
  // end to end.
  const WAVE = 4;
  let from = PAGE;
  for (;;) {
    const reqs = [];
    for (let k = 0; k < WAVE; k++) {
      const start = from + k * PAGE;
      reqs.push(build().range(start, start + PAGE - 1));
    }
    // Ranges past the end simply come back empty, which is what ends the loop.
    const results = await Promise.all(reqs);
    for (const r of results) {
      if (r.error) return { data: null, error: r.error };
    }
    let short = false;
    for (const r of results) {
      const batch = r.data || [];
      out = out.concat(batch);
      if (batch.length < PAGE) { short = true; break; }
    }
    if (short) break;
    from += WAVE * PAGE;
  }
  return { data: out, error: null };
}

// Cached front door. The uncached version below is the actual fetch.
//
// Key matches what peekCache({api:'getAllCustomers'}) already looks for in
// Dashboard, so the warm-paint path that has been silently missing since this
// endpoint moved to Supabase works again — and the seven call sites now share
// one in-flight request instead of each doing its own full-table read.
export async function getAllCustomers() {
  return cachedRead({ api: 'getAllCustomers' }, _LONG_TTL, _fetchAllCustomers);
}

// ── Server-side customer search ────────────────────────────────────────────
// 🚨 The Customers tab used to hold every row in memory to render fifty of
// them. Everything below asks the database for ONE PAGE and ONE COUNT, so the
// client never holds the table — the only fix that keeps working as the list
// grows. `getAllCustomers` survives for the bulk paths that genuinely need
// everyone (a blast, the booking picker); it is no longer how a list renders.

// One source of truth for the segment date boundaries. `customer_stats` takes
// these as PARAMETERS rather than computing its own, so the tile counts and
// the list filters are driven by identical timestamps. A tile whose count the
// list cannot reproduce is worse than no tile, and independently-computed
// "now" values drift at every month boundary.
export function customerSegmentBounds(now) {
  const n = now || new Date();
  return {
    new90From: new Date(n.getFullYear(), n.getMonth(), n.getDate() - 90).toISOString(),
    monthStart: new Date(n.getFullYear(), n.getMonth(), 1).toISOString(),
    // ⚠️ _monthsBetween is a CALENDAR-month difference, not 90 days: "3+ months
    // stale" means last seen before the 1st of two months ago. Off-by-one here
    // silently mis-sizes the win-back audience.
    staleBefore: new Date(n.getFullYear(), n.getMonth() - 2, 1).toISOString(),
  };
}

// Mirrors the `pred` of each entry in customerSegments() (Dashboard.jsx) and
// the FILTER clauses in customer_stats(). All three must move together.
function _applyCustomerSegment(q, segment, b) {
  switch (segment) {
    case 'new90':     return q.gte('first_seen', b.new90From);
    case 'returning': return q.gte('visit_count', 2);
    case 'active':    return q.gte('last_seen', b.monthStart);
    case 'stale':     return q.lt('last_seen', b.staleBefore).gt('visit_count', 0);
    case 'v0':        return q.eq('visit_count', 0);
    case 'v1':        return q.eq('visit_count', 1);
    case 'v2':        return q.eq('visit_count', 2);
    case 'v3':        return q.gte('visit_count', 3);
    case 'redflag':   return q.eq('red_flag', true);
    // Messaging reach (2026-10-10). Blocked = unreachable on at least one
    // channel: texts (opt_out — replied STOP) or email (unsubscribed, or a hard
    // bounce / spam complaint, which send-email.js suppresses). Must match the
    // clauses in customer_stats() and the predicates in customerSegments().
    case 'blocked':      return q.or('opt_out.eq.true,email_opt_out.eq.true,email_bounce_type.in.(hard,complaint)');
    case 'msgok':        return q.eq('opt_out', false).eq('email_opt_out', false).or('email_bounce_type.is.null,email_bounce_type.not.in.(hard,complaint)');
    case 'smsblocked':   return q.eq('opt_out', true);
    case 'emailblocked': return q.or('email_opt_out.eq.true,email_bounce_type.in.(hard,complaint)');
    default:          return q;
  }
}

// ⚠️ A stable tiebreaker is NOT optional. Postgres gives no row order for ties,
// so paging an ordered-by-last_seen list where 3,009 rows share a NULL would
// repeat some customers and skip others between pages. `id` breaks every tie.
// The vehicle sorts read `vehicle_year` / `vehicle_sort`, denormalised onto the
// customer by 20260820190000 — PostgREST can't order a parent by a child table,
// and a per-row subquery would undo the index paging this tab depends on.
// `nullsFirst: false` is not cosmetic: it's what makes each ORDER BY match its
// index exactly, and it keeps customers with no car on file at the BOTTOM of
// both directions rather than floating to the top of "oldest first".
function _applyCustomerSort(q, sort) {
  switch (sort) {
    case 'visits': return q.order('visit_count', { ascending: false }).order('id');
    case 'spend':  return q.order('total_spend', { ascending: false }).order('id');
    case 'recent': return q.order('last_seen', { ascending: false, nullsFirst: false }).order('id');
    case 'vehicle_new': return q.order('vehicle_year', { ascending: false, nullsFirst: false }).order('id');
    case 'vehicle_old': return q.order('vehicle_year', { ascending: true, nullsFirst: false }).order('id');
    case 'make':   return q.order('vehicle_sort', { ascending: true, nullsFirst: false }).order('id');
    default:       return q.order('last_name').order('first_name').order('id');
  }
}

export async function searchCustomers(opts) {
  const o = opts || {};
  const page = Math.max(0, Number(o.page) || 0);
  const pageSize = Number(o.pageSize) > 0 ? Number(o.pageSize) : 50;
  const b = o.bounds || customerSegmentBounds();
  const term = String(o.query || '').trim();
  try {
    // A vehicle match is a match on its CUSTOMER, so those ids are resolved
    // first and folded into the SAME `or` group as the name and phone clauses.
    // PostgREST ANDs repeated `or` params, so two groups would mean "name
    // matches AND vehicle matches" — silently returning almost nothing.
    let vehIds = [];
    if (term.length >= 2) {
      const v = await supabase.from('vehicles').select('customer_id')
        .ilike('search_text', '%' + term.toLowerCase() + '%').limit(400);
      if (!v.error) vehIds = Array.from(new Set((v.data || []).map((r) => r.customer_id).filter(Boolean)));
    }
    const build = (countMode) => {
      let q = countMode
        ? supabase.from('customers').select(CUSTOMER_COLS, { count: 'exact' })
        : supabase.from('customers').select(CUSTOMER_COLS);
      q = _applyCustomerSegment(q, o.segment, b);
      // Reachable-by-text only. Used with pageSize 1 purely to read the COUNT,
      // so the win-back bar can size an audience without fetching it.
      if (o.textable) q = q.not('phone', 'is', null).neq('phone', '').eq('opt_out', false);
      // An explicit id list (the "upcoming" sort hands one down) narrows first.
      if (Array.isArray(o.ids)) q = q.in('id', o.ids.length ? o.ids : ['__none__']);
      if (term) {
        // `or` is a comma/paren-delimited grammar, so those characters in a
        // typed term would corrupt the clause rather than fail loudly.
        const esc = term.replace(/[%,()\\]/g, ' ').toLowerCase();
        const digits = term.replace(/\D/g, '');
        const clauses = [];
        if (esc.trim()) clauses.push('search_name.ilike.%' + esc + '%');
        if (digits.length >= 3) clauses.push('phone_digits.like.%' + digits + '%');
        if (vehIds.length) clauses.push('id.in.(' + vehIds.join(',') + ')');
        if (clauses.length) q = q.or(clauses.join(','));
      }
      return _applyCustomerSort(q, o.sort);
    };

    let rows, total;
    if (o.all) {
      // Bulk path (a win-back send needs the whole segment, not the page the
      // operator happens to be looking at).
      const r = await selectAllRows(() => build(false));
      if (r.error) return failResp(r.error);
      rows = r.data || []; total = rows.length;
    } else {
      const from = page * pageSize;
      const r = await build(true).range(from, from + pageSize - 1);
      if (r.error) return failResp(r.error);
      rows = r.data || []; total = typeof r.count === 'number' ? r.count : rows.length;
    }

    // Vehicles and member badges for THIS PAGE only — the join that used to
    // pull 3,133 vehicle rows now pulls the handful the page actually shows.
    const ids = rows.map((c) => c.id).filter(Boolean);
    const [veh, mem] = ids.length ? await Promise.all([
      supabase.from('vehicles').select('*').in('customer_id', ids),
      supabase.from('members').select('id,customer_id,status,plan').in('customer_id', ids),
    ]) : [{ data: [] }, { data: [] }];
    const byCustomer = {};
    ((veh && veh.error ? [] : (veh && veh.data) || [])).forEach((v) => { (byCustomer[v.customer_id] ||= []).push(mapVehicle(v)); });
    const memberByCustomer = {};
    ((mem && mem.error ? [] : (mem && mem.data) || [])).forEach((m) => { if (m.customer_id) memberByCustomer[m.customer_id] = m; });

    const results = rows.map((c) => {
      const m = memberByCustomer[c.id];
      return {
        ...mapCustomer(c, byCustomer[c.id] || []),
        memberId: m ? S(m.id) : '',
        memberStatus: m ? S(m.status) : '',
        memberPlan: m ? S(m.plan) : '',
      };
    });
    return okResp({ results, total });
  } catch (e) { return failResp(e); }
}

// The segment tiles, in one round trip. Counting in SQL is what lets the tab
// show "3,015 customers" without ever fetching 3,015 customers.
export async function customerStats(bounds) {
  const b = bounds || customerSegmentBounds();
  try {
    const { data, error } = await supabase.rpc('customer_stats', {
      p_new90_from: b.new90From,
      p_month_start: b.monthStart,
      p_stale_before: b.staleBefore,
    });
    if (error) return failResp(error);
    return okResp({ stats: data || {} });
  } catch (e) { return failResp(e); }
}

async function _fetchAllCustomers() {
  try {
    // Membership now lives in the `members` table. Attach a light member summary
    // to each customer (status + plan) so the Customers tab can show a badge and
    // the "Add as member" action knows whether they're already enrolled. The full
    // membership system (visits, spend, Stripe) lives in the Membership tab.
    const [cust, veh, mem] = await Promise.all([
      selectAllRows(() => supabase.from('customers').select(CUSTOMER_COLS).order('id')),
      selectAllRows(() => supabase.from('vehicles').select('*').order('make').order('model').order('id')),
      supabase.from('members').select('id,customer_id,status,plan'),
    ]);
    if (cust.error) return failResp(cust.error);
    if (veh.error) return failResp(veh.error);
    const byCustomer = {};
    (veh.data || []).forEach((v) => { (byCustomer[v.customer_id] ||= []).push(mapVehicle(v)); });
    // Member map is best-effort — a read failure just omits the badge.
    const memberByCustomer = {};
    (mem.error ? [] : (mem.data || [])).forEach((m) => { if (m.customer_id) memberByCustomer[m.customer_id] = m; });
    const results = (cust.data || []).map((c) => {
      const m = memberByCustomer[c.id];
      return {
        ...mapCustomer(c, byCustomer[c.id] || []),
        memberId: m ? S(m.id) : '',
        memberStatus: m ? S(m.status) : '',   // '' = not a member
        memberPlan: m ? S(m.plan) : '',
      };
    });
    return okResp({ results });
  } catch (e) { return failResp(e); }
}
export async function updateCustomer(customerId, data) {
  // Cached read invalidated at the write, not after it — a stale customer
  // list is the failure this cache would otherwise introduce.
  bustClientCache('getAllCustomers');
  try {
    const row = {};
    const set = (k, v) => { if (v !== undefined) row[k] = v; };
    set('first_name', data.firstName); set('last_name', data.lastName);
    set('full_name', data.fullName); set('email', data.email); set('phone', data.phone);
    // Additional phone numbers (the primary stays in `phone`). Normalize to
    // [{number,label}] and drop blank rows so we never store empty entries.
    if (data.phones !== undefined) {
      row.phones = (Array.isArray(data.phones) ? data.phones : [])
        .map((p) => ({ number: String((p && p.number) || '').trim(), label: String((p && p.label) || '').trim() }))
        .filter((p) => p.number);
    }
    set('notes', data.notes);
    // `.select('id')` so we can tell whether the update ACTUALLY applied. A stale/
    // expired JWT makes auth_role() null, so the customers_upd RLS policy silently
    // matches 0 rows and returns NO error — which used to look like a successful
    // save that never persisted ("sometimes data doesn't save"). On a 0-row result
    // we refresh the session once and retry; if it still writes nothing, surface it.
    let { data: updated, error } = await supabase.from('customers').update(row).eq('id', customerId).select('id');
    if (!error && (!updated || updated.length === 0)) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      ({ data: updated, error } = await supabase.from('customers').update(row).eq('id', customerId).select('id'));
    }
    if (error) return failResp(error);
    if (!updated || updated.length === 0) return failResp('Save didn’t go through — your session may have expired. Reopen the app and try again.');
    _bustPhoneMap();
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function upsertCustomer(data) {
  // Cached read invalidated at the write, not after it — a stale customer
  // list is the failure this cache would otherwise introduce.
  bustClientCache('getAllCustomers');
  try {
    const { data: res, error } = await supabase.rpc('upsert_customer', { p: data });
    if (error) return failResp(error);
    _bustPhoneMap();
    return okResp({ customerId: res.id, created: res.created });
  } catch (e) { return failResp(e); }
}
export async function deleteCustomer(customerId) {
  // Cached read invalidated at the write, not after it — a stale customer
  // list is the failure this cache would otherwise introduce.
  bustClientCache('getAllCustomers');
  try {
    const { error } = await supabase.from('customers').delete().eq('id', customerId);
    if (error) return failResp(error);
    _bustPhoneMap();
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function addVehicle(customerId, data) {
  // Cached read invalidated at the write, not after it — a stale customer
  // list is the failure this cache would otherwise introduce.
  bustClientCache('getAllCustomers');
  try {
    const row = { id: 'V-' + genIdSuffix(), customer_id: customerId, year: data.year ? Number(data.year) : null,
      make: data.make, model: data.model, trim: data.trim, color: data.color, vin: data.vin, size: data.size };
    const { error } = await supabase.from('vehicles').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
// Canonical vehicle label — the exact format used everywhere a vehicle desc is
// built from a record ([year make model], see Booking.jsx / Dashboard.jsx).
function _vehDescStr(v) { return [v && v.year, v && v.make, v && v.model].filter(Boolean).join(' ').trim(); }
// Literal (non-regex) global replace — used to swap the old vehicle mention for
// the new one inside already-composed email/reminder text.
function _replaceAllLiteral(str, find, repl) {
  if (!find || String(find) === String(repl)) return String(str || '');
  return String(str || '').split(String(find)).join(String(repl));
}

export async function updateVehicle(vehicleId, data) {
  // Cached read invalidated at the write, not after it — a stale customer
  // list is the failure this cache would otherwise introduce.
  bustClientCache('getAllCustomers');
  try {
    // Read the row BEFORE the write so we know the old description/size and the
    // owning customer — needed to propagate the change into denormalized copies.
    let before = null;
    try {
      const { data: b } = await supabase.from('vehicles').select('customer_id,year,make,model,size').eq('id', vehicleId).maybeSingle();
      before = b || null;
    } catch (_) {}
    const row = {};
    const set = (k, v) => { if (v !== undefined) row[k] = v; };
    if (data.year !== undefined) row.year = data.year === '' ? null : Number(data.year);
    set('make', data.make); set('model', data.model); set('trim', data.trim);
    set('color', data.color); set('vin', data.vin); set('size', data.size);
    const { error } = await supabase.from('vehicles').update(row).eq('id', vehicleId);
    if (error) return failResp(error);
    // Fan the change out to bookings / Outbox drafts / reminders / invoicer
    // drafts so nothing keeps showing (or emailing) the old car. Best-effort:
    // a propagation hiccup never fails the vehicle save itself.
    try { await _propagateVehicleChange(vehicleId, before, row); } catch (_) {}
    return okResp({});
  } catch (e) { return failResp(e); }
}

// Push an edited vehicle's description/size into every place a snapshot of it
// was stored. Keyed off vehicle_id (bookings, reminders) and, for the invoicer's
// service-role draft store, off customer + old description.
async function _propagateVehicleChange(vehicleId, before, row) {
  if (!vehicleId) return;
  const merged = { ...(before || {}), ...row };
  const newDesc = _vehDescStr(merged);
  const oldDesc = _vehDescStr(before || {});
  const sizeChanged = row.size !== undefined && String(row.size || '') !== String((before && before.size) || '');
  const newSize = merged.size || null;
  const customerId = before && before.customer_id;
  if (newDesc === oldDesc && !sizeChanged) return; // nothing meaningful changed
  const TERMINAL = ['complete', 'delivered', 'cancelled', 'deleted'];

  // 1) Bookings for this vehicle. Overwrite the label everywhere (same car,
  //    corrected text); update the booked size only on still-active bookings so
  //    finalized/charged history keeps its size. Keep each booking's old desc
  //    for the precise in-place swap in its drafts below.
  let bookings = [];
  try {
    const { data } = await supabase.from('bookings').select('id,vehicle_desc,status').eq('vehicle_id', vehicleId);
    bookings = data || [];
  } catch (_) {}
  for (const b of bookings) {
    const upd = {};
    if (newDesc !== (b.vehicle_desc || '')) upd.vehicle_desc = newDesc;
    if (sizeChanged && !TERMINAL.includes(b.status)) upd.vehicle_size = newSize;
    if (Object.keys(upd).length) { try { await supabase.from('bookings').update(upd).eq('id', b.id); } catch (_) {} }
  }

  // 2) Outbox — replace the vehicle mention in PENDING drafts of those bookings,
  //    keeping any hand edits (only the vehicle words change).
  const bookingIds = bookings.map((b) => b.id).filter(Boolean);
  if (bookingIds.length && newDesc) {
    let drafts = [];
    try {
      const { data } = await supabase.from('email_drafts').select('id,booking_id,subject,body').in('booking_id', bookingIds).eq('status', 'pending');
      drafts = data || [];
    } catch (_) {}
    const oldByBooking = {};
    bookings.forEach((b) => { oldByBooking[b.id] = b.vehicle_desc || oldDesc; });
    for (const d of drafts) {
      const from = oldByBooking[d.booking_id] || oldDesc;
      if (!from || from === newDesc) continue;
      const subject = _replaceAllLiteral(d.subject, from, newDesc);
      const body = _replaceAllLiteral(d.body, from, newDesc);
      if (subject !== (d.subject || '') || body !== (d.body || '')) {
        try { await supabase.from('email_drafts').update({ subject, body }).eq('id', d.id); } catch (_) {}
      }
    }
  }

  // 3) Reminders keyed to this vehicle — refresh the stored desc + swap mentions
  //    in the title/notes free text (pending rows only).
  try {
    const { data: rems } = await supabase.from('reminders').select('id,vehicle_desc,title,notes,status').eq('vehicle_id', vehicleId);
    for (const r of (rems || [])) {
      if (r.status && r.status !== 'pending') continue;
      const from = r.vehicle_desc || oldDesc;
      const upd = { vehicle_desc: newDesc };
      if (from && from !== newDesc) {
        if (r.title) upd.title = _replaceAllLiteral(r.title, from, newDesc);
        if (r.notes) upd.notes = _replaceAllLiteral(r.notes, from, newDesc);
      }
      try { await supabase.from('reminders').update(upd).eq('id', r.id); } catch (_) {}
    }
  } catch (_) {}

  // 4) Invoicer drafts live in the service-role invoice_drafts store — hand off
  //    to /api/invoicer to update any un-filed drafts that match this vehicle.
  if (customerId && (oldDesc || newDesc)) {
    try {
      await fetch('/api/invoicer', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'propagate-vehicle', customerId, oldDesc, newDesc,
          year: merged.year != null ? String(merged.year) : '', make: merged.make || '', model: merged.model || '',
          size: sizeChanged ? (newSize || '') : '',
        }),
      });
    } catch (_) {}
  }

  // Invalidate the client caches for the views that just changed so the
  // Schedule, Outbox, and Reminders repaint with the corrected vehicle on the
  // next read instead of waiting out the TTL. (dynamic import mirrors the
  // existing apiLegacy usage below; avoids a static import cycle.)
  try {
    const leg = await import('./apiLegacy');
    ['getBookings', 'getBookingsRange', 'getDashboardOverview', 'getEmailDrafts', 'getReminders', 'getVipInbox']
      .forEach((k) => { try { leg.bustClientCache(k); } catch (_) {} });
  } catch (_) {}
}
export async function deleteVehicle(vehicleId) {
  // Cached read invalidated at the write, not after it — a stale customer
  // list is the failure this cache would otherwise introduce.
  bustClientCache('getAllCustomers');
  try {
    const { error } = await supabase.from('vehicles').delete().eq('id', vehicleId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// STOCK
// ===========================================================================
export async function getStock() {
  try {
    const { data, error } = await supabase.from('stock').select('*');
    if (error) return failResp(error);
    return okResp({ items: (data || []).map(mapStock) });
  } catch (e) { return failResp(e); }
}
export async function updateStockLevel(stockId, qty) {
  try {
    const { error } = await supabase.from('stock').update({ current_qty: num(qty), last_updated: new Date().toISOString() }).eq('id', stockId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function saveStockItem(data) {
  try {
    const row = {
      id: data.id || 'S-' + genIdSuffix(), category: data.category, name: data.name,
      unit: data.unit, max_qty: num(data.maxQty), current_qty: num(data.currentQty),
      low_threshold: num(data.lowThreshold), notes: data.notes, last_updated: new Date().toISOString(),
    };
    const { error } = await supabase.from('stock').upsert(row, { onConflict: 'id' });
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
// Seed one shop's stock catalog from another's.
//
// Adding stock to a second location already works — the table is site-scoped
// and an insert made while working there is stamped with that site. COPYING is
// what cannot be done from the browser: stock_sel is in_active_site(site), so a
// session working in Albany cannot read Kingston's rows at all. That isolation
// is correct, so the copy runs server-side under an explicit allow-list check
// rather than being loosened here. Catalog only — quantities start at 0.
export async function copyStockToSite(from, to) {
  try {
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    const res = await fetch('/api/invoicer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
      body: JSON.stringify({ action: 'stock-copy-site', from, to }),
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp({ copied: j.copied, skipped: j.skipped, total: j.total }) : failResp((j && j.error) || 'Copy failed');
  } catch (e) { return failResp(e); }
}
export async function deleteStockItem(stockId) {
  try {
    const { error } = await supabase.from('stock').delete().eq('id', stockId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// Append one row to the stock change log. Best-effort — the caller fires this
// after the stock level write lands and never blocks/rolls back on failure.
// delta is in eighths (signed): negative = consumed, positive = restocked.
// kind: 'use' | 'restock' | 'adjust' (adjust = edit-form correction, excluded
// from the consumption-rate math so typo fixes don't skew averages).
export async function logStockChange(entry) {
  try {
    const row = {
      id: 'SL-' + genIdSuffix(),
      stock_id: entry.stockId,
      delta_eighths: num(entry.deltaEighths),
      eighths_after: num(entry.eighthsAfter),
      container_volume: entry.containerVolume || '',
      kind: entry.kind || (num(entry.deltaEighths) < 0 ? 'use' : 'restock'),
    };
    const { error } = await supabase.from('stock_log').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}

// Read the change log for one stock item since an ISO timestamp, oldest first.
export async function getStockLog(stockId, sinceISO) {
  try {
    let q = supabase.from('stock_log').select('*').eq('stock_id', stockId);
    if (sinceISO) q = q.gte('ts', sinceISO);
    const { data, error } = await q.order('ts', { ascending: true });
    if (error) return failResp(error);
    return okResp({ log: data || [] });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// CHECKLISTS
// ===========================================================================
export async function getChecklist(bookingId) {
  try {
    const { data, error } = await supabase.from('checklist_tasks').select('*').eq('booking_id', bookingId).order('sort_order');
    if (error) return failResp(error);
    return okResp({ tasks: (data || []).map(mapChecklistTask) });
  } catch (e) { return failResp(e); }
}
export async function tickTask(taskId, completedBy) {
  try {
    const { error } = await supabase.from('checklist_tasks').update({ completed: true, completed_at: new Date().toISOString(), completed_by: completedBy || 'detailer' }).eq('id', taskId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function untickTask(taskId) {
  try {
    const { error } = await supabase.from('checklist_tasks').update({ completed: false, completed_at: null, completed_by: null }).eq('id', taskId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
// One vehicle, by id — the mechanical worksheet needs the car's own facts
// (odometer, oil spec), which no booking read carries. select('*') so a column
// added to vehicles later reaches mapVehicle without a second edit here.
//
// ⚠️ Returns `vehicle: null` rather than an error when the booking has no
// vehicle_id. That is a real and common state (~17 of 139 bookings historically
// carry none), and the worksheet says so plainly instead of failing.
export async function getVehicleById(vehicleId) {
  try {
    if (!vehicleId) return okResp({ vehicle: null });
    const { data, error } = await supabase.from('vehicles').select('*').eq('id', vehicleId).maybeSingle();
    if (error) return failResp(error);
    return okResp({ vehicle: data ? { ...mapVehicle(data), vin: S(data.vin), customerId: S(data.customer_id) } : null });
  } catch (e) { return failResp(e); }
}

// Answer a CONDITION CHECK line on a mechanical worksheet: pass | fail | na,
// with the measurement behind it. `state: ''` clears the answer.
//
// 🚨 Deliberately does NOT touch `completed`. A check line's doneness is its
// state (see isTaskDone in lib/checklistState) — writing `completed` here too
// would give one line two sources of truth that could disagree.
//
// ⚠️ This is a CONDITION CHECK, not an inspection. 3C is not a New York State
// inspection station; nothing built on this may be worded as one.
export async function setTaskState(taskId, state, reading, by) {
  try {
    if (!taskId) return failResp('Missing taskId');
    const st = state ? String(state) : null;
    if (st && !['pass', 'fail', 'na'].includes(st)) return failResp('Unknown check state');
    const patch = {
      state: st,
      reading: reading == null ? undefined : (String(reading).trim() || null),
      // Who answered it, reusing the column the tick path already stamps so the
      // card can say "answered by" without a second column.
      completed_by: st ? (by || 'detailer') : null,
      completed_at: st ? new Date().toISOString() : null,
    };
    if (patch.reading === undefined) delete patch.reading;
    const { error } = await supabase.from('checklist_tasks').update(patch).eq('id', taskId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// The odometer + oil spec on a vehicle, written from the bay.
//
// 🚨 Through the RPC, never a table write: `vehicles_upd` is owner/principal
// only and auth_role() folds automotive-supervisor into 'detailer', so a direct
// update matches ZERO ROWS AND RETURNS NO ERROR (verified). The function is the
// only door and re-checks the role and the shop itself. Pass null to leave a
// field alone, '' to clear it.
export async function setVehicleServiceFacts(vehicleId, { odometer, oilType, oilQty, oilFilter, by } = {}) {
  try {
    if (!vehicleId) return failResp('Missing vehicleId');
    const { data, error } = await supabase.rpc('set_vehicle_service_facts', {
      p_vehicle_id: String(vehicleId),
      p_odometer: odometer == null || odometer === '' ? null : Math.round(Number(odometer)),
      p_oil_type: oilType == null ? null : String(oilType),
      p_oil_qty: oilQty == null || oilQty === '' ? null : Number(oilQty),
      p_oil_filter: oilFilter == null ? null : String(oilFilter),
      p_by: by || null,
    });
    if (error) return failResp(error);
    const row = (Array.isArray(data) ? data[0] : data) || {};
    bustClientCache('getAllCustomers');
    bustClientCache('searchCustomers');
    return okResp({
      odometer: row.odometer == null ? null : Number(row.odometer),
      odometerAt: row.odometer_at || '', odometerBy: S(row.odometer_by),
      oilType: S(row.oil_type), oilQty: row.oil_qty == null ? null : Number(row.oil_qty),
      oilFilter: S(row.oil_filter),
    });
  } catch (e) { return failResp(e); }
}

// ── Mechanical service catalog ─────────────────────────────────────────────
// The templates themselves. Owner/principal write; the floor reads, so the
// Automotive Supervisor can see the worksheet its own jobs are built from.
const mapMechService = (r) => ({
  id: r.id, name: S(r.name),
  hours: r.hours == null ? null : Number(r.hours),
  price: r.price == null ? null : Number(r.price),
  // [{label, kind}] in order. Normalised on read so a hand-edited row can't
  // put a bad `kind` on a card.
  tasks: (Array.isArray(r.tasks) ? r.tasks : []).map((t) => ({
    label: S(t && t.label), kind: (t && t.kind) === 'check' ? 'check' : 'step',
  })).filter((t) => t.label),
  active: r.active !== false, sortOrder: Number(r.sort_order) || 0,
  site: S(r.site), updatedAt: isoTs(r.updated_at), updatedBy: S(r.updated_by),
});

export async function listMechServices({ includeInactive } = {}) {
  try {
    let q = supabase.from('mech_services').select('*');
    if (!includeInactive) q = q.eq('active', true);
    const { data, error } = await q.order('sort_order').order('name');
    if (error) return failResp(error);
    return okResp({ services: (data || []).map(mapMechService) });
  } catch (e) { return failResp(e); }
}

export async function saveMechService(svc) {
  try {
    const s = svc || {};
    const name = String(s.name || '').trim();
    if (!name) return failResp('A service name is required');
    const tasks = (Array.isArray(s.tasks) ? s.tasks : [])
      .map((t) => ({ label: String((t && t.label) || '').trim(), kind: (t && t.kind) === 'check' ? 'check' : 'step' }))
      .filter((t) => t.label);
    const row = {
      name,
      hours: s.hours === '' || s.hours == null ? null : Number(s.hours),
      price: s.price === '' || s.price == null ? null : Number(s.price),
      tasks,
      active: s.active !== false,
      sort_order: Number(s.sortOrder) || 0,
      updated_at: new Date().toISOString(),
      updated_by: s.by || null,
    };
    if (s.id) {
      // 🚨 .select() for the row count: an RLS-denied UPDATE returns 0 rows and
      // NO error, so without this a lapsed session reads as a successful save
      // and the template silently reverts. Same guard as updateCustomer.
      const { data, error } = await supabase.from('mech_services').update(row).eq('id', s.id).select('id');
      if (error) return failResp(_mechNameClash(error) || error);
      if (!data || !data.length) {
        await supabase.auth.refreshSession();
        const retry = await supabase.from('mech_services').update(row).eq('id', s.id).select('id');
        if (retry.error) return failResp(_mechNameClash(retry.error) || retry.error);
        if (!retry.data || !retry.data.length) return failResp("That didn't save — your session may have expired. Reopen the app and try again.");
      }
      bustClientCache('listMechServices');
      return okResp({ id: s.id });
    }
    const id = 'MS-' + genIdSuffix();
    const { error } = await supabase.from('mech_services').insert({ id, ...row });
    if (error) return failResp(_mechNameClash(error) || error);
    bustClientCache('listMechServices');
    return okResp({ id });
  } catch (e) { return failResp(e); }
}

// The unique index exists so two services can't share a name — the picker is the
// only place a service is identified, and two identical entries there are
// indistinguishable. Turn the raw violation into something readable.
function _mechNameClash(error) {
  const m = String((error && error.message) || '');
  if (/mech_services_name_site_idx|duplicate key/i.test(m)) return 'A mechanical service with that name already exists at this shop.';
  return null;
}

export async function deleteMechService(id) {
  try {
    if (!id) return failResp('Missing id');
    const { error } = await supabase.from('mech_services').delete().eq('id', id);
    if (error) return failResp(error);
    bustClientCache('listMechServices');
    return okResp({});
  } catch (e) { return failResp(e); }
}

export async function addCustomTask(bookingId, task, addedBy) {
  try {
    const { data: maxRow } = await supabase.from('checklist_tasks').select('sort_order').eq('booking_id', bookingId).order('sort_order', { ascending: false }).limit(1);
    const nextOrder = (maxRow && maxRow[0] ? Number(maxRow[0].sort_order) || 0 : 0) + 1;
    const row = { id: 'CT-' + genIdSuffix(), booking_id: bookingId, task, sort_order: nextOrder, is_final: false, completed: false, custom: true };
    const { error } = await supabase.from('checklist_tasks').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
export async function removeChecklistTask(taskId) {
  try {
    const { data: row } = await supabase.from('checklist_tasks').select('*').eq('id', taskId).maybeSingle();
    const { error } = await supabase.from('checklist_tasks').delete().eq('id', taskId);
    if (error) return failResp(error);
    if (row) await _toTrash('checklist_task', taskId, row, row.task || 'Checklist task', null);
    return okResp({});
  } catch (e) { return failResp(e); }
}
// Re-sync a booking's checklist after its service changes. Keeps completed +
// custom tasks, drops uncompleted tasks no longer in the new service, and adds
// the new service's tasks. (Supabase checklists have no wash/clean dividers, so
// this is simpler than the GAS version.)
export async function amendChecklist(bookingId, service) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    // Panel-aware: rebuild from the booking's vehicle so a coating/correction
    // service change re-derives the right panels for that body style.
    let veh = {};
    try { const b = await _bookingRow(bookingId); if (b) veh = { desc: b.vehicle_desc, size: b.vehicle_size }; } catch (_) {}
    const target = tasksForServiceAndVehicle(service, veh);
    const targetSet = new Set(target);
    const { data: rows, error } = await supabase.from('checklist_tasks').select('*').eq('booking_id', bookingId).order('sort_order');
    if (error) return failResp(error);
    const existing = rows || [];
    const done = (r) => r.completed === true || r.completed === 'TRUE';
    const existingNames = new Set(existing.map((r) => r.task));

    // Drop uncompleted, non-custom tasks that aren't in the new service.
    const toDelete = existing.filter((r) => !r.custom && !done(r) && !targetSet.has(r.task)).map((r) => r.id);
    if (toDelete.length) { const d = await supabase.from('checklist_tasks').delete().in('id', toDelete); if (d.error) return failResp(d.error); }

    // Append the new service's tasks that aren't already on the checklist.
    let maxOrder = existing.reduce((m, r) => Math.max(m, Number(r.sort_order) || 0), 0);
    const fresh = target.filter((t) => !existingNames.has(t));
    if (fresh.length) {
      const ins = fresh.map((t, i) => ({ id: 'CT-' + genIdSuffix(), booking_id: bookingId, task: t, sort_order: maxOrder + i + 1, is_final: false, completed: false, custom: false }));
      const a = await supabase.from('checklist_tasks').insert(ins);
      if (a.error) return failResp(a.error);
    }
    return okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// TASKS (role to-dos)
// ===========================================================================
export async function listTasks({ status, scope } = {}) {
  try {
    let q = supabase.from('tasks').select('*');
    if (status) q = q.eq('status', status);
    const { data, error } = await q;
    if (error) return failResp(error);
    const rank = { high: 0, medium: 1, low: 2 };
    const out = (data || []).map(mapTask).sort((a, b) => {
      if (a.status !== b.status) return a.status === 'open' ? -1 : 1;
      if (a.dueDate && b.dueDate && a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;
      if (a.dueDate && !b.dueDate) return -1;
      if (!a.dueDate && b.dueDate) return 1;
      return (rank[a.priority] ?? 1) - (rank[b.priority] ?? 1);
    });
    return okResp({ tasks: out });
  } catch (e) { return failResp(e); }
}
function taskToRow(d) {
  const row = {};
  const set = (k, v) => { if (v !== undefined) row[k] = v; };
  set('assigned_to', d.assignedTo); set('assigned_by', d.assignedBy);
  set('title', d.title); set('notes', d.notes); set('priority', d.priority);
  if (d.dueDate !== undefined) row.due_date = d.dueDate || null;
  return row;
}
export async function addTask(data) {
  try {
    const row = { id: 'T-' + genIdSuffix(), status: 'open', created_at: new Date().toISOString(), ...taskToRow(data) };
    const { error } = await supabase.from('tasks').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
export async function updateTask(data) {
  try {
    const { error } = await supabase.from('tasks').update(taskToRow(data)).eq('id', data.id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
// Insert a role-scoped notification (bell + NotificationsAlert pick it up via
// getNotifications(role)). Best-effort — a notify failure never blocks the
// action that triggered it. RLS allows any role to insert.
async function _insertNotification({ type, message, forRole, bookingId, customerName, meta }) {
  if (!forRole || !message) return;
  try {
    await supabase.from('notifications').insert({
      id: 'N-' + genIdSuffix(),
      type: type || 'info',
      message,
      for_role: forRole,
      booking_id: bookingId || null,
      customer_name: customerName || null,
      // jsonb side-channel for detail too long to live in `message` — e.g. the
      // full body of a sent SMS, which `message` only previews at 60 chars.
      meta: meta || null,
      created_at: new Date().toISOString(),
      dismissed_by: '',
    });
  } catch (_) {}
}
// Manual "we're low — please reorder" ping from the Stock tab. Fires a bell
// notification to BOTH owner and principal for one item. Distinct from the
// automatic green→yellow→red transition notifications (server-side): this is an
// on-demand nudge anyone (incl. a detailer mid-shift) can send when an item
// hits its last container. Best-effort; never throws back to the button.
export async function notifyLowStock(item) {
  try {
    const name = String((item && item.name) || 'A stock item').trim();
    const cat = String((item && item.category) || '').trim();
    const fill = Number(item && item.currentFill) || 0;
    const containers = Number(item && item.containerQty) || 0;
    const state = containers === 0 ? 'OUT of stock' : ('low — last container at ' + fill + '/8');
    const message = '📦 Reorder needed: ' + name + (cat ? ' (' + cat + ')' : '') + ' is ' + state + '.';
    await _insertNotification({ type: 'stock_low', message, forRole: 'owner' });
    await _insertNotification({ type: 'stock_low', message, forRole: 'principal' });
    return okResp({ notified: true });
  } catch (e) { return failResp(e); }
}
export async function markTaskDone(id, completedByRole) {
  try {
    // Read the task first so we can notify its submitter on completion.
    let task = null;
    try {
      const { data } = await supabase.from('tasks').select('*').eq('id', id).maybeSingle();
      if (data) task = mapTask(data);
    } catch (_) {}
    const { error } = await supabase.from('tasks').update({ status: 'done', completed_at: new Date().toISOString() }).eq('id', id);
    if (error) return failResp(error);
    // Notify the assigner their task is done — including self-assigned tasks and
    // the case where the assigner completed it themselves, so whoever
    // administers a task always gets a completion confirmation on the bell.
    if (task && task.assignedBy) {
      const full = String(task.title || '');
      const selfAssigned = task.assignedBy === task.assignedTo;
      await _insertNotification({
        type: 'task_done',
        message: (selfAssigned ? '✅ Task complete: ' : '✅ Your assigned task is complete: ') + full.slice(0, 80) + (full.length > 80 ? '…' : ''),
        forRole: task.assignedBy,
      });
    }
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function reopenTask(id) {
  try {
    const { error } = await supabase.from('tasks').update({ status: 'open', completed_at: null }).eq('id', id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function deleteTask(id) {
  try {
    const { data: row } = await supabase.from('tasks').select('*').eq('id', id).maybeSingle();
    const { error } = await supabase.from('tasks').delete().eq('id', id);
    if (error) return failResp(error);
    if (row) await _toTrash('task', id, row, row.title || 'Task', row.assigned_to || null);
    return okResp({});
  } catch (e) { return failResp(e); }
}
// On-demand "ping the assignee" reminder (owner/principal → 🔔 button on a task).
// Drops a bell notification for the task's assignee role; the web_push trigger
// (web_push_on_notification, type='task_reminder') then pushes that role's
// subscribed devices. Bell always lands; push only if the assignee installed
// the PWA + granted notifications — degrades gracefully either way.
export async function remindTask(task) {
  try {
    const t = task || {};
    const assignee = String(t.assignedTo || '').trim();
    if (!assignee) return failResp(new Error('Task has no assignee to remind.'));
    const title = String(t.title || 'a task').trim();
    const short = title.length > 80 ? title.slice(0, 80) + '…' : title;
    const message = '🔔 Reminder: ' + short + (t.dueDate ? ' (due ' + t.dueDate + ')' : '');
    const { error } = await supabase.from('notifications').insert({
      id: 'N-' + genIdSuffix(),
      type: 'task_reminder',
      message,
      for_role: assignee,
      booking_id: null,
      customer_name: null,
      created_at: new Date().toISOString(),
      dismissed_by: '',
    });
    if (error) return failResp(error);
    return okResp({ reminded: true, role: assignee });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// THE POOL — communal daily task scheduler (pool_people + pool_tasks)
// A calendar of tasks assigned to a custom roster of "people", each with a
// dot color. Linked people (role set) get a bell + web-push notification on
// assignment; completing a task notifies whoever assigned it. See migration
// 20260726000000_pool_tasks.sql.
// ===========================================================================
const mapPoolPerson = (r) => ({
  id: r.id, name: S(r.name), color: r.color || '#2563eb',
  role: r.role || '', active: r.active !== false, sortOrder: Number(r.sort_order) || 0,
  // Where their Pool reminders text to. Blank = bell + push only.
  phone: S(r.phone),
});
export async function listPoolPeople() {
  try {
    const { data, error } = await supabase.from('pool_people').select('*').order('sort_order', { ascending: true }).order('name', { ascending: true });
    if (error) return failResp(error);
    return okResp({ people: (data || []).map(mapPoolPerson) });
  } catch (e) { return failResp(e); }
}
export async function savePoolPerson(person) {
  try {
    const p = person || {};
    const name = String(p.name || '').trim();
    if (!name) return failResp('Name is required');
    const row = {
      name, color: p.color || '#2563eb',
      role: p.role ? String(p.role) : null,
      active: p.active !== false,
      sort_order: Number(p.sortOrder) || 0,
      // Stored as digits only — dispatchReminder hands it straight to Twilio, and
      // a number typed as (845) 901-5573 would not survive that trip.
      phone: String(p.phone || '').replace(/\D/g, '') || null,
    };
    if (p.id) {
      const { error } = await supabase.from('pool_people').update(row).eq('id', p.id);
      if (error) return failResp(error);
      // Their phone or linked login may have just changed, and both decide how
      // their already-armed digests get delivered. Rebuild the ones still ahead.
      await _armPoolPersonUpcoming(p.id);
      return okResp({ id: p.id });
    }
    row.id = 'PP-' + genIdSuffix();
    row.created_at = new Date().toISOString();
    const { error } = await supabase.from('pool_people').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
export async function deletePoolPerson(id) {
  try {
    if (!id) return failResp('Missing id');
    const { error } = await supabase.from('pool_people').delete().eq('id', id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

const mapPoolTask = (r) => ({
  id: r.id, title: S(r.title), notes: S(r.notes),
  date: String(r.task_date || '').slice(0, 10),
  startMin: r.start_min == null ? null : Number(r.start_min),
  assigneeId: r.assignee_id || '', assigneeName: S(r.assignee_name),
  assigneeColor: r.assignee_color || '#94a3b8', assigneeRole: r.assignee_role || '',
  assignedBy: r.assigned_by || '', status: r.status || 'open',
  doneAt: isoTs(r.done_at), doneBy: r.done_by || '', createdAt: isoTs(r.created_at),
  // Set when this task was assigned to several people at once — one row each,
  // linked. See migration 20260910190000.
  groupId: r.group_id || '',
  // Which shop the work is at (kingston/albany) — the payload, like a shift's.
  // See migration 20260915140000.
  location: r.location || r.site || '',
});
// Range read for the calendar. start/end are 'YYYY-MM-DD' (inclusive).
export async function listPoolTasks(start, end) {
  try {
    let q = supabase.from('pool_tasks').select('*');
    if (start) q = q.gte('task_date', start);
    if (end) q = q.lte('task_date', end);
    const { data, error } = await q.order('task_date', { ascending: true }).order('start_min', { ascending: true, nullsFirst: true });
    if (error) return failResp(error);
    return okResp({ tasks: (data || []).map(mapPoolTask) });
  } catch (e) { return failResp(e); }
}
// `assignees` is an array of {id,name,color,role} — one row is written per
// person, sharing a group_id (see migration 20260910190000 for why this is a
// fan-out rather than an array column). The legacy single-assignee fields are
// still accepted so any caller not yet updated keeps working unchanged.
export async function addPoolTask(data) {
  try {
    const d = data || {};
    const title = String(d.title || '').trim();
    if (!title) return failResp('Title is required');
    if (!d.date) return failResp('Date is required');
    const people = (Array.isArray(d.assignees) && d.assignees.length)
      ? d.assignees
      : [{ id: d.assigneeId, name: d.assigneeName, color: d.assigneeColor, role: d.assigneeRole }];
    if (!people.length || !people[0]) return failResp('Pick who it is for');
    const groupId = people.length > 1 ? 'PTG-' + genIdSuffix() : null;
    const nowIso = new Date().toISOString();
    const base = {
      title, notes: String(d.notes || '') || null,
      task_date: String(d.date).slice(0, 10),
      start_min: (d.startMin === '' || d.startMin == null) ? null : Number(d.startMin),
      assigned_by: d.assignedBy ? String(d.assignedBy) : null,
      status: 'open',
      created_at: nowIso,
      // Which shop the work is at. Attached only when given — the DB trigger
      // defaults an omitted location to the row's own site — and never as
      // `location: undefined` (the bulk-insert explicit-NULL trap).
      ...(d.location ? { location: String(d.location) } : {}),
    };
    const rows = people.map((p) => ({
      ...base,
      id: 'PT-' + genIdSuffix(),
      assignee_id: (p && p.id) || null,
      assignee_name: (p && p.name) || null,
      assignee_color: (p && p.color) || null,
      assignee_role: (p && p.role) ? String(p.role) : null,
      // Attached conditionally, never as `group_id: undefined` — on a BULK
      // insert supabase-js builds its column list from Object.keys(), so an
      // undefined key writes an explicit NULL rather than falling back to the
      // default. Harmless on a nullable column, but the habit is what keeps the
      // documented `site` version of this trap from reappearing.
      ...(groupId ? { group_id: groupId } : {}),
    }));
    const row = rows[0];
    const { error } = await supabase.from('pool_tasks').insert(rows);
    if (error) return failResp(error);
    // Notify the assignee's linked login role — bell + (via the notifications
    // web-push trigger) their phone. Skip pure self-assignment and unlinked
    // (dot-only) people. automotive-supervisor keeps its own for_role string so
    // the A.S. bell (getNotifications('automotive-supervisor')) sees it.
    // One notification per person, each addressed to their own role — a group
    // assignment is several people being told about their own work, not one
    // broadcast. Roles can repeat across the roster (two detailer logins), and
    // that is fine: the bell is per role, so each login sees it once.
    const short = title.slice(0, 80) + (title.length > 80 ? '…' : '');
    // The date and time ride along. A bell reading only "New task assigned:
    // Ceramic prep" makes the reader open the app to find out WHEN — which is
    // the one thing they needed and the reason the shift bell has said it all
    // along. Same fact, same sentence, both entry types.
    const whenBits = _shiftDayLabel(row.task_date)
      + (row.start_min != null ? ' at ' + _shiftTimeLabel(row.start_min) : '');
    for (const r of rows) {
      if (!r.assignee_role || r.assignee_role === r.assigned_by) continue;
      await _insertNotification({
        type: 'task_assigned',
        forRole: r.assignee_role,
        message: '📋 New task assigned' + (r.assignee_name ? ' for ' + r.assignee_name : '')
          + ': ' + short + ' — ' + whenBits,
      });
    }
    // Text them too. The bell + push above are the in-app half; push is the
    // channel that quietly fails, and this is the one that actually arrives.
    await _poolNotify('task', rows.map((r) => r.id));
    // Everyone named now has something on that day — rebuild each digest.
    await _armPoolPairs(rows.map((r) => [r.assignee_id, r.task_date]));
    return okResp({ id: row.id, count: rows.length, groupId: groupId || '' });
  } catch (e) { return failResp(e); }
}
export async function updatePoolTask(id, data) {
  try {
    if (!id) return failResp('Missing id');
    // Read the BEFORE state first: an edit can move a task to another person or
    // another day, and the digest it is leaving has to be rebuilt without it.
    let prev = null;
    try { const { data: p } = await supabase.from('pool_tasks').select('assignee_id,task_date,group_id').eq('id', id).maybeSingle(); prev = p || null; } catch (_) {}
    const d = data || {}, patch = {};
    if (d.title !== undefined) patch.title = String(d.title || '').trim();
    if (d.notes !== undefined) patch.notes = String(d.notes || '') || null;
    if (d.date !== undefined) patch.task_date = String(d.date).slice(0, 10);
    if (d.startMin !== undefined) patch.start_min = (d.startMin === '' || d.startMin == null) ? null : Number(d.startMin);
    if (d.assigneeId !== undefined) patch.assignee_id = d.assigneeId || null;
    if (d.assigneeName !== undefined) patch.assignee_name = d.assigneeName || null;
    if (d.assigneeColor !== undefined) patch.assignee_color = d.assigneeColor || null;
    if (d.assigneeRole !== undefined) patch.assignee_role = d.assigneeRole ? String(d.assigneeRole) : null;
    if (d.location !== undefined && d.location) patch.location = String(d.location);
    const { error } = await supabase.from('pool_tasks').update(patch).eq('id', id);
    if (error) return failResp(error);

    // 🚨 A grouped task edits as ONE task, across everybody on it. The rows are
    // an implementation detail (one per person so each renders, notifies and
    // reminds on its own) — to the person editing, this is a single entry with
    // several names on it. Changing the time on their copy alone would leave
    // four people holding the old time with nothing anywhere saying they had
    // diverged, and no screen shows the group side by side to catch it.
    //
    // The ASSIGNEE fields are excluded: those are what make the rows different
    // people, so copying them across would collapse the group onto one name.
    let touched = [];
    if (prev && prev.group_id) {
      const { assignee_id, assignee_name, assignee_color, assignee_role, ...shared } = patch;
      if (Object.keys(shared).length) {
        try {
          const { data: sibs } = await supabase.from('pool_tasks')
            .select('id,assignee_id,task_date').eq('group_id', prev.group_id).neq('id', id);
          if (sibs && sibs.length) {
            await supabase.from('pool_tasks').update(shared).eq('group_id', prev.group_id).neq('id', id);
            // Their digests move too — captured BEFORE the update, so the day
            // they are leaving is rebuilt as well as the one they land on.
            touched = sibs.flatMap((s) => [
              [s.assignee_id, s.task_date],
              [s.assignee_id, shared.task_date !== undefined ? shared.task_date : s.task_date],
            ]);
          }
        } catch (_) { /* best-effort — the row the user edited is already saved */ }
      }
    }

    await _armPoolPairs([
      [prev && prev.assignee_id, prev && prev.task_date],
      [patch.assignee_id !== undefined ? patch.assignee_id : (prev && prev.assignee_id),
        patch.task_date !== undefined ? patch.task_date : (prev && prev.task_date)],
      ...touched,
    ]);
    return okResp({});
  } catch (e) { return failResp(e); }
}
// Toggle done. On completion, notify whoever assigned it (assigned_by role) —
// mirrors markTaskDone. Self-assigned still confirms so the assigner always
// gets closure on the bell.
export async function setPoolTaskDone(id, done, completedByRole) {
  try {
    if (!id) return failResp('Missing id');
    let task = null;
    try { const { data } = await supabase.from('pool_tasks').select('*').eq('id', id).maybeSingle(); if (data) task = mapPoolTask(data); } catch (_) {}
    const patch = done
      ? { status: 'done', done_at: new Date().toISOString(), done_by: completedByRole || null }
      : { status: 'open', done_at: null, done_by: null };
    const { error } = await supabase.from('pool_tasks').update(patch).eq('id', id);
    if (error) return failResp(error);
    if (done && task && task.assignedBy) {
      const full = String(task.title || '');
      await _insertNotification({
        type: 'task_done',
        forRole: task.assignedBy,
        message: '✅ Pool task complete: ' + full.slice(0, 80) + (full.length > 80 ? '…' : ''),
      });
    }
    // Closing a task drops it from that person's remaining digests; reopening it
    // puts it back. _armPoolDay reads status='open', so this needs no branch.
    if (task) await _armPoolDay(task.assigneeId, task.date);
    return okResp({});
  } catch (e) { return failResp(e); }
}
// `opts.group` deletes everyone's copy of a multi-person task, not just this
// person's. Each row is trashed individually so any one of them can be restored
// on its own — a group delete is still N ordinary deletions underneath.
export async function deletePoolTask(id, opts) {
  try {
    if (!id) return failResp('Missing id');
    const { data: row } = await supabase.from('pool_tasks').select('*').eq('id', id).maybeSingle();
    if (opts && opts.group && row && row.group_id) {
      const { data: all } = await supabase.from('pool_tasks').select('*').eq('group_id', row.group_id);
      const rows = all || [row];
      const { error } = await supabase.from('pool_tasks').delete().eq('group_id', row.group_id);
      if (error) return failResp(error);
      for (const r of rows) await _toTrash('pool_task', r.id, r, r.title || 'Pool task', r.assigned_by || null);
      await _armPoolPairs(rows.map((r) => [r.assignee_id, r.task_date]));
      return okResp({ group: true, count: rows.length });
    }
    const { error } = await supabase.from('pool_tasks').delete().eq('id', id);
    if (error) return failResp(error);
    if (row) await _toTrash('pool_task', id, row, row.title || 'Pool task', row.assigned_by || null);
    if (row) await _armPoolDay(row.assignee_id, row.task_date);
    return okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// THE POOL — the business schedule (pool_shifts)
// "The owner can say he needs this person at this location on this day."
//
// A shift is a calendar EVENT, not a task: no done-state, an optional time
// range, a free-text request, and a LOCATION. It is brand-wide on purpose —
// the location is the payload, not the tenancy, so an Albany shift is legible
// from Kingston where the person reading it is standing. See migration
// 20260813120000_pool_shifts.sql for why the column is `location`, not `site`.
// ===========================================================================
const mapPoolShift = (r) => ({
  id: r.id, title: S(r.title), notes: S(r.notes),
  date: String(r.shift_date || '').slice(0, 10),
  allDay: r.all_day !== false,
  startMin: r.start_min == null ? null : Number(r.start_min),
  endMin: r.end_min == null ? null : Number(r.end_min),
  location: r.location || 'kingston',
  assigneeId: r.assignee_id || '', assigneeName: S(r.assignee_name),
  assigneeColor: r.assignee_color || '#94a3b8', assigneeRole: r.assignee_role || '',
  seriesId: r.series_id || '', groupId: r.group_id || '', createdBy: r.created_by || '',
  status: r.status || 'scheduled',
  ackAt: isoTs(r.ack_at), ackBy: r.ack_by || '',
  createdAt: isoTs(r.created_at),
});

// Message-only date/time formatting for the notification body. Deliberately
// local to this block: the shift's `date` is a plain 'YYYY-MM-DD' calendar day
// with no timezone in it, so it is split by hand rather than pushed through a
// Date — which is the repo-wide gotcha that shifts a date back one day in NY.
const _SHIFT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const _SHIFT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function _shiftDayLabel(ds) {
  const p = String(ds || '').slice(0, 10).split('-').map(Number);
  if (p.length < 3 || !p[0]) return String(ds || '');
  const d = new Date(p[0], p[1] - 1, p[2], 12);
  return _SHIFT_DAYS[d.getDay()] + ' ' + _SHIFT_MONTHS[d.getMonth()] + ' ' + p[2];
}
function _shiftTimeLabel(m) {
  if (m == null || m === '') return '';
  const mm = Number(m), h = Math.floor(mm / 60), min = mm % 60;
  let hh = h % 12; if (hh === 0) hh = 12;
  return hh + ':' + String(min).padStart(2, '0') + (h >= 12 ? 'pm' : 'am');
}
const _SHIFT_SITE_NAMES = { kingston: '3C Kingston', albany: '3C Albany' };
function _shiftWhen(row) {
  const day = _shiftDayLabel(row.shift_date);
  if (row.all_day === false && row.start_min != null) {
    const end = row.end_min != null ? '–' + _shiftTimeLabel(row.end_min) : '';
    return day + ', ' + _shiftTimeLabel(row.start_min) + end;
  }
  return day;
}

// Range read for the calendar. start/end are 'YYYY-MM-DD' (inclusive).
export async function listPoolShifts(start, end) {
  try {
    let q = supabase.from('pool_shifts').select('*');
    if (start) q = q.gte('shift_date', start);
    if (end) q = q.lte('shift_date', end);
    const { data, error } = await q
      .order('shift_date', { ascending: true })
      .order('start_min', { ascending: true, nullsFirst: true });
    if (error) return failResp(error);
    return okResp({ shifts: (data || []).map(mapPoolShift) });
  } catch (e) { return failResp(e); }
}

// Build the row payload shared by create + repeat. `date` varies per row.
// `person` (optional) overrides the single-assignee fields on `d`, so one call
// site can build a row per person for a multi-person shift. `groupId` links
// them. Orthogonal to seriesId: seriesId is the same shift on another DATE,
// groupId the same shift for another PERSON.
function _shiftRow(d, date, seriesId, person, groupId) {
  const allDay = d.allDay !== false;
  const who = person || { id: d.assigneeId, name: d.assigneeName, color: d.assigneeColor, role: d.assigneeRole };
  return {
    id: 'PS-' + genIdSuffix(),
    title: String(d.title || '').trim() || null,
    notes: String(d.notes || '').trim() || null,
    shift_date: String(date).slice(0, 10),
    all_day: allDay,
    // A time range is meaningless on an all-day shift, and leaving a stale
    // start_min behind would sort it as though it had one.
    start_min: allDay || d.startMin == null || d.startMin === '' ? null : Number(d.startMin),
    end_min: allDay || d.endMin == null || d.endMin === '' ? null : Number(d.endMin),
    location: d.location || 'kingston',
    assignee_id: (who && who.id) || null,
    assignee_name: (who && who.name) || null,
    assignee_color: (who && who.color) || null,
    assignee_role: (who && who.role) ? String(who.role) : null,
    series_id: seriesId || null,
    group_id: groupId || null,
    created_by: d.createdBy ? String(d.createdBy) : null,
    status: 'scheduled',
    created_at: new Date().toISOString(),
  };
}

// Create one shift, or a whole repeat run.
//
// `repeat` is { weekdays: [0-6], until: 'YYYY-MM-DD' } — every matching weekday
// from the start date through `until` inclusive. One notification is sent for
// the run, not one per row: "you're at Albany every Tue+Thu until Sep 30" is a
// single thing to be told, and 12 pushes for it would be a reason to turn
// notifications off.
export async function addPoolShift(data) {
  try {
    const d = data || {};
    if (!d.date) return failResp('Date is required');
    if (!d.location) return failResp('Pick a location');
    const crew = (Array.isArray(d.assignees) && d.assignees.length)
      ? d.assignees
      : (d.assigneeId ? [{ id: d.assigneeId, name: d.assigneeName, color: d.assigneeColor, role: d.assigneeRole }] : []);
    if (!crew.length) return failResp('Pick who the shift is for');
    const groupId = crew.length > 1 ? 'PSG-' + genIdSuffix() : null;

    // The dates the repeat covers, resolved ONCE and then written per person.
    const dates = [];
    const rep = d.repeat;
    let seriesId = null;
    if (rep && Array.isArray(rep.weekdays) && rep.weekdays.length && rep.until) {
      seriesId = 'PSR-' + genIdSuffix();
      const start = String(d.date).slice(0, 10);
      const until = String(rep.until).slice(0, 10);
      if (until < start) return failResp('The repeat end date is before the start date');
      const p = start.split('-').map(Number);
      const cur = new Date(p[0], p[1] - 1, p[2], 12);
      for (let guard = 0; guard < 400; guard++) {
        const ds = cur.getFullYear() + '-' + String(cur.getMonth() + 1).padStart(2, '0') + '-' + String(cur.getDate()).padStart(2, '0');
        if (ds > until) break;
        if (rep.weekdays.includes(cur.getDay())) dates.push(ds);
        cur.setDate(cur.getDate() + 1);
      }
      if (!dates.length) return failResp('That repeat covers no days — check the weekdays and end date.');
    } else {
      dates.push(String(d.date).slice(0, 10));
    }

    // 🚨 The 400-row guard now has to count PEOPLE × DATES, not dates. It exists
    // because a runaway "until 2099" writes a five-figure batch with no undo —
    // and picking the whole crew multiplies that by the roster size, so counting
    // only dates would let five people × 400 days through as 2,000 rows. Refused
    // with the actual numbers rather than silently truncated: a schedule that
    // quietly stops halfway is worse than one that didn't save.
    const total = dates.length * crew.length;
    if (total > 400) {
      return failResp('That would create ' + total + ' shifts (' + crew.length + ' people × ' + dates.length + ' days). Shorten the repeat or pick fewer people.');
    }

    const rows = [];
    for (const person of crew) for (const ds of dates) rows.push(_shiftRow(d, ds, seriesId, person, groupId));

    const { error } = await supabase.from('pool_shifts').insert(rows);
    if (error) return failResp(error);

    // Tell each person about THEIR OWN run, not about the whole save. Counting
    // rows.length here would tell a five-person one-day meeting that everybody
    // has "+4 more" shifts they do not have — the "+N more" is about repeat
    // dates, so it has to be counted per person.
    const first = rows[0];
    const where = _SHIFT_SITE_NAMES[first.location] || first.location;
    const note = first.notes ? ' — ' + first.notes.slice(0, 80) + (first.notes.length > 80 ? '…' : '') : '';
    for (const person of crew) {
      const mine = rows.filter((r) => r.assignee_id === (person && person.id));
      if (!mine.length) continue;
      const head = mine[0];
      // Skip pure self-scheduling and dot-only (unlinked) people.
      if (!head.assignee_role || head.assignee_role === head.created_by) continue;
      const when = mine.length > 1 ? _shiftWhen(head) + ' + ' + (mine.length - 1) + ' more' : _shiftWhen(head);
      await _insertNotification({
        type: 'shift_assigned',
        forRole: head.assignee_role,
        message: '📍 You’re scheduled at ' + where + ': ' + when + note,
      });
    }
    // Text them too — same reasoning as the task path.
    await _poolNotify('shift', rows.map((r) => r.id));
    // Every (person, day) the save touched. _armPoolPairs groups by person, so
    // this stays one batch each rather than one pass per generated row.
    await _armPoolPairs(rows.map((r) => [r.assignee_id, r.shift_date]));
    return okResp({ id: first.id, count: rows.length, seriesId: first.series_id || '', groupId: groupId || '' });
  } catch (e) { return failResp(e); }
}

export async function updatePoolShift(id, data) {
  try {
    if (!id) return failResp('Missing id');
    // BEFORE state, for the same reason updatePoolTask reads one: moving a shift
    // to another person or another day leaves a digest behind that still has it.
    let prev = null;
    try { const { data: p } = await supabase.from('pool_shifts').select('assignee_id,shift_date,group_id').eq('id', id).maybeSingle(); prev = p || null; } catch (_) {}
    const d = data || {}, patch = { updated_at: new Date().toISOString() };
    if (d.title !== undefined) patch.title = String(d.title || '').trim() || null;
    if (d.notes !== undefined) patch.notes = String(d.notes || '').trim() || null;
    if (d.date !== undefined) patch.shift_date = String(d.date).slice(0, 10);
    if (d.location !== undefined) patch.location = d.location || 'kingston';
    if (d.status !== undefined) patch.status = d.status || 'scheduled';
    if (d.allDay !== undefined) {
      patch.all_day = d.allDay !== false;
      if (patch.all_day) { patch.start_min = null; patch.end_min = null; }
    }
    if (patch.all_day !== true) {
      if (d.startMin !== undefined) patch.start_min = (d.startMin === '' || d.startMin == null) ? null : Number(d.startMin);
      if (d.endMin !== undefined) patch.end_min = (d.endMin === '' || d.endMin == null) ? null : Number(d.endMin);
    }
    if (d.assigneeId !== undefined) patch.assignee_id = d.assigneeId || null;
    if (d.assigneeName !== undefined) patch.assignee_name = d.assigneeName || null;
    if (d.assigneeColor !== undefined) patch.assignee_color = d.assigneeColor || null;
    if (d.assigneeRole !== undefined) patch.assignee_role = d.assigneeRole ? String(d.assigneeRole) : null;
    // An edited shift is no longer the one that was acknowledged — the whole
    // point of the ✓ is "I saw THIS". Clear it whenever the substance moves, so
    // a changed time or location can't inherit a stale confirmation.
    const substantive = ['shift_date', 'location', 'start_min', 'end_min', 'all_day', 'assignee_id'];
    if (substantive.some((k) => patch[k] !== undefined)) { patch.ack_at = null; patch.ack_by = null; }
    const { error } = await supabase.from('pool_shifts').update(patch).eq('id', id);
    if (error) return failResp(error);

    // Same rule as updatePoolTask: a grouped shift edits as one shift across
    // everybody on it, minus the assignee fields that make them different
    // people. See the note there for why divergence is the failure to avoid.
    // The ack is cleared for the siblings too when the substance moved — the
    // ✓ means "I saw THIS", and it means that for all of them equally.
    let touched = [];
    if (prev && prev.group_id) {
      const { assignee_id, assignee_name, assignee_color, assignee_role, ...shared } = patch;
      if (Object.keys(shared).length > 1) { // more than just updated_at
        try {
          const { data: sibs } = await supabase.from('pool_shifts')
            .select('id,assignee_id,shift_date').eq('group_id', prev.group_id).neq('id', id);
          if (sibs && sibs.length) {
            await supabase.from('pool_shifts').update(shared).eq('group_id', prev.group_id).neq('id', id);
            touched = sibs.flatMap((s) => [
              [s.assignee_id, s.shift_date],
              [s.assignee_id, shared.shift_date !== undefined ? shared.shift_date : s.shift_date],
            ]);
          }
        } catch (_) { /* best-effort — the row the user edited is already saved */ }
      }
    }

    await _armPoolPairs([
      [prev && prev.assignee_id, prev && prev.shift_date],
      [patch.assignee_id !== undefined ? patch.assignee_id : (prev && prev.assignee_id),
        patch.shift_date !== undefined ? patch.shift_date : (prev && prev.shift_date)],
      ...touched,
    ]);
    return okResp({});
  } catch (e) { return failResp(e); }
}

// The assignee confirms they've seen it. Notifies whoever scheduled it, so the
// owner knows the message landed without having to chase it.
export async function ackPoolShift(id, byRole) {
  try {
    if (!id) return failResp('Missing id');
    let row = null;
    try { const { data } = await supabase.from('pool_shifts').select('*').eq('id', id).maybeSingle(); if (data) row = data; } catch (_) {}
    const { error } = await supabase.from('pool_shifts')
      .update({ ack_at: new Date().toISOString(), ack_by: byRole || null })
      .eq('id', id);
    if (error) return failResp(error);
    if (row && row.created_by && row.created_by !== byRole) {
      const where = _SHIFT_SITE_NAMES[row.location] || row.location;
      await _insertNotification({
        type: 'shift_ack',
        forRole: row.created_by,
        message: '✅ ' + (row.assignee_name || 'They') + ' confirmed ' + where + ' — ' + _shiftWhen(row),
      });
    }
    return okResp({});
  } catch (e) { return failResp(e); }
}

// Delete one shift, or the whole repeat run it belongs to.
export async function deletePoolShift(id, opts) {
  try {
    if (!id) return failResp('Missing id');
    const wholeSeries = !!(opts && opts.series);
    if (wholeSeries) {
      const { data } = await supabase.from('pool_shifts').select('series_id').eq('id', id).maybeSingle();
      const sid = data && data.series_id;
      if (sid) {
        // Grab who/when for the whole run BEFORE it is gone — afterwards there is
        // nothing left to tell us which digests need rebuilding.
        const { data: gone } = await supabase.from('pool_shifts')
          .select('assignee_id,shift_date').eq('series_id', sid);
        const { error } = await supabase.from('pool_shifts').delete().eq('series_id', sid);
        if (error) return failResp(error);
        await _armPoolPairs((gone || []).map((r) => [r.assignee_id, r.shift_date]));
        return okResp({ series: true });
      }
    }
    const { data: one } = await supabase.from('pool_shifts').select('assignee_id,shift_date,group_id').eq('id', id).maybeSingle();
    // `opts.group` drops everyone's copy of a multi-person shift for this date.
    // Orthogonal to `opts.series` above, which drops this person's whole repeat
    // run — a repeating all-staff shift can be cancelled along either axis.
    if (opts && opts.group && one && one.group_id) {
      const { data: gone } = await supabase.from('pool_shifts')
        .select('assignee_id,shift_date').eq('group_id', one.group_id);
      const { error } = await supabase.from('pool_shifts').delete().eq('group_id', one.group_id);
      if (error) return failResp(error);
      await _armPoolPairs((gone || []).map((r) => [r.assignee_id, r.shift_date]));
      return okResp({ group: true, count: (gone || []).length });
    }
    const { error } = await supabase.from('pool_shifts').delete().eq('id', id);
    if (error) return failResp(error);
    if (one) await _armPoolDay(one.assignee_id, one.shift_date);
    return okResp({});
  } catch (e) { return failResp(e); }
}

// Immediate "this is yours" text at assignment. Fire-and-forget: the entry is
// already saved by the time this runs, and a Twilio hiccup must never surface
// as a failed save. The server composes the message from the row itself — this
// sends ids and nothing else, so the browser can never name a recipient or a
// body (see the pool-notify handler in api/invoicer.js for why that matters).
async function _poolNotify(kind, ids) {
  try {
    const list = (ids || []).filter(Boolean);
    if (!list.length) return;
    const { data: sess } = await supabase.auth.getSession();
    const tok = sess && sess.session && sess.session.access_token;
    await fetch('/api/invoicer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
      body: JSON.stringify({ action: 'pool-notify', kind, ids: list }),
    });
  } catch (_) { /* best-effort — the bell + push already went out */ }
}

// ===========================================================================
// THE POOL — reminding the person, not just telling them once
// ===========================================================================
// A Pool task or shift notifies its assignee exactly once, when it is created
// (task_assigned / shift_assigned). For anything scheduled more than a day out
// that notification has been read and forgotten long before it matters. These
// are the reminders that actually land near the work: one the NIGHT BEFORE and
// one the MORNING OF, as a bell + web push AND a text.
//
// ---------------------------------------------------------------------------
// One digest per person per day — NOT one reminder per entry
// ---------------------------------------------------------------------------
// This is the whole design and it is worth being explicit about, because the
// obvious implementation is wrong. Arming a reminder per task means a detailer
// with five things on Thursday gets FIVE texts on Wednesday night and five more
// on Thursday morning — ten messages, ten Twilio segments, for one day of work.
// That is not a reminder, that is a reason to mute the number. So the unit here
// is (person, date): one row per phase per channel, rebuilt from scratch on
// every Pool write, listing everything that person has that day. Four rows per
// person per day, whether they have one task or twenty.
//
// That is also why the ids are DETERMINISTIC rather than 'R-' + a random
// suffix. Re-arming has to overwrite the row it wrote last time, and a random
// id would leave the previous one behind to fire alongside it — the duplicate
// storm this design exists to prevent, arriving by a different route.
// ---------------------------------------------------------------------------
const _POOL_REMINDER_BY = 'pool-reminder';

// Fire points, as fixed UTC anchors on the entry's own calendar day:
//   'eve'  -> 00:00 UTC on the date = 8pm ET the evening BEFORE it
//   'morn' -> 12:00 UTC on the date = 8am ET the morning OF it
// Fixed UTC rather than a real ET conversion, exactly as _apptReminderDueIso
// does it: both drift an hour to 7pm/7am under EST and that is fine — these are
// "this evening" and "first thing", not appointments. 12:00 UTC also keeps the
// morning digest clear of the 13:00 UTC pass that sends customer appointment
// reminders, so staff and customers are never composing texts in the same tick.
function _poolDueIso(dateKey, phase) {
  const p = String(dateKey || '').slice(0, 10).split('-').map(Number);
  if (p.length !== 3 || !p[0]) return null;
  return new Date(Date.UTC(p[0], p[1] - 1, p[2], phase === 'eve' ? 0 : 12, 0, 0)).toISOString();
}

// Stable per (person, day, phase, channel) — see the note on determinism above.
function _poolReminderId(assigneeId, dateKey, phase, channel) {
  const who = String(assigneeId || '').replace(/[^A-Za-z0-9]/g, '');
  return 'R-POOL-' + who + '-' + String(dateKey).slice(0, 10).replace(/-/g, '') + '-' + phase + '-' + channel;
}

// What this person has across a RANGE of dates, bucketed by day. Cancelled
// shifts and completed tasks are excluded here rather than at send time, which
// is what makes closing a task out at 4pm quietly drop it from tonight's text.
//
// 🚨 Range, not per-day, because of repeat series. addPoolShift will happily
// write 400 rows from one "every weekday until June" save, and arming those one
// day at a time would fire four round trips per day — well over a thousand
// requests behind a single button, from a browser, on shop wifi. Two queries
// cover the whole span instead, and the extra days the range sweeps up but the
// caller did not ask for are simply not read.
async function _poolRangeLoad(assigneeId, lo, hi) {
  const [t, s] = await Promise.all([
    supabase.from('pool_tasks').select('title,start_min,task_date')
      .eq('assignee_id', assigneeId).gte('task_date', lo).lte('task_date', hi).eq('status', 'open'),
    supabase.from('pool_shifts').select('location,all_day,start_min,end_min,shift_date')
      .eq('assignee_id', assigneeId).gte('shift_date', lo).lte('shift_date', hi).neq('status', 'cancelled'),
  ]);
  const byDay = {};
  const slot = (d) => (byDay[d] || (byDay[d] = { tasks: [], shifts: [] }));
  for (const r of ((t && t.data) || [])) slot(String(r.task_date).slice(0, 10)).tasks.push(r);
  for (const r of ((s && s.data) || [])) slot(String(r.shift_date).slice(0, 10)).shifts.push(r);
  return byDay;
}

// The staff-facing body, sent verbatim (meta.sms_full_body). Kept tight on
// purpose: a text is billed by the segment, so the task list caps at three and
// then counts the rest rather than reading out a fourteen-item inventory.
//
// 🚨 ASCII ONLY, deliberately — no em dash, no '·', no curly quotes. A single
// character outside GSM-7 flips the whole message to UCS-2, which drops the
// segment size from 153 characters to 67 and turns a 2-segment text into a
// 4-segment one. This body goes out twice a day to every person on the
// schedule, so that multiplier is paid over and over. (The customer-facing
// appointment reminders do use an em dash and quietly pay it; those are one-off
// per booking, this is not. Not a reason to repeat it here.)
function _poolDigestBody(dateKey, phase, shifts, tasks) {
  const lead = '3C - ' + (phase === 'eve' ? 'tomorrow' : 'today') + ' (' + _shiftDayLabel(dateKey) + '):';
  const parts = [];
  for (const s of shifts) {
    const where = _SHIFT_SITE_NAMES[s.location] || s.location;
    const time = (s.all_day === false && s.start_min != null)
      ? ' ' + _shiftTimeLabel(s.start_min) + (s.end_min != null ? '-' + _shiftTimeLabel(s.end_min) : '')
      : '';
    parts.push('At ' + where + time);
  }
  const titles = tasks
    .slice()
    .sort((a, b) => (a.start_min == null ? 1e9 : a.start_min) - (b.start_min == null ? 1e9 : b.start_min))
    .map((t) => String(t.title || '').trim())
    .filter(Boolean);
  if (titles.length) {
    const shown = titles.slice(0, 3).join(', ');
    parts.push('Tasks: ' + shown + (titles.length > 3 ? ' +' + (titles.length - 3) + ' more' : ''));
  }
  return parts.length ? lead + ' ' + parts.join('; ') : '';
}

// Rebuild one person's reminders for one day. Called after EVERY Pool write, so
// it has to be cheap, idempotent, and silent on failure — arming has never been
// allowed to block the write that triggered it.
//
// 🚨 Only FUTURE phases are ever written. That is the guard against re-sending:
// editing a task at 9am would otherwise reset that morning's already-delivered
// row to pending and text the person a second time. A phase whose moment has
// passed is skipped, so it can be neither re-armed nor re-sent — while the
// phase still ahead of it picks up the edit, which is the behaviour you want.
async function _armPoolDays(assigneeId, dateKeys) {
  try {
    const who = String(assigneeId || '').trim();
    if (!who) return;
    const days = Array.from(new Set((dateKeys || [])
      .map((d) => String(d || '').slice(0, 10))
      .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)))).sort();
    if (!days.length) return;

    const byDay = await _poolRangeLoad(who, days[0], days[days.length - 1]);

    // The roster row carries both delivery addresses: `role` is the bell/push
    // target, `phone` the text. A dot-only person (no linked login) still gets
    // a text if there is a number; someone with neither is simply not remindable
    // and every row below is skipped for them.
    let role = '', phone = '', name = '';
    try {
      const { data: p } = await supabase.from('pool_people').select('name,role,phone').eq('id', who).maybeSingle();
      role = (p && p.role) || '';
      phone = String((p && p.phone) || '').replace(/\D/g, '');
      name = String((p && p.name) || '').trim();
    } catch (_) {}

    const nowMs = Date.now();
    const nowIso = new Date().toISOString();
    const stale = [];   // ids to clear: nothing left to say, or nowhere to send it
    const rows = [];

    for (const day of days) {
      const bucket = byDay[day] || { tasks: [], shifts: [] };
      for (const phase of ['eve', 'morn']) {
        const body = _poolDigestBody(day, phase, bucket.shifts, bucket.tasks);
        const dueIso = _poolDueIso(day, phase);
        const future = dueIso && new Date(dueIso).getTime() > nowMs;
        const appId = _poolReminderId(who, day, phase, 'app');
        const smsId = _poolReminderId(who, day, phase, 'sms');

        // Nothing on the calendar any more (last task closed, shift cancelled),
        // or the moment has passed — either way the pending row must not survive.
        if (!body || !future) { stale.push(appId, smsId); continue; }
        if (!role) stale.push(appId);
        if (!phone) stale.push(smsId);

        const base = {
          type: 'custom', title: 'Pool — ' + (phase === 'eve' ? 'tomorrow' : 'today'),
          notes: body, due_at: dueIso, status: 'pending',
          created_by: _POOL_REMINDER_BY, created_at: nowIso, updated_at: nowIso,
          meta: { kind: 'pool_digest', phase, date: day, assignee_id: who, sms_full_body: true },
        };
        // NOTE: `site` is deliberately absent rather than set to undefined. On a
        // bulk insert an explicit undefined writes NULL instead of falling back
        // to the column default (auth_site()), and a NULL site fails the
        // reminders RLS site predicate on read — the row would arm and then be
        // invisible. Same trap documented on createBooking's checklist_tasks.
        //
        // The row therefore carries the shop of whoever SAVED it, which for a
        // brand-wide Albany shift scheduled from Kingston is Kingston. That only
        // affects the bell (notifications are in_hub_scope'd, so the recipient
        // may not see it while toggled to the other shop) — the web push and the
        // text ignore RLS and land either way. Exactly the tradeoff 20260813120000
        // already accepted for shift_assigned; the Pool tab itself is the record.
        // 🚨 The bell is addressed by ROLE, and a role is not a person — Ric and
        // Evan are both `detailer` on the live roster, so an unlabelled digest
        // would show each of them the other's day with nothing to tell them
        // apart. The name goes on the bell copy to fix that. It is NOT on the
        // text: that one is delivered to a single phone, where the name is both
        // redundant and paid for by the segment.
        const appBody = name ? name + ' - ' + body : body;
        if (role) rows.push({ ...base, id: appId, channel: 'app', for_role: role, notes: appBody });
        if (phone) rows.push({ ...base, id: smsId, channel: 'sms', phone });
      }
    }

    // Stand down the rows that should no longer fire, so a person who closed out
    // their last task for the day is not still texted about it tonight. Pending
    // only — a delivered row is history and stays.
    //
    // 🚨 A STATUS FLIP, NOT A DELETE. `reminders_del` is owner/principal only,
    // and the single most common caller of this whole path is a DETAILER ticking
    // a task done. A delete here fails their RLS check, arming swallows the
    // error, the row survives as 'pending' — and that evening they get a text
    // listing the job they finished at four o'clock. An update is something they
    // are actually allowed to do, and 'dismissed' is already a status the schema
    // defines. runDueReminders only ever claims 'pending', so a dismissed row is
    // inert; if the work comes back, the upsert below flips it to 'pending'
    // again, which a delete could not have done either.
    if (stale.length) {
      try {
        await supabase.from('reminders')
          .update({ status: 'dismissed', updated_at: nowIso })
          .in('id', stale).eq('status', 'pending');
      } catch (_) {}
    }
    if (rows.length) {
      try { await supabase.from('reminders').upsert(rows, { onConflict: 'id' }); } catch (_) {}
    }
  } catch (_) { /* best-effort — a reminder that fails to arm must never fail the write */ }
}

function _armPoolDay(assigneeId, dateKey) { return _armPoolDays(assigneeId, [dateKey]); }

// A Pool write can move an entry BETWEEN people or BETWEEN days, which leaves
// the place it came from holding a digest that still lists it. Re-arm both ends
// and the old one rebuilds itself without the entry — or deletes itself if that
// was the last thing there. Grouped by person so each one is a single batch.
async function _armPoolPairs(pairs) {
  const byWho = new Map();
  for (const pr of pairs || []) {
    const who = pr && pr[0], day = pr && pr[1];
    if (!who || !day) continue;
    if (!byWho.has(who)) byWho.set(who, []);
    byWho.get(who).push(day);
  }
  for (const [who, days] of byWho) await _armPoolDays(who, days);
}

// Re-arm every day this person still has ahead of them.
//
// Needed because the two things that decide HOW someone is reminded live on the
// roster row, not on the task: `phone` and `role`. Adding a number to someone
// who already has three weeks of shifts on the board would otherwise change
// nothing — their digests were armed without an SMS row, and only a fresh write
// against each of those days would add one. Which is to say: the first time
// anybody fills in a phone number, the feature would appear not to work.
//
// Deliberately unbounded on the far end. A cutoff here would silently drop the
// reminders for anything scheduled past it, and "no text arrived" is not a
// failure anyone would connect back to a horizon constant.
async function _armPoolPersonUpcoming(assigneeId) {
  try {
    const who = String(assigneeId || '').trim();
    if (!who) return;
    const n = new Date();
    const from = n.getFullYear() + '-' + String(n.getMonth() + 1).padStart(2, '0') + '-' + String(n.getDate()).padStart(2, '0');
    const [t, s] = await Promise.all([
      supabase.from('pool_tasks').select('task_date')
        .eq('assignee_id', who).gte('task_date', from).eq('status', 'open'),
      supabase.from('pool_shifts').select('shift_date')
        .eq('assignee_id', who).gte('shift_date', from).neq('status', 'cancelled'),
    ]);
    const days = ((t && t.data) || []).map((r) => r.task_date)
      .concat(((s && s.data) || []).map((r) => r.shift_date));
    if (days.length) await _armPoolDays(who, days);
  } catch (_) {}
}

// ===========================================================================
// STAFF CHECK-IN / CHECK-OUT (the detailer profile's two users)
//
// The detailer PIN opens the PROFILE; a personal PIN says WHO is standing
// there. Everything sensitive happens in Postgres: RLS on employees/timeclock
// is owner-only and the PIN can't be compared anywhere the browser can reach,
// so these are all SECURITY DEFINER RPCs (migration 20260814000000). The
// client never sees a hash, a wage, or anyone else's row — just names, times
// and booleans.
//
// The RPCs return their own {success,error} envelope rather than throwing, so
// a wrong PIN is ordinary data here, not an exception.
// ===========================================================================
function _rpcResp(data, error) {
  if (error) return failResp(error);
  const d = data || {};
  if (d.success === false) return { success: false, error: d.error || 'That didn’t work.' };
  return { success: true, ...d };
}

// The check-in dropdown. Only employees with a PIN set appear — which is how
// the roster stays "Eric and Evan" without either name living in the code.
export async function getStaffRoster() {
  try {
    const { data, error } = await supabase.rpc('staff_roster');
    if (error) return failResp(error);
    return okResp({
      roster: (data || []).map((r) => ({
        id: r.id, name: S(r.name),
        checkedIn: !!r.checked_in, since: isoTs(r.since),
        // Today's "isn't in" mark. `checkedIn` is an OPEN punch and `absent` is
        // a statement about the whole day — a person is one, the other, or
        // still owed an answer.
        absent: !!r.absent, absentBy: S(r.absent_by), absentReason: S(r.absent_reason),
      })),
    });
  } catch (e) { return failResp(e); }
}

// The managers' view of the roster: every active employee plus whether a PIN
// is set, so somebody who isn't on the check-in screen yet can be given one.
export async function getStaffRosterAdmin() {
  try {
    const { data, error } = await supabase.rpc('staff_roster_admin');
    if (error) return failResp(error);
    return okResp({ roster: (data || []).map((r) => ({ id: r.id, name: S(r.name), hasPin: !!r.has_pin, locked: !!r.locked })) });
  } catch (e) { return failResp(e); }
}

export async function staffCheckIn(employeeId, pin) {
  try {
    const { data, error } = await supabase.rpc('staff_check_in', { p_employee_id: employeeId, p_pin: String(pin || '') });
    return _rpcResp(data, error);
  } catch (e) { return failResp(e); }
}

// `done` is the array of ticked checklist items. The "at least one" rule is
// enforced server-side too — a rule that only exists in the UI is a suggestion.
export async function staffCheckOut(employeeId, pin, done) {
  try {
    const { data, error } = await supabase.rpc('staff_check_out', {
      p_employee_id: employeeId,
      p_pin: String(pin || ''),
      p_done: Array.isArray(done) ? done : [],
    });
    return _rpcResp(data, error);
  } catch (e) { return failResp(e); }
}

// One present person marks another not in today. `byEmployeeId` is who is
// vouching — the server insists that person has actually checked in, so this
// can't settle the day in an empty shop.
export async function staffMarkAbsent(employeeId, byEmployeeId, reason) {
  try {
    const { data, error } = await supabase.rpc('staff_mark_absent', {
      p_employee_id: employeeId,
      p_by_employee_id: byEmployeeId || null,
      p_reason: reason ? String(reason) : null,
    });
    return _rpcResp(data, error);
  } catch (e) { return failResp(e); }
}

// Retract it — they turned up after all. (Their own check-in clears it too.)
export async function staffClearAbsent(employeeId) {
  try {
    const { data, error } = await supabase.rpc('staff_clear_absent', { p_employee_id: employeeId });
    return _rpcResp(data, error);
  } catch (e) { return failResp(e); }
}

// Managers only (enforced in the function, not here).
export async function setStaffPin(employeeId, pin) {
  try {
    const { data, error } = await supabase.rpc('set_staff_pin', { p_employee_id: employeeId, p_pin: pin ? String(pin) : null });
    return _rpcResp(data, error);
  } catch (e) { return failResp(e); }
}

// ── the closing checklist template (plain RLS table) ──
const mapChecklistItem = (r) => ({ id: r.id, label: S(r.label), sortOrder: Number(r.sort_order) || 0, active: r.active !== false });

export async function listCheckoutChecklist() {
  try {
    const { data, error } = await supabase.from('checkout_checklist_items').select('*')
      .order('sort_order', { ascending: true });
    if (error) return failResp(error);
    return okResp({ items: (data || []).map(mapChecklistItem) });
  } catch (e) { return failResp(e); }
}

export async function saveCheckoutChecklistItem(item) {
  try {
    const p = item || {};
    const label = String(p.label || '').trim();
    if (!label) return failResp('Give the item a label');
    const row = { label, sort_order: Number(p.sortOrder) || 0, active: p.active !== false };
    if (p.id) {
      const { error } = await supabase.from('checkout_checklist_items').update(row).eq('id', p.id);
      return error ? failResp(error) : okResp({ id: p.id });
    }
    row.id = 'CCL-' + genIdSuffix();
    const { error } = await supabase.from('checkout_checklist_items').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}

export async function deleteCheckoutChecklistItem(id) {
  try {
    if (!id) return failResp('Missing id');
    const { error } = await supabase.from('checkout_checklist_items').delete().eq('id', id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// Today's check-ins, for the managers' "who's on the floor" readout.
export async function listTodayCheckins() {
  try {
    const now = new Date();
    const today = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
    const { data, error } = await supabase.from('staff_checkins').select('*')
      .eq('work_date', today).order('checked_in_at', { ascending: true });
    if (error) return failResp(error);
    return okResp({
      checkins: (data || []).map((r) => ({
        id: r.id, employeeId: r.employee_id, name: S(r.employee_name),
        checkedInAt: isoTs(r.checked_in_at), checkedOutAt: isoTs(r.checked_out_at),
        minutes: r.minutes_worked == null ? null : Number(r.minutes_worked),
        done: Array.isArray(r.checklist_done) ? r.checklist_done : [],
      })),
    });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// CUSTOMER RULES — standing presets tagged onto a customer.
//
// A LIBRARY of rules defined once ("Cash pay", "No mats", "Friends & family
// 10%") plus the assignment of any number of them to a customer. Every rule
// shows as a chip wherever that customer appears; a rule may also carry ONE
// effect the invoicer knows how to apply — see migration 20260814200000, where
// the effect set is closed by a check constraint precisely so an unrecognised
// effect can't look applied while doing nothing.
// ===========================================================================
const mapCustomerRule = (r) => ({
  id: r.id, label: S(r.label),
  effect: r.effect || 'none',
  effectValue: r.effect_value == null ? null : Number(r.effect_value),
  note: S(r.note),
  color: r.color || '',
  sortOrder: Number(r.sort_order) || 0,
  active: r.active !== false,
});

// One read for both tables — every consumer needs them together, and the
// assignment map is useless without the labels.
export async function listCustomerRules() {
  try {
    const [rules, assign] = await Promise.all([
      supabase.from('customer_rules').select('*').order('sort_order', { ascending: true }),
      supabase.from('customer_rule_assignments').select('customer_id,rule_id'),
    ]);
    if (rules.error) return failResp(rules.error);
    if (assign.error) return failResp(assign.error);
    const byCustomer = {};
    (assign.data || []).forEach((a) => {
      (byCustomer[a.customer_id] || (byCustomer[a.customer_id] = [])).push(a.rule_id);
    });
    return okResp({ rules: (rules.data || []).map(mapCustomerRule), assignments: byCustomer });
  } catch (e) { return failResp(e); }
}

export async function saveCustomerRule(rule) {
  try {
    const p = rule || {};
    const label = String(p.label || '').trim();
    if (!label) return failResp('Give the rule a name');
    const effect = ['none', 'no_card_fee', 'discount_pct', 'invoice_note'].includes(p.effect) ? p.effect : 'none';
    // Mirror the DB's check constraints with readable messages — the constraint
    // is the guarantee, this is the explanation.
    const pct = Number(p.effectValue);
    if (effect === 'discount_pct' && !(pct > 0 && pct <= 100)) return failResp('A discount rule needs a percent between 0 and 100.');
    const note = String(p.note || '').trim();
    if (effect === 'invoice_note' && !note) return failResp('An invoice-note rule needs the text to print.');
    const row = {
      label,
      effect,
      // Null out the field the chosen effect doesn't use, so switching a rule's
      // effect can't leave a stale percent behind that a later switch revives.
      effect_value: effect === 'discount_pct' ? pct : null,
      note: effect === 'invoice_note' ? note : (note || null),
      color: p.color || null,
      sort_order: Number(p.sortOrder) || 0,
      active: p.active !== false,
    };
    if (p.id) {
      const { error } = await supabase.from('customer_rules').update(row).eq('id', p.id);
      if (error) return failResp(_ruleErr(error));
      return okResp({ id: p.id });
    }
    row.id = 'CRULE-' + genIdSuffix();
    const { error } = await supabase.from('customer_rules').insert(row);
    if (error) return failResp(_ruleErr(error));
    return okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
function _ruleErr(error) {
  const m = String((error && error.message) || '');
  if (m.includes('customer_rules_label_uniq')) return 'There is already a rule with that name.';
  return error;
}

// Deleting a rule un-tags every customer who had it (FK cascade).
export async function deleteCustomerRule(id) {
  try {
    if (!id) return failResp('Missing id');
    const { error } = await supabase.from('customer_rules').delete().eq('id', id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// Replace a customer's whole rule set. Delete-then-insert rather than a diff:
// the set is tiny, and it makes the result a pure function of what was passed
// instead of depending on what happened to already be there.
export async function setCustomerRulesFor(customerId, ruleIds, by) {
  try {
    const cid = String(customerId || '').trim();
    if (!cid) return failResp('customerId required');
    const ids = Array.from(new Set((ruleIds || []).map((r) => String(r)).filter(Boolean)));
    const del = await supabase.from('customer_rule_assignments').delete().eq('customer_id', cid);
    if (del.error) return failResp(del.error);
    if (ids.length) {
      const rows = ids.map((rid) => ({ customer_id: cid, rule_id: rid, assigned_by: by ? String(by) : null }));
      const { error } = await supabase.from('customer_rule_assignments').insert(rows);
      if (error) return failResp(error);
    }
    return okResp({ count: ids.length });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// CUSTOMER PACKAGES (customer_packages + customer_package_uses)
// A negotiated block of work at one flat price — "2 cars for $700". See
// migration 20260817000000.
//
// POSTPAID, which is the part that matters everywhere else: the cars come in on
// different days and each gets its own invoice, but every use bills $0 except
// the last, which carries the whole block price. Nothing is owed, and no
// revenue is booked, until the block completes.
//
// The use LEDGER is written only by the invoicer's server function at Mark Paid
// (RLS here grants no insert), so everything in this file is read + define.
// ===========================================================================
const mapCustomerPackage = (p) => {
  const uses = (p.customer_package_uses || [])
    .slice().sort((a, b) => (a.use_index || 0) - (b.use_index || 0))
    .map((u) => ({
      invoiceId: u.invoice_id, soNum: S(u.so_number), useIndex: Number(u.use_index) || 0,
      billed: !!u.billed, usedAt: u.used_at, vehicle: S(u.vehicle),
      coveredAmount: u.covered_amount == null ? null : Number(u.covered_amount),
    }));
  const usesTotal = Number(p.uses_total) || 1;
  return {
    id: p.id, customerId: p.customer_id, label: S(p.label),
    price: Number(p.price) || 0,
    usesTotal, usesUsed: uses.length, remaining: Math.max(0, usesTotal - uses.length),
    status: p.status || 'open', note: S(p.note), expiresOn: p.expires_on || '',
    createdAt: p.created_at, uses,
    // Prepaid (Track Package): paid when sold, every use is a $0 credit.
    prepaid: !!p.prepaid, creditValue: p.credit_value == null ? null : Number(p.credit_value),
  };
};

// Every package with its use ledger embedded — the progress ("1 of 2 used") is
// the point of showing a package at all, so the two are never read apart.
export async function listCustomerPackages() {
  try {
    const { data, error } = await supabase
      .from('customer_packages')
      .select('*, customer_package_uses(invoice_id,so_number,use_index,billed,used_at,vehicle,covered_amount)')
      .order('created_at', { ascending: false });
    if (error) return failResp(error);
    return okResp({ packages: (data || []).map(mapCustomerPackage) });
  } catch (e) { return failResp(e); }
}

export async function saveCustomerPackage(pkg) {
  try {
    const p = pkg || {};
    const label = String(p.label || '').trim();
    if (!label) return failResp('Give the package a name');
    const price = Number(p.price);
    if (!(price > 0)) return failResp('A package needs a price greater than $0.');
    const usesTotal = Math.round(Number(p.usesTotal));
    if (!(usesTotal >= 1 && usesTotal <= 20)) return failResp('A package covers between 1 and 20 vehicles.');
    const row = {
      label, price, uses_total: usesTotal,
      prepaid: !!p.prepaid,
      // One visit's worth on a prepaid package; defaults to price ÷ paid credits.
      credit_value: p.prepaid ? (Number(p.creditValue) > 0 ? Number(p.creditValue) : Math.round((price / Math.max(1, usesTotal - 1)) * 100) / 100) : null,
      note: String(p.note || '').trim() || null,
      expires_on: /^\d{4}-\d{2}-\d{2}$/.test(String(p.expiresOn || '')) ? p.expiresOn : null,
    };
    if (p.id) {
      // uses_total is editable, but never below what's already been run — the
      // DB would then read as complete-and-overdrawn and the block could never
      // bill. customer_id is deliberately not patchable: the use ledger is tied
      // to invoices for THIS customer.
      const { data: cur, error: cerr } = await supabase
        .from('customer_packages')
        .select('uses_total, customer_package_uses(invoice_id)')
        .eq('id', p.id).maybeSingle();
      if (cerr) return failResp(cerr);
      const used = ((cur && cur.customer_package_uses) || []).length;
      if (usesTotal < used) return failResp(`This block has already run ${used} vehicle${used === 1 ? '' : 's'} — it can't be shrunk below that.`);
      const { error } = await supabase.from('customer_packages').update(row).eq('id', p.id);
      if (error) return failResp(error);
      return okResp({ id: p.id });
    }
    const cid = String(p.customerId || '').trim();
    if (!cid) return failResp('customerId required');
    row.id = 'CPKG-' + genIdSuffix();
    row.customer_id = cid;
    row.created_by = p.by ? String(p.by) : null;
    const { error } = await supabase.from('customer_packages').insert(row);
    if (error) return failResp(error);
    return okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}

// Deleting a package CASCADES its use ledger away, and those rows are the
// record of which real invoices ran against the block. So a block that has
// already been drawn on is cancelled (kept, inert, refused for new uses by the
// insert guard) rather than deleted; only an untouched one is actually removed.
export async function removeCustomerPackage(id) {
  try {
    if (!id) return failResp('Missing id');
    const { data, error } = await supabase
      .from('customer_package_uses').select('invoice_id').eq('package_id', id).limit(1);
    if (error) return failResp(error);
    if (data && data.length) {
      const { error: uerr } = await supabase.from('customer_packages').update({ status: 'cancelled' }).eq('id', id);
      return uerr ? failResp(uerr) : okResp({ cancelled: true });
    }
    const { error: derr } = await supabase.from('customer_packages').delete().eq('id', id);
    return derr ? failResp(derr) : okResp({ deleted: true });
  } catch (e) { return failResp(e); }
}

// Bring a cancelled block back. Status otherwise follows the ledger by trigger,
// so this only ever moves cancelled → open and lets the trigger's own rule
// (complete when full) reassert on the next use.
export async function reopenCustomerPackage(id) {
  try {
    if (!id) return failResp('Missing id');
    const { error } = await supabase.from('customer_packages').update({ status: 'open' }).eq('id', id).eq('status', 'cancelled');
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// REMINDERS (unified reminder tracker — reminders table)
// One row per tracked reminder (maintenance / appointment / email / custom).
// The nightly cron + a manual "Run due now" button call the server run-reminders
// action, which dispatches each due row by channel (bell+push / SMS / Outbox
// email draft) and rolls recurring rows forward.
// ===========================================================================
function mapReminder(r) {
  return {
    id: r.id, type: r.type || 'custom', title: r.title || '', notes: r.notes || '',
    dueAt: r.due_at, channel: r.channel || 'app', status: r.status || 'pending',
    recurDays: r.recur_days == null ? null : Number(r.recur_days),
    customerId: r.customer_id || '', customerName: r.customer_name || '',
    bookingId: r.booking_id || '', vehicleId: r.vehicle_id || '', vehicleDesc: r.vehicle_desc || '',
    phone: r.phone || '', email: r.email || '',
    lastFiredAt: r.last_fired_at || null, createdBy: r.created_by || '', createdAt: r.created_at,
  };
}
function reminderToRow(d) {
  const row = {};
  const set = (k, v) => { if (v !== undefined) row[k] = v; };
  set('type', d.type); set('title', d.title); set('notes', d.notes || null);
  set('due_at', d.dueAt); set('channel', d.channel); set('status', d.status);
  set('recur_days', d.recurDays === '' || d.recurDays == null ? null : Number(d.recurDays));
  set('customer_id', d.customerId || null); set('customer_name', d.customerName || null);
  set('booking_id', d.bookingId || null); set('vehicle_id', d.vehicleId || null);
  set('vehicle_desc', d.vehicleDesc || null); set('phone', d.phone || null); set('email', d.email || null);
  return row;
}
export async function getReminders() {
  try {
    const { data, error } = await supabase.from('reminders').select('*').order('due_at', { ascending: true });
    if (error) return failResp(error);
    return okResp({ reminders: (data || []).map(mapReminder) });
  } catch (e) { return failResp(e); }
}
// Watchdog for the Dashboard banner: automated messages (appointment reminders +
// ready-for-pickup texts) that should have gone out but are still pending/sending
// past their send time — the tell-tale of the reminder cron having stopped (e.g.
// a 401 like the missing-CRON_SECRET outage). 4h grace covers the twice-daily
// cron cadence so a just-due row isn't flagged. Returns { count, oldest }.
export async function getStuckReminderCount() {
  try {
    const cutoff = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString();
    const { data, error } = await supabase.from('reminders')
      .select('due_at')
      .in('status', ['pending', 'sending'])
      .in('created_by', ['auto-appt-reminder', 'ready-text'])
      .lt('due_at', cutoff)
      .order('due_at', { ascending: true });
    if (error) return okResp({ count: 0, oldest: null });
    const rows = data || [];
    return okResp({ count: rows.length, oldest: rows.length ? rows[0].due_at : null });
  } catch (e) { return okResp({ count: 0, oldest: null }); }
}
export async function saveReminder(d) {
  try {
    const row = reminderToRow(d);
    row.updated_at = new Date().toISOString();
    if (d.id) {
      const { error } = await supabase.from('reminders').update(row).eq('id', d.id);
      return error ? failResp(error) : okResp({ id: d.id });
    }
    row.id = 'R-' + genIdSuffix();
    row.created_by = d.createdBy || null;
    row.created_at = new Date().toISOString();
    row.status = row.status || 'pending';
    const { error } = await supabase.from('reminders').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
export async function setReminderStatus(id, status) {
  try {
    const { error } = await supabase.from('reminders').update({ status, updated_at: new Date().toISOString() }).eq('id', id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
// Push a reminder's due date out by N days and re-arm it (status → pending).
export async function snoozeReminder(id, days) {
  try {
    const { data: row } = await supabase.from('reminders').select('due_at').eq('id', id).maybeSingle();
    const base = row && row.due_at ? new Date(row.due_at) : new Date();
    base.setDate(base.getDate() + (Number(days) || 1));
    const iso = base.toISOString();
    const { error } = await supabase.from('reminders').update({ due_at: iso, status: 'pending', updated_at: new Date().toISOString() }).eq('id', id);
    return error ? failResp(error) : okResp({ dueAt: iso });
  } catch (e) { return failResp(e); }
}
export async function deleteReminder(id) {
  try {
    const { error } = await supabase.from('reminders').delete().eq('id', id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
// Owner/principal "run due reminders now" — the server dispatches every pending
// reminder whose due date has passed (same code the nightly cron runs).
export async function runDueReminders() {
  try {
    const { data: sess } = await supabase.auth.getSession();
    const tok = sess && sess.session && sess.session.access_token;
    const res = await fetch('/api/invoicer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
      body: JSON.stringify({ action: 'run-reminders' }),
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp(j) : failResp((j && j.error) || 'Run failed');
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// SHOP NEEDS (detailer submits → owner/principal review)
// ===========================================================================
const _IMP_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };
function shopNeedToRow(d) {
  const row = {};
  const set = (k, v) => { if (v !== undefined) row[k] = v; };
  set('title', d.title); set('description', d.description);
  set('category', d.category); set('importance', d.importance);
  if (d.customFields !== undefined) row.custom_fields = Array.isArray(d.customFields) ? d.customFields : [];
  return row;
}
export async function listShopNeeds({ status } = {}) {
  try {
    let q = supabase.from('shop_needs').select('*');
    if (status) q = q.eq('status', status);
    const { data, error } = await q;
    if (error) return failResp(error);
    const out = (data || []).map(mapShopNeed).sort((a, b) => {
      const aOpen = a.status === 'new', bOpen = b.status === 'new';
      if (aOpen !== bOpen) return aOpen ? -1 : 1;
      const ir = (_IMP_RANK[a.importance] ?? 2) - (_IMP_RANK[b.importance] ?? 2);
      if (ir !== 0) return ir;
      return (b.submittedAt || '').localeCompare(a.submittedAt || '');
    });
    return okResp({ needs: out });
  } catch (e) { return failResp(e); }
}
export async function addShopNeed(data) {
  try {
    const row = {
      id: 'SN-' + genIdSuffix(),
      submitted_by: data.submittedBy || 'detailer',
      status: 'new',
      submitted_at: new Date().toISOString(),
      ...shopNeedToRow(data),
    };
    const { error } = await supabase.from('shop_needs').insert(row);
    if (error) return failResp(error);
    const who = data.submittedBy || 'detailer';
    // The bell row gets a K/A chip from SiteTag, but the PUSH is plain text on
    // a lock screen — so the shop is named in the message itself.
    const where = siteName(getActiveSite());
    const msg = '🛒 New shop need from ' + who + ' at ' + where + ': ' + String(data.title || '').slice(0, 70);
    await _insertNotification({ type: 'shop_need', message: msg, forRole: 'owner' });
    await _insertNotification({ type: 'shop_need', message: msg, forRole: 'principal' });
    return okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
export async function reviewShopNeed(id, status, reviewNote, reviewedBy) {
  try {
    let need = null;
    try {
      const { data } = await supabase.from('shop_needs').select('*').eq('id', id).maybeSingle();
      if (data) need = mapShopNeed(data);
    } catch (_) {}
    const patch = { status, reviewed_by: reviewedBy || '', reviewed_at: new Date().toISOString() };
    if (reviewNote !== undefined) patch.review_note = reviewNote || '';
    const { error } = await supabase.from('shop_needs').update(patch).eq('id', id);
    if (error) return failResp(error);
    if (need && need.submittedBy) {
      const verb = status === 'approved' ? 'approved' : status === 'declined' ? 'declined' : 'resolved';
      const icon = status === 'declined' ? '🚫' : '✅';
      await _insertNotification({
        type: 'shop_need_reviewed',
        message: icon + ' Your shop need was ' + verb + ': ' + String(need.title || '').slice(0, 60),
        forRole: need.submittedBy,
      });
    }
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function deleteShopNeed(id) {
  try {
    const { error } = await supabase.from('shop_needs').delete().eq('id', id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// WAITLIST (customers waiting for the next opening)
// ===========================================================================
const mapWaitlistEntry = (r) => ({
  id: r.id, customerId: r.customer_id, name: S(r.name), email: S(r.email),
  phone: S(r.phone), notes: S(r.notes), addedBy: S(r.added_by), createdAt: isoTs(r.created_at),
});
export async function listWaitlist() {
  try {
    const { data, error } = await supabase.from('waitlist').select('*').order('created_at');
    if (error) return failResp(error);
    return okResp({ waitlist: (data || []).map(mapWaitlistEntry) });
  } catch (e) { return failResp(e); }
}
export async function addWaitlist(d) {
  try {
    const row = {
      id: 'WL-' + genIdSuffix(),
      customer_id: (d && d.customerId) || null,
      name: (d && d.name) || '',
      email: (d && d.email) || '',
      phone: (d && d.phone) || '',
      notes: (d && d.notes) || '',
      added_by: (d && d.addedBy) || '',
      created_at: new Date().toISOString(),
    };
    const { error } = await supabase.from('waitlist').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
export async function removeWaitlist(id) {
  try {
    const { error } = await supabase.from('waitlist').delete().eq('id', id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
// Manually lock a freed slot (owner "Mark filled" — booked someone into it
// directly), so waitlist claim links for this slot show "just taken". Same
// signature the claim RPC uses. Returns {alreadyLocked} if someone beat you to it.
export async function lockWaitlistSlot(slot, claimant) {
  try {
    const date = String((slot && slot.date) || '').trim();
    const time = String((slot && slot.startTime) || '').trim();
    const sig = date + '|' + time;
    if (sig === '|') return failResp('Set the opening date & time first.');
    const { data: existing } = await supabase.from('claimed_slots').select('slot_sig').eq('slot_sig', sig).maybeSingle();
    if (existing) return okResp({ alreadyLocked: true });
    const { error } = await supabase.from('claimed_slots').insert({
      slot_sig: sig, date, start_time: time,
      service: (slot && slot.service) || null, claimant: claimant || 'Filled manually',
    });
    if (error) return failResp(error);
    return okResp({ locked: true });
  } catch (e) { return failResp(e); }
}
// Release a claimed slot's lock (from claim_waitlist_slot / lockWaitlistSlot) so it
// reopens for other waitlisters — called on Deny or "Undo mark filled". Signature
// must match the RPC's ("<date>|<startTime>"). Best-effort; a no-op pre-migration.
export async function releaseWaitlistSlot(slot) {
  try {
    const sig = String((slot && slot.date) || '').trim() + '|' + String((slot && slot.startTime) || '').trim();
    if (sig === '|') return okResp({}); // no date/time signature to unlock
    await supabase.from('claimed_slots').delete().eq('slot_sig', sig);
    return okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// BLOCKS / TIME OFF
// ===========================================================================
export async function getBlocks(startISO, endISO) {
  try {
    let q = supabase.from('blocks').select('*');
    if (saneDate(startISO)) q = q.gte('date', startISO);
    if (saneDate(endISO)) q = q.lte('date', endISO);
    const { data, error } = await q;
    return error ? failResp(error) : okResp({ blocks: (data || []).map(mapBlock) });
  } catch (e) { return failResp(e); }
}
export async function addBlock(date, bay, reason, createdBy, timeOfDay) {
  try {
    const row = { id: 'BLK-' + genIdSuffix(), date, bay: bay || '', reason: reason || '', created_by: createdBy || '', created_at: new Date().toISOString(), time_of_day: timeOfDay || '' };
    const { error } = await supabase.from('blocks').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
export async function removeBlock(id) {
  try { const { error } = await supabase.from('blocks').delete().eq('id', id); return error ? failResp(error) : okResp({}); }
  catch (e) { return failResp(e); }
}
export async function getTimeOff(startISO, endISO) {
  try {
    let q = supabase.from('time_off').select('*');
    if (saneDate(startISO)) q = q.gte('date', startISO);
    if (saneDate(endISO)) q = q.lte('date', endISO);
    const { data, error } = await q;
    return error ? failResp(error) : okResp({ timeOff: (data || []).map(mapTimeOff) });
  } catch (e) { return failResp(e); }
}
export async function addTimeOff(date, role, reason, createdBy) {
  try {
    const row = { id: 'TO-' + genIdSuffix(), date, role: role || '', reason: reason || '', created_by: createdBy || '', created_at: new Date().toISOString() };
    const { error } = await supabase.from('time_off').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
export async function removeTimeOff(id) {
  try { const { error } = await supabase.from('time_off').delete().eq('id', id); return error ? failResp(error) : okResp({}); }
  catch (e) { return failResp(e); }
}

// ===========================================================================
// GIFT CARDS
// ===========================================================================
export async function getGiftCards() {
  try {
    const { data, error } = await supabase.from('gift_cards').select('*');
    if (error) return failResp(error);
    const cards = (data || []).map(mapGiftCard).sort((a, b) => {
      if (a.status !== b.status) return a.status === 'open' ? -1 : 1;
      return String(b.issued).localeCompare(String(a.issued));
    });
    return okResp({ cards });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// NOTIFICATIONS
// ===========================================================================
// Short one-line preview of an SMS for the confirmation notification — strips
// the automated-message/STOP footer and collapses whitespace.
function _smsPreview(body) {
  return String(body || '')
    .replace(/\(Automated message from 3C Detailing\. Reply STOP to opt out\.\)\s*$/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60);
}
// Drop a principal-only bell notification confirming an SMS was accepted by
// Twilio, or that it failed. Best-effort and non-blocking. Note this reports
// send-acceptance vs immediate failure — a true delivery bounce after Twilio
// accepts the message would need a status-callback webhook (not wired yet).
function _notifySmsResult(to, body, ok, detail) {
  const prev = _smsPreview(body);
  const message = ok
    ? '📱 Text sent to ' + (detail || to || 'customer') + (prev ? ': "' + prev + '"' : '')
    : '📵 Text to ' + (to || 'customer') + ' failed' + (detail ? ': ' + detail : '');
  // Stash the FULL text (and recipient) in meta — `message` only carries a
  // 60-char preview, and the Notification Hub expands to show the whole thing.
  _insertNotification({
    type: ok ? 'sms_sent' : 'sms_failed', message, forRole: 'principal',
    meta: { smsBody: String(body || ''), smsTo: String(detail || to || '') },
  });
}
// Send a text via the Twilio-backed Vercel function (owner/principal session).
// opts.quiet suppresses the per-send principal bell — used for bulk blasts (e.g.
// the waitlist notice) where one notification per recipient would be noise; the
// caller reports its own summary instead.
// `opts.bookingId` lets the SERVER decide the real recipient: a job raised by a
// dealership is alerted to the dealership, never to their customer. Pass it on
// anything job-shaped — the redirect can only apply where it's supplied.
export async function sendSms(to, body, opts) {
  const quiet = !!(opts && opts.quiet);
  // `who` overrides the recipient in the bell copy: on a dealer job the
  // customer's number is not the recipient and never was, so "Text to customer
  // failed" would be a lie about who we were even trying to reach.
  const notify = (ok, detail, who) => { if (!quiet) _notifySmsResult(who || to, body, ok, detail); };
  try {
    // A dealer booking legitimately has no customer number to send to — the
    // server resolves the dealership's instead — so only short-circuit here when
    // there is nothing for it to resolve FROM.
    if (!to && !(opts && opts.bookingId)) { notify(false, 'No phone number on file'); return failResp('No phone number on file'); }
    async function postWith(tok) {
      const res = await fetch('/api/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
        body: JSON.stringify({ to, body, bookingId: (opts && opts.bookingId) || undefined }),
      });
      const j = await res.json().catch(() => ({}));
      return { status: res.status, j };
    }
    const { data: sess } = await supabase.auth.getSession();
    let tok = sess && sess.session && sess.session.access_token;
    let { status, j } = await postWith(tok);
    // Stale/expired access token (a backgrounded tab can let it lapse) → the
    // server rejects with 401 "unauthorized". Refresh the session once and retry
    // so a slightly-stale login doesn't drop a real text.
    if (status === 401 || (j && /unauthor/i.test(String(j.error || '')))) {
      let fresh = '';
      try { const r = await supabase.auth.refreshSession(); fresh = r && r.data && r.data.session && r.data.session.access_token; } catch (_) {}
      if (fresh && fresh !== tok) ({ status, j } = await postWith(fresh));
    }
    if (j && j.success) { notify(true, j.redirectedTo || j.to); return okResp({ sid: j.sid, redirectedTo: S(j.redirectedTo) }); }
    // Friendlier copy for a genuinely-dead session than a bare "unauthorized".
    const errMsg = (status === 401 || (j && /unauthor/i.test(String(j.error || ''))))
      ? 'session expired — reopen the app and try again'
      : ((j && j.error) || 'SMS failed');
    notify(false, errMsg, j && j.dealer);
    const fr = failResp(errMsg);
    if (j && j.optedOut) fr.optedOut = true; // let bulk callers count opt-outs separately from real failures
    // The card shows its own copy for "this dealership has no number yet", so
    // the verdict has to survive failResp(), which only keeps {success,error}.
    if (j && j.dealerMissingPhone) { fr.dealerMissingPhone = true; fr.dealer = S(j.dealer); }
    return fr;
  } catch (e) {
    notify(false, (e && e.message) || 'Network error');
    return failResp(e);
  }
}

// ===========================================================================
// MEMBERS (Stripe-backed memberships) — data layer for the Membership tab.
// Staff add a customer as a member ('pending'), text them a Stripe Payment Link
// (sendMembershipSignup: link carries client_reference_id=<customerId> +
// prefilled_email), and on signup the membership-webhook (api/invoicer.js)
// re-fetches the Stripe session/subscription and flips the row to 'active' with
// plan/price/renewal. Visits + amount spent are computed live from the
// customer's completed bookings / linked invoices since joined_at.
// ===========================================================================
const mapMember = (r, cust, extra) => ({
  id: S(r.id), customerId: S(r.customer_id),
  status: S(r.status) || 'pending', plan: S(r.plan), price: N(r.price), interval: S(r.interval),
  stripeCustomerId: S(r.stripe_customer_id), stripeSubscriptionId: S(r.stripe_subscription_id),
  joinedAt: isoTs(r.joined_at), currentPeriodEnd: isoTs(r.current_period_end),
  signupLinkSentAt: isoTs(r.signup_link_sent_at), canceledAt: isoTs(r.canceled_at),
  notes: S(r.notes), createdAt: isoTs(r.created_at), updatedAt: isoTs(r.updated_at),
  // Manual (non-Stripe) billing — staff record the service, the amount paid and
  // the recurring payment date, and the daily biller posts that amount into the
  // revenue stream each time the date comes around (api/invoicer.js
  // runMembershipBilling). 'stripe' (default) leaves dues to the invoice.paid
  // webhook. billingAmount/billingService fall back to price/plan when blank.
  billingMode: S(r.billing_mode) || 'stripe',
  billingService: S(r.billing_service), billingAmount: N(r.billing_amount),
  nextBillingDate: S(r.next_billing_date).slice(0, 10),
  lastBilledOn: S(r.last_billed_on).slice(0, 10),
  name: cust ? (S(cust.full_name) || [cust.first_name, cust.last_name].filter(Boolean).join(' ')) : '',
  phone: cust ? S(cust.phone) : '', email: cust ? S(cust.email) : '',
  // ---- Counters. Every one is DERIVED from real rows (invoices / bookings /
  // the customer record), so anything that happened before 3CHA tracked it read
  // as zero with no way to fix it. Each now carries a signed ADJUSTMENT from the
  // member row: the exposed value is counted + adjustment, and `*Raw` / `*Adj`
  // are kept alongside so the editor can show "N counted +M adj." and compute a
  // new adjustment from the real number the operator types.
  lifetimeVisits: (cust ? (Number(cust.visit_count) || 0) : 0) + N(r.lifetime_visits_adj),
  lifetimeSpend: (cust ? (Number(cust.total_spend) || 0) : 0) + N(r.lifetime_spend_adj),
  lifetimeVisitsRaw: cust ? (Number(cust.visit_count) || 0) : 0,
  lifetimeSpendRaw: cust ? (Number(cust.total_spend) || 0) : 0,
  lifetimeVisitsAdj: N(r.lifetime_visits_adj), lifetimeSpendAdj: N(r.lifetime_spend_adj),
  visitsRaw: (extra && extra.visits) || 0, visitsAdj: N(r.visits_adj),
  duesPaidRaw: (extra && extra.duesPaid) || 0, duesPaidAdj: N(r.dues_paid_adj),
  monthsPaidRaw: (extra && extra.monthsPaid) || 0, monthsPaidAdj: N(r.months_paid_adj),
  washPaidRaw: (extra && extra.washPaid) || 0, washPaidAdj: N(r.wash_paid_adj),
  washUsedRaw: (extra && extra.washUsed) || 0, washUsedAdj: N(r.wash_used_adj),
  counterAdjAt: isoTs(r.counter_adj_at), counterAdjBy: S(r.counter_adj_by),
  visits: ((extra && extra.visits) || 0) + N(r.visits_adj),  // completed/delivered visits since joined_at
  // Money this member has accrued through membership FEES (all-time sum of their
  // MEMBER- dues rows) — NOT appointment revenue. Membership washes always
  // invoice at $0, so appointment totals would read ~$0 and mean nothing here.
  duesPaid: ((extra && extra.duesPaid) || 0) + N(r.dues_paid_adj),
  // Billing cycles paid, all-time (one paid MEMBER- dues invoice = one month on
  // a monthly plan). Pairs with duesPaid: "$550 accrued · 2 mo paid".
  monthsPaid: ((extra && extra.monthsPaid) || 0) + N(r.months_paid_adj),
  // Wash-allowance ledger for the current calendar year, run as a real
  // payments-in / washes-out balance: `washPaid` = monthly dues payments recorded
  // this year (each MEMBER- revenue row), `washUsed` = membership washes redeemed
  // this year (each is_membership invoice). Balance > 0 = washes banked (e.g.
  // unused winter months, spendable in summer); < 0 = used ahead of what's been
  // paid (OK while the year total stays under the 12 cap).
  washYear: (extra && extra.washYear) || new Date().getFullYear(),
  washPaid: ((extra && extra.washPaid) || 0) + N(r.wash_paid_adj),
  washUsed: ((extra && extra.washUsed) || 0) + N(r.wash_used_adj),
  washCap: 12,
  washBalance: (((extra && extra.washPaid) || 0) + N(r.wash_paid_adj))
    - (((extra && extra.washUsed) || 0) + N(r.wash_used_adj)),
});

// Default membership signup link until a real Stripe subscription Payment Link
// is set in the Membership tab's Settings. Points at the marketing site's shop
// page (Bronze/Silver/Gold tiers). client_reference_id + prefilled_email get
// appended; the shop page ignores unknown params. NOTE: auto-population of a
// member on signup needs a real Stripe Payment Link + the webhook (see the
// 2026-07-15 "To go live" TODO) — this interim link just gets them to the tiers.
const _DEFAULT_MEMBERSHIP_LINK = 'https://3cdetailingpro.com/shop';

// Build the Stripe Payment Link for a specific customer — client_reference_id
// lets the webhook match the completed signup back to this exact customer;
// prefilled_email pre-fills Stripe's hosted page.
// The link a member should be texted: a plan-specific one when their plan
// matches a key in config.planLinks (case-insensitive — staff type "gold
// winter", the key is "Gold Winter"), else nothing, and the caller falls back
// to the general link. Winter Wash Club tiers live here.
function _planLinkFor(cfg, plan) {
  const links = cfg && cfg.planLinks && typeof cfg.planLinks === 'object' ? cfg.planLinks : null;
  const want = String(plan || '').trim().toLowerCase();
  if (!links || !want) return '';
  for (const k of Object.keys(links)) {
    if (String(k).trim().toLowerCase() === want && links[k]) return String(links[k]);
  }
  return '';
}
function _membershipSignupUrl(base, customerId, email) {
  try {
    const u = new URL(base);
    if (customerId) u.searchParams.set('client_reference_id', customerId);
    if (email) u.searchParams.set('prefilled_email', email);
    return u.toString();
  } catch (_) {
    const parts = [];
    if (customerId) parts.push('client_reference_id=' + encodeURIComponent(customerId));
    if (email) parts.push('prefilled_email=' + encodeURIComponent(email));
    if (!parts.length) return base;
    return base + (String(base).includes('?') ? '&' : '?') + parts.join('&');
  }
}

export async function listMembers() {
  try {
    const [mem, cust] = await Promise.all([
      supabase.from('members').select('*').order('created_at', { ascending: false }),
      selectAllRows(() => supabase.from('customers').select('id,first_name,last_name,full_name,phone,email,visit_count,total_spend').order('id')),
    ]);
    if (mem.error) return failResp(mem.error);
    const custById = {}; (cust.data || []).forEach((c) => { custById[c.id] = c; });

    // Visits + amount spent SINCE joining (best-effort — a read failure just
    // yields 0 for the period metrics; lifetime totals still render).
    const joinedByCust = {};
    (mem.data || []).forEach((m) => { if (m.customer_id) joinedByCust[m.customer_id] = m.joined_at ? String(m.joined_at).slice(0, 10) : ''; });
    const custIds = Object.keys(joinedByCust);
    const now = new Date();
    const yearStart = now.getFullYear() + '-01-01';
    const visitsById = {}, duesById = {}, monthsById = {}, washPaidById = {}, washUsedById = {};
    if (custIds.length) {
      const [bk, wl] = await Promise.all([
        supabase.from('bookings').select('id,customer_id,date,status').in('customer_id', custIds),
        // Membership rows for these customers, ALL-TIME: dues payments (MEMBER-)
        // and redeemed membership washes (is_membership), both keyed on
        // customer_id. Dues are summed all-time for `duesPaid`; the wash tallies
        // stay scoped to the current calendar year (filtered below).
        supabase.from('invoices').select('id,customer_id,date,total,is_membership').in('customer_id', custIds),
      ]);
      (bk.error ? [] : (bk.data || [])).forEach((b) => {
        const bd = String(b.date || '').slice(0, 10);
        const j = joinedByCust[b.customer_id] || '';
        if (['complete', 'delivered'].includes(b.status) && (!j || bd >= j)) {
          visitsById[b.customer_id] = (visitsById[b.customer_id] || 0) + 1;
        }
      });
      (wl.error ? [] : (wl.data || [])).forEach((r) => {
        const cid = r.customer_id; if (!cid) return;
        const isDues = String(r.id || '').startsWith('MEMBER-');
        // Money accrued from membership FEES (all-time) — deliberately NOT
        // appointment revenue: membership washes always invoice at $0, so
        // appointment totals say nothing about what a member has paid in.
        // …and how many billing cycles they've actually paid for (one MEMBER- row
        // per paid dues invoice = one month on a monthly plan).
        if (isDues) {
          duesById[cid] = (duesById[cid] || 0) + (Number(r.total) || 0);
          monthsById[cid] = (monthsById[cid] || 0) + 1;
        }
        if (String(r.date || '').slice(0, 10) < yearStart) return;
        if (isDues) washPaidById[cid] = (washPaidById[cid] || 0) + 1;
        else if (r.is_membership) washUsedById[cid] = (washUsedById[cid] || 0) + 1;
      });
    }

    const members = (mem.data || []).map((m) => mapMember(m, custById[m.customer_id], {
      visits: visitsById[m.customer_id] || 0, duesPaid: duesById[m.customer_id] || 0,
      monthsPaid: monthsById[m.customer_id] || 0,
      washYear: now.getFullYear(),
      washPaid: washPaidById[m.customer_id] || 0,
      washUsed: washUsedById[m.customer_id] || 0,
    }));
    return okResp({ members });
  } catch (e) { return failResp(e); }
}

// Add a customer as a member ('pending'). One member row per customer — if they
// already have one, returns it (idempotent) rather than erroring.
export async function addMember(customerId, opts) {
  try {
    if (!customerId) return failResp('customerId required');
    const { data: existing } = await supabase.from('members').select('id').eq('customer_id', customerId).maybeSingle();
    if (existing && existing.id) return okResp({ id: existing.id, existed: true });
    const o = opts || {};
    const now = new Date().toISOString();
    const row = {
      id: 'M-' + genIdSuffix(), customer_id: customerId, status: 'pending',
      plan: o.plan ? String(o.plan) : null,
      price: (o.price !== undefined && o.price !== '' && o.price !== null) ? Number(o.price) : null,
      interval: o.interval ? String(o.interval) : null,
      notes: o.notes ? String(o.notes) : null,
      created_at: now, updated_at: now,
    };
    const { error } = await supabase.from('members').insert(row);
    if (error) return failResp(error);
    _bustPhoneMap();
    return okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}

// Staff edit of a member row (status/plan/price/interval/notes). Stripe-driven
// fields are normally set by the webhook, but staff can correct them here.
export async function updateMember(id, patch) {
  try {
    if (!id) return failResp('id required');
    const p = patch || {};
    const row = { updated_at: new Date().toISOString() };
    if (p.status !== undefined) row.status = String(p.status);
    if (p.plan !== undefined) row.plan = p.plan === '' ? null : String(p.plan);
    if (p.price !== undefined) row.price = (p.price === '' || p.price === null) ? null : Number(p.price);
    if (p.interval !== undefined) row.interval = p.interval === '' ? null : String(p.interval);
    if (p.notes !== undefined) row.notes = p.notes === '' ? null : String(p.notes);
    // Manual-billing details. Empty string clears the column (back to the
    // price/plan fallback, or "no recurring date set" → the biller skips them).
    if (p.billingMode !== undefined) row.billing_mode = p.billingMode === 'manual' ? 'manual' : 'stripe';
    if (p.billingService !== undefined) row.billing_service = p.billingService === '' ? null : String(p.billingService);
    if (p.billingAmount !== undefined) row.billing_amount = (p.billingAmount === '' || p.billingAmount === null) ? null : Number(p.billingAmount);
    if (p.nextBillingDate !== undefined) {
      const d = String(p.nextBillingDate || '').slice(0, 10);
      row.next_billing_date = /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
    }
    // Counter corrections. The caller sends a signed ADJUSTMENT per counter
    // (typed real number − what's counted), so future invoices/washes/bookings
    // keep adding on top instead of the correction going stale. 0 = no correction.
    const adjCols = {
      washPaidAdj: 'wash_paid_adj', washUsedAdj: 'wash_used_adj',
      duesPaidAdj: 'dues_paid_adj', monthsPaidAdj: 'months_paid_adj',
      visitsAdj: 'visits_adj', lifetimeVisitsAdj: 'lifetime_visits_adj',
      lifetimeSpendAdj: 'lifetime_spend_adj',
    };
    let touchedAdj = false;
    Object.keys(adjCols).forEach((k) => {
      if (p[k] === undefined) return;
      const v = Number(p[k]);
      row[adjCols[k]] = Number.isFinite(v) ? v : 0;
      touchedAdj = true;
    });
    if (touchedAdj) {
      row.counter_adj_at = new Date().toISOString();
      if (p.adjBy) row.counter_adj_by = String(p.adjBy);
    }
    if (p.status === 'canceled') row.canceled_at = new Date().toISOString();
    // .select() for a row COUNT, not just an error: members_upd is
    // auth_role()-gated, so a lapsed JWT makes this match 0 rows and return NO
    // error — a silent no-op (the documented stale-JWT → RLS-deny pattern).
    // Refresh + retry once, then report honestly. Matters more now that billing
    // details (amount + recurring date) live on this row.
    let { data: hit, error } = await supabase.from('members').update(row).eq('id', id).select('id');
    if (error) return failResp(error);
    if (!hit || !hit.length) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      const retry = await supabase.from('members').update(row).eq('id', id).select('id');
      if (retry.error) return failResp(retry.error);
      if (!retry.data || !retry.data.length) return failResp("Save didn't go through — session may have expired. Reopen the app and try again.");
    }
    _bustPhoneMap();
    return okResp({});
  } catch (e) { return failResp(e); }
}

// "Record payment now" — run the manual biller for one member (owner/principal).
// Posts only cycles that are actually DUE, keyed per cycle, so pressing it twice
// (or pressing it before the date) can't double-count or pull a payment forward.
export async function billMemberNow(memberId) {
  try {
    if (!memberId) return failResp('memberId required');
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    if (!tok) return failResp('no session');
    const res = await fetch('/api/invoicer?action=bill-member-now', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + tok },
      body: JSON.stringify({ memberId }),
    });
    const j = await res.json().catch(() => ({}));
    if (!j || !j.success) return failResp((j && j.error) || 'Could not record the payment');
    await _bustRevenueCaches(); // dues just landed in the ledger
    return okResp(j);
  } catch (e) { return failResp(e.message); }
}

export async function removeMember(id) {
  try {
    if (!id) return failResp('id required');
    const { error } = await supabase.from('members').delete().eq('id', id);
    if (error) return failResp(error);
    _bustPhoneMap();
    return okResp({});
  } catch (e) { return failResp(e); }
}

// ---------------------------------------------------------------------------
// Customer Tabs — the running "who owes us money" account (Revenue → Tab).
// ---------------------------------------------------------------------------
// customer_tabs is the registry (a customer can sit on the tab at $0);
// customer_tab_entries is the ledger, `kind` 'charge' | 'payment' with a
// always-positive amount. Balance = charged − paid.
//
// ⚠️ Tab entries are NOT revenue. The invoice already recorded its full amount
// to `invoices` when it was marked Paid — the tab only tracks the slice that
// hasn't been collected yet. So none of these writes touch the revenue caches,
// and settling a tab must never post income a second time.

// Shape one registry row + its ledger into what the UI renders.
function _mapTab(reg, entries, cust) {
  const list = (entries || []).slice().sort((a, b) => String(b.entry_date || '').localeCompare(String(a.entry_date || '')) || String(b.created_at || '').localeCompare(String(a.created_at || '')));
  const charged = list.filter((e) => e.kind !== 'payment').reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const paid = list.filter((e) => e.kind === 'payment').reduce((s, e) => s + (Number(e.amount) || 0), 0);
  // Running balance AFTER each entry, so the ledger reads as a statement when a
  // customer has several invoices on their tab. Accumulated oldest→newest (the
  // only order that makes sense), then read back onto the newest-first list.
  const asc = list.slice().reverse();
  const runningById = {};
  let run = 0;
  asc.forEach((e) => {
    run += (e.kind === 'payment' ? -1 : 1) * (Number(e.amount) || 0);
    runningById[e.id] = Math.round(run * 100) / 100;
  });
  // Distinct invoices represented — the collapsed row shows this so "3 invoices"
  // is visible without expanding.
  const invoiceNums = Array.from(new Set(list.map((e) => String(e.so_number || '').trim()).filter(Boolean)));
  // Per-invoice rollup, so someone paying an invoice off across several
  // instalments can see what's left on each one. A payment counts against an
  // invoice only when it was explicitly applied to it (`applies_to_so`);
  // payments left unallocated reduce the overall balance and are reported
  // separately rather than being spread across invoices by guesswork.
  const invoices = invoiceNums.map((so) => {
    const chargedOn = list.filter((e) => e.kind !== 'payment' && String(e.so_number || '').trim() === so)
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const paidOn = list.filter((e) => e.kind === 'payment' && String(e.applies_to_so || '').trim() === so)
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const first = list.filter((e) => String(e.so_number || '').trim() === so).slice(-1)[0];
    return {
      so,
      charged: Math.round(chargedOn * 100) / 100,
      paid: Math.round(paidOn * 100) / 100,
      remaining: Math.round(Math.max(0, chargedOn - paidOn) * 100) / 100,
      date: (first && first.entry_date) || '',
    };
  }).sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const allocatedPaid = list.filter((e) => e.kind === 'payment' && String(e.applies_to_so || '').trim())
    .reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const name = (cust && (cust.full_name || [cust.first_name, cust.last_name].filter(Boolean).join(' '))) || reg.customer_name || '';
  return {
    id: reg.id,
    label: String(reg.label || 'Main'),
    customerId: reg.customer_id,
    name: String(name || '').trim() || reg.customer_id,
    phone: (cust && String(cust.phone || '')) || '',
    email: (cust && String(cust.email || '')) || '',
    notes: reg.notes || '',
    active: reg.active !== false,
    openedAt: reg.opened_at || null,
    charged: Math.round(charged * 100) / 100,
    paid: Math.round(paid * 100) / 100,
    balance: Math.round((charged - paid) * 100) / 100,
    invoiceNums,
    invoiceCount: invoiceNums.length,
    invoices,
    // Payments not tied to any invoice — they still reduce the balance.
    unallocatedPaid: Math.round((paid - allocatedPaid) * 100) / 100,
    entries: list.map((e) => ({
      id: e.id,
      kind: e.kind === 'payment' ? 'payment' : 'charge',
      amount: Number(e.amount) || 0,
      soNumber: e.so_number || '',
      appliesToSo: e.applies_to_so || '',
      note: e.note || '',
      date: e.entry_date || '',
      createdBy: e.created_by || '',
      running: runningById[e.id] || 0,
    })),
  };
}

// A customer can hold SEVERAL named tabs ("Personal", "Halloran Excavating").
// Returns both shapes: `tabs` is the flat list (one entry per tab), and
// `customers` is the same data grouped for the UI, which renders a customer
// headline with their tabs nested under it.
//
// ⚠️ Entries are bucketed by `tab_id`, never by customer_id — see migration
// 20260814100000. Grouping by customer here would fold a customer's second tab
// into their first and quietly misreport both balances.
export async function listCustomerTabs() {
  try {
    const [reg, ent, cust] = await Promise.all([
      supabase.from('customer_tabs').select('*'),
      supabase.from('customer_tab_entries').select('*'),
      selectAllRows(() => supabase.from('customers').select('id,first_name,last_name,full_name,phone,email').order('id')),
    ]);
    if (reg.error) return failResp(reg.error);
    if (ent.error) return failResp(ent.error);
    const custById = {}; (cust.data || []).forEach((c) => { custById[c.id] = c; });
    const byTab = {};
    (ent.data || []).forEach((e) => { (byTab[e.tab_id] || (byTab[e.tab_id] = [])).push(e); });
    // No orphan fallback any more: `tab_id` carries an ON DELETE CASCADE FK, so
    // an entry can no longer outlive the tab it belongs to.
    const tabs = (reg.data || [])
      .map((r) => _mapTab(r, byTab[r.id], custById[r.customer_id]))
      .sort((a, b) => b.balance - a.balance || a.name.localeCompare(b.name) || a.label.localeCompare(b.label));

    const byCustomer = {};
    tabs.forEach((t) => {
      const g = byCustomer[t.customerId] || (byCustomer[t.customerId] = {
        customerId: t.customerId, name: t.name, phone: t.phone, email: t.email,
        balance: 0, charged: 0, paid: 0, tabs: [],
      });
      g.tabs.push(t);
      g.balance = Math.round((g.balance + t.balance) * 100) / 100;
      g.charged = Math.round((g.charged + t.charged) * 100) / 100;
      g.paid = Math.round((g.paid + t.paid) * 100) / 100;
    });
    const customers = Object.values(byCustomer)
      .sort((a, b) => b.balance - a.balance || a.name.localeCompare(b.name));

    return okResp({
      tabs,
      customers,
      totalOutstanding: Math.round(tabs.reduce((s, t) => s + t.balance, 0) * 100) / 100,
    });
  } catch (e) { return failResp(e); }
}

// Open a tab (balance starts at $0). Called both to put a customer on the tab
// for the first time and to add a second, third… tab to someone already on it.
//
// Idempotent on (customer, label): re-adding the same name reports existed:true
// rather than wiping that tab's notes. The DB enforces this too, with a
// case-insensitive unique index — this check just turns the constraint
// violation into a friendly answer.
export async function addCustomerTab(customerId, opts) {
  try {
    const cid = String(customerId || '').trim();
    if (!cid) return failResp('customerId required');
    const o = opts || {};
    const label = String(o.label || 'Main').trim() || 'Main';
    const { data: existing } = await supabase.from('customer_tabs')
      .select('id,label').eq('customer_id', cid);
    const dupe = (existing || []).find((t) => String(t.label || '').toLowerCase() === label.toLowerCase());
    if (dupe) return okResp({ existed: true, id: dupe.id });
    const id = 'CTAB-' + genIdSuffix();
    const { error } = await supabase.from('customer_tabs').insert({
      id,
      customer_id: cid,
      customer_name: o.name ? String(o.name) : null,
      label,
      notes: o.notes ? String(o.notes) : null,
      opened_by: o.by ? String(o.by) : null,
    });
    if (error) return failResp(error);
    return okResp({ existed: false, id });
  } catch (e) { return failResp(e); }
}

// Rename a tab. The unique index means two tabs on one customer can't collide.
export async function renameCustomerTab(tabId, label) {
  try {
    const id = String(tabId || '').trim();
    const name = String(label || '').trim();
    if (!id) return failResp('tabId required');
    if (!name) return failResp('Give the tab a name');
    const { error } = await supabase.from('customer_tabs').update({ label: name }).eq('id', id);
    if (error) {
      if (String(error.message || '').includes('customer_tabs_cust_label_uniq')) {
        return failResp('That customer already has a tab with that name.');
      }
      return failResp(error);
    }
    return okResp({});
  } catch (e) { return failResp(e); }
}

// Close ONE tab. Its ledger goes with it — now via the FK's ON DELETE CASCADE
// rather than a separate delete, so the two can't get out of step.
export async function removeCustomerTab(tabId) {
  try {
    const id = String(tabId || '').trim();
    if (!id) return failResp('tabId required');
    const { error } = await supabase.from('customer_tabs').delete().eq('id', id);
    if (error) return failResp(error);
    return okResp({});
  } catch (e) { return failResp(e); }
}

// Add a manual charge or record a payment against a specific tab. `kind` is
// 'charge' | 'payment'; amount is always positive.
export async function addTabEntry(tabId, opts) {
  try {
    const tid = String(tabId || '').trim();
    if (!tid) return failResp('tabId required');
    const o = opts || {};
    const amount = Math.round((Number(o.amount) || 0) * 100) / 100;
    if (!(amount > 0)) return failResp('Enter an amount greater than $0');
    // customer_id is denormalised on the ledger, so read it off the tab rather
    // than trusting a caller to pass a matching pair.
    const { data: tab, error: tabErr } = await supabase.from('customer_tabs')
      .select('id,customer_id').eq('id', tid).maybeSingle();
    if (tabErr) return failResp(tabErr);
    if (!tab) return failResp('That tab no longer exists — reload and try again.');
    const kind = o.kind === 'payment' ? 'payment' : 'charge';
    const d = String(o.date || '').slice(0, 10);
    const row = {
      id: 'TABE-' + genIdSuffix(),
      tab_id: tid,
      customer_id: tab.customer_id,
      kind,
      amount,
      note: o.note ? String(o.note) : null,
      created_by: o.by ? String(o.by) : null,
    };
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) row.entry_date = d;
    // Only a PAYMENT can be applied to an invoice — a charge already carries its
    // own so_number, and letting a charge set this too would double-count it in
    // the per-invoice rollup.
    if (kind === 'payment' && o.appliesToSo) row.applies_to_so = String(o.appliesToSo);
    // .select() for a row COUNT, not just an error: these policies are
    // auth_role()-gated, so a lapsed JWT inserts 0 rows and returns NO error
    // (the documented stale-JWT → silent RLS deny). Refresh + retry once.
    let { data: hit, error } = await supabase.from('customer_tab_entries').insert(row).select('id');
    if (error) return failResp(error);
    if (!hit || !hit.length) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      const retry = await supabase.from('customer_tab_entries').insert(row).select('id');
      if (retry.error) return failResp(retry.error);
      if (!retry.data || !retry.data.length) return failResp("That didn't go through — session may have expired. Reopen the app and try again.");
    }
    return okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}

// ---------------------------------------------------------------------------
// Dealer portal (3COT) — a dealership's three surfaces.
// ---------------------------------------------------------------------------
// Every function here is scoped by RLS off the JWT's `dealer` claim, not by
// anything passed from the client: a dealership reads only its own customers,
// its own requests and its own bookings, and sees the rest of the location's
// schedule as anonymised free/busy. See migration 20260810230000.

// Every dealership — owner/principal only (a dealer's own SELECT policy
// narrows this to their single row, so it's safe to share the reader).
export async function listDealers() {
  try {
    const { data, error } = await supabase.from('dealers').select('*').order('name');
    if (error) return failResp(error);
    return okResp({
      dealers: (data || []).map((d) => ({
        slug: d.slug, name: d.name, site: d.site,
        discountPercent: Number(d.discount_percent) || 0,
        active: d.active !== false, notes: d.notes || '',
        // Where this dealership's job alerts go instead of their customer.
        phone: S(d.phone),
        // Where the invoice email goes when one of their jobs completes.
        billingEmail: S(d.billing_email),
      })),
    });
  } catch (e) { return failResp(e); }
}

// Change a dealership's rate. The PIN gate in front of this is a UI speed
// bump against a mis-click; the real boundary is `dealers_all`, which only
// owner/principal satisfy — a dealer calling this writes 0 rows.
// The number that dealer-sourced job alerts are redirected to. Blank clears
// it — and a cleared number means those texts DON'T SEND, rather than
// reverting to texting the dealership's customer directly.
export async function setDealerPhone(slug, phone) {
  try {
    const s = String(slug || '').trim();
    if (!s) return failResp('Missing dealer');
    // \D (non-digits), not /D/ — the earlier regex stripped capital Ds and left
    // every dash and paren in the stored number.
    const digits = String(phone || '').replace(/\D/g, '');
    if (digits && digits.length < 10) return failResp('That phone number is too short');
    const { error } = await supabase.from('dealers').update({ phone: digits || null }).eq('slug', s);
    return error ? failResp(error) : okResp({ slug: s, phone: digits });
  } catch (e) { return failResp(e); }
}

export async function setDealerDiscount(slug, percent) {
  try {
    const s = String(slug || '').trim();
    if (!s) return failResp('slug required');
    // 🚨 A blank field is NOT zero. Number('') is 0, and 0 passes the range
    // check — which is how OTTO's rate silently went from 20% to 0% and four
    // jobs were quoted at full retail (2026-09-11 → 09-17). Blank is refused;
    // an explicit 0 still saves, because typing it is a decision.
    if (percent === '' || percent === null || percent === undefined || (typeof percent === 'string' && !percent.trim())) {
      return failResp('Enter the partner rate before saving (blank would have saved as 0%)');
    }
    const pct = Number(percent);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) return failResp('Enter a percentage between 0 and 100');
    const { data: hit, error } = await supabase.from('dealers')
      .update({ discount_percent: Math.round(pct * 100) / 100 }).eq('slug', s).select('slug');
    if (error) return failResp(error);
    if (!hit || !hit.length) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      const retry = await supabase.from('dealers')
        .update({ discount_percent: Math.round(pct * 100) / 100 }).eq('slug', s).select('slug');
      if (retry.error) return failResp(retry.error);
      if (!retry.data || !retry.data.length) return failResp("That didn't save — session may have expired. Reopen the app and try again.");
    }
    return okResp({ slug: s, discountPercent: Math.round(pct * 100) / 100 });
  } catch (e) { return failResp(e); }
}

// Where a dealership's invoice email goes when one of its jobs completes.
// Blank is a real state: the portal row still unlocks, nothing is mailed.
export async function setDealerBillingEmail(slug, email) {
  try {
    const s = String(slug || '').trim();
    if (!s) return failResp('Missing dealer');
    const em = String(email || '').trim().toLowerCase();
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return failResp('That does not look like an email address');
    const { data: hit, error } = await supabase.from('dealers').update({ billing_email: em || null }).eq('slug', s).select('slug');
    if (error) return failResp(error);
    if (!hit || !hit.length) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      const retry = await supabase.from('dealers').update({ billing_email: em || null }).eq('slug', s).select('slug');
      if (retry.error) return failResp(retry.error);
      if (!retry.data || !retry.data.length) return failResp("That didn't save — session may have expired. Reopen the app and try again.");
    }
    return okResp({ slug: s, billingEmail: em });
  } catch (e) { return failResp(e); }
}

// ── Dealer invoices ────────────────────────────────────────────────────────
// One row per dealer job, written only by the sync_dealer_invoice() trigger.
// RLS decides what comes back: a dealer login sees its own UNLOCKED rows and
// nothing else; owner/principal see every row at every shop they can reach.
function mapDealerInvoice(r) {
  return {
    id: r.id, bookingId: r.booking_id || '', dealer: S(r.dealer), site: S(r.site), number: S(r.number),
    customerName: S(r.customer_name), vehicleDesc: S(r.vehicle_desc), service: S(r.service),
    jobDate: r.job_date ? String(r.job_date).slice(0, 10) : '',
    amount: r.amount == null ? null : Number(r.amount),
    status: S(r.status) || 'locked',
    createdAt: r.created_at || null, unlockedAt: r.unlocked_at || null,
    emailedAt: r.emailed_at || null, emailedTo: S(r.emailed_to),
    paidAt: r.paid_at || null, paidNote: S(r.paid_note),
    // True for an invoice the COO typed for a job that was never booked
    // (create_manual_dealer_invoice). No booking behind it; voidable by hand.
    manual: r.manual === true,
    // Itemized lines on a manual invoice ([{description, amount}]); null = the
    // single line (service, amount) every booking-backed invoice has.
    lines: Array.isArray(r.lines) && r.lines.length
      ? r.lines.map((l) => ({ description: S(l && l.description), amount: Number(l && l.amount) || 0 }))
      : null,
  };
}
export async function listDealerInvoices() {
  try {
    // By NUMBER, not unlocked_at: the backfill stamped every historical row
    // with the same unlock time, and a tie there is an arbitrary order. The
    // number is zero-padded, so a string sort is a numeric sort.
    const res = await selectAllRows(() => supabase.from('dealer_invoices').select('*')
      .order('number', { ascending: false }).order('id'));
    if (res.error) return failResp(res.error);
    return okResp({ invoices: (res.data || []).map(mapDealerInvoice) });
  } catch (e) { return failResp(e); }
}
// Mark paid / unpaid. `.select()` row-count + refresh-and-retry, because an
// RLS-denied UPDATE returns 0 rows with no error, and a "paid" that silently
// did not save is a statement that gets chased twice.
export async function setDealerInvoicePaid(id, paid, note) {
  try {
    const iid = String(id || '').trim();
    if (!iid) return failResp('id required');
    const patch = paid
      ? { paid_at: new Date().toISOString(), paid_note: String(note || '').trim() || null }
      : { paid_at: null, paid_note: null };
    let r = await supabase.from('dealer_invoices').update(patch).eq('id', iid).select('id');
    if (r.error) return failResp(r.error);
    if (!r.data || !r.data.length) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      r = await supabase.from('dealer_invoices').update(patch).eq('id', iid).select('id');
      if (r.error) return failResp(r.error);
      if (!r.data || !r.data.length) return failResp("That didn't save — session may have expired. Reopen the app and try again.");
    }
    return okResp({ id: iid, paidAt: patch.paid_at });
  } catch (e) { return failResp(e); }
}
// ── Customer profile (the expanded "baseball card") ─────────────────────────
// Everything the card's stats, garage and history need for ONE customer, read
// when the card is opened — never for the list. Each read is independent and
// best-effort: RLS hides invoices from hd and storage from everyone but
// owner/principal, and a hidden table must cost a stat, not the whole card.
export async function getCustomerProfile(customerId) {
  try {
    const id = String(customerId || '').trim();
    if (!id) return failResp('customerId required');
    const bk = await supabase.from('bookings')
      .select('id,date,service,status,vehicle_id,vehicle_desc,dealer,invoice_so_num,deposit_status,deposit_amount,site')
      .eq('customer_id', id).order('date', { ascending: false }).limit(500);
    if (bk.error) return failResp(bk.error);
    const bookings = (bk.data || []).filter((r) => r.status !== 'deleted').map((r) => ({
      id: r.id, date: String(r.date || '').slice(0, 10), service: S(r.service), status: S(r.status),
      vehicleId: S(r.vehicle_id), vehicleDesc: S(r.vehicle_desc), dealer: S(r.dealer),
      soNumber: S(r.invoice_so_num).trim(), depositStatus: S(r.deposit_status),
      depositAmount: r.deposit_amount == null ? 0 : Number(r.deposit_amount) || 0,
    }));

    // Invoices: by customer_id (stamped since 2026-07) AND by the S.O.#s on
    // their bookings (older rows have no customer_id). De-duplicated by id.
    const invById = {};
    try {
      const a = await supabase.from('invoices').select('id,so_number,date,total,is_membership').eq('customer_id', id);
      (a.data || []).forEach((r) => { invById[r.id] = r; });
      const sos = [...new Set(bookings.map((b) => b.soNumber).filter(Boolean))];
      for (let i = 0; i < sos.length; i += 150) {
        const r = await supabase.from('invoices').select('id,so_number,date,total,is_membership').in('so_number', sos.slice(i, i + 150));
        (r.data || []).forEach((x) => { invById[x.id] = x; });
      }
    } catch (_) {}
    const invoices = Object.values(invById).map((r) => ({
      id: r.id, soNumber: S(r.so_number).trim(), date: String(r.date || '').slice(0, 10),
      total: Number(r.total) || 0, isMembership: !!r.is_membership,
    }));

    let storage = { subs: [], vehicles: [] };
    try {
      const s = await supabase.from('storage_subscriptions')
        .select('id,status,label,monthly_amount,start_month,end_month,billing_mode').eq('customer_id', id);
      const subs = (s.data || []).map((r) => ({
        id: r.id, status: S(r.status), label: S(r.label), monthlyAmount: Number(r.monthly_amount) || 0,
        startMonth: S(r.start_month), endMonth: S(r.end_month), billingMode: S(r.billing_mode),
      }));
      let vehicles = [];
      if (subs.length) {
        const v = await supabase.from('storage_vehicles').select('*').in('subscription_id', subs.map((x) => x.id));
        vehicles = (v.data || []).map(mapStorageVehicle);
      }
      storage = { subs, vehicles, readable: !s.error };
    } catch (_) {}

    // Condition checks (proactive care, 20261104120000) — newest first, for the
    // garage tile's health panel. Best-effort: a role the RLS denies just sees
    // no panel.
    let conditionChecks = [];
    try {
      const cc = await supabase.from('vehicle_condition_checks').select('*').eq('customer_id', id).order('checked_at', { ascending: false }).limit(200);
      conditionChecks = (cc.data || []).map(mapConditionCheck);
    } catch (_) {}

    return okResp({ bookings, invoices, storage, conditionChecks });
  } catch (e) { return failResp(e); }
}

// ── Payments tracker (Revenue → Payments) ───────────────────────────────────
// Every appointment in [from, to] with the payment facts lib/paymentStatus.js
// needs to decide paid / owed. Reads only — the money lives in the tables it
// already lives in. Matching an invoice is by S.O.# ONLY: invoices.booking_id
// also points at DEPOSIT- and DEALER- rows, which are not the job's invoice.
export async function getPaymentTracker(from, to) {
  try {
    const bk = await selectAllRows(() => {
      let q = supabase.from('bookings').select('*');
      if (saneDate(from)) q = q.gte('date', from);
      if (saneDate(to)) q = q.lte('date', to);
      return q.order('date').order('id');
    });
    if (bk.error) return failResp(bk.error);
    const bookings = (bk.data || []).filter((r) => r.status !== 'deleted').map((r) => {
      const b = mapBooking(r);
      b.paymentClearedAt = isoTs(r.payment_cleared_at);
      b.paymentClearedBy = S(r.payment_cleared_by);
      b.paymentClearedNote = S(r.payment_cleared_note);
      return b;
    });
    const sos = [...new Set(bookings.map((b) => String(b.invoiceSoNum || '').trim()).filter(Boolean))];
    const dealerIds = bookings.filter((b) => String(b.dealer || '').trim()).map((b) => b.id);

    const chunk = (arr, n) => { const o = []; for (let i = 0; i < arr.length; i += n) o.push(arr.slice(i, i + n)); return o; };
    const invoiceBySo = {};
    for (const part of chunk(sos, 150)) {
      const { data, error } = await supabase.from('invoices').select('so_number,total,is_membership').in('so_number', part);
      if (error) return failResp(error);
      (data || []).forEach((r) => { invoiceBySo[String(r.so_number).trim()] = { total: Number(r.total) || 0, isMembership: !!r.is_membership }; });
    }

    const dealerByBooking = {};
    for (const part of chunk(dealerIds, 150)) {
      const { data, error } = await supabase.from('dealer_invoices').select('booking_id,status,amount,paid_at').in('booking_id', part);
      if (error) return failResp(error);
      (data || []).forEach((r) => { dealerByBooking[r.booking_id] = { status: r.status, amount: r.amount == null ? null : Number(r.amount), paidAt: r.paid_at || null }; });
    }

    // Tab: find the tabs that carry these invoices, then read those tabs whole
    // so an unallocated payment can still settle them.
    let tabEntries = [];
    const paidSos = sos.filter((s) => invoiceBySo[s]);
    const tabIds = new Set();
    for (const part of chunk(paidSos, 150)) {
      const { data, error } = await supabase.from('customer_tab_entries').select('tab_id').eq('kind', 'charge').in('so_number', part);
      if (error) return failResp(error);
      (data || []).forEach((r) => { if (r.tab_id) tabIds.add(r.tab_id); });
    }
    for (const part of chunk([...tabIds], 150)) {
      const { data, error } = await supabase.from('customer_tab_entries').select('tab_id,customer_id,kind,amount,so_number,applies_to_so').in('tab_id', part);
      if (error) return failResp(error);
      tabEntries = tabEntries.concat(data || []);
    }
    return okResp({ bookings, invoiceBySo, dealerByBooking, tabEntries });
  } catch (e) { return failResp(e); }
}

// The deposit LEDGER (Revenue → Payments → Deposits). Keyed on the money
// dates, not the job date: a deposit paid in October for a December job is
// October's money. Three reads merged by booking — paid in the window (whatever
// happened to it since), refunded in the window, and requests still unpaid that
// were asked for in the window. `from`/`to` are YYYY-MM-DD, inclusive.
export async function getDepositLedger(from, to) {
  try {
    const lo = saneDate(from) ? from : null;
    const hi = saneDate(to) ? to + 'T23:59:59.999' : null;
    const pick = async (col) => {
      let q = supabase.from('bookings').select('*').not(col, 'is', null);
      if (lo) q = q.gte(col, lo);
      if (hi) q = q.lte(col, hi);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    };
    const [paid, refunded, requested] = await Promise.all([pick('deposit_paid_at'), pick('deposit_refunded_at'), pick('deposit_requested_at')]);
    const byId = {};
    paid.forEach((r) => { byId[r.id] = { r, why: 'paid' }; });
    refunded.forEach((r) => { if (!byId[r.id]) byId[r.id] = { r, why: 'refunded' }; });
    requested.forEach((r) => { if (!byId[r.id] && S(r.deposit_status) === 'requested') byId[r.id] = { r, why: 'requested' }; });
    const rows = Object.values(byId).filter((x) => x.r.status !== 'deleted').map(({ r, why }) => ({ ...mapBooking(r), ledgerWhy: why }));
    return okResp({ rows });
  } catch (e) { return failResp(e); }
}

// "Mark resolved" / undo on the Payments tracker. A note on the job, never
// money. `.select()` row-count + refresh-retry: an RLS-denied UPDATE matches 0
// rows with no error, and a resolve that silently didn't stick would bring the
// job straight back onto tomorrow's list.
export async function setBookingPaymentCleared(bookingId, cleared, note, by) {
  try {
    const id = String(bookingId || '').trim();
    if (!id) return failResp('bookingId required');
    const patch = cleared
      ? { payment_cleared_at: new Date().toISOString(), payment_cleared_by: String(by || '') || null, payment_cleared_note: String(note || '').trim() || null }
      : { payment_cleared_at: null, payment_cleared_by: null, payment_cleared_note: null };
    let r = await supabase.from('bookings').update(patch).eq('id', id).select('id');
    if (r.error) return failResp(r.error);
    if (!r.data || !r.data.length) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      r = await supabase.from('bookings').update(patch).eq('id', id).select('id');
      if (r.error) return failResp(r.error);
      if (!r.data || !r.data.length) return failResp("That didn't save — session may have expired. Reopen the app and try again.");
    }
    return okResp({ id, clearedAt: patch.payment_cleared_at });
  } catch (e) { return failResp(e); }
}
// Re-send the invoice email to the dealership's billing address (staff only;
// the server re-checks the role). Same email the unlock sends.
export async function resendDealerInvoice(id) {
  try {
    const iid = String(id || '').trim();
    if (!iid) return failResp('id required');
    let tok = '';
    try { const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || ''; } catch (_) {}
    if (!tok) return failResp('Not signed in');
    const call = (t) => fetch('/api/invoicer', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t },
      body: JSON.stringify({ action: 'dealer-invoice-resend', invoiceId: iid }),
    });
    let res = await call(tok);
    if (res.status === 401) {
      try { await supabase.auth.refreshSession(); const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || tok; } catch (_) {}
      res = await call(tok);
    }
    const j = await res.json().catch(() => null);
    if (!j || j.success === false) return failResp((j && j.error) || ('HTTP ' + res.status));
    return okResp(j);
  } catch (e) { return failResp(e); }
}
// Outbox → balance draft toggles (pick-up before/after, address, lockbox).
// The server rebuilds the email from the booking's totals; `peek` only reads.
export async function balanceDraftOptions(draftId, opts = {}) {
  try {
    let tok = '';
    try { const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || ''; } catch (_) {}
    if (!tok) return failResp('Not signed in');
    const call = (t) => fetch('/api/invoicer', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t },
      body: JSON.stringify({ action: 'balance-draft-options', draftId, ...opts }),
    });
    let res = await call(tok);
    if (res.status === 401) {
      try { await supabase.auth.refreshSession(); const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || tok; } catch (_) {}
      res = await call(tok);
    }
    const j = await res.json().catch(() => null);
    if (!j || j.success === false) return failResp((j && j.error) || ('HTTP ' + res.status));
    if (!opts.peek) { try { bustClientCache('getEmailDrafts'); } catch (_) {} }
    return okResp(j);
  } catch (e) { return failResp(e); }
}
// ☣ Biohazard fee on a dealer job (Albany side). The server folds the
// dealership's fee into the job + invoice, emails their billing contact,
// pushes to their devices and posts a 3COT notice. `on:false` reverses it.
export async function setDealerBiohazard(bookingId, on = true, note = '') {
  try {
    let tok = '';
    try { const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || ''; } catch (_) {}
    if (!tok) return failResp('Not signed in');
    const call = (t) => fetch('/api/invoicer', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t },
      body: JSON.stringify({ action: 'dealer-biohazard', bookingId, on: on !== false, note: String(note || '') }),
    });
    let res = await call(tok);
    if (res.status === 401) {
      try { await supabase.auth.refreshSession(); const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || tok; } catch (_) {}
      res = await call(tok);
    }
    const j = await res.json().catch(() => null);
    if (!j || j.success === false) return failResp((j && j.error) || ('HTTP ' + res.status));
    try { bustClientCache('getBookings'); } catch (_) {}
    return okResp(j);
  } catch (e) { return failResp(e); }
}
// 3COT: biohazard fees 3C added that the dealership hasn't acknowledged yet.
export async function listDealerBiohazardNotices() {
  try {
    const { data, error } = await supabase.rpc('dealer_biohazard_notices');
    if (error) return failResp(error);
    return okResp({ notices: (data || []).map((n) => ({
      id: n.id, date: n.date ? String(n.date).slice(0, 10) : '', customerName: n.customer_name || '',
      vehicleDesc: n.vehicle_desc || '', service: n.service || '', fee: Number(n.fee) || 0,
      note: n.note || '', notifiedAt: n.notified_at || null,
    })) });
  } catch (e) { return failResp(e); }
}
export async function ackDealerBiohazard(bookingId) {
  try {
    const { error } = await supabase.rpc('dealer_ack_biohazard', { p_booking_id: bookingId });
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
// 🧹 Custom soil fee on a dealer job (COO, from the appointment card). The
// server folds the typed amount into the job + invoice, emails the billing
// contact, pushes OTTO and posts a 3COT notice. A new amount replaces the old;
// `on:false` removes it. Same stale-token retry as the biohazard call.
export async function setDealerSoilFee(bookingId, { amount = 0, note = '', on = true } = {}) {
  try {
    let tok = '';
    try { const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || ''; } catch (_) {}
    if (!tok) return failResp('Not signed in');
    const call = (t) => fetch('/api/invoicer', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t },
      body: JSON.stringify({ action: 'dealer-soil-fee', bookingId, on: on !== false, amount: Number(amount) || 0, note: String(note || '') }),
    });
    let res = await call(tok);
    if (res.status === 401) {
      try { await supabase.auth.refreshSession(); const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || tok; } catch (_) {}
      res = await call(tok);
    }
    const j = await res.json().catch(() => null);
    if (!j || j.success === false) return failResp((j && j.error) || ('HTTP ' + res.status));
    try { bustClientCache('getBookings'); } catch (_) {}
    return okResp(j);
  } catch (e) { return failResp(e); }
}
// 3COT: every fee 3C added (biohazard or soil) that the dealership hasn't
// acknowledged yet. Supersedes listDealerBiohazardNotices for the banner.
export async function listDealerFeeNotices() {
  try {
    const { data, error } = await supabase.rpc('dealer_fee_notices');
    if (error) return failResp(error);
    return okResp({ notices: (data || []).map((n) => ({
      kind: n.kind === 'soil' ? 'soil' : 'biohazard',
      id: n.id, date: n.date ? String(n.date).slice(0, 10) : '', customerName: n.customer_name || '',
      vehicleDesc: n.vehicle_desc || '', service: n.service || '', fee: Number(n.fee) || 0,
      note: n.note || '', notifiedAt: n.notified_at || null,
    })) });
  } catch (e) { return failResp(e); }
}
export async function ackDealerFee(bookingId, kind) {
  try {
    const { error } = await supabase.rpc('dealer_ack_fee', { p_booking_id: bookingId, p_kind: kind === 'soil' ? 'soil' : 'biohazard' });
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
// Manual 3COT invoice — a dealer job that was never on the schedule. The
// server (principal-only) mints the row through create_manual_dealer_invoice()
// and emails it when `send` is true. Same stale-token retry as the re-send.
export async function createDealerInvoice({ dealer, customerName, vehicleDesc, service, jobDate, amount, lines, send = true } = {}) {
  try {
    // Itemized lines, when given, ARE the amount (the server re-derives it).
    const items = (Array.isArray(lines) ? lines : [])
      .map((l) => ({ description: String((l && l.description) || '').trim(), amount: Math.round((Number(l && l.amount) || 0) * 100) / 100 }))
      .filter((l) => l.description || l.amount);
    if (items.some((l) => !l.description)) return failResp('Every line needs a description');
    if (items.some((l) => !(l.amount > 0))) return failResp('Every line needs an amount above $0');
    const amt = items.length ? Math.round(items.reduce((a, l) => a + l.amount, 0) * 100) / 100 : (Number(amount) || 0);
    if (!String(dealer || '').trim()) return failResp('Pick a dealership');
    if (!(amt > 0)) return failResp('Amount must be greater than zero');
    if (!String(vehicleDesc || '').trim() && !String(customerName || '').trim()) return failResp('A vehicle or a customer name is required');
    let tok = '';
    try { const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || ''; } catch (_) {}
    if (!tok) return failResp('Not signed in');
    const payload = {
      action: 'dealer-invoice-create', dealer: String(dealer).trim().toLowerCase(),
      customerName: String(customerName || '').trim(), vehicleDesc: String(vehicleDesc || '').trim(),
      service: String(service || '').trim(), jobDate: String(jobDate || '').slice(0, 10), amount: amt, send: send !== false,
      ...(items.length ? { lines: items } : {}),
    };
    const call = (t) => fetch('/api/invoicer', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t },
      body: JSON.stringify(payload),
    });
    let res = await call(tok);
    if (res.status === 401) {
      try { await supabase.auth.refreshSession(); const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || tok; } catch (_) {}
      res = await call(tok);
    }
    const j = await res.json().catch(() => null);
    if (!j || j.success === false) return failResp((j && j.error) || ('HTTP ' + res.status));
    return okResp(j);
  } catch (e) { return failResp(e); }
}
// Customer lookup for the manual 3COT invoice: searches the DEALERSHIP's shop
// (OTTO = Albany) regardless of which shop this session is toggled to. Server
// side on purpose — the client's RLS read only sees the active shop. Returns
// at most 15 matches with their vehicles. Read-only; same token retry as the
// other dealer calls.
export async function searchDealerCustomers({ dealer, q } = {}) {
  try {
    const term = String(q || '').trim();
    if (!String(dealer || '').trim()) return failResp('dealer required');
    if (term.length < 2) return okResp({ customers: [] });
    let tok = '';
    try { const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || ''; } catch (_) {}
    if (!tok) return failResp('Not signed in');
    const call = (t) => fetch('/api/invoicer', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t },
      body: JSON.stringify({ action: 'dealer-customer-search', dealer: String(dealer).trim().toLowerCase(), q: term }),
    });
    let res = await call(tok);
    if (res.status === 401) {
      try { await supabase.auth.refreshSession(); const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || tok; } catch (_) {}
      res = await call(tok);
    }
    const j = await res.json().catch(() => null);
    if (!j || j.success === false) return failResp((j && j.error) || ('HTTP ' + res.status));
    return okResp({ customers: j.customers || [], site: j.site || '' });
  } catch (e) { return failResp(e); }
}
// Void a MANUAL dealer invoice that was created by mistake. Only an unpaid,
// unlocked, manual row — a booking-backed row is voided by cancelling its
// booking, and a paid one is final. Staff UPDATE policy covers the write;
// `.select()` row-count + refresh-retry per the silent-RLS-deny pattern. The
// revenue row follows via the sync_manual_dealer_revenue_row trigger.
export async function voidDealerInvoice(id) {
  try {
    const iid = String(id || '').trim();
    if (!iid) return failResp('id required');
    const q = () => supabase.from('dealer_invoices').update({ status: 'void' })
      .eq('id', iid).eq('manual', true).eq('status', 'unlocked').is('paid_at', null).select('id');
    let r = await q();
    if (r.error) return failResp(r.error);
    if (!r.data || !r.data.length) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      r = await q();
      if (r.error) return failResp(r.error);
      if (!r.data || !r.data.length) return failResp("That didn't void — it may be paid, not manual, or the session expired.");
    }
    return okResp({ id: iid });
  } catch (e) { return failResp(e); }
}

// Days the OTHER shop has appointments, as { 'YYYY-MM-DD': count }. Drives the
// "R" marker on the Kingston calendar (Ric is at Albany whenever Albany has a
// job). Server-side because RLS scopes bookings to the active shop; the
// server re-checks the site against the caller's own claims.
export async function getSiteBookedDays(site, from, to) {
  try {
    let tok = '';
    try { const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || ''; } catch (_) {}
    if (!tok) return failResp('Not signed in');
    const url = '/api/invoicer?action=site-booked-days&site=' + encodeURIComponent(site) + '&from=' + encodeURIComponent(from) + '&to=' + encodeURIComponent(to);
    let res = await fetch(url, { headers: { Authorization: 'Bearer ' + tok } });
    if (res.status === 401) {
      try { await supabase.auth.refreshSession(); const { data } = await supabase.auth.getSession(); tok = (data && data.session && data.session.access_token) || tok; } catch (_) {}
      res = await fetch(url, { headers: { Authorization: 'Bearer ' + tok } });
    }
    const j = await res.json().catch(() => null);
    if (!j || j.success === false) return failResp((j && j.error) || ('HTTP ' + res.status));
    return okResp({ site: j.site, days: j.days || {} });
  } catch (e) { return failResp(e); }
}

// The caller's own dealer row — name + discount %. One row by construction.
export async function getDealerConfig() {
  try {
    const { data, error } = await supabase.from('dealers').select('*').limit(1).maybeSingle();
    if (error) return failResp(error);
    if (!data) return failResp('No dealership is linked to this account');
    return okResp({
      dealer: {
        slug: data.slug, name: data.name, site: data.site,
        discountPercent: Number(data.discount_percent) || 0,
        // Biohazard handling fee billed to the dealership (default $150).
        biohazardFee: data.biohazard_fee == null ? 150 : Number(data.biohazard_fee),
        active: data.active !== false,
      },
    });
  } catch (e) { return failResp(e); }
}

export async function listDealerCustomers() {
  try {
    const [cust, veh] = await Promise.all([
      selectAllRows(() => supabase.from('customers').select('*').order('first_name').order('id')),
      selectAllRows(() => supabase.from('vehicles').select('*').order('make').order('model').order('id')),
    ]);
    if (cust.error) return failResp(cust.error);
    const byCustomer = {};
    (veh.error ? [] : (veh.data || [])).forEach((v) => { (byCustomer[v.customer_id] ||= []).push(mapVehicle(v)); });
    return okResp({ results: (cust.data || []).map((c) => mapCustomer(c, byCustomer[c.id] || [])) });
  } catch (e) { return failResp(e); }
}

// Create a customer under this dealership. `dealer` + `site` are set from the
// caller's own claims rather than accepted as arguments — the INSERT policy
// requires them to match, so a client that lied would simply be rejected.
export async function addDealerCustomer(data) {
  try {
    const d = data || {};
    const first = String(d.firstName || '').trim();
    const last = String(d.lastName || '').trim();
    if (!first && !last) return failResp('A name is required');
    // Nobody enters this book unreachable. A customer with no number can't be
    // sent a reminder, a portal link or a balance request, and the gap only
    // shows up later — at the appointment, when somebody has to chase it.
    if (String(d.phone || '').replace(/\D/g, '').length < 10) return failResp('A phone number is required');
    // Same rule at creation. A dealer customer added without an address is a
    // blocked booking later, further from whoever knew the address.
    const _ae = String(d.email || '').trim();
    const _aat = _ae.indexOf('@'), _adot = _ae.lastIndexOf('.');
    if (!(_aat > 0 && _aat === _ae.lastIndexOf('@') && _adot > _aat + 1 && _adot < _ae.length - 2 && !_ae.includes(' '))) return failResp('An email address is required');
    const cfgRes = await getDealerConfig();
    if (!cfgRes.success) return cfgRes;
    const cfg = cfgRes.dealer;
    const row = {
      id: 'C-' + genIdSuffix(),
      first_name: first, last_name: last,
      full_name: [first, last].filter(Boolean).join(' '),
      email: String(d.email || '').trim() || null,
      phone: String(d.phone || '').trim() || null,
      notes: String(d.notes || '').trim() || null,
      source: 'dealer', dealer: cfg.slug, site: cfg.site,
      first_seen: new Date().toISOString(), last_seen: new Date().toISOString(),
    };
    const { data: hit, error } = await supabase.from('customers').insert(row).select('id');
    if (error) return failResp(error);
    if (!hit || !hit.length) return failResp("That didn't save — reopen the app and try again.");
    // Optional vehicle in the same gesture; a failure here must not lose the
    // customer, so it's reported but not fatal.
    let vehicleId = '';
    if (d.vehicle && (d.vehicle.make || d.vehicle.model)) {
      vehicleId = 'V-' + genIdSuffix();
      const ve = await supabase.from('vehicles').insert({
        id: vehicleId, customer_id: row.id,
        year: d.vehicle.year ? (Number(d.vehicle.year) || null) : null,
        make: d.vehicle.make || '', model: d.vehicle.model || '',
        size: d.vehicle.size || '', site: cfg.site,
      });
      if (ve.error) vehicleId = '';
    }
    return okResp({ id: row.id, vehicleId });
  } catch (e) { return failResp(e); }
}

// Fill in a contact detail that was missing on somebody already in the book —
// the 3,015 imported off the OTTO CSV include a handful with no phone, and the
// booker now refuses to send a request for an unreachable customer. Writing it
// back here means it's typed once, not at every appointment. RLS
// (`customers_dealer_upd`) confines this to that dealership's own rows; the
// column list confines it to contact details.
export async function updateDealerCustomerContact(id, contact) {
  try {
    const customerId = String(id || '').trim();
    if (!customerId) return failResp('No customer to update');
    const c = contact || {};
    const patch = {};
    if (c.phone !== undefined) patch.phone = String(c.phone || '').trim() || null;
    if (c.email !== undefined) patch.email = String(c.email || '').trim() || null;
    if (!Object.keys(patch).length) return okResp({ id: customerId });
    const { error } = await supabase.from('customers').update(patch).eq('id', customerId);
    return error ? failResp(error) : okResp({ id: customerId });
  } catch (e) { return failResp(e); }
}

// Erase a customer from this dealership's book, with their vehicles.
//
// Goes through `dealer_delete_customer` rather than a plain .delete(): every FK
// onto customers is ON DELETE SET NULL, so deleting a customer who still has a
// booking does not fail — it quietly orphans that booking (the documented
// walk-around breakage of 2026-07-27). The refusal has to happen server-side,
// where it can see rows this account cannot. See migration 20260813000000.
export async function deleteDealerCustomer(id) {
  try {
    const customerId = String(id || '').trim();
    if (!customerId) return failResp('No customer to remove');
    const { data, error } = await supabase.rpc('dealer_delete_customer', { p_id: customerId });
    // Migration not applied yet: PostgREST answers PGRST202 ("could not find
    // the function"). Left as a plain sentence rather than a raw API error,
    // because the app ships to Vercel the moment it's pushed and the SQL is
    // run by hand afterwards — this is a real window, not a theoretical one.
    const notDeployed = error && (error.code === 'PGRST202' ||
      /could not find the function|does not exist/i.test(error.message || ''));
    if (notDeployed) return failResp("Erasing customers isn't switched on yet — the database update hasn't been applied.");
    if (error) return failResp(error);
    if (!data) return failResp("That didn't go through — reopen the app and try again.");
    if (data.deleted) return okResp({});
    if (data.reason === 'has-activity') {
      return failResp('This customer has an appointment or invoice on file, so their record has to stay. Ask 3C to remove it.');
    }
    return failResp("That customer isn't in your book — reopen the app and try again.");
  } catch (e) { return failResp(e); }
}

// Anonymised free/busy for the whole location + full detail on their own jobs.
export async function listDealerSchedule(fromDate, toDate) {
  try {
    const { data, error } = await supabase.rpc('dealer_schedule', { p_from: fromDate, p_to: toDate });
    if (error) return failResp(error);
    return okResp({
      jobs: (data || []).map((r) => ({
        id: r.id, date: r.date, startMin: r.start_time, endMin: r.end_time,
        bay: r.bay || '', status: r.status || '', mine: !!r.mine,
        service: r.service || '', customerName: r.customer_name || '', vehicleDesc: r.vehicle_desc || '',
        // Null on someone else's job — the RPC only returns these on `mine`.
        arrivalState: r.arrival_state || '', arrivalEtaMin: r.arrival_eta_min == null ? null : Number(r.arrival_eta_min),
        arrivalNote: r.arrival_note || '', arrivalAt: r.arrival_at || '',
      })),
    });
  } catch (e) { return failResp(e); }
}

// Tell the shop a car is on the way, or running late, with the time the
// dealership now expects to drop it off. `state` is 'en_route' | 'delayed', or
// '' / null to withdraw an update that no longer holds.
//
// 🚨 Goes through the dealer_set_arrival RPC, not a table write: the dealer
// role has no UPDATE policy on bookings and is not getting one. The function
// re-derives the dealership and the shop from the JWT, refuses a booking that
// isn't theirs or has already checked in, and rings the `hd` role (Ric) with a
// bell + web push. See migration 20260925120000.
export async function setDealerArrival({ bookingId, state, etaMin, note } = {}) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    const { data, error } = await supabase.rpc('dealer_set_arrival', {
      p_booking_id: String(bookingId),
      p_state: state ? String(state) : null,
      p_eta: etaMin == null || etaMin === '' ? null : Number(etaMin),
      p_note: note ? String(note) : null,
    });
    if (error) return failResp(error);
    const row = (Array.isArray(data) ? data[0] : data) || {};
    return okResp({
      arrivalState: row.arrival_state || '',
      arrivalEtaMin: row.arrival_eta_min == null ? null : Number(row.arrival_eta_min),
      arrivalNote: row.arrival_note || '',
      arrivalAt: row.arrival_at || '',
    });
  } catch (e) { return failResp(e); }
}

// OTTO cancels one of its own appointments. The RPC re-checks ownership, refuses
// once the car is checked in, records who/why, drops the customer's queued
// reminder texts and notifies the floor (Ric's bell + push, a detailer task, the
// shop phone); the principal's bell comes from the booking-change trigger. The
// calendar event can only be removed from here, same as a staff cancel.
export async function setDealerCancel({ bookingId, reason } = {}) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    const { data, error } = await supabase.rpc('dealer_cancel_booking', {
      p_booking_id: String(bookingId),
      p_reason: reason ? String(reason) : null,
    });
    if (error) return failResp(error);
    _fireCalendar(String(bookingId), 'delete');
    const row = (Array.isArray(data) ? data[0] : data) || {};
    return okResp({ status: row.status || 'cancelled', cancelledAt: row.cancelled_at || '' });
  } catch (e) { return failResp(e); }
}

// Withdraw a request 3C hasn't acted on yet (dealer_withdraw_request — dealer
// + site from the JWT, pending only). An approved one is cancelled from the
// Schedule tab instead (setDealerCancel); the RPC says so.
export async function withdrawDealerRequest({ requestId, reason } = {}) {
  try {
    if (!requestId) return failResp('Missing requestId');
    const { data, error } = await supabase.rpc('dealer_withdraw_request', {
      p_request_id: String(requestId),
      p_reason: reason ? String(reason) : null,
    });
    if (error) return failResp(error);
    const row = (Array.isArray(data) ? data[0] : data) || {};
    return okResp({ status: row.status || 'withdrawn', withdrawnAt: row.withdrawn_at || '' });
  } catch (e) { return failResp(e); }
}
// Raise a booking REQUEST. It lands in the principal's Pending Requests panel
// in 3CAL exactly like a website request — same table, same approve/deny.
// `price` is the DEALER's cost (what we'll bill them), not the retail quote.
// 🚨 A dealer request is a JOB 3C has to run, so it arrives complete or it
// doesn't arrive. Every field below is something the shop cannot work without:
// the crew can't prep a bay for an unknown car, and a customer with no number
// gets no reminder, no portal link and no balance request. Those gaps surface
// on the morning of the job, not when the request is made.
//
// This is the data layer's copy of the rule, not the enforcement — it runs in
// the browser, same as the booker's own form check. The last word is the CHECK
// constraint `booking_requests_dealer_complete` (20260820200000), because that
// table is written by a plain PostgREST insert. Keep the three in step; the
// constraint is the one that's actually binding.
//
// Email is deliberately NOT required: the phone covers every channel that
// matters, and a real customer without an address would otherwise be unbookable.
function _dealerRequestGap(r) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(r.date || ''))) return 'Pick a date';
  // Was defaulting to '540' — a missing slot silently became a 9am appointment
  // that nobody chose.
  if (!Number.isFinite(Number(r.startMin))) return 'Pick a time';
  if (!r.service) return 'Pick a service';
  if (!String(r.size || '').trim()) return 'Pick a vehicle size';
  if (!String(r.customerName || '').trim()) return 'Pick a customer';
  if (String(r.phone || '').replace(/\D/g, '').length < 10) {
    return 'A phone number is required — it is how 3C sends the reminder, the portal link and the balance request.';
  }
  // Ryder, 2026-09-02: an email is required too. ⚠️ This reverses the
  // 2026-08-20 decision that it stays optional ("the phone covers the reminder,
  // the portal link and the balance request"). 600 of the 3,021 imported OTTO
  // customers have no address, so the booker offers an inline "Email *" that
  // saves back to the record — otherwise this rule alone makes a fifth of their
  // book unbookable.
  // Plain string checks, not a regex: a mangled character class here would
  // silently reject real addresses instead of failing loudly.
  const _em = String(r.email || '').trim();
  const _at = _em.indexOf('@');
  const _dot = _em.lastIndexOf('.');
  if (!(_at > 0 && _at === _em.lastIndexOf('@') && _dot > _at + 1 && _dot < _em.length - 2 && !_em.includes(' '))) {
    return 'An email address is required — it is where the confirmation and the invoice go.';
  }
  const missing = [];
  if (!/^\d{4}$/.test(String(r.vehicleYear || '').trim())) missing.push('year');
  if (!String(r.vehicleMake || '').trim()) missing.push('make');
  if (!String(r.vehicleModel || '').trim()) missing.push('model');
  if (missing.length) return 'The vehicle ' + missing.join(', ') + ' ' + (missing.length > 1 ? 'are' : 'is') + ' required.';
  return '';
}

export async function submitDealerBookingRequest(req) {
  try {
    const r = req || {};
    const gap = _dealerRequestGap(r);
    if (gap) return failResp(gap);
    const cfgRes = await getDealerConfig();
    if (!cfgRes.success) return cfgRes;
    const cfg = cfgRes.dealer;
    const row = {
      id: 'BR-' + genIdSuffix(),
      date: r.date,
      time: String(Number(r.startMin)),
      service: String(r.service),
      price: Number(r.price) || 0,
      category: 'dealer',
      size: String(r.size || ''),
      customer_name: String(r.customerName).trim(),
      phone: String(r.phone || '').trim(),
      email: String(r.email || '').trim(),
      vehicle_year: String(r.vehicleYear || ''),
      vehicle_make: String(r.vehicleMake || ''),
      vehicle_model: String(r.vehicleModel || ''),
      notes: String(r.notes || '').trim(),
      submitted_at: new Date().toISOString(),
      status: 'pending',
      dealer: cfg.slug, site: cfg.site,
      // ☣ ticked in the booker; `price` already includes the fee.
      biohazard: !!r.biohazard,
    };
    const { data: hit, error } = await supabase.from('booking_requests').insert(row).select('id');
    if (error) return failResp(error);
    if (!hit || !hit.length) return failResp("That didn't send — reopen the app and try again.");
    return okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}

// Their own requests, newest first — so they can see what's still pending and
// what the principal approved or denied.
export async function listDealerRequests() {
  try {
    const { data, error } = await supabase.from('booking_requests').select('*')
      .order('submitted_at', { ascending: false }).limit(200);
    if (error) return failResp(error);
    return okResp({
      requests: (data || []).map((r) => ({
        id: r.id, date: r.date, time: r.time, service: r.service,
        price: Number(r.price) || 0, size: r.size || '',
        biohazard: r.biohazard === true,
        customerName: r.customer_name || '', status: r.status || 'pending',
        rejectionReason: r.rejection_reason || '',
        withdrawnReason: r.withdrawn_reason || '',
        vehicle: [r.vehicle_year, r.vehicle_make, r.vehicle_model].filter(Boolean).join(' '),
        submittedAt: r.submitted_at, approvedAt: r.approved_at || null,
      })),
    });
  } catch (e) { return failResp(e); }
}

// Wipe a customer's whole ledger but LEAVE them on the tab (at $0). Distinct
// from removeCustomerTab, which also drops the registry row and de-lists them.
// Wipe ONE tab's ledger but keep the tab itself listed at $0. Scoped by
// tab_id, so clearing a customer's "Personal" tab leaves their business tab
// untouched.
export async function clearCustomerTabEntries(tabId) {
  try {
    const tid = String(tabId || '').trim();
    if (!tid) return failResp('tabId required');
    const { data: hit, error } = await supabase.from('customer_tab_entries').delete().eq('tab_id', tid).select('id');
    if (error) return failResp(error);
    return okResp({ deleted: (hit || []).length });
  } catch (e) { return failResp(e); }
}

export async function deleteTabEntry(id) {
  try {
    if (!id) return failResp('id required');
    const { data: hit, error } = await supabase.from('customer_tab_entries').delete().eq('id', id).select('id');
    if (error) return failResp(error);
    if (!hit || !hit.length) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      const retry = await supabase.from('customer_tab_entries').delete().eq('id', id).select('id');
      if (retry.error) return failResp(retry.error);
      if (!retry.data || !retry.data.length) return failResp("Delete didn't go through — session may have expired. Reopen the app and try again.");
    }
    return okResp({});
  } catch (e) { return failResp(e); }
}

// ---------------------------------------------------------------------------
// Vehicle Storage — monthly subscriptions with a start month and an end month.
// ---------------------------------------------------------------------------
// The row is the agreement: this customer, this car, this much a month, from
// this month through that one. Everything else is derived from it.
//
// Money is written by the server only — never here. A storage month is ONE
// `invoices` row (STORAGE-<sub>-<month>) and nothing on any tab (Ryder,
// 2026-10-22). A manual agreement books it on schedule; a card agreement books
// it when the principal presses "Record as revenue" on the Stripe payment
// (storage_payments, pending → booked). These functions only ever edit the
// AGREEMENT, so a mistake in the UI can't post or unpost a dollar. Ending
// storage marks the row; it never deletes the ledger behind it.

// Months are 'YYYY-MM' strings everywhere, because they compare correctly as
// text and never carry a timezone. These are the only three operations needed.
export function storageMonthNow() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }).slice(0, 7);
}
export function storageMonthAdd(m, n) {
  const mm = /^(\d{4})-(\d{2})$/.exec(String(m || ''));
  if (!mm) return '';
  const total = Number(mm[1]) * 12 + (Number(mm[2]) - 1) + Number(n || 0);
  return Math.floor(total / 12) + '-' + String((total % 12) + 1).padStart(2, '0');
}
const _MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function storageMonthLabel(m) {
  const mm = /^(\d{4})-(\d{2})$/.exec(String(m || ''));
  return mm ? _MONTH_ABBR[Number(mm[2]) - 1] + ' ' + mm[1] : '';
}
// Inclusive span: '2026-10'..'2027-04' is 7 months. Null when open-ended.
export function storageMonthSpan(start, end) {
  const a = /^(\d{4})-(\d{2})$/.exec(String(start || ''));
  const b = /^(\d{4})-(\d{2})$/.exec(String(end || ''));
  if (!a || !b) return null;
  return (Number(b[1]) * 12 + Number(b[2])) - (Number(a[1]) * 12 + Number(a[2])) + 1;
}

export function mapStorageVehicle(v) {
  return {
    id: v.id, subscriptionId: v.subscription_id, vehicleId: v.vehicle_id || '', label: S(v.label), kind: S(v.kind),
    monthlyAmount: Number(v.monthly_amount) || 0, startMonth: S(v.start_month), endMonth: S(v.end_month),
    status: S(v.status) || 'stored', pulledAt: v.pulled_at || null, addedBy: S(v.added_by),
    // Customer asked for it back (still stored + billed until "Mark delivered").
    pullRequestedAt: v.pull_requested_at || null,
    pullRequestedFor: v.pull_requested_for ? String(v.pull_requested_for).slice(0, 10) : '',
    pullRequestNote: S(v.pull_request_note),
    deliveredAt: v.delivered_at || null, deliveredBy: S(v.delivered_by),
  };
}
// A card payment that landed: pending until "Record as revenue", then booked.
export function mapStoragePayment(p) {
  return {
    id: p.id, subscriptionId: p.subscription_id, amount: Number(p.amount) || 0,
    month: S(p.month), paidAt: p.paid_at || null, status: S(p.status) || 'pending',
    bookedAt: p.booked_at || null, bookedBy: S(p.booked_by), revenueId: S(p.revenue_id),
  };
}
function _mapStorageSub(row, cust, veh, payments, storageVehicles) {
  const now = storageMonthNow();
  const cars = Array.isArray(storageVehicles) ? storageVehicles : [];
  const stored = cars.filter((v) => v.status === 'stored');
  const start = S(row.start_month);
  const end = S(row.end_month);
  const span = storageMonthSpan(start, end);
  const amount = Number(row.monthly_amount) || 0;
  // Months already billed, from the watermark — not from the calendar, so a
  // subscription the biller hasn't caught up on reads as behind rather than
  // as if the money were already in.
  const billed = row.last_posted_month
    ? Math.max(0, (storageMonthSpan(start, S(row.last_posted_month)) || 0))
    : 0;
  const name = cust
    ? String(cust.full_name || [cust.first_name, cust.last_name].filter(Boolean).join(' ') || '').trim()
    : '';
  const vehicle = veh
    ? [veh.year, veh.make, veh.model].filter(Boolean).join(' ').trim()
    : '';
  return {
    id: row.id,
    customerId: row.customer_id,
    name: name || row.customer_id,
    phone: (cust && S(cust.phone)) || '',
    email: (cust && S(cust.email)) || '',
    vehicleId: row.vehicle_id || '',
    // The car line: every car still stored, or the legacy single vehicle.
    vehicle: stored.length ? stored.map((v) => v.label).filter(Boolean).join(' + ') : vehicle,
    vehicles: cars,
    storedCount: stored.length,
    // The customer's self-serve link credential (public/3c-storage.html?t=…).
    portalToken: S(row.portal_token),
    label: S(row.label),
    monthlyAmount: amount,
    startMonth: start,
    endMonth: end,
    status: S(row.status) || 'active',
    billingMode: S(row.billing_mode) || 'stripe',
    tabId: row.tab_id || '',
    paymentLinkUrl: S(row.payment_link_url),
    linkSentAt: row.signup_link_sent_at || null,
    stripeSubscriptionId: S(row.stripe_subscription_id),
    onCard: !!row.stripe_subscription_id,
    lastPostedMonth: S(row.last_posted_month),
    notes: S(row.notes),
    // Which shop physically holds the vehicle. Shown on the card and read-only
    // in the edit form; the biller also stamps the month's tab charge and
    // revenue row with it, so it decides whose books the storage income lands
    // in as well as what the operator sees.
    site: S(row.site) || 'kingston',
    createdAt: row.created_at || null,
    // Derived: what the agreement is worth and how far through it is.
    months: span,                                   // null = open-ended
    monthsBilled: billed,
    monthsLeft: span == null ? null : Math.max(0, span - billed),
    contractValue: span == null ? null : Math.round(span * amount * 100) / 100,
    // Months that should have posted by now but haven't — the number that
    // matters when the biller has been failing quietly.
    monthsBehind: (() => {
      if (S(row.status) !== 'active') return 0;
      const through = end && end < now ? end : now;
      if (!start || through < start) return 0;
      const due = storageMonthSpan(start, through) || 0;
      return Math.max(0, due - billed);
    })(),
    // Card payments waiting for "Record as revenue" (2026-10-22). Nothing is
    // owed on a tab any more — a month is either on the books or it is not.
    pendingPayments: (payments || []).filter((p) => p.status === 'pending').map(mapStoragePayment),
    pendingTotal: Math.round((payments || []).filter((p) => p.status === 'pending').reduce((t, p) => t + (Number(p.amount) || 0), 0) * 100) / 100,
  };
}

// The roster. One read per table rather than a join, matching how the rest of
// this layer talks to PostgREST, then stitched in memory.
export async function listStorageSubs() {
  try {
    const { data: rows, error } = await supabase.from('storage_subscriptions')
      .select('*').order('created_at', { ascending: false });
    if (error) return failResp(error);
    const list = rows || [];
    const custIds = [...new Set(list.map((r) => r.customer_id).filter(Boolean))];
    const vehIds = [...new Set(list.map((r) => r.vehicle_id).filter(Boolean))];
    const subIds = list.map((r) => r.id);
    const [custR, vehR, payR, svR] = await Promise.all([
      custIds.length ? supabase.from('customers').select('id,full_name,first_name,last_name,phone,email').in('id', custIds) : Promise.resolve({ data: [] }),
      vehIds.length ? supabase.from('vehicles').select('id,year,make,model').in('id', vehIds) : Promise.resolve({ data: [] }),
      // Card payments on each agreement — the pending ones are what the card
      // and the summary surface (storage_payments, 2026-10-22).
      subIds.length ? supabase.from('storage_payments').select('*').in('subscription_id', subIds).order('paid_at') : Promise.resolve({ data: [] }),
      // The cars on each agreement (2026-10-19: several per card, each on its
      // own rate). Read-only here; adds/pulls go through the server.
      subIds.length ? supabase.from('storage_vehicles').select('*').in('subscription_id', subIds).order('created_at') : Promise.resolve({ data: [] }),
    ]);
    const custById = {}; (custR.data || []).forEach((c) => { custById[c.id] = c; });
    const vehById = {}; (vehR.data || []).forEach((v) => { vehById[v.id] = v; });
    const svBySub = {}; (svR.data || []).forEach((v) => { (svBySub[v.subscription_id] ||= []).push(mapStorageVehicle(v)); });
    const payBySub = {}; (payR.data || []).forEach((p) => { (payBySub[p.subscription_id] ||= []).push(p); });
    const subs = list.map((r) => _mapStorageSub(r, custById[r.customer_id], vehById[r.vehicle_id], payBySub[r.id] || [], svBySub[r.id] || []));
    const active = subs.filter((s) => s.status === 'active');
    return okResp({
      subs,
      monthlyTotal: Math.round(active.reduce((t, s) => t + s.monthlyAmount, 0) * 100) / 100,
      activeCount: active.length,
      pendingTotal: Math.round(subs.reduce((t, s) => t + (s.pendingTotal || 0), 0) * 100) / 100,
      pendingCount: subs.reduce((t, s) => t + (s.pendingPayments || []).length, 0),
      month: storageMonthNow(),
    });
  } catch (e) { return failResp(e); }
}

// Create or edit an agreement. The month range is validated here as well as by
// the table's CHECK constraints — the constraint is what makes a bad row
// impossible, this is what makes the message readable.
export async function saveStorageSub(sub) {
  try {
    const s = sub || {};
    const customerId = S(s.customerId).trim();
    if (!customerId) return failResp('Pick a customer first.');
    const start = S(s.startMonth).trim();
    const end = S(s.endMonth).trim();
    if (!/^\d{4}-\d{2}$/.test(start)) return failResp('Start month must look like 2026-10.');
    if (end && !/^\d{4}-\d{2}$/.test(end)) return failResp('End month must look like 2027-04.');
    if (end && end < start) return failResp('The end month is before the start month.');
    const amount = Number(s.monthlyAmount);
    if (!s.id && (!Number.isFinite(amount) || amount <= 0)) return failResp('Enter a monthly amount.');
    const row = {
      customer_id: customerId,
      label: S(s.label).trim() || 'Vehicle storage',
      start_month: start,
      end_month: end || null,
      billing_mode: s.billingMode === 'manual' ? 'manual' : 'stripe',
      notes: S(s.notes).trim() || null,
    };
    // 🚨 Set on CREATE only. `site` is immutable on this table (a trigger
    // refuses the change), and including it in the update payload would make
    // every ordinary edit — a changed amount, a new note — fail the moment the
    // value round-tripped differently. The create branch below adds it.
    //
    // 🚨 monthly_amount and vehicle_id are CREATE-only too (2026-10-19): the
    // agreement's amount is the trigger-kept sum of its storage_vehicles rows,
    // and the cars are added/pulled on the card through the server. Writing
    // the amount here would be overwritten by the next vehicle change.
    if (s.id) {
      const { error } = await supabase.from('storage_subscriptions').update(row).eq('id', s.id);
      if (error) return failResp(error);
      // The end month is a promise made to Stripe too — push it, or a shortened
      // agreement keeps charging the card after the last month we billed.
      const sync = await _storageAction('storage-sync-end', { subId: s.id });
      return okResp({ id: s.id, stripeSynced: !!(sync && sync.success) });
    }
    row.id = 'STOR-' + genIdSuffix();
    row.status = 'active';
    row.vehicle_id = S(s.vehicleId).trim() || null;
    row.monthly_amount = amount;
    // Falls back to the column default (auth_site()) when the caller says
    // nothing, which is exactly the old behaviour.
    if (S(s.site).trim()) row.site = S(s.site).trim();
    row.created_by = S(s.by) || null;
    const { error } = await supabase.from('storage_subscriptions').insert(row);
    if (error) return failResp(error);
    // The cars on the agreement, every one billing from the agreement's own
    // start. Through the server, which is the only writer of storage_vehicles
    // (and which re-sums monthly_amount from the rows as each lands). One car
    // failing does not undo the others — the caller is told which, by index,
    // so they can be added on the card.
    // `vehicles: [{label, kind, amount, vehicleId}]`; the older single-car
    // shape (vehicleLabel/vehicleKind + the agreement amount) still works.
    const list = Array.isArray(s.vehicles) && s.vehicles.length ? s.vehicles : [{
      label: S(s.vehicleLabel).trim() || row.label, kind: S(s.vehicleKind) || '', amount, vehicleId: row.vehicle_id || '',
    }];
    const vehicleErrors = [];
    for (const v of list) {
      const r = await _storageAction('storage-vehicle', {
        subId: row.id, op: 'add', label: S(v && v.label).trim() || row.label, kind: S(v && v.kind) || '',
        amount: Number(v && v.amount), vehicleId: S(v && v.vehicleId).trim() || '', startMonth: start,
      });
      vehicleErrors.push(r && r.success ? '' : ((r && r.error) || 'Could not add ' + (S(v && v.label).trim() || 'the vehicle')));
    }
    const added = vehicleErrors.filter((e) => !e).length;
    return okResp({ id: row.id, vehiclesAdded: added, vehicleErrors, vehicleAdded: added > 0, vehicleError: vehicleErrors.find(Boolean) || '' });
  } catch (e) { return failResp(e); }
}

// Add / pull / re-rate one car on an agreement. Server-side so the Stripe
// charge moves with the ledger. `payload` per op:
//   add    { label, kind, amount, vehicleId?, startMonth? }
//   pull   { vehicleId }   (the storage_vehicles row id)
//   update { vehicleId, amount?, label?, kind? }
export async function storageVehicleAction(subId, op, payload) {
  try {
    if (!subId) return failResp('id required');
    const r = await _storageAction('storage-vehicle', { subId, op, ...(payload || {}) });
    if (!r || !r.success) return failResp((r && r.error) || 'Could not update the vehicles');
    await _bustRevenueCaches();
    return okResp(r);
  } catch (e) { return failResp(e); }
}

// Text the customer their own storage page — where they add or pull a car
// themselves. The token is on the row (readable by owner/principal under RLS);
// the page lives at /3c-storage.html.
export async function sendStoragePortalLink(id) {
  try {
    if (!id) return failResp('id required');
    const { data: sub } = await supabase.from('storage_subscriptions')
      .select('customer_id,label,portal_token').eq('id', id).maybeSingle();
    if (!sub || !sub.portal_token) return failResp('No storage link on this agreement yet — reload and try again.');
    const url = window.location.origin + '/3c-storage.html?t=' + encodeURIComponent(sub.portal_token);
    const { data: cust } = sub.customer_id
      ? await supabase.from('customers').select('first_name,full_name,phone').eq('id', sub.customer_id).maybeSingle()
      : { data: null };
    const phone = (cust && S(cust.phone)) || '';
    if (!phone) return okResp({ url, sent: false, error: 'No phone on file — copy the link instead.' });
    const first = (cust && (S(cust.first_name) || String(S(cust.full_name)).split(' ')[0])) || 'there';
    const body = composeSms(`Hi ${first}, here's your 3C Detailing storage page — see what's in storage, add a vehicle or let us know you're picking one up: ${url}`);
    const r = await sendSms(phone, body);
    _bustPhoneMap();
    return okResp({ url, sent: !!(r && r.success), error: r && r.error });
  } catch (e) { return failResp(e); }
}

// Pause / resume. A paused subscription stops accruing — the biller's cron
// query only sees 'active'.
//
// 🚨 Resuming has to MOVE THE WATERMARK, and this is the whole subtlety of the
// pause. The biller resumes from the month after `last_posted_month`, so a
// subscription paused in November and resumed in February would walk forward
// from November and bill all three paused months at once — the exact opposite
// of what pausing means. So resume jumps the watermark to last month, and
// billing restarts with the current one.
//
// The watermark is only ever pushed FORWARD (never rewound), so resuming twice
// in one month, or resuming a subscription that is already caught up, changes
// nothing. A subscription whose start month is still in the future is left
// alone — there is nothing yet to skip.
export async function setStorageStatus(id, status) {
  try {
    if (!id) return failResp('id required');
    if (['active', 'paused', 'ended', 'cancelled'].indexOf(status) < 0) return failResp('Unknown status');
    const patch = { status };
    if (status === 'active') {
      const { data: cur } = await supabase.from('storage_subscriptions')
        .select('start_month,last_posted_month,status').eq('id', id).maybeSingle();
      // Only when coming back from a pause — a plain re-activation of a
      // cancelled card should not skip months the customer still owes.
      if (cur && cur.status === 'paused') {
        const skipTo = storageMonthAdd(storageMonthNow(), -1);
        const start = S(cur.start_month);
        const mark = S(cur.last_posted_month);
        if (start && start <= skipTo && skipTo > mark) patch.last_posted_month = skipTo;
      }
    }
    const { error } = await supabase.from('storage_subscriptions')
      .update(patch).eq('id', id);
    if (error) return failResp(error);
    return okResp({ id, status });
  } catch (e) { return failResp(e); }
}

// Delete the AGREEMENT. The tab charges and revenue rows it already posted stay
// exactly where they are — this is not an undo for billing, and the UI says so.
export async function removeStorageSub(id) {
  try {
    if (!id) return failResp('id required');
    const { data: hit, error } = await supabase.from('storage_subscriptions').delete().eq('id', id).select('id');
    if (error) return failResp(error);
    if (!hit || !hit.length) return failResp("Delete didn't go through — session may have expired. Reopen the app and try again.");
    return okResp({});
  } catch (e) { return failResp(e); }
}

// Shared POST to the invoicer for the storage actions that need the service
// role (Stripe calls + the biller).
async function _storageAction(action, payload) {
  const { data } = await supabase.auth.getSession();
  const tok = data && data.session && data.session.access_token;
  if (!tok) return failResp('no session');
  const res = await fetch('/api/invoicer?action=' + action, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + tok },
    body: JSON.stringify(payload || {}),
  });
  return await res.json().catch(() => ({ success: false, error: 'Bad response from the server' }));
}

// Mint this subscription's own monthly Stripe Payment Link (per-car pricing, so
// it cannot share the membership link) and text it to the customer.
export async function sendStorageSignup(id) {
  try {
    if (!id) return failResp('id required');
    const link = await _storageAction('storage-payment-link', { subId: id });
    if (!link || !link.success) return failResp((link && link.error) || 'Could not create the payment link');
    const { data: sub } = await supabase.from('storage_subscriptions')
      .select('customer_id,label').eq('id', id).maybeSingle();
    const { data: cust } = sub && sub.customer_id
      ? await supabase.from('customers').select('first_name,full_name,phone').eq('id', sub.customer_id).maybeSingle()
      : { data: null };
    const phone = (cust && S(cust.phone)) || '';
    if (!phone) return okResp({ url: link.url, sent: false, error: 'No phone on file — copy the link instead.' });
    const first = (cust && (S(cust.first_name) || String(S(cust.full_name)).split(' ')[0])) || 'there';
    const what = (sub && S(sub.label)) || 'vehicle storage';
    const body = composeSms(`Hi ${first}, here's the monthly payment setup for your 3C Detailing ${what}: ${link.url}`);
    const r = await sendSms(phone, body);
    if (r && r.success) {
      await supabase.from('storage_subscriptions')
        .update({ signup_link_sent_at: new Date().toISOString() }).eq('id', id);
    }
    _bustPhoneMap();
    return okResp({ url: link.url, sent: !!(r && r.success), error: r && r.error });
  } catch (e) { return failResp(e); }
}

// Just the link, for copying — same mint-or-reuse path, no text.
// A self-serve manage/cancel link for the customer — Stripe's own portal,
// covering every subscription they hold with us rather than just this one.
export async function storagePortalLink(id) {
  try {
    if (!id) return failResp('id required');
    const r = await _storageAction('storage-portal-link', { subId: id });
    if (!r || !r.success) return failResp((r && r.error) || 'Could not create the portal link');
    return okResp({ url: r.url });
  } catch (e) { return failResp(e); }
}

export async function storagePaymentLink(id) {
  try {
    if (!id) return failResp('id required');
    const r = await _storageAction('storage-payment-link', { subId: id });
    if (!r || !r.success) return failResp((r && r.error) || 'Could not create the payment link');
    return okResp({ url: r.url, reused: !!r.reused });
  } catch (e) { return failResp(e); }
}

// Catch a subscription up by hand. Only posts months that are actually due, so
// it can never pull a future month forward.
export async function postStorageNow(id) {
  try {
    if (!id) return failResp('id required');
    const r = await _storageAction('post-storage-now', { subId: id });
    if (!r || !r.success) return failResp((r && r.error) || 'Could not record the storage month');
    await _bustRevenueCaches();   // a month of storage just landed in the ledger
    return okResp(r);
  } catch (e) { return failResp(e); }
}

// "Record as revenue" on a card payment (storage_payments.pending): books the
// month it covers at the amount that arrived. Server-side so the revenue row,
// the payment and the agreement's watermark move together.
export async function approveStoragePayment(paymentId) {
  try {
    if (!paymentId) return failResp('paymentId required');
    const r = await _storageAction('storage-payment-approve', { paymentId });
    if (!r || !r.success) return failResp((r && r.error) || 'Could not record the payment');
    await _bustRevenueCaches();
    try { bustClientCache('getNotifications'); } catch (_) {}
    return okResp(r);
  } catch (e) { return failResp(e); }
}

// Stop collecting. `immediate` kills the card charge now; otherwise it runs to
// the end of the period already paid for. The ledger is deliberately untouched
// — an outstanding balance survives the card being switched off.
export async function cancelStorageSub(id, immediate) {
  try {
    if (!id) return failResp('id required');
    const r = await _storageAction('storage-cancel', { subId: id, immediate: !!immediate });
    if (!r || !r.success) return failResp((r && r.error) || 'Could not cancel the subscription');
    return okResp(r);
  } catch (e) { return failResp(e); }
}

// ---------------------------------------------------------------------------
// TAR upsells — offer a customer a recommended service, with approval.
// ---------------------------------------------------------------------------
// A technician writes a TAR ("rear seats have heavy pet hair"); somebody senior
// turns that into an OFFER of a named, priced service and queues it. The
// principal approves before a word reaches the customer — which is the whole
// point of the change: the old 📱 Text button sent immediately, so an upsell
// went out with nobody but its author having read it.
//
// 🚨 The draft is deliberately written with `to_addr: null`. That is not an
// oversight — it is the switch that makes this an SMS. `sendEmailDraft` emails
// a draft that has an address (and texts alongside it), and TEXTS ONLY when
// there is no address. So an upsell rides the existing approve-and-send path
// untouched, and putting an email on this row would silently start emailing
// upsells too.
//
// Nothing here sends. It writes a pending row and rings a bell; the send
// happens when the principal approves in Comms → Outbox.
export async function createUpsellDraft(opts) {
  try {
    const o = opts || {};
    const bookingId = S(o.bookingId).trim();
    const service = S(o.service).trim();
    const body = S(o.body).trim();
    if (!service) return failResp('Pick a service to offer.');
    if (!body) return failResp('The message is empty.');
    // A phone is what this draft will actually be sent on. Refuse up front
    // rather than queueing something that can only fail at approval time.
    if (!S(o.phone).replace(/\D/g, '')) return failResp('No phone on file for this customer — nothing to text.');

    const price = Number(o.price);
    const id = 'ED-UPS-' + genIdSuffix();
    const row = {
      id,
      booking_id: bookingId || null,
      customer_id: S(o.customerId).trim() || null,
      to_addr: null,                       // ← see the note above; this is the SMS switch
      subject: 'Upsell — ' + service + (Number.isFinite(price) && price > 0 ? ' ($' + price + ')' : ''),
      body,
      status: 'pending',
      kind: 'upsell',
      drafted_at: new Date().toISOString(),
    };
    const { error } = await supabase.from('email_drafts').insert(row);
    if (error) return failResp(error);

    const who = S(o.customerName).trim();
    // The durable record. The draft above is transient — sendEmailDraft ERASES
    // it once the text goes out — so everything worth keeping about the offer is
    // written here instead, and stamped 'sent' from the send path.
    // Best-effort on purpose: a history write must never cost a proposal that
    // has already been queued.
    const offerId = 'UPS-' + genIdSuffix();
    try {
      await supabase.from('upsell_offers').insert({
        id: offerId,
        booking_id: bookingId || null,
        customer_id: S(o.customerId).trim() || null,
        customer_name: who || null,
        tar_id: S(o.tarId).trim() || null,
        tar_text: S(o.tarText).trim() || null,
        service,
        price: Number.isFinite(price) ? price : null,
        message: body,
        status: 'pending',
        draft_id: id,
        proposed_by: S(o.by).trim() || null,
      });
    } catch (_) { /* the offer is queued either way — history is not the gate */ }
    _insertNotification({
      type: 'upsell_draft',
      message: '💬 Upsell ready to approve: ' + service
        + (Number.isFinite(price) && price > 0 ? ' — $' + price : '')
        + (who ? ' for ' + who : '') + '. Comms → Outbox → Upsell.',
      forRole: 'principal',
      bookingId: bookingId || null,
      customerName: who || null,
    });

    bustClientCache('getEmailDrafts');
    return okResp({ id });
  } catch (e) { return failResp(e); }
}

// The offer history. `status` is what we did, `outcome` is what the customer
// said — see the header of 20260908120000_upsell_offers.sql for why those are
// two columns and not one.
export async function listUpsellOffers(opts) {
  try {
    const o = opts || {};
    let q = supabase.from('upsell_offers').select('*').order('proposed_at', { ascending: false });
    if (o.status) q = q.eq('status', o.status);
    if (o.limit) q = q.limit(o.limit);
    else q = q.limit(200);
    const { data, error } = await q;
    if (error) return failResp(error);
    // Appointment context, so the accept button isn't pressed blind: what the
    // job is, when, and whether its invoice is already cut (after which the
    // service can no longer be added from here).
    const bkIds = [...new Set((data || []).map((r) => r.booking_id).filter(Boolean))];
    const bkById = {};
    if (bkIds.length) {
      const { data: bks } = await supabase.from('bookings')
        .select('id,date,start_time,service,vehicle_desc,status,invoice_so_num,customer_name')
        .in('id', bkIds);
      (bks || []).forEach((b) => { bkById[b.id] = b; });
    }
    const offers = (data || []).map((r) => ({
      id: r.id,
      bookingId: S(r.booking_id),
      booking: (() => {
        const b = bkById[r.booking_id];
        if (!b) return null;
        return {
          date: String(b.date || '').slice(0, 10),
          service: S(b.service),
          vehicle: S(b.vehicle_desc),
          status: S(b.status),
          invoiceSoNum: S(b.invoice_so_num),
          customerName: S(b.customer_name),
        };
      })(),
      customerId: S(r.customer_id),
      name: S(r.customer_name),
      tarId: S(r.tar_id),
      tarText: S(r.tar_text),
      service: S(r.service),
      price: r.price == null ? null : Number(r.price),
      message: S(r.message),
      status: S(r.status) || 'pending',
      outcome: S(r.outcome) || '',
      outcomeAt: r.outcome_at || null,
      proposedBy: S(r.proposed_by),
      proposedAt: r.proposed_at || null,
      sentAt: r.sent_at || null,
    }));
    const sent = offers.filter((x) => x.status === 'sent');
    const accepted = sent.filter((x) => x.outcome === 'accepted');
    return okResp({
      offers,
      // Only ANSWERED offers count in the rate — dividing by everything sent
      // would report an upsell programme as failing simply because most
      // customers haven't replied yet.
      sentCount: sent.length,
      answered: sent.filter((x) => x.outcome).length,
      acceptedCount: accepted.length,
      acceptedValue: Math.round(accepted.reduce((t, x) => t + (x.price || 0), 0) * 100) / 100,
    });
  } catch (e) { return failResp(e); }
}

// The customer said yes — make it real.
// ---------------------------------------------------------------------------
// Recording "accepted" is bookkeeping; this is the part that changes the job.
// Three writes, in an order chosen so a failure part-way leaves the safest
// state:
//   1. the booking's `service` gains the sold service
//   2. the checklist is amended so the work appears on the detailer's list
//   3. a task lands on the detailer's alert banner telling them why it changed
// and only then is the offer stamped accepted.
//
// 🚨 `service` is the load-bearing write, and it is what "amends the invoice".
// The invoicer builds its line items from the booking's service string
// (CreateInvoiceBtn passes it through), and priceForService SUMS a
// comma-separated list — so appending the service both bills it and makes it
// count in every revenue projection. Writing a price anywhere else here would
// create a second, disagreeing source of truth.
//
// 🚨 amendChecklist DELETES uncompleted non-custom tasks that aren't in the
// service it is given. It must therefore be called with the COMBINED string,
// never the sold service alone — that would wipe the original service's
// remaining tasks off a car that is mid-detail.
export async function acceptUpsellOffer(offerId, by) {
  try {
    if (!offerId) return failResp('id required');
    const { data: offer, error } = await supabase.from('upsell_offers').select('*').eq('id', offerId).maybeSingle();
    if (error) return failResp(error);
    if (!offer) return failResp('Offer not found');
    if (offer.status !== 'sent') return failResp('That offer has not been sent to the customer yet.');
    if (offer.outcome === 'accepted') return failResp('That offer is already accepted.');
    if (!offer.booking_id) return failResp('This offer has no appointment attached — add the service to the booking by hand.');

    const b = await _bookingRow(offer.booking_id);
    if (!b) return failResp('The appointment behind this offer no longer exists.');

    const service = S(offer.service).trim();
    const current = S(b.service).trim();
    const parts = current.split(',').map((x) => x.trim()).filter(Boolean);
    // Idempotent: accepting twice, or selling something already on the job,
    // must not append a duplicate line the invoicer would then bill twice.
    const already = parts.some((x) => x.toLowerCase() === service.toLowerCase());
    const combined = already ? current : (parts.length ? parts.join(', ') + ', ' + service : service);

    if (!already) {
      const up = await supabase.from('bookings').update({ service: combined }).eq('id', offer.booking_id);
      if (up.error) return failResp(up.error);
      // Checklist second: if this fails the service is still sold and billed,
      // which is recoverable by hand. The reverse — tasks for work nobody is
      // charging for — is the worse way round.
      try { await amendChecklist(offer.booking_id, combined); } catch (_) {}
    }

    // Tell the detailers. A `for_role` bell would be written and never seen —
    // the detailer shell has no notification centre — but its TaskAlertBanner
    // shows OPEN TASKS ASSIGNED TO THEM, so a task is the surface that actually
    // reaches the floor. It also lands in their Tasks tab.
    const veh = S(b.vehicle_desc) || 'the vehicle';
    const who = S(offer.customer_name) || S(b.customer_name) || 'the customer';
    try {
      await addTask({
        assignedTo: 'detailer',
        assignedBy: S(by) || 'principal',
        title: '➕ Added to ' + veh + ': ' + service,
        notes: who + ' approved this upsell'
          + (offer.price ? ' ($' + Number(offer.price) + ')' : '')
          + '. The checklist has been updated — the new tasks are on the job.'
          + (offer.tar_text ? '\nFrom the recommendation: "' + S(offer.tar_text) + '"' : ''),
        priority: 'high',
      });
    } catch (_) { /* the work is on the checklist regardless */ }

    const upd = await supabase.from('upsell_offers').update({
      outcome: 'accepted', outcome_at: new Date().toISOString(), outcome_by: S(by) || null,
    }).eq('id', offerId);
    if (upd.error) return failResp(upd.error);

    bustClientCache('getBookings');
    bustClientCache('getChecklist');
    // An invoice already cut can't be amended from here — the invoicer owns it
    // once it has an S.O. number. Report it so the caller can say so out loud
    // rather than leaving someone to discover the line is missing at payment.
    return okResp({
      id: offerId, service, added: !already, alreadyOnJob: already,
      invoiceSoNum: S(b.invoice_so_num),
      bookingStatus: S(b.status),
    });
  } catch (e) { return failResp(e); }
}

// Record what the customer said. Clearing (outcome '') puts it back to
// unanswered — a misclick on someone's reply shouldn't be permanent.
export async function setUpsellOutcome(id, outcome, by) {
  try {
    if (!id) return failResp('id required');
    const val = S(outcome).trim();
    if (val && val !== 'accepted' && val !== 'declined') return failResp('Unknown outcome');
    const { error } = await supabase.from('upsell_offers').update({
      outcome: val || null,
      outcome_at: val ? new Date().toISOString() : null,
      outcome_by: val ? (S(by) || null) : null,
    }).eq('id', id);
    if (error) return failResp(error);
    return okResp({ id, outcome: val });
  } catch (e) { return failResp(e); }
}

// Pull a proposal that shouldn't go out. Deletes the queued draft with it —
// leaving the draft behind would let it be approved after the offer was
// withdrawn, which is the one outcome this button exists to prevent.
export async function withdrawUpsellOffer(id) {
  try {
    if (!id) return failResp('id required');
    const { data: row } = await supabase.from('upsell_offers').select('draft_id,status').eq('id', id).maybeSingle();
    if (row && row.status === 'sent') return failResp('That offer has already gone to the customer.');
    const { error } = await supabase.from('upsell_offers')
      .update({ status: 'withdrawn' }).eq('id', id).eq('status', 'pending');
    if (error) return failResp(error);
    if (row && row.draft_id) {
      try { await supabase.from('email_drafts').delete().eq('id', row.draft_id); } catch (_) {}
      bustClientCache('getEmailDrafts');
    }
    return okResp({ id });
  } catch (e) { return failResp(e); }
}

// ---------------------------------------------------------------------------
// Red Flag — perpetual media retention + a 🚩 identifier on the customer.
// ---------------------------------------------------------------------------
// Flagging does NOT stamp keep=true on their existing media rows: the
// purge-old-media job reads the flag live, so anything they upload LATER is
// held too. That's the whole point of "perpetual". Unflagging therefore puts
// their media back on the normal 3-month clock — including old files that are
// already past it, which the next nightly run will then delete. The UI says so
// before you turn it off.

export async function setCustomerRedFlag(customerId, on, opts) {
  try {
    const cid = String(customerId || '').trim();
    if (!cid) return failResp('customerId required');
    const o = opts || {};
    const row = on
      ? { red_flag: true, red_flag_at: new Date().toISOString(), red_flag_by: o.by ? String(o.by) : null, red_flag_note: o.note ? String(o.note) : null }
      : { red_flag: false, red_flag_at: null, red_flag_by: null, red_flag_note: null };
    // .select() for a row COUNT, not just an error: customers_upd is
    // auth_role()-gated, so a lapsed JWT updates 0 rows and returns NO error
    // (the documented stale-JWT → silent RLS deny). Refresh + retry once.
    // Critical here — a flag that silently didn't save means media the operator
    // believes is protected quietly stays on the 3-month purge clock.
    let { data: hit, error } = await supabase.from('customers').update(row).eq('id', cid).select('id');
    if (error) return failResp(error);
    if (!hit || !hit.length) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      const retry = await supabase.from('customers').update(row).eq('id', cid).select('id');
      if (retry.error) return failResp(retry.error);
      if (!retry.data || !retry.data.length) return failResp("That didn't go through — session may have expired. Reopen the app and try again.");
    }
    // Dynamic import mirrors the existing usage elsewhere in this file — a
    // static import of apiLegacy here would create a cycle.
    try {
      const leg = await import('./apiLegacy');
      ['getAllCustomers', 'getDashboardOverview'].forEach((k) => { try { leg.bustClientCache(k); } catch (_) {} });
    } catch (_) {}
    return okResp({ redFlag: !!on });
  } catch (e) { return failResp(e); }
}

// "Purge IP" — erase EVERY walk-around/insurance media item for one customer.
// Storage objects have to go through the Storage API (a bare row delete orphans
// the object and keeps billing for it), so this runs server-side on the service
// role via /api/invoicer rather than as a client delete.
export async function purgeCustomerMedia(customerId) {
  try {
    const cid = String(customerId || '').trim();
    if (!cid) return failResp('customerId required');
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    if (!tok) return failResp('no session');
    const res = await fetch('/api/invoicer?action=purge-customer-media', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + tok },
      body: JSON.stringify({ customerId: cid }),
    });
    const j = await res.json().catch(() => ({}));
    if (!j || !j.success) return failResp((j && j.error) || 'Purge failed');
    return okResp(j);
  } catch (e) { return failResp(e); }
}

// Text the selected members their Stripe membership signup link. The link
// carries client_reference_id so the webhook auto-populates their record on
// signup. Stamps signup_link_sent_at on each successful send.
export async function sendMembershipSignup(memberIds, opts) {
  try {
    const ids = Array.isArray(memberIds) ? memberIds : [memberIds];
    if (!ids.length) return failResp('No members selected');
    const cfg = await _getSetting('MEMBERSHIP', null);
    const explicitLink = (opts && opts.paymentLink) || '';
    const baseLink = explicitLink || (cfg && cfg.paymentLink) || _DEFAULT_MEMBERSHIP_LINK;
    if (!baseLink) return failResp('No membership Payment Link set yet — add it in the Membership tab settings.');
    const { data: mems, error } = await supabase.from('members').select('id,customer_id,status,plan').in('id', ids);
    if (error) return failResp(error);
    const custIds = [...new Set((mems || []).map((m) => m.customer_id).filter(Boolean))];
    const { data: custs } = custIds.length
      ? await supabase.from('customers').select('id,full_name,first_name,phone,email').in('id', custIds)
      : { data: [] };
    const custById = {}; (custs || []).forEach((c) => { custById[c.id] = c; });
    const results = [];
    for (const m of (mems || [])) {
      const c = custById[m.customer_id] || {};
      const phone = S(c.phone);
      const first = S(c.first_name) || String(S(c.full_name) || '').split(' ')[0] || 'there';
      if (!phone) { results.push({ id: m.id, ok: false, error: 'No phone on file' }); continue; }
      // A plan with its own link (a Winter Wash Club tier) gets that link; an
      // explicitly passed link still wins over everything.
      const planLink = explicitLink ? '' : _planLinkFor(cfg, m.plan);
      const link = _membershipSignupUrl(planLink || baseLink, m.customer_id, S(c.email));
      const plan = planLink ? String(m.plan || '').trim() : '';
      const body = composeSms(plan
        ? `Hi ${first}, here's your 3C Detailing ${plan} membership signup: ${link}`
        : `Hi ${first}, become a 3C Detailing member — sign up here: ${link}`);
      const r = await sendSms(phone, body);
      if (r && r.success) {
        await supabase.from('members').update({ signup_link_sent_at: new Date().toISOString() }).eq('id', m.id);
      }
      results.push({ id: m.id, ok: !!(r && r.success), error: r && r.error });
    }
    _bustPhoneMap();
    const sent = results.filter((r) => r.ok).length;
    return okResp({ results, sent, failed: results.length - sent });
  } catch (e) { return failResp(e); }
}

// Membership settings (Stripe Payment Link + optional plan labels) — stored in
// app_settings so the owner can set them from the tab without a redeploy.
export async function getMembershipConfig() {
  try {
    const cfg = await _getSetting('MEMBERSHIP', null);
    const config = (cfg && typeof cfg === 'object') ? { ...cfg } : { plans: [] };
    if (!config.paymentLink) config.paymentLink = _DEFAULT_MEMBERSHIP_LINK; // interim shop-page link until a real Stripe Payment Link is set
    if (!config.planLinks || typeof config.planLinks !== 'object') config.planLinks = {};
    return okResp({ config });
  } catch (e) { return failResp(e); }
}
export async function saveMembershipConfig(config) {
  try {
    const cur = await _getSetting('MEMBERSHIP', null);
    const merged = { ...(cur && typeof cur === 'object' ? cur : {}), ...(config || {}) };
    await _setSetting('MEMBERSHIP', merged);
    return okResp({ config: merged });
  } catch (e) { return failResp(e); }
}

export async function getNotifications(role) {
  try {
    const { data, error } = await supabase.from('notifications').select('*').eq('for_role', role).order('created_at', { ascending: false });
    if (error) return failResp(error);
    const notifications = (data || []).filter((r) => !String(r.dismissed_by || '').split(',').map((s) => s.trim()).includes(role)).map(mapNotification);
    return okResp({ notifications });
  } catch (e) { return failResp(e); }
}

// Fire one or more staff bell notifications. Exported wrapper around
// _insertNotification for callers that want a SINGLE summary bell (e.g. the
// waitlist blast) instead of one-per-item spam.
export async function notifyStaff({ type, message, roles }) {
  try {
    const rs = Array.isArray(roles) ? roles : [roles];
    for (const r of rs) { if (r) await _insertNotification({ type: type || 'info', message, forRole: r }); }
    return okResp({});
  } catch (e) { return failResp(e); }
}

// Notification Hub (principal/owner Background): the full notification LOG for a
// role over the last 7 days — including already-dismissed ones (so it's a history,
// not the actionable bell list). Lazily purges anything older than 7 days so the
// table self-empties (no cron), mirroring the trash-bin pattern. Each item carries
// a `dismissed` flag so the UI can dim ones already cleared from the bell.
export async function listNotificationHistory(role) {
  try {
    const cutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    // Best-effort self-clean of this role's >7-day-old rows (RLS-gated delete may
    // no-op harmlessly — the query below still bounds the view to 7 days regardless).
    try { await supabase.from('notifications').delete().eq('for_role', role).lt('created_at', cutoff); } catch (_) {}
    const { data, error } = await supabase.from('notifications').select('*')
      .eq('for_role', role).gte('created_at', cutoff).order('created_at', { ascending: false });
    if (error) return failResp(error);
    const notifications = (data || []).map((r) => ({
      ...mapNotification(r),
      dismissed: String(r.dismissed_by || '').split(',').map((s) => s.trim()).includes(role),
    }));
    return okResp({ notifications });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// VIP INBOX
// ===========================================================================
export async function getVipInbox(role) {
  try {
    const [notesRes, bookingsRes] = await Promise.all([
      supabase.from('vip_notes').select('*').order('added_at', { ascending: false }),
      supabase.from('bookings').select('id,vehicle_desc,service,status'),
    ]);
    if (notesRes.error) return failResp(notesRes.error);
    const bmap = {}; (bookingsRes.data || []).forEach((b) => { bmap[b.id] = b; });
    let notes = (notesRes.data || []).map((r) => {
      const b = bmap[r.booking_id] || {};
      const id = String(r.id || '');
      // Source by id prefix: 'sms' = inbound Twilio reply, 'lead' = website
      // booking-form lead, 'review' = a post-delivery star rating, 'portal' = a
      // note the customer left in the VIP portal.
      //
      // 'review' is its own source specifically so it does NOT fall through to
      // 'portal', which is what the detailer filter below keys on.
      //
      // ⚠️ That filter is a DISPLAY choice, not an access control — it runs
      // after the rows have already been fetched. The actual protection is in
      // RLS (20260820000000_vip_notes_review_scope.sql): review rows are
      // owner/principal only, matching service_reviews itself. Both exist on
      // purpose; do not delete the policy because this filter looks sufficient.
      const source = id.startsWith('VN-SMS-') ? 'sms'
        : id.startsWith('VN-FORM-') ? 'lead'
          : id.startsWith('VN-REV-') ? 'review' : 'portal';
      return { id: r.id, source, bookingId: r.booking_id, customerName: r.customer_name, note: r.note,
        addedAt: isoTs(r.added_at), vehicleDesc: b.vehicle_desc || '', service: b.service || '', bookingStatus: b.status || '',
        site: r.site || 'kingston' };
    });
    // Detailers only get CUSTOMER-PORTAL interactions in their inbox — not Twilio
    // SMS replies or website leads (those are owner/principal comms).
    if (role === 'detailer') notes = notes.filter((n) => n.source === 'portal');
    return okResp({ notes });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// REVENUE SUMMARY (computed client-side from invoices + booking membership)
// ===========================================================================
export async function getRevenueSummary() {
  try {
    const [inv, bk, mem] = await Promise.all([
      // Ordered (oldest first) so the Dashboard's `.reverse()` reliably puts
      // the newest invoice at the top of the list. Without an explicit order
      // PostgREST returns heap order and a freshly-upserted row scatters into
      // the middle, so a just-created invoice appears to "not show up".
      supabase.from('invoices').select('*').order('date', { nullsFirst: true }).order('imported_at', { nullsFirst: true }),
      // `dealer` rides along so every revenue row can say who produced the
      // job — OTTO's work or 3C's own. See lib/jobSource.js.
      supabase.from('bookings').select('id,customer_id,dealer'),
      supabase.from('members').select('customer_id').eq('status', 'active'),
    ]);
    if (inv.error) return failResp(inv.error);
    // An invoice counts as "membership" when its booking's customer is an active
    // member (membership moved off the per-booking flag to the members table).
    const activeMembers = new Set((mem.error ? [] : (mem.data || [])).map((m) => m.customer_id));
    const memberByBooking = {}; (bk.data || []).forEach((b) => { memberByBooking[b.id] = activeMembers.has(b.customer_id); });
    const dealerByBooking = {}; (bk.data || []).forEach((b) => { if (b.dealer) dealerByBooking[b.id] = b.dealer; });
    const now = new Date();
    const y = now.getFullYear(), mo = now.getMonth(), d = now.getDate();
    const weekStart = new Date(y, mo, d - now.getDay());
    let today = 0, week = 0, month = 0, total = 0;
    const byService = {};
    const invoices = (inv.data || []).map((r) => {
      const amt = Number(r.total) || 0; total += amt;
      const dt = r.date ? new Date(r.date + 'T12:00:00') : null;
      if (dt) {
        if (dt.getFullYear() === y && dt.getMonth() === mo && dt.getDate() === d) today += amt;
        if (dt >= weekStart) week += amt;
        if (dt.getFullYear() === y && dt.getMonth() === mo) month += amt;
      }
      const svc = r.service || 'Other'; (byService[svc] ||= { count: 0, total: 0 }); byService[svc].count++; byService[svc].total += amt;
      return { id: r.id, soNumber: r.so_number, soNum: r.so_number, date: dateOut(r.date), customer: r.customer, phone: S(r.phone),
        vehicle: r.vehicle, service: r.service, subtotal: r.subtotal, tax: r.tax, total: r.total,
        fileName: r.file_name, fileId: r.file_id, cardFee: r.cc_fee || 0, bookingId: r.booking_id,
        // Membership when the invoice's booking-customer is an active member, OR
        // it's a Stripe subscription-dues row (id prefixed MEMBER-), OR it's a
        // redeemed membership wash (is_membership column set by the invoicer).
        isMembership: !!memberByBooking[r.booking_id] || String(r.id || '').startsWith('MEMBER-') || r.is_membership === true,
        // Which dealership produced the job, '' for 3C's own work. Taken from
        // the BOOKING, never from the site — an Albany walk-in is 3C's.
        dealer: S(dealerByBooking[r.booking_id] || ''),
        site: S(r.site) };
    });
    const byServiceArr = Object.keys(byService).map((k) => ({ service: k, count: byService[k].count, total: byService[k].total })).sort((a, b) => b.total - a.total);
    return okResp({ summary: { today, week, month, total, byService: byServiceArr, invoices, totalCount: invoices.length } });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// EMAIL DRAFTS (read)
// ===========================================================================
export async function getEmailDrafts(status) {
  try {
    let q = supabase.from('email_drafts').select('*');
    if (status && status !== '') {
      if (['pre', 'post', 'balance'].includes(status)) q = q.eq('kind', status);
      else q = q.eq('status', status);
    }
    const [{ data, error }, pm] = await Promise.all([
      q.order('drafted_at', { ascending: false }),
      phoneMap(),
    ]);
    if (error) return failResp(error);
    // Attach the customer's phone to every draft so the Outbox card knows an
    // SMS fallback is available when a draft has no recipient email. (Pre drafts
    // re-resolve phone from their booking's customer below.)
    const drafts = (data || []).map((r) => { const d = mapDraft(r); d.phone = pm[r.customer_id] || ''; return d; });
    // Enrich post drafts with their booking's service so the Outbox can show
    // which vehicle care guide (ceramic / 1051) will auto-attach on send. One
    // batched lookup; best-effort (the guide attach itself is server-side).
    try {
      const ids = [...new Set(drafts.filter(d => d.kind === 'post' && d.bookingId).map(d => d.bookingId))];
      if (ids.length) {
        const { data: bks } = await supabase.from('bookings').select('id,service').in('id', ids);
        const svc = {}; (bks || []).forEach(b => { svc[b.id] = b.service || ''; });
        drafts.forEach(d => { if (svc[d.bookingId] !== undefined) d.service = svc[d.bookingId]; });
      }
    } catch (_) {}
    // Enrich pre drafts with the appointment details + customer phone so the
    // Outbox can render the paired "confirmation text" SMS panel and let the
    // owner send it via Twilio. Best-effort; the email side works without it.
    try {
      const preIds = [...new Set(drafts.filter(d => d.kind === 'pre' && d.bookingId).map(d => d.bookingId))];
      if (preIds.length) {
        const { data: bks } = await supabase.from('bookings').select('id,service,date,start_time,customer_id,customer_name,vehicle_desc,site,dealer').in('id', preIds);
        const bmap = {}; (bks || []).forEach(b => { bmap[b.id] = b; });
        const custIds = [...new Set((bks || []).map(b => b.customer_id).filter(Boolean))];
        const pmap = {};
        if (custIds.length) {
          const { data: cs } = await supabase.from('customers').select('id,phone').in('id', custIds);
          (cs || []).forEach(c => { pmap[c.id] = c.phone || ''; });
        }
        drafts.forEach(d => {
          const b = bmap[d.bookingId];
          if (d.kind === 'pre' && b) {
            d.service = b.service || '';
            d.apptDate = b.date || '';
            d.apptStart = b.start_time;
            d.customerName = b.customer_name || '';
            d.vehicleDesc = b.vehicle_desc || '';
            // Carried for the paired confirmation SMS below the draft: which shop,
            // and whether a dealership raised the job. `d.site` already holds the
            // DRAFT row's site; this is the booking's, and the two are set together.
            d.site = b.site || d.site;
            d.dealer = b.dealer || '';
            d.phone = (b.customer_id && pmap[b.customer_id]) || '';
          }
        });
      }
    } catch (_) {}
    return okResp({ drafts });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// EMAIL DRAFTS (generate / regenerate / edit / delete)
// Ports gas/EodEmails.js content builders so the Outbox populates post-cutover.
// Pre-drafts carry the deposit request + VIP portal link + access code, same
// format as the GAS version. Portal/pay-page URLs are derived from the current
// origin so they follow whatever domain the app is served from.
// ===========================================================================
const _draftId = () => 'ED-' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 5).toUpperCase();
function _pageUrl(file) { try { return window.location.origin + '/' + file; } catch (_) { return '/' + file; } }
function _fmtMin(min) {
  if (min == null || min === '') return '';
  const h = Math.floor(min / 60), m = min % 60, ap = h >= 12 ? 'PM' : 'AM';
  let hh = h % 12; if (hh === 0) hh = 12;
  return hh + ':' + String(m).padStart(2, '0') + ' ' + ap;
}
// "HH:mm" -> "h:mm AM/PM"
function _hmPretty(hm) {
  const m = String(hm || '').match(/^(\d{1,2}):(\d{2})/);
  if (!m) return '';
  let h = +m[1]; const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12;
  return h + ':' + m[2] + ' ' + ap;
}
// Normalize "9:00 AM" / "9:00" / "21:00" -> "HH:mm" (24h) for safe comparison.
function _to24h(s) {
  const str = String(s || '').trim();
  let m = str.match(/^(\d{1,2}):(\d{2})\s*([ap]\.?m\.?)?$/i);
  if (!m) { const m2 = str.match(/^(\d{1,2})\s*([ap]\.?m\.?)$/i); if (m2) m = [null, m2[1], '00', m2[2]]; }
  if (!m) return str;
  let h = +m[1]; const min = m[2]; const ap = (m[3] || '').toLowerCase().replace(/\./g, '');
  if (ap === 'pm' && h < 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  return String(h).padStart(2, '0') + ':' + min;
}
function _prettyDate(dateStr) {
  if (!saneDate(dateStr)) return '';
  const p = String(dateStr).split('-');
  try { return new Date(+p[0], +p[1] - 1, +p[2], 12, 0, 0).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }); } catch (_) { return ''; }
}
async function _bookingRow(bookingId) {
  const { data, error } = await supabase.from('bookings').select('*').eq('id', bookingId).maybeSingle();
  if (error) throw error;
  return data;
}
async function _draftToAddr(customerId, customerName) {
  try {
    if (customerId) {
      const { data } = await supabase.from('customers').select('email').eq('id', customerId).maybeSingle();
      if (data && data.email) return String(data.email).trim();
    }
    if (customerName) {
      const { data } = await supabase.from('customers').select('email').eq('full_name', customerName).limit(1);
      if (data && data[0] && data[0].email) return String(data[0].email).trim();
    }
  } catch (_) {}
  return '';
}
// Phone counterpart to _draftToAddr — powers the SMS fallback when a customer
// has no email on file. Returns '' when no phone is known.
async function _draftToPhone(customerId, customerName) {
  try {
    if (customerId) {
      const { data } = await supabase.from('customers').select('phone').eq('id', customerId).maybeSingle();
      if (data && data.phone) return String(data.phone).trim();
    }
    if (customerName) {
      const { data } = await supabase.from('customers').select('phone').eq('full_name', customerName).limit(1);
      if (data && data[0] && data[0].phone) return String(data[0].phone).trim();
    }
  } catch (_) {}
  return '';
}
async function _hasActiveDraft(bookingId, kind) {
  const { data } = await supabase.from('email_drafts').select('status').eq('booking_id', bookingId).eq('kind', kind);
  return (data || []).some((d) => d.status === 'pending' || d.status === 'sent');
}
// A vehicle description, softened for customer copy.
//
// ⚠️ The dealer feed stores them SHOUTING — "2018 CADILLAC ESCALADE" — because
// that is how the DMS exports. Fine in a work queue, wrong in an email to the
// person who owns it.
//
// 🚨 Only fully-uppercase words of 4+ letters with NO digits are touched, which
// is what protects the model codes: CT6, XT5, A7 and Q50 keep their case, and
// so do BMW, GMC and RAM (three letters). Anything already mixed-case is left
// exactly as typed — a human wrote that and knows better than this function.
//
// Presentation only. The stored description is never rewritten.
function _prettyVehicle(desc) {
  const raw = String(desc || '').trim();
  if (!raw) return raw;
  return raw.split(/\s+/).map((w) => {
    if (!/^[A-Z]{4,}$/.test(w)) return w;
    return w.charAt(0) + w.slice(1).toLowerCase();
  }).join(' ');
}

// The dealership's public name, for copy a CUSTOMER reads.
//
// 🚨 This person booked through the dealership and may never have heard of 3C.
// An email from a detailer they did not choose, about a car they left for
// mechanical work, reads like a mistake — or worse, a scam — unless it says
// plainly who we are and why we have their car. Hence the partner line.
//
// Read from the `dealers` table so the name has ONE home (it is also the
// portal header). Cached per session: a marketing string on a one-row table
// does not change mid-draft, and this runs inside draft generation.
let _dealerNames = null;
async function _dealerLabel(slug) {
  const key = String(slug || '').trim();
  if (!key) return '';
  if (!_dealerNames) {
    try {
      const { data } = await supabase.from('dealers').select('slug,name');
      _dealerNames = {};
      (data || []).forEach((d) => { _dealerNames[d.slug] = String(d.name || '').trim(); });
    } catch (_) { return ''; }
  }
  return _dealerNames[key] || '';
}

// Does the CUSTOMER get told what time their detail is?
//
// 🚨 Not at Albany. Those bookings come through the dealership, where the
// customer is usually there for mechanical work — the car is already on site
// and the detailing slot is our scheduling detail, not theirs. Quoting a time
// invites them to plan around a number that was never really theirs to hold,
// and then to be annoyed when the shop sequences the day differently.
//
// ⚠️ This hides the time from CUSTOMER-FACING copy only. The detailers keep it
// everywhere they work: the schedule, the job card, the checklist and the
// staff-side views are untouched. It is the same booking, told differently to
// two audiences.
//
// Keyed on the BOOKING's site, never the active one — whoever drafts this may
// be toggled to the other shop, exactly as siteAddress() is.
function _tellCustomerTheTime(b) {
  // A DEALERSHIP job never carries a time, whatever shop it sits at. Ryder,
  // 2026-08-24: "any appointment produced by otto should not receive a time...
  // they often have an appointment with the mechanic at the dealership first.
  // we dont need to confuse them with the appointment time of the mechanical
  // work and the appointment time for the detail."
  //
  // Two tests rather than one: OTTO is at Albany today, so the site check
  // already covered it — but a dealer job MOVED to Kingston, or a second
  // dealership opening at another shop, would silently start telling their
  // customers a time again.
  if (String((b && b.dealer) || '').trim()) return false;
  if (String((b && b.site) || KINGSTON) === ALBANY) return false;
  // Kingston too, since 2026-10-14: its customers are told there are no set
  // appointment times. One rule for every customer message — lib/dropoff.js.
  return customerSeesTime(b);
}

function _buildPreContent(b, opts) {
  const firstName = String(b.customer_name || '').trim().split(/\s+/)[0] || 'there';
  const service = b.service || '', vehicleDesc = _prettyVehicle(b.vehicle_desc), vipCode = String(b.vip_code || '').trim();
  const prettyDate = _prettyDate(b.date), prettyTime = _fmtMin(b.start_time);
  // The body already says only "on <date>"; the time was in the subject line.
  // ⚠️ Spelled out for a dealership customer: "3C" alone means nothing to
  // someone who booked through the dealer and is scanning an inbox.
  const brand = (opts && opts.dealerName) ? '3C Detailing' : '3C';
  const subject = 'Your detail at ' + brand + ' — ' + (prettyDate || 'see you soon')
    // No time when the body explains there are no set appointment times
    // (Ryder, 2026-10-14) — a subject reading "at 12:00 PM" would contradict it.
    + (prettyTime && _tellCustomerTheTime(b) ? ' at ' + prettyTime : '');
  const lines = ['Hi ' + firstName + ',', ''];
  // Time-change note (e.g. when a booking request is approved at a different
  // time than requested) leads the email, before the standard welcome copy.
  if (opts && opts.changeNote) lines.push(opts.changeNote, '');
  // A dealership customer is told who we are and how we relate to the shop
  // they actually dealt with; everyone else gets the plain welcome.
  const partner = String((opts && opts.dealerName) || '').trim();
  // A dealership job is billed to the DEALERSHIP. Their customer is not our
  // payer and must never be asked to secure an appointment they did not book
  // — see the deposit block below.
  const dealerJob = !!String(b.dealer || '').trim();
  const forCar = (vehicleDesc || 'vehicle') + (prettyDate ? ' on ' + prettyDate : '')
    + (service ? ' (' + service + ')' : '') + '.';
  lines.push(partner
    ? 'Thanks for booking through ' + partner + '. Your appointment is with 3C Detailing — their '
      + "official detailing partner — and we're looking forward to taking care of your " + forCar
    : "Thanks for booking with 3C Detailing. We're looking forward to taking care of your " + forCar);
  // The drop-off window, above the deposit block. The appointment time still
  // rides in the subject line; this is the line that tells them what to do with
  // it. Copy + audience rule both live in lib/dropoff.js — see there for why a
  // dealership customer never sees it.
  if (dropoffWindowApplies(b)) lines.push('', DROPOFF_EMAIL_LINE);
  // 🚨 Never on a dealership job. 3C invoices OTTO at the partner rate; the
  // customer settles with the dealership, usually alongside the mechanical
  // work they actually came in for. A deposit link here would ask them to pay
  // a company they have no account with, for a car already sitting at their
  // dealer's shop — which reads as a scam and is also simply wrong.
  // ⚠️ TWO different reasons the deposit block can be absent, and they are not
  // the same thing: `dealerJob` is automatic and not a choice (their customer
  // is not our payer), while `opts.noDeposit` is the operator ticking it off
  // this one draft. Keeping them separate is what lets a Regenerate honour the
  // tick without hard-coding an exception for dealers into the toggle.
  // 🚨 Colonie replaces the deposit ask ENTIRELY rather than adding to it.
  // Asking for a deposit and then saying the whole balance arrives
  // electronically before pick-up is two payment stories in one email — and
  // Albany takes no cash, which the customer has to know before they drive
  // over with notes in their hand. See lib/albanyPayment.js for why it
  // carries no link: at booking time there is no invoice, so a balance link
  // resolves to nothing and the pay page refuses to render a button.
  const albanyPay = albanyPaymentApplies(b);
  if (albanyPay) {
    lines.push(...albanyPaymentLines());
  } else if (!dealerJob && !(opts && opts.noDeposit) && String(b.deposit_status || '') !== 'paid') {
    const displayAmt = Number(b.deposit_amount) > 0 ? Number(b.deposit_amount) : 100;
    const depositUrl = _pageUrl('3c-deposit.html') + '?bookingId=' + encodeURIComponent(b.id);
    lines.push(...depositLines(displayAmt, depositUrl));
  }
  lines.push('', 'You can follow your detail in real time — service status, photos, and live progress — through our customer portal:', '',
    _pageUrl('3c-vip.html'), '', 'Your access code: ' + (vipCode || '____'), '',
    'Call or text me at 845-901-5573 if you have any questions before your appointment.', '', '— 3C Detailing');
  // Under the sign-off rather than in the greeting: it reassures on a second
  // read without making the opening about the dealership instead of their car.
  if (partner) lines.push('Official detailing partner of ' + partner);
  // Where to drive to, under the sign-off. Taken from the BOOKING's shop, not
  // the active location — the person drafting may be toggled to the other one,
  // and this is the line the customer navigates by. A shop with no address on
  // file contributes nothing rather than borrowing the other one's.
  const addr = siteAddress(b.site);
  if (addr) lines.push(addr);
  const body = lines.join('\n');
  // Regenerate keeps a draft's biohazard-waiver opt-in, so rebuild the note here
  // through the same helper the Outbox toggle uses — identical text is what lets
  // the toggle cleanly strip it again later.
  return { subject, body: opts && opts.biohazard ? withBiohazardNote(body) : body };
}
function _buildPostContent(b) {
  const firstName = String(b.customer_name || '').trim().split(/\s+/)[0] || 'there';
  const vehicleDesc = _prettyVehicle(b.vehicle_desc) || 'your vehicle', prettyDate = _prettyDate(b.date);
  const lines = [
    'Hi ' + firstName + ',', '',
    'Thank you for trusting 3C Detailing with your ' + vehicleDesc + (prettyDate ? ' on ' + prettyDate : '') + '. It was a pleasure taking care of it.', '',
    'Leave us a Google review and get 10% off your next service — just show it at your next visit!', '',
    'Questions or ready to book again? Call or text 845-901-5573.', '', '— 3C Detailing',
  ];
  return { subject: 'Thank you from 3C Detailing', body: lines.join('\n') };
}
// amountOverride (optional): a manually-typed balance that wins over the
// invoice-derived figure. Used by the "Request Balance" flow on the booking
// card, where the owner types exactly what the customer should pay.
function _buildBalanceContent(b, amountOverride) {
  const invoiceTotal = Number(b.invoice_total) || 0;
  const depositPaid = String(b.deposit_status || '') === 'paid' ? (Number(b.deposit_amount) || 0) : 0;
  const manual = (amountOverride != null && Number(amountOverride) > 0) ? Number(amountOverride) : 0;
  const amountDue = manual > 0 ? manual : (invoiceTotal - depositPaid);
  if (!(amountDue > 0)) throw new Error('No balance due — enter an amount, or wait for the invoice PDF to import.');
  if (String(b.balance_status || '') === 'paid') throw new Error('Balance already paid');
  const firstName = String(b.customer_name || '').trim().split(/\s+/)[0] || 'there';
  const vehicle = _prettyVehicle(b.vehicle_desc) || 'your vehicle', svc = b.service || 'service', prettyDate = _prettyDate(b.date);
  const balanceUrl = _pageUrl('3c-balance.html') + '?bookingId=' + encodeURIComponent(b.id);
  const lines = [
    'Hi ' + firstName + ',', '',
    'Thanks again for choosing 3C Detailing' + (prettyDate ? ' on ' + prettyDate : '') + '. Your balance for ' + vehicle + ' (' + svc + ') is $' + amountDue.toFixed(2) + '.',
  ];
  // Only mention the applied deposit when we fell back to invoice math —
  // a typed amount is already the final figure the owner wants charged.
  if (manual <= 0 && depositPaid > 0) lines.push('', "We've already applied your $" + depositPaid.toFixed(2) + ' deposit to the invoice.');
  lines.push('', 'Pay securely here:', balanceUrl, '', 'Questions? Reply to this email or call/text 845-901-5573.', '', '— 3C Detailing');
  return { subject: '3C Detailing — balance for ' + vehicle, body: lines.join('\n') };
}
// Ensure the booking has a VIP portal code before a pre-draft is built — older
// bookings (and any creation path that skipped it) may have none, which made the
// pre-draft show "Your access code: ____". Mint + persist one so the code is
// real on both the email and the /3c-vip.html portal. Mutates b.vip_code.
async function _ensureVipCode(b) {
  let code = String((b && b.vip_code) || '').trim();
  if (code) return code;
  code = _genVipCode();
  try { await supabase.from('bookings').update({ vip_code: code }).eq('id', b.id); b.vip_code = code; } catch (_) { /* draft still builds */ }
  return code;
}
async function _insertDraft(b, kind, content, attachmentId, biohazardWaiver, depositRemoved) {
  const to = await _draftToAddr(b.customer_id, b.customer_name);
  const { error } = await supabase.from('email_drafts').insert({
    id: _draftId(), booking_id: b.id, customer_id: b.customer_id || null, to_addr: to,
    subject: content.subject, body: content.body, status: 'pending', drafted_at: new Date().toISOString(),
    attachment_id: attachmentId || null, kind,
    // Whether api/send-email.js attaches the waiver PDF. The matching note is
    // already in `content.body` — the two are set together or the email
    // promises an attachment that never arrives.
    biohazard_waiver: !!biohazardWaiver,
    // Pre drafts only; every other kind omits the arg and gets the column
    // default, which is 'the deposit request stays'.
    deposit_removed: !!depositRemoved,
    // A draft belongs to its BOOKING's shop, not to whichever location the
    // drafter happens to be toggled to. Every kind (pre / post / reschedule /
    // balance) inserts through here, so this one line keeps the Outbox's K/A
    // tags honest — and keeps a Kingston-toggled draft-for-an-Albany-job from
    // filing itself under the wrong shop.
    site: b.site || undefined,
  });
  return error;
}
// Find the invoice PDF's Drive file id for a booking — by booking link first,
// then by customer name. Used to attach the invoice to post (thank-you) drafts.
async function _invoiceFileIdForBooking(b) {
  try {
    const byB = await supabase.from('invoices').select('file_id').eq('booking_id', b.id).not('file_id', 'is', null).limit(1);
    if (byB.data && byB.data[0] && byB.data[0].file_id) return byB.data[0].file_id;
    if (b.customer_name) {
      const byN = await supabase.from('invoices').select('file_id,date').ilike('customer', String(b.customer_name)).not('file_id', 'is', null).order('date', { ascending: false }).limit(1);
      if (byN.data && byN.data[0] && byN.data[0].file_id) return byN.data[0].file_id;
    }
  } catch (_) {}
  return '';
}

export async function generatePreDraft(bookingId, opts) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    const b = await _bookingRow(bookingId); if (!b) return failResp('Booking not found');
    if (await _hasActiveDraft(bookingId, 'pre')) return okResp({ created: false, error: 'A pre draft already exists for this booking' });
    await _ensureVipCode(b);
    // ☣ A booking marked biohazard brings the waiver with it. The point of that
    // waiver is that it arrives SIGNED at the appointment, so it has to ride the
    // welcome email — the one sent when the job is booked — not be remembered
    // later. Two things move together, same as the Outbox toggle: the note in
    // the body and the column that makes api/send-email.js attach the PDF.
    const bio = !!(b.biohazard || (opts && opts.biohazard));
    // The operator ticking the deposit request off this one email. On a NEW
    // draft the caller's opts are the only source — there is no prior row to
    // read deposit_removed from, which is what regeneratePreDraft does instead.
    // ⚠️ Distinct from the DEALER suppression: _buildPreContent drops the ask
    // for a dealer job on its own, so this stays false there and the column
    // keeps meaning "a person removed it".
    const noDep = !!(opts && opts.noDeposit);
    const dealerName = await _dealerLabel(b.dealer);
    const err = await _insertDraft(b, 'pre', _buildPreContent(b, { ...(opts || {}), biohazard: bio, noDeposit: noDep, dealerName }), null, bio, noDep);
    return err ? failResp(err) : okResp({ created: true });
  } catch (e) { return failResp(e); }
}
// ---------------------------------------------------------------------------
// Appointment SMS reminders — armed automatically at booking creation and
// re-armed on reschedule. Two texts per appointment: 2 days before and 1 day
// before, each sent ~9am ET by the daily cron (see api/invoicer.js
// dispatchReminder + the poll-leads reminder pass). Rows are tagged
// created_by='auto-appt-reminder' so re-arm/cancel only touch ours, and
// meta.sms_full_body tells the dispatcher to send `notes` verbatim (no generic
// "3C reminder:" prefix) for a clean customer-facing message.
// ---------------------------------------------------------------------------
// Resolve the customer's phone (digits only) for a booking row — prefers the
// linked customer registry, falls back to any phone on the booking itself.
async function _bookingPhoneDigits(b) {
  let phone = '';
  if (b && b.customer_id) {
    try { const { data: c } = await supabase.from('customers').select('phone').eq('id', b.customer_id).maybeSingle(); phone = (c && c.phone) || ''; } catch (_) {}
  }
  if (!phone) phone = (b && b.phone) || '';
  return String(phone).replace(/\D/g, '');
}
function _apptReminderDueIso(dateKey, offsetDays) {
  const p = String(dateKey || '').slice(0, 10).split('-').map(Number);
  if (p.length !== 3 || !p[0]) return null;
  // Appointment calendar day at 12:00 UTC, minus the offset. 12:00 UTC sits
  // after the 05:01 UTC ("1am ET") cron pass — so reminders never fire
  // overnight — and before the 13:00 UTC ("9am ET") pass, which sends them.
  const dt = new Date(Date.UTC(p[0], p[1] - 1, p[2], 12, 0, 0));
  dt.setUTCDate(dt.getUTCDate() - offsetDays);
  return dt.toISOString();
}
async function clearAppointmentReminders(bookingId) {
  try {
    await supabase.from('reminders').delete()
      .eq('booking_id', bookingId).eq('created_by', 'auto-appt-reminder').eq('status', 'pending');
  } catch (_) { /* best-effort */ }
}
export async function armAppointmentReminders(bookingId) {
  try {
    if (!bookingId) return { armed: 0, phone: false };
    const b = await _bookingRow(bookingId);
    if (!b) return { armed: 0, phone: false };
    // Clear our prior pending rows first so a reschedule re-dates cleanly and we
    // never double-book reminders for the same appointment.
    await clearAppointmentReminders(bookingId);
    if (['cancelled', 'delivered', 'complete'].includes(String(b.status || ''))) return { armed: 0, phone: false };
    const phone = await _bookingPhoneDigits(b);
    if (!phone) return { armed: 0, phone: false }; // no number on file → nothing to schedule
    const first = String(b.customer_name || '').trim().split(/\s+/)[0] || 'there';
    const svc = b.service || 'detail';
    const veh = _prettyVehicle(b.vehicle_desc);
    const forVeh = veh ? ` for your ${veh}` : '';
    const t = _fmtMin(b.start_time);
    // ⚠️ Same rule as the drafts. These are not Outbox drafts, but they are the
    // same fact reaching the same customer — an Albany reminder that texted the
    // time would undo what the emails just stopped saying.
    const when = _prettyDate(b.date) + (t && _tellCustomerTheTime(b) ? ' at ' + t : '');
    // The drop-off window rides on both reminders — this is the message the
    // customer actually has in hand the morning of, so it is the one that has
    // to say when to arrive. It carries the shop number itself, so the generic
    // "call 845-901-5573 with any questions" tail comes OFF when it goes on:
    // the number twice in one text is two wasted segments, not two invitations.
    const drop = dropoffWindowApplies(b) ? ' ' + DROPOFF_SMS_LINE : '';
    const tail2 = drop || ' Reply or call 845-901-5573 with any questions.';
    const tail1 = drop || ' Questions? Call or text 845-901-5573.';
    const bodyFor = (off) => off === 2
      ? `Hi ${first}, reminder from 3C Detailing — your ${svc} appointment${forVeh} is in 2 days, ${when}.${tail2}`
      : `Hi ${first}, reminder from 3C Detailing — your ${svc} appointment${forVeh} is tomorrow, ${when}. See you then!${tail1}`;
    const nowMs = Date.now();
    const nowIso = new Date().toISOString();
    const rows = [];
    for (const off of [2, 1]) {
      const iso = _apptReminderDueIso(b.date, off);
      if (!iso || new Date(iso).getTime() <= nowMs) continue; // skip a send whose morning already passed
      const r = {
        id: 'R-' + genIdSuffix(), type: 'appointment', title: `Appointment reminder (${off}-day)`,
        notes: bodyFor(off), due_at: iso, channel: 'sms', status: 'pending', phone,
        booking_id: bookingId, customer_id: b.customer_id || null, customer_name: b.customer_name || null,
        meta: { sms_full_body: true, kind: 'appt_reminder', offset_days: off },
        created_by: 'auto-appt-reminder', created_at: nowIso, updated_at: nowIso,
      };
      // The reminder belongs to its booking's shop — armed at booking time,
      // fired days later by a cron that has no idea where it came from.
      // Attached conditionally: this is a BULK insert, where `site: undefined`
      // writes an explicit NULL instead of falling back to the column default.
      // See the checklist_tasks insert in createBooking for the full mechanism.
      if (b.site) r.site = b.site;
      rows.push(r);
    }
    if (rows.length) { try { await supabase.from('reminders').insert(rows); } catch (_) { return { armed: 0, phone: true }; } }
    return { armed: rows.length, phone: true };
  } catch (_) { return { armed: 0, phone: false }; /* arming is best-effort — never block the booking flow */ }
}

// ---------------------------------------------------------------------------
// Completion "ready for pickup" text. When a job is closed, the SchedCard /
// BayJobItem popup either sends immediately ("Send now" → sendSms) or schedules
// it 10/20/30 min out here as a reminders row (channel 'sms', verbatim body via
// meta.sms_full_body). Delayed rows are tagged created_by='ready-text' so an
// open dashboard's 60s poller (runReadyTexts) fires them on time — with the
// twice-daily reminder cron as an eventual backstop. The automated-message +
// STOP footer is appended server-side by dispatchReminder, so `body` here is the
// bare customer-facing message.
// ---------------------------------------------------------------------------
// `dealer` says this job belongs to a dealership, which changes who the text
// is for: dispatchReminder (api/invoicer.js) resolves the dealership's number
// when it fires, so the customer may legitimately have no number here at all.
// The row's `phone` is provisional on a dealer job — the dispatcher has the
// last word, and it is the only place that word is spoken.
export async function scheduleReadyText({ bookingId, phone, customerName, body, minutes, dealer }) {
  try {
    const digits = String(phone || '').replace(/\D/g, '');
    if (!digits && !dealer) return failResp('No phone number on file for this customer.');
    const core = String(body || '').trim();
    if (!core) return failResp('Message is empty.');
    const mins = Math.max(1, Number(minutes) || 0);
    const nowIso = new Date().toISOString();
    const dueIso = new Date(Date.now() + mins * 60000).toISOString();
    const row = {
      id: 'R-' + genIdSuffix(), type: 'appointment', title: 'Ready-for-pickup text',
      notes: core, due_at: dueIso, channel: 'sms', status: 'pending', phone: digits || null,
      booking_id: bookingId || null, customer_name: customerName || null,
      meta: { sms_full_body: true, kind: 'ready_text' },
      created_by: 'ready-text', created_at: nowIso, updated_at: nowIso,
    };
    const { error } = await supabase.from('reminders').insert(row);
    return error ? failResp(error) : okResp({ id: row.id, dueAt: dueIso });
  } catch (e) { return failResp(e); }
}
// Fire any due ready-for-pickup texts now (server-side send). Called by the
// open-dashboard poller; safe to call frequently — the server claims each row
// atomically so concurrent devices never double-send.
export async function runReadyTexts() {
  try {
    const { data: sess } = await supabase.auth.getSession();
    const tok = sess && sess.session && sess.session.access_token;
    const res = await fetch('/api/invoicer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
      body: JSON.stringify({ action: 'run-ready-texts' }),
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp(j) : failResp((j && j.error) || 'Run failed');
  } catch (e) { return failResp(e); }
}

// 3C EBS — Emergency Broadcast System. Text one message to the staff members
// named in `personIds` (pool_people ids). Principal-only; the real gate is
// server-side in api/invoicer.js, since a client-side check on something that
// texts the whole company is a suggestion rather than a control.
//
// Returns per-person results so the panel can say exactly who got it and who
// did not — a broadcast that half-worked is worse than one that failed loudly,
// because nobody finds out which half.
export async function ebsBroadcast(message, personIds) {
  try {
    const msg = String(message || '').trim();
    if (!msg) return failResp('Message is required');
    const ids = (Array.isArray(personIds) ? personIds : []).filter(Boolean).map(String);
    if (!ids.length) return failResp('Pick at least one recipient');
    const { data: sess } = await supabase.auth.getSession();
    const tok = sess && sess.session && sess.session.access_token;
    const res = await fetch('/api/invoicer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
      body: JSON.stringify({ action: 'ebs-broadcast', message: msg, personIds: ids }),
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? okResp(j) : failResp((j && j.error) || 'Broadcast failed');
  } catch (e) { return failResp(e); }
}

// One-time backfill: arm the 2-day/1-day SMS reminders for appointments that
// were booked BEFORE the auto-arm feature shipped. Idempotent (armAppointment-
// Reminders clears + re-inserts, future-only), so re-running is harmless. Owner/
// principal-triggered from the Reminders tab. `fromDate` (YYYY-MM-DD) is the
// earliest appointment day to cover (defaults to today); only non-terminal
// bookings on/after it are touched, and past reminder dates no-op regardless.
export async function backfillAppointmentReminders(fromDate) {
  try {
    const now = new Date();
    const todayStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
    const cutoff = (typeof fromDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fromDate)) ? fromDate : todayStr;
    const { data, error } = await supabase.from('bookings').select('id,date,status');
    if (error) return failResp(error);
    const upcoming = (data || []).filter((b) =>
      !['cancelled', 'delivered', 'complete', 'deleted'].includes(String(b.status || '')) &&
      String(b.date || '').slice(0, 10) >= cutoff);
    let appointments = 0, withPhone = 0, scheduled = 0;
    for (const b of upcoming) {
      appointments += 1;
      const r = await armAppointmentReminders(b.id);
      if (r && r.phone) withPhone += 1;
      if (r && r.armed) scheduled += r.armed;
    }
    return okResp({ appointments, withPhone, scheduled, fromDate: cutoff });
  } catch (e) { return failResp(e); }
}


export async function regeneratePreDraft(bookingId) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    const b = await _bookingRow(bookingId); if (!b) return failResp('Booking not found');
    await _ensureVipCode(b);
    // Re-resolve BOTH contact channels live from the customer row so a
    // regenerate picks up an email OR phone that was corrected/added after the
    // draft was first queued. `to` is persisted on the row; `phone` is returned
    // so the Outbox card adopts the fresh number for its paired confirmation
    // text (it's joined at list-load, not stored on the draft).
    const to = await _draftToAddr(b.customer_id, b.customer_name);
    const phone = await _draftToPhone(b.customer_id, b.customer_name);
    // Grab the most recent pre draft regardless of status — so a draft that was
    // already SENT (and went out wrong) can be rebuilt AND reopened for
    // editing/resending, not just a still-pending one.
    const { data: existing } = await supabase.from('email_drafts')
      .select('id,status,biohazard_waiver,deposit_removed').eq('booking_id', bookingId).eq('kind', 'pre')
      .order('drafted_at', { ascending: false }).limit(1);
    // A rebuild must not silently drop the waiver the operator opted into — the
    // flag still says "attach the PDF", so the note has to come back with it.
    //
    // ☣ OR the booking's own mark, which is the stronger statement: ticking
    // biohazard on the appointment after the draft was auto-built is the common
    // order of events, and without this the waiver could never catch up to it.
    // The trade is that a regenerate re-asserts the waiver on a biohazard job
    // the operator had switched off — on a job that needs a signed waiver, that
    // is the safer direction to fail in.
    const biohazard = !!((existing && existing[0] && existing[0].biohazard_waiver) || b.biohazard);
    // 🚨 Unlike the waiver, this is NOT OR'd with anything on the booking — it
    // is purely the operator's decision about this email, so the draft row is
    // the only authority. A rebuild that re-added the deposit request would
    // make the tick feel like it hadn't saved.
    const noDeposit = !!(existing && existing[0] && existing[0].deposit_removed);
    // Regenerate must rebuild the SAME copy, partner line included.
    const c = _buildPreContent(b, { biohazard, noDeposit, dealerName: await _dealerLabel(b.dealer) });
    if (existing && existing[0]) {
      const wasSent = existing[0].status === 'sent';
      const { error } = await supabase.from('email_drafts')
        .update({ subject: c.subject, body: c.body, to_addr: to, status: 'pending', sent_at: null, drafted_at: new Date().toISOString(), biohazard_waiver: biohazard, deposit_removed: noDeposit })
        .eq('id', existing[0].id);
      // Return the rebuilt content so the Outbox card can adopt it in place
      // (edit + resend) without a list-dropping refetch.
      return error ? failResp(error) : okResp({ created: false, regenerated: true, reopened: wasSent, id: existing[0].id, subject: c.subject, body: c.body, to, phone, biohazardWaiver: biohazard, depositRemoved: noDeposit });
    }
    const err = await _insertDraft(b, 'pre', c, null, biohazard, noDeposit);
    return err ? failResp(err) : okResp({ created: true, subject: c.subject, body: c.body, to, phone, biohazardWaiver: biohazard, depositRemoved: noDeposit });
  } catch (e) { return failResp(e); }
}
export async function generatePostDraft(bookingId) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    const b = await _bookingRow(bookingId); if (!b) return failResp('Booking not found');
    if (await _hasActiveDraft(bookingId, 'post')) return okResp({ created: false, error: 'A post draft already exists for this booking' });
    const fid = await _invoiceFileIdForBooking(b);
    const err = await _insertDraft(b, 'post', _buildPostContent(b), fid);
    return err ? failResp(err) : okResp({ created: true });
  } catch (e) { return failResp(e); }
}
export async function generateBalanceDraft(bookingId) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    const b = await _bookingRow(bookingId); if (!b) return failResp('Booking not found');
    if (await _hasActiveDraft(bookingId, 'balance')) return okResp({ created: false, error: 'A balance draft already exists for this booking' });
    let content; try { content = _buildBalanceContent(b); } catch (be) { return failResp(be); }
    const err = await _insertDraft(b, 'balance', content);
    return err ? failResp(err) : okResp({ created: true });
  } catch (e) { return failResp(e); }
}
// Reschedule notice — sent when an already-scheduled booking's date changes.
// Independent of the welcome (pre) draft: confirms the NEW date/time, notes the
// prior date when known, and points back to the VIP portal. Owner reviews +
// sends from Outbox → Reschedule tab.
function _buildRescheduleContent(b, oldDateStr, dealerName) {
  const firstName = String(b.customer_name || '').trim().split(/\s+/)[0] || 'there';
  const service = b.service || '', vehicleDesc = _prettyVehicle(b.vehicle_desc) || 'your vehicle';
  const vipCode = String(b.vip_code || '').trim();
  const prettyNew = _prettyDate(b.date), prettyTime = _fmtMin(b.start_time);
  const newKey = String(b.date || '').slice(0, 10), oldKey = String(oldDateStr || '').slice(0, 10);
  const prettyOld = (oldKey && oldKey !== newKey) ? _prettyDate(oldKey) : '';
  const subject = 'Your 3C Detailing appointment has moved' + (prettyNew ? ' to ' + prettyNew : '');
  const lines = [
    'Hi ' + firstName + ',', '',
    "Quick heads-up — we've updated the date for your " + vehicleDesc + (service ? ' (' + service + ')' : '')
      + ' appointment' + (dealerName ? ' with 3C Detailing, detailing partner to ' + dealerName : '') + '.', '',
    'New date: ' + (prettyNew || 'TBD') + (prettyTime && _tellCustomerTheTime(b) ? ' at ' + prettyTime : ''),
  ];
  if (prettyOld) lines.push('Previously scheduled for ' + prettyOld + '.');
  // Carry the deposit ask over when it's still outstanding — a reschedule must
  // not silently drop a pending deposit request. Reuses the same booking-scoped
  // deposit link as the welcome (pre) draft, so nothing new is minted.
  // ⚠️ Same rule as the welcome draft. This one carries the ask FORWARD, so
  // missing it here would re-introduce the demand on the very next date
  // change — the quieter half of the same bug.
  const depositOutstanding = !String(b.dealer || '').trim()
    && String(b.deposit_status || '') !== 'paid';
  if (depositOutstanding) {
    const displayAmt = Number(b.deposit_amount) > 0 ? Number(b.deposit_amount) : 100;
    const depositUrl = _pageUrl('3c-deposit.html') + '?bookingId=' + encodeURIComponent(b.id);
    lines.push('', 'To lock in your appointment, your $' + displayAmt.toFixed(2) + ' deposit link is still here:', '',
      depositUrl, '', 'Deposits are non-refundable and credited to your final invoice.');
  }
  lines.push('',
    (depositOutstanding ? '' : 'Everything else stays the same. ')
      + "If this new time doesn't work for you, just reply to this email or call/text 845-901-5573 and we'll find a better slot.", '',
    'You can view your appointment details anytime in our customer portal:', '',
    _pageUrl('3c-vip.html'), '', 'Your access code: ' + (vipCode || '____'), '',
    '— 3C Detailing');
  return { subject, body: lines.join('\n') };
}
// Insert OR refresh the pending reschedule draft for a booking. Refreshing
// (rather than appending) keeps repeated date edits from piling up duplicate
// notices before the owner sends. Returns a Supabase error or null/undefined.
async function _upsertRescheduleDraft(b, oldDateStr) {
  await _ensureVipCode(b);
  const content = _buildRescheduleContent(b, oldDateStr, await _dealerLabel(b.dealer));
  const to = await _draftToAddr(b.customer_id, b.customer_name);
  const { data: existing } = await supabase.from('email_drafts')
    .select('id').eq('booking_id', b.id).eq('kind', 'reschedule').eq('status', 'pending').limit(1);
  if (existing && existing[0]) {
    const { error } = await supabase.from('email_drafts')
      .update({ subject: content.subject, body: content.body, to_addr: to, drafted_at: new Date().toISOString() })
      .eq('id', existing[0].id);
    return error;
  }
  return _insertDraft(b, 'reschedule', content);
}
// Manual trigger from the Outbox (Reschedule tab → "Draft for any booking").
// No prior date is known here, so it just confirms the current date.
export async function generateRescheduleDraft(bookingId) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    const b = await _bookingRow(bookingId); if (!b) return failResp('Booking not found');
    const err = await _upsertRescheduleDraft(b, '');
    return err ? failResp(err) : okResp({ created: true });
  } catch (e) { return failResp(e); }
}
// Explicit "appointment moved" email production, driven by the SchedCard edit
// flow after a date/time change (the owner is prompted; this runs on confirm).
//  - Reuses the booking's existing VIP key. _upsertRescheduleDraft → _ensureVipCode
//    only mints a key when the booking has none (older/imported rows); we diff
//    before/after and report newKeyMinted so the UI can surface the rare new key.
//  - _upsertRescheduleDraft overwrites the pending reschedule draft in place, so
//    repeated reschedules never pile up duplicate notices.
//  - Also refreshes any still-pending welcome (pre) draft to the new date so a
//    stale date can never leave the Outbox.
export async function produceRescheduleEmail(bookingId, oldDateStr) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    const b = await _bookingRow(bookingId); if (!b) return failResp('Booking not found');
    const hadKey = !!String(b.vip_code || '').trim();
    const err = await _upsertRescheduleDraft(b, oldDateStr || ''); // mutates b.vip_code if it had to mint one
    if (err) return failResp(err);
    const newKeyMinted = !hadKey && !!String(b.vip_code || '').trim();
    // Keep a still-pending welcome draft in sync with the new date.
    let preRefreshed = false;
    try {
      const { data: pre } = await supabase.from('email_drafts')
        .select('id').eq('booking_id', bookingId).eq('kind', 'pre').eq('status', 'pending').limit(1);
      if (pre && pre[0]) { await regeneratePreDraft(bookingId); preRefreshed = true; }
    } catch (_) {}
    return okResp({ created: true, newKeyMinted, key: String(b.vip_code || ''), preRefreshed });
  } catch (e) { return failResp(e); }
}
// Owner typed a balance amount on the booking card → store it on the booking
// (so the pay page + charge use it verbatim) and drop a pending balance draft
// into the Outbox → Balance tab. Does NOT send — the owner reviews and sends
// from there. Re-running with a new amount updates the stored figure and the
// existing pending draft so they stay in sync.
export async function draftBalanceRequest(bookingId, amount) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    const amt = Math.round((Number(amount) || 0) * 100) / 100;
    if (!(amt > 0)) return failResp('Enter a balance amount greater than $0.');
    const b = await _bookingRow(bookingId); if (!b) return failResp('Booking not found');
    if (String(b.balance_status || '') === 'paid') return failResp('Balance already paid for this booking');
    const { error: upErr } = await supabase.from('bookings').update({
      balance_amount: amt, balance_status: 'requested', balance_requested_at: new Date().toISOString(),
    }).eq('id', bookingId);
    if (upErr) return failResp(upErr);
    b.balance_amount = amt; b.balance_status = 'requested';
    let content; try { content = _buildBalanceContent(b, amt); } catch (be) { return failResp(be); }
    // Update an existing pending balance draft rather than refusing/duplicating.
    const { data: existing } = await supabase.from('email_drafts')
      .select('id').eq('booking_id', bookingId).eq('kind', 'balance').eq('status', 'pending').limit(1);
    if (existing && existing[0]) {
      const to = await _draftToAddr(b.customer_id, b.customer_name);
      const { error } = await supabase.from('email_drafts')
        .update({ subject: content.subject, body: content.body, to_addr: to, drafted_at: new Date().toISOString() })
        .eq('id', existing[0].id);
      return error ? failResp(error) : okResp({ created: false, updated: true, amount: amt });
    }
    const err = await _insertDraft(b, 'balance', content);
    return err ? failResp(err) : okResp({ created: true, amount: amt });
  } catch (e) { return failResp(e); }
}
// Batch "Generate post drafts now" (the Outbox Post tab). Builds a thank-you
// post draft for each recently-finished job (complete/delivered) that doesn't
// already have one. Window = last 30 days of appointment dates: since the
// 2026-05-28 change jobs persist until closed/delivered, so a job is often
// delivered days after its appointment date — a yesterday+today window (the
// old behavior, inherited from the today-only legacy EOD cron) missed those
// and the Post tab came up empty. _hasActiveDraft skips dupes, so a wider
// window can't double-draft or re-draft already-sent jobs. For anything older,
// use the per-booking generate in the manual-draft search panel.
const POST_DRAFT_LOOKBACK_DAYS = 30;
export async function buildEodDrafts() {
  try {
    const n = new Date();
    const ymd = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    const today = ymd(n);
    const since = ymd(new Date(n.getFullYear(), n.getMonth(), n.getDate() - POST_DRAFT_LOOKBACK_DAYS));
    const { data: bks, error } = await supabase.from('bookings')
      .select('*').in('status', ['complete', 'delivered']).gte('date', since).lte('date', today);
    if (error) return failResp(error);
    let created = 0, skipped = 0;
    for (const b of (bks || [])) {
      if (await _hasActiveDraft(b.id, 'post')) { skipped++; continue; }
      const fid = await _invoiceFileIdForBooking(b);
      const err = await _insertDraft(b, 'post', _buildPostContent(b), fid);
      if (err) skipped++; else created++;
    }
    return okResp({ created, skipped });
  } catch (e) { return failResp(e); }
}
// Send an email via the Resend-backed Vercel function. Passes the staff JWT so
// the function authorizes; marks the draft sent server-side when draftId given.
async function _sendEmail({ to, subject, body, html, draftId, unsubToken, marketing, partner }) {
  async function postWith(tok) {
    const res = await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
      body: JSON.stringify({ to, subject, body, html, draftId, unsubToken, marketing, partner }),
    });
    const j = await res.json().catch(() => ({}));
    return { status: res.status, j };
  }
  const { data: sess } = await supabase.auth.getSession();
  const tok = sess && sess.session && sess.session.access_token;
  let { status, j } = await postWith(tok);
  // The same lapsed-token retry sendSms has always had. A blast runs for minutes
  // and sends ALL its email after ALL its texts, so this path is the more likely
  // of the two to meet an expired JWT — it just never had the retry.
  if (status === 401 || (j && /unauthor/i.test(String(j.error || '')))) {
    let fresh = '';
    try { const r = await supabase.auth.refreshSession(); fresh = r && r.data && r.data.session && r.data.session.access_token; } catch (_) {}
    if (fresh && fresh !== tok) ({ status, j } = await postWith(fresh));
  }
  return j && j.success ? okResp({ id: j.id }) : failResp((j && j.error) || 'send failed');
}
function _buildDepositContent(b) {
  const firstName = String(b.customer_name || '').trim().split(/\s+/)[0] || 'there';
  const vehicle = b.vehicle_desc || 'your vehicle';
  const amt = (Number(b.deposit_amount) > 0 ? Number(b.deposit_amount) : 100).toFixed(2);
  const url = _pageUrl('3c-deposit.html') + '?bookingId=' + encodeURIComponent(b.id);
  const lines = [
    'Hi ' + firstName + ',', '',
    'To lock in your appointment for ' + vehicle + ', please send a $' + amt + ' deposit:', '', url, '',
    'Deposits are non-refundable and protect against no-shows; the full amount is credited to your final invoice.', '',
    'Questions? Call or text 845-901-5573.', '', '— 3C Detailing',
  ];
  return { subject: 'Deposit for your 3C Detailing appointment', body: lines.join('\n') };
}
// Hitting Send on an Outbox draft pushes BOTH channels: it emails the customer
// (when an address is on file) AND texts them (when a phone is on file). If
// there's no email at all, the email draft can never go out, so we send the
// text and then void + erase the dead draft instead of leaving it in the Outbox.
export async function sendEmailDraft(draftId) {
  try {
    if (!draftId) return failResp('Missing draftId');
    const { data: d, error } = await supabase.from('email_drafts').select('*').eq('id', draftId).maybeSingle();
    if (error) return failResp(error);
    if (!d) return failResp('Draft not found');
    // Resolve the customer's phone (linked customer first, then the booking's
    // customer) so we can text alongside — or in place of — the email.
    let phone = await _draftToPhone(d.customer_id, null);
    if (!phone && d.booking_id) {
      try { const b = await _bookingRow(d.booking_id); if (b) phone = await _draftToPhone(b.customer_id, b.customer_name); } catch (_) {}
    }
    if (d.to_addr) {
      // Email on file — send it (this marks the draft sent server-side) and, when
      // we also have a phone, fire the text too so the customer gets both.
      // Balance drafts ship an HTML part too, so the pay link is a real button
      // rather than a bare URL the customer has to trust or paste. Built from
      // d.body — the copy AFTER any Outbox edits — so the two parts always
      // state the same amount. Other kinds stay text-only for now; widening it
      // is one more `kind` in this test.
      const html = d.kind === 'balance' ? renderEmailHtml(d.body) : '';
      const er = await _sendEmail({ to: d.to_addr, subject: d.subject, body: d.body, html, draftId });
      if (er && er.success && phone) {
        const sr = await sendSms(phone, composeSms(d.body));
        er.smsVia = sr && sr.success ? 'sent' : 'failed';
      }
      return er;
    }
    // No email on file — this draft can never be emailed. Text the body first when
    // we have a phone. If that text FAILS, keep the draft pending so the owner can
    // retry it from the Outbox ("redeploy") instead of silently losing it — only
    // erase the dead draft once there's nothing left to send (text delivered, or no
    // phone to text in the first place).
    const r = phone ? await sendSms(phone, composeSms(d.body)) : okResp({ via: 'none' });
    if (phone && !(r && r.success)) {
      return failResp((r && r.error) || 'Text failed — draft kept in the Outbox to retry.');
    }
    // 🚨 An upsell's history has to be stamped HERE, before the draft row is
    // deleted below — after that there is nothing left linking the send to the
    // offer. Only on a real send: a failed text returned above, so reaching this
    // line means it went out (or there was no phone, which is not a send).
    if (d.kind === 'upsell' && phone && r && r.success) {
      try {
        await supabase.from('upsell_offers')
          .update({ status: 'sent', sent_at: new Date().toISOString() })
          .eq('draft_id', draftId).eq('status', 'pending');
      } catch (_) { /* the customer got the text; history is best-effort */ }
    }
    try { await supabase.from('email_drafts').delete().eq('id', draftId); } catch (_) {}
    if (r && r.success) { r.via = phone ? 'sms' : 'none'; r.to = phone; }
    if (r) r.erasedDraft = true;
    // Confirm the void in the principal's Notification Center. Resolve a display
    // name best-effort (linked customer, then the booking's customer).
    let cname = '';
    try {
      if (d.customer_id) { const { data: c } = await supabase.from('customers').select('full_name').eq('id', d.customer_id).maybeSingle(); if (c) cname = c.full_name || ''; }
      if (!cname && d.booking_id) { const b = await _bookingRow(d.booking_id); if (b) cname = b.customer_name || ''; }
    } catch (_) {}
    const tail = phone
      ? (r && r.success ? ' Texted the message instead.' : ' Text attempt failed.')
      : ' No phone on file — nothing was sent.';
    _insertNotification({
      type: 'draft_voided',
      message: '🗑 Voided email draft' + (cname ? ' for ' + cname : '') + ' — no email on file.' + tail,
      forRole: 'principal',
      bookingId: d.booking_id || null,
      customerName: cname || null,
    });
    return r;
  } catch (e) { return failResp(e); }
}
export async function sendDepositRequestEmail(bookingId) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    const b = await _bookingRow(bookingId); if (!b) return failResp('Booking not found');
    // 🚨 Same rule as the welcome and reschedule emails: 3C bills the
    // DEALERSHIP, so their customer is never asked to secure an appointment
    // they didn't book, with a company they have no account with. The card
    // no longer offers this on a dealer job — this is the copy of the rule
    // that holds when the card is stale, or when some future surface calls it.
    if (String(b.dealer || '').trim()) {
      return failResp('This is a dealer job — 3C bills the dealership, so their customer is not asked for a deposit.');
    }
    // Colonie takes no deposit at all. The welcome email tells that customer
    // their whole balance arrives electronically before pick-up, so a deposit
    // demand on top is the second, contradictory payment story this change
    // exists to remove. Refused here and not only on the card, for the same
    // reason the dealer rule is: a stale card, or a future surface, still calls
    // this function.
    if (albanyPaymentApplies(b)) {
      return failResp('Colonie does not take deposits — the balance goes out electronically before pick-up instead.');
    }
    const to = await _draftToAddr(b.customer_id, b.customer_name);
    const c = _buildDepositContent(b);
    let r;
    if (to) {
      r = await _sendEmail({ to, subject: c.subject, body: c.body });
    } else {
      // No email — text the deposit link instead of erroring out.
      const phone = await _draftToPhone(b.customer_id, b.customer_name);
      if (!phone) return failResp('No email or phone on file for this customer.');
      r = await sendSms(phone, composeSms(c.body));
      if (r && r.success) r.via = 'sms';
    }
    if (r.success && String(b.deposit_status || '') !== 'paid') {
      try { await supabase.from('bookings').update({ deposit_status: 'requested', deposit_requested_at: new Date().toISOString() }).eq('id', bookingId); } catch (_) {}
    }
    return r;
  } catch (e) { return failResp(e); }
}
export async function sendBalanceRequestEmail(bookingId) {
  try {
    if (!bookingId) return failResp('Missing bookingId');
    const b = await _bookingRow(bookingId); if (!b) return failResp('Booking not found');
    const to = await _draftToAddr(b.customer_id, b.customer_name);
    let c; try { c = _buildBalanceContent(b); } catch (be) { return failResp(be); }
    let r;
    if (to) {
      r = await _sendEmail({ to, subject: c.subject, body: c.body, html: renderEmailHtml(c.body) });
    } else {
      // No email — text the balance-pay link instead of erroring out.
      const phone = await _draftToPhone(b.customer_id, b.customer_name);
      if (!phone) return failResp('No email or phone on file for this customer.');
      r = await sendSms(phone, composeSms(c.body));
      if (r && r.success) r.via = 'sms';
    }
    if (r.success && String(b.balance_status || '') !== 'paid') {
      try { await supabase.from('bookings').update({ balance_status: 'requested', balance_requested_at: new Date().toISOString() }).eq('id', bookingId); } catch (_) {}
    }
    return r;
  } catch (e) { return failResp(e); }
}
export async function updateEmailDraft(draftId, data) {
  try {
    if (!draftId) return failResp('Missing draftId');
    const d = data || {}, patch = {};
    if (d.subject !== undefined) patch.subject = d.subject;
    if (d.body !== undefined) patch.body = d.body;
    if (d.to !== undefined) patch.to_addr = d.to;
    if (d.attachmentDriveId !== undefined) patch.attachment_id = d.attachmentDriveId || null;
    // Care-guide override for post drafts: 'ceramic' | '1051' | 'none'; anything
    // else clears it back to auto (service-derived) — see careGuide.js / send-email.js.
    if (d.careGuide !== undefined) patch.care_guide = ['ceramic', '1051', 'none'].includes(d.careGuide) ? d.careGuide : null;
    // Biohazard-waiver opt-in for pre drafts — see src/lib/waivers.js.
    if (d.biohazardWaiver !== undefined) patch.biohazard_waiver = !!d.biohazardWaiver;
    // Pre drafts: the operator took the deposit request off this one — see
    // src/lib/depositBlock.js. Read back by _buildPreContent so Regenerate honours it.
    if (d.depositRemoved !== undefined) patch.deposit_removed = !!d.depositRemoved;
    const { error } = await supabase.from('email_drafts').update(patch).eq('id', draftId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function deleteEmailDraft(draftId) {
  try {
    if (!draftId) return failResp('Missing draftId');
    // Grab the full row first — needed for a recoverable copy in the trash bin,
    // and so a delete that no-ops can be detected.
    const { data: row } = await supabase.from('email_drafts').select('*').eq('id', draftId).maybeSingle();
    if (!row) { // already gone
      try { const leg = await import('./apiLegacy'); leg.bustClientCache('getEmailDrafts'); } catch (_) {}
      return okResp({ alreadyGone: true });
    }
    // An RLS-denied DELETE (lapsed token) removes 0 rows and returns NO error, so
    // the "X" looked like it worked but the draft came back on refresh. Detect the
    // no-op via .select(), refresh the session, and retry once so it truly erases.
    let del = await supabase.from('email_drafts').delete().eq('id', draftId).select('id');
    if (!del.error && (!del.data || del.data.length === 0)) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      del = await supabase.from('email_drafts').delete().eq('id', draftId).select('id');
    }
    if (del.error) return failResp(del.error);
    if (!del.data || del.data.length === 0) return failResp('Could not delete — your session may have expired. Reopen the app and try again.');
    // Really deleted → drop a recoverable copy in the trash bin (kept 24h). The
    // payload is the full row so restore re-inserts it verbatim.
    const kindWord = { pre: 'Pre', post: 'Post', balance: 'Balance', reschedule: 'Reschedule' }[row.kind] || 'Email';
    let who = '';
    try {
      if (row.customer_id) { const { data: c } = await supabase.from('customers').select('full_name').eq('id', row.customer_id).maybeSingle(); if (c) who = c.full_name || ''; }
      if (!who && row.booking_id) { const b = await _bookingRow(row.booking_id); if (b) who = b.customer_name || ''; }
    } catch (_) {}
    const label = kindWord + ' email' + (who ? ' — ' + who : (row.subject ? ' — ' + row.subject : ''));
    await _toTrash('email_draft', draftId, row, label, null);
    try { const leg = await import('./apiLegacy'); leg.bustClientCache('getEmailDrafts'); } catch (_) {}
    return okResp({});
  } catch (e) { return failResp(e); }
}
// Hard-delete a draft row outright — no trip through the trash bin. Used by the
// Outbox Trash tab's "Delete forever" on legacy `rejected` drafts (the pile from
// before deleteEmailDraft moved things to the recoverable bin). deleteEmailDraft
// is the soft path (→ trash); this is the irreversible one. Same 0-row-no-op /
// session-refresh retry so a lapsed token can't make it silently do nothing.
export async function purgeEmailDraft(draftId) {
  try {
    if (!draftId) return failResp('Missing draftId');
    let del = await supabase.from('email_drafts').delete().eq('id', draftId).select('id');
    if (!del.error && (!del.data || del.data.length === 0)) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      del = await supabase.from('email_drafts').delete().eq('id', draftId).select('id');
    }
    if (del.error) return failResp(del.error);
    try { const leg = await import('./apiLegacy'); leg.bustClientCache('getEmailDrafts'); } catch (_) {}
    return okResp({ purged: (del.data && del.data.length) || 0 });
  } catch (e) { return failResp(e); }
}
// Reopen an already-sent (or rejected) draft for editing + resending — flips it
// back to pending and clears sent_at, so the Outbox card unlocks its fields and
// Send button. Used to fix an email that went out wrong.
export async function reopenEmailDraft(draftId) {
  try {
    if (!draftId) return failResp('Missing draftId');
    const { error } = await supabase.from('email_drafts').update({ status: 'pending', sent_at: null }).eq('id', draftId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
// Inbox → Outbox: turn a VIP-portal note into a reply draft (quotes the note,
// addressed to the customer). kind 'reply' surfaces in the Post tab via mapDraft.
export async function createReplyDraft(bookingId, note, customerName) {
  try {
    if (!bookingId) return failResp('bookingId required');
    const noteText = String(note || '').trim();
    if (!noteText) return failResp('note required');
    const b = await _bookingRow(bookingId);
    if (!b) return failResp('Booking not found');
    const cName = String(b.customer_name || customerName || '');
    const firstName = cName.trim().split(/\s+/)[0] || 'there';
    const to = await _draftToAddr(b.customer_id, cName);
    const quoted = noteText.split('\n').map((l) => '> ' + l).join('\n');
    const subject = 'Re: your detail at 3C' + (b.service ? ' — ' + b.service : '');
    const body = ['Hi ' + firstName + ',', '', 'Thanks for the note —', '', quoted, '', '[Your reply here]', '', '— 3C Detailing'].join('\n');
    const { error } = await supabase.from('email_drafts').insert({
      id: _draftId(), booking_id: bookingId, customer_id: b.customer_id || null, to_addr: to,
      subject, body, status: 'pending', drafted_at: new Date().toISOString(), kind: 'reply',
    });
    return error ? failResp(error) : okResp({ draftId: 'ok' });
  } catch (e) { return failResp(e); }
}
// Stale-customer "we miss you" outreach (10% off). No booking; addressed by
// email/customerId. kind 'comeback' surfaces in the Post tab via mapDraft.
export async function createComebackDraft(opts) {
  try {
    const { customerId, customerName, email, monthsSinceLastVisit } = opts || {};
    const cName = String(customerName || '').trim();
    if (!cName) return failResp('customerName required');
    const firstName = cName.split(/\s+/)[0] || 'there';
    const months = Number(monthsSinceLastVisit) || 0;
    let to = String(email || '').trim();
    if (!to && customerId) to = await _draftToAddr(customerId, cName);
    const body = [
      'Hi ' + firstName + ',', '',
      "It's been " + (months > 0 ? months + ' months' : 'a while') + ' since we last took care of your car — wanted to check in.', '',
      "If you're due for a detail, I'd love to get you back on the schedule. As a thank-you for being a returning customer, here's 10% off your next service — just reply to this email and we'll get you booked.", '',
      "No pressure either way — happy to answer questions if you're thinking about it.", '',
      'Talk soon,', '— 3C Detailing',
    ].join('\n');
    const { error } = await supabase.from('email_drafts').insert({
      id: _draftId(), booking_id: null, customer_id: customerId || null, to_addr: to,
      subject: 'We miss you at 3C — 10% off your next detail', body,
      status: 'pending', drafted_at: new Date().toISOString(), kind: 'comeback',
    });
    return error ? failResp(error) : okResp({ draftId: 'ok' });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// INVOICE SYNC — Drive → Sheet (GAS) → Supabase  ("Sync Invoices" button)
// Fully client-side, no service account: GAS importAllInvoices pulls each PDF
// from the Drive folder into the Revenue sheet (SO# parsed from the filename,
// fileId = the Drive file). We read that sheet via GAS getRevenueSummary —
// which returns each invoice's fileId — and upsert into Supabase using the
// signed-in owner/principal session. The fileId is what powers the Revenue
// tab's "View PDF" link.
// ===========================================================================
// Call an Apps Script sync endpoint through the Supabase-authed Vercel proxy
// (api/invoicer.js ?action=gas-sync). Auth is the current Supabase session's
// JWT — NOT the legacy GAS session token, which the Supabase-only login never
// mints and which expires after 1 h (the "Session expired" bug on Sync). The
// proxy returns the GAS JSON verbatim, so callers see the same shape they got
// from the old direct leg.* calls.
async function gasSync(name) {
  const { data: sess } = await supabase.auth.getSession();
  const tok = sess && sess.session && sess.session.access_token;
  const res = await fetch('/api/invoicer?action=gas-sync&name=' + encodeURIComponent(name), {
    headers: { ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok && j && j.error) throw new Error(j.error);
  return j;
}

export async function syncInvoices(onProgress) {
  // Stage reporter — lets the Sync button surface live progress through the
  // multi-step pipeline (Drive import → sheet read → Supabase upsert →
  // reconcile) instead of an opaque "Syncing…". No-op if no callback passed.
  const report = (msg) => { try { if (typeof onProgress === 'function') onProgress(msg); } catch (_) {} };
  report('Checking for new invoices…');

  // 0) Fast gate (Tier 1). Ask GAS only for the 15 newest Drive FILENAMES +
  //    their S.O.# (folder metadata — no OCR, no sheet read), then compare
  //    against the invoices we already have in Supabase WITH a PDF attached. If
  //    nothing's new, return immediately and skip the entire heavy path below
  //    (two GAS round-trips + cold starts + whole-sheet reads + customer sync).
  //    A file is "new" only if no file_id-bearing row already has its S.O.# —
  //    so a number that exists ONLY as a link-less placeholder (record-on-print
  //    SO-*, deposits) still imports its real PDF and the reconcile cleans up.
  try {
    // The GAS filename peek and the Supabase "what do we already have" read are
    // independent — fire them together so the gate costs one round-trip of
    // latency (max of the two), not two (sum). On any error/invalid shape we
    // simply fall through to the full sync below, so a transient blip here never
    // aborts the click — it just skips the fast-path optimisation.
    const [lr, exRes] = await Promise.all([
      gasSync('listRecentInvoiceFilenames'),
      supabase.from('invoices').select('so_number,file_id'),
    ]);
    if (lr && lr.success && Array.isArray(lr.files) && !exRes.error) {
      const haveWithFile = new Set((exRes.data || []).filter((r) => r.file_id).map((r) => String(r.so_number)));
      const fresh = lr.files.filter((f) => f.soNum && !haveWithFile.has(String(f.soNum)));
      if (fresh.length === 0) {
        return okResp({ read: 0, written: 0, cleaned: 0, imported: 0, skipped: lr.files.length, scanned: lr.files.length });
      }
    }
  } catch (_) { /* pre-check unreachable — fall through to the full sync */ }

  // 1) Import newly-uploaded Drive PDFs into the Revenue sheet now. SURFACE the
  //    result (imported/skipped) instead of swallowing it — "0 imported" is the
  //    signal that a just-uploaded PDF couldn't be parsed (no text layer / wrong
  //    format) or is in the wrong folder, which is otherwise invisible.
  let imported = null, skipped = null, importError = '';
  report('Importing new PDFs from Drive…');
  try {
    const ir = await gasSync('syncInvoices');
    if (ir && typeof ir.imported === 'number') { imported = ir.imported; skipped = ir.skipped; }
    else if (ir && ir.success === false) { importError = ir.error || 'import failed'; }
  } catch (e) { importError = (e && e.message) || 'Drive import call failed'; }

  // 2) Read the Revenue sheet (each row carries the Drive fileId + the SO# the
  //    importer parsed from the filename) and upsert into Supabase.
  let summary;
  report('Reading the Revenue sheet…');
  try { const r = await gasSync('getRevenueSummary'); summary = r && r.summary; }
  catch (e) { return failResp(e); }
  if (!summary || !Array.isArray(summary.invoices)) return failResp('Could not read the Revenue sheet (GAS unreachable?)');

  // Cash-basis dating: a NEW invoice counts in TODAY's revenue (it's being
  // filed/synced because it was paid today), NOT its draft date. But sync
  // re-upserts EVERY invoice each run, so we must only date NEW rows today and
  // PRESERVE the date already stored for invoices Supabase has seen — otherwise
  // every sync would re-stamp all history to today.
  const today = new Date().toISOString().slice(0, 10);
  let existingDate = new Map();
  try {
    const { data: prior } = await supabase.from('invoices').select('id,date');
    existingDate = new Map((prior || []).map((r) => [String(r.id), r.date]));
  } catch (_) { /* if this read fails, fall back to dating everything today */ }
  const dateFor = (inv) => existingDate.has(String(inv.id)) ? existingDate.get(String(inv.id)) : today;

  // Sparse: only upsert the 15 most-recently-added invoices, not all ~320.
  // Historical rows never change, so re-writing them every sync is wasted work.
  // Sort by Revenue.IMPORTED_AT (the timestamp the row was appended to the
  // sheet) — NOT by SO#. SO# correlates with order date, so a brand-new
  // invoice with an old SO# (e.g. backfilled paperwork) would fall outside
  // the by-SO window and never sync. Falls back to the invoice date for
  // very old rows where IMPORTED_AT was never populated.
  const importedTs = (inv) => {
    if (inv.importedAt) { const t = new Date(inv.importedAt).getTime(); if (Number.isFinite(t)) return t; }
    if (inv.date) { const t = new Date(inv.date).getTime(); if (Number.isFinite(t)) return t; }
    return 0;
  };
  const recent = summary.invoices
    .filter((inv) => inv && inv.id)
    .slice()
    .sort((a, b) => importedTs(b) - importedTs(a))
    .slice(0, 15);

  const rows = recent.map((inv) => ({
    id: String(inv.id),
    so_number: inv.soNumber != null && inv.soNumber !== '' ? String(inv.soNumber) : null,
    date: dateFor(inv), customer: inv.customer || null, vehicle: inv.vehicle || null,
    phone: inv.phone != null && inv.phone !== '' ? String(inv.phone) : null, service: inv.service || null,
    subtotal: num(inv.subtotal), tax: num(inv.tax), total: num(inv.total),
    file_name: inv.fileName || null, file_id: inv.fileId || null,
    cc_fee: num(inv.cardFee), booking_id: inv.bookingId || null,
  }));

  report(`Saving ${rows.length} invoice${rows.length === 1 ? '' : 's'} to the database…`);
  let written = 0;
  for (let i = 0; i < rows.length; i += 500) {
    const { error } = await supabase.from('invoices').upsert(rows.slice(i, i + 500), { onConflict: 'id' });
    if (error) return failResp(error);
    written += Math.min(500, rows.length - i);
  }

  // 3) Reconcile invoicer "record-on-print" rows: a row keyed SO-<n> with no
  //    file_id, once its Drive-sourced twin (same so_number, a non-SO id WITH a
  //    file_id) has synced in, is the link-less duplicate — delete it. Only
  //    fires when a linked replacement exists, so nothing is ever lost.
  report('Reconciling records…');
  let cleaned = 0;
  try {
    const { data: all } = await supabase.from('invoices').select('id,so_number,file_id');
    if (Array.isArray(all)) {
      const linkedSo = new Set(all.filter((x) => x.file_id && !String(x.id).startsWith('SO-')).map((x) => String(x.so_number)));
      const orphans = all
        .filter((x) => String(x.id).startsWith('SO-') && !x.file_id && linkedSo.has(String(x.so_number)))
        .map((x) => x.id);
      if (orphans.length) { const { error } = await supabase.from('invoices').delete().in('id', orphans); if (!error) cleaned = orphans.length; }
    }
  } catch (_) { /* cleanup best-effort */ }

  return okResp({ read: rows.length, written, cleaned, imported, skipped, importError: importError || undefined });
}

// Sync Customers — imports customers off the Revenue sheet on the GAS side.
// Routed through the same Supabase-authed proxy as syncInvoices so it no longer
// depends on a live GAS session token. Returns the GAS JSON ({ success, added })
// verbatim; the SyncButton reads `added`.
export async function syncCustomers() {
  try {
    const r = await gasSync('syncCustomers');
    if (r && r.success === false) return failResp(r.error || 'Customer sync failed');
    return okResp({ added: (r && r.added) || 0 });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// DASHBOARD OVERVIEW (compound — bookings + revenue summary)
// ===========================================================================
export async function getDashboardOverview(startISO, endISO) {
  try {
    const [r, rev] = await Promise.all([getBookingsRange(startISO, endISO), getRevenueSummary()]);
    if (r.success === false) return r;
    return okResp({ bookings: r.bookings || [], revenueSummary: (rev && rev.summary) || null });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// GOALS (app_settings key/value; legacy used Script Properties)
// ===========================================================================
// app_settings is a flat key→value store with `key` as its primary key and NO
// site column — it's the one table the Phase 1 sweep couldn't scope, because
// scoping it means widening that PK.
//
// Most of its keys are genuinely brand-wide (SOPS, WEEKLY_QUOTE, and
// PROFILE_BANNERS, which is already keyed by role — `hd` and `detailer` are
// distinct, so Ric's two banners don't collide). But three describe ONE SHOP
// and would otherwise show Kingston's numbers to Albany:
//   WEEKLY_GOAL / ANNUAL_GOAL — revenue targets. HD has these on his overview.
//   POWER_WASHER              — that shop's own equipment.
//
// Namespacing the KEY sidesteps the PK change entirely. Kingston keeps its
// EXACT existing keys, so there is nothing to migrate and no way to break the
// live values; Albany writes its own rows the first time someone sets a goal
// there, and reads the default until then.
const SITE_SCOPED_SETTINGS = ['WEEKLY_GOAL', 'ANNUAL_GOAL', 'POWER_WASHER'];
function _settingKey(key) {
  if (!SITE_SCOPED_SETTINGS.includes(key)) return key;
  const s = getActiveSite();
  return s === KINGSTON ? key : `${key}@${s}`;
}
async function _getSetting(key, dflt) {
  const { data } = await supabase.from('app_settings').select('value').eq('key', _settingKey(key)).maybeSingle();
  return data && data.value != null ? data.value : dflt;
}
async function _setSetting(key, value) {
  return supabase.from('app_settings').upsert({ key: _settingKey(key), value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
}
export async function getWeeklyGoal() {
  try {
    const [goal, quote] = await Promise.all([
      _getSetting('WEEKLY_GOAL', 5000),
      _getSetting('WEEKLY_QUOTE', 'Great work is done by people who are not afraid to be great.'),
    ]);
    return okResp({ goal: Number(goal) || 5000, quote: String(quote) });
  } catch (e) { return failResp(e); }
}
export async function saveWeeklyGoal(goal, quote) {
  try {
    await Promise.all([_setSetting('WEEKLY_GOAL', Number(goal) || 5000), _setSetting('WEEKLY_QUOTE', String(quote || ''))]);
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function getAnnualGoal() {
  try { return okResp({ goal: Number(await _getSetting('ANNUAL_GOAL', 0)) || 0 }); }
  catch (e) { return failResp(e); }
}
export async function saveAnnualGoal(goal) {
  try { await _setSetting('ANNUAL_GOAL', Number(goal) || 0); return okResp({}); }
  catch (e) { return failResp(e); }
}

// ===========================================================================
// EQUIPMENT USAGE — power washer lifespan (app_settings 'POWER_WASHER')
// ===========================================================================
const PW_KEY = 'POWER_WASHER';
const PW_DEFAULT = { baselineHours: 0, baselineAt: null, lifespanHours: 500, minutesPerWash: 45 };
export async function getEquipmentUsage() {
  try {
    const cfg = await _getSetting(PW_KEY, null);
    const merged = { ...PW_DEFAULT, ...(cfg && typeof cfg === 'object' ? cfg : {}) };
    // Per-wash time isn't user-configurable — force the canonical value so a
    // stale persisted minutesPerWash (e.g. the old 30 or 35 — 45 since 2026-10-10) can't override it.
    merged.minutesPerWash = PW_DEFAULT.minutesPerWash;
    return okResp({ powerWasher: merged });
  } catch (e) { return failResp(e); }
}
// Set the current hours used (baseline) and/or total lifespan. Stamps
// baselineAt = now so future qualifying washes accrue from this point.
export async function saveEquipmentUsage(data) {
  try {
    const cur = await _getSetting(PW_KEY, null);
    const merged = { ...PW_DEFAULT, ...(cur && typeof cur === 'object' ? cur : {}) };
    if (data && data.usedHours !== undefined) {
      merged.baselineHours = Math.max(0, Number(data.usedHours) || 0);
      merged.baselineAt = new Date().toISOString();
    }
    if (data && data.lifespanHours !== undefined) merged.lifespanHours = Math.max(1, Number(data.lifespanHours) || 500);
    if (data && data.minutesPerWash !== undefined) merged.minutesPerWash = Math.max(1, Number(data.minutesPerWash) || 45);
    await _setSetting(PW_KEY, merged);
    return okResp({ powerWasher: merged });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// RECURRING EXPENSES (3CAM). The old recurring "memberships" that lived here
// were removed with the legacy membership system — memberships now live in the
// Membership tab (see the MEMBERS section below).
// ===========================================================================
const mapRecurExp = (r) => ({
  id: S(r.id), vendor: S(r.vendor), category: S(r.category), amount: Number(r.amount) || 0,
  cadence: S(r.cadence) || 'monthly', startDate: S(r.start_date).slice(0, 10),
  nextDueDate: S(r.next_due_date).slice(0, 10), lastFiredAt: isoTs(r.last_fired_at),
  notes: S(r.notes), active: r.active === true, createdAt: isoTs(r.created_at), updatedAt: isoTs(r.updated_at),
});
export async function listRecurringExpenses() {
  try {
    const { data, error } = await supabase.from('recurring_expenses').select('*');
    if (error) return failResp(error);
    const schedules = (data || []).map(mapRecurExp).sort((a, b) => (a.active !== b.active ? (a.active ? -1 : 1) : String(a.nextDueDate).localeCompare(String(b.nextDueDate))));
    return okResp({ schedules });
  } catch (e) { return failResp(e); }
}
// --- Recurring-expense writes (migrated from gas/RecurringExpenses.js) ------
const RECUR_CADENCES = ['weekly', 'biweekly', 'monthly', 'quarterly', 'annual'];
function _advanceDateForCadence(ymd, cadence) {
  const p = String(ymd).split('-').map(Number);
  const d = new Date(p[0], p[1] - 1, p[2], 12, 0, 0);
  switch (cadence) {
    case 'weekly': d.setDate(d.getDate() + 7); break;
    case 'biweekly': d.setDate(d.getDate() + 14); break;
    case 'quarterly': d.setMonth(d.getMonth() + 3); break;
    case 'annual': d.setFullYear(d.getFullYear() + 1); break;
    default: d.setMonth(d.getMonth() + 1); break; // monthly
  }
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}
export async function addRecurringExpense(data) {
  try {
    const p = (typeof data === 'string') ? JSON.parse(data) : (data || {});
    if (!p.vendor) return failResp('vendor required');
    if (!p.category) return failResp('category required');
    const amt = Number(p.amount);
    if (!Number.isFinite(amt) || amt <= 0) return failResp('amount must be a positive number');
    const cadence = String(p.cadence || 'monthly');
    if (RECUR_CADENCES.indexOf(cadence) < 0) return failResp('invalid cadence');
    const startDate = String(p.startDate || '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return failResp('startDate must be YYYY-MM-DD');
    const id = 'REC-' + genIdSuffix();
    const nowIso = new Date().toISOString();
    const { error } = await supabase.from('recurring_expenses').insert({
      id, vendor: String(p.vendor).trim(), category: String(p.category).trim(), amount: amt,
      cadence, start_date: startDate, next_due_date: startDate, last_fired_at: null,
      notes: String(p.notes || '').trim(), active: true, created_at: nowIso, updated_at: nowIso,
    });
    return error ? failResp(error) : okResp({ id });
  } catch (e) { return failResp(e); }
}
export async function updateRecurringExpense(data) {
  try {
    const p = (typeof data === 'string') ? JSON.parse(data) : (data || {});
    if (!p.id) return failResp('id required');
    const row = { updated_at: new Date().toISOString() };
    if (typeof p.vendor === 'string') row.vendor = p.vendor.trim();
    if (typeof p.category === 'string') row.category = p.category.trim();
    if (typeof p.notes === 'string') row.notes = p.notes;
    if (p.amount !== undefined && p.amount !== '') { const a = Number(p.amount); if (Number.isFinite(a) && a > 0) row.amount = a; }
    if (p.cadence && RECUR_CADENCES.indexOf(p.cadence) >= 0) row.cadence = p.cadence;
    if (p.nextDueDate && /^\d{4}-\d{2}-\d{2}$/.test(p.nextDueDate)) row.next_due_date = p.nextDueDate;
    if (p.active !== undefined) row.active = !!p.active;
    const { error } = await supabase.from('recurring_expenses').update(row).eq('id', String(p.id));
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function deleteRecurringExpense(id) {
  try {
    if (!id) return failResp('id required');
    const { error } = await supabase.from('recurring_expenses').delete().eq('id', String(id));
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
// Catch-up sweep: for every active schedule due on/before today, post an Expense
// row dated to its nextDueDate and advance the cadence (retroactively, capped at
// 24 iterations). Idempotent per day — once nextDueDate passes today it stops,
// so it's safe to call on 3CAM open (replaces the old GAS daily trigger).
export async function runDueRecurringExpenses() {
  try {
    const { data, error } = await supabase.from('recurring_expenses').select('*').eq('active', true);
    if (error) return failResp(error);
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    let fired = 0;
    for (const rowData of (data || [])) {
      const s = mapRecurExp(rowData);
      let nextDue = s.nextDueDate;
      let lastFired = null;
      let safety = 24;
      while (safety-- > 0 && nextDue && nextDue <= today) {
        const r = await addExpense({
          date: nextDue, vendor: s.vendor, category: s.category, amount: s.amount,
          notes: (s.notes ? s.notes + ' · ' : '') + '[recurring]', source: 'recurring', bankRowId: '',
        });
        if (r && r.success === false) break;
        fired += 1;
        nextDue = _advanceDateForCadence(nextDue, s.cadence);
        lastFired = new Date().toISOString();
      }
      if (lastFired) {
        await supabase.from('recurring_expenses')
          .update({ next_due_date: nextDue, last_fired_at: lastFired, updated_at: new Date().toISOString() })
          .eq('id', s.id);
      }
    }
    return okResp({ fired });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// BOOKING REQUESTS (read; approve stays on GAS — it creates a booking +
// calendar event)
// ===========================================================================
export async function listPendingBookingRequests() {
  try {
    const { data, error } = await supabase.from('booking_requests').select('*').eq('status', 'pending').order('submitted_at', { ascending: false });
    if (error) return failResp(error);
    const requests = (data || []).map((r) => ({
      id: r.id, date: S(r.date).slice(0, 10), time: S(r.time), service: r.service,
      price: Number(r.price) || 0, category: r.category, size: r.size,
      customerName: r.customer_name, phone: r.phone, email: r.email,
      vehicleYear: r.vehicle_year, vehicleMake: r.vehicle_make, vehicleModel: r.vehicle_model,
      biohazard: r.biohazard === true,
      vehicleColor: r.vehicle_color, vehiclePlate: r.vehicle_plate, notes: r.notes,
      submittedAt: isoTs(r.submitted_at), defaultBay: 'wash_bay',
      // The customer's expected drop-off (2026-10-14) — website requests carry
      // it; older ones and OTTO's read as the normal 9–10 window.
      dropoff: S(r.dropoff), dropoffMin: r.dropoff_min == null ? null : Number(r.dropoff_min),
      dealer: S(r.dealer),
      // Requests from both shops queue in the one hub so Ryder approves or
      // denies either without switching location. Approval inherits THIS site
      // for the booking it creates — see approveBookingRequest.
      site: r.site || 'kingston',
    }));
    return okResp({ requests });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// BOOKING REQUEST approve / reject
// approve creates the booking in Supabase (+ checklist via createBooking) and
// marks the request approved. NOTE: the Google Calendar event is NOT created
// here yet — calendar sync moves to a Vercel function with Google OAuth
// (Phase 5). Approved bookings appear in the app immediately; the calendar
// event lands once that integration ships.
// ===========================================================================
export async function approveBookingRequest(requestId, { bay, startTime, date, bypassConflict, service: serviceOverride, dropoff, dropoffMin } = {}) {
  try {
    const { data: r, error } = await supabase.from('booking_requests').select('*').eq('id', requestId).maybeSingle();
    if (error) return failResp(error);
    if (!r) return failResp(new Error('Request not found'));
    if (r.status !== 'pending') return failResp(new Error('Request already ' + r.status));

    // An "Int. or Ext." request is ambiguous — staff resolve it to a concrete
    // Interior/Exterior service at approval time (see PendingRequestsPanel), and
    // that choice arrives as serviceOverride. Falls back to the requested string.
    const service = serviceOverride || r.service || '';
    const dealerRequest = !!String(r.dealer || '').trim();
    // 🚨 No customer is booked to a TIME any more (Ryder, 2026-10-14): a day
    // holds up to BOOKING_DAY_CAP jobs and the request carries an expected
    // drop-off instead. So the hidden start is 9:00 for every website request.
    // OTTO still picks one of its three slots in the portal, so a dealer
    // request keeps its own — normalised, because it arrives as minutes
    // ("540"), which _addMinutesToTimeStr cannot read (end_time came out equal
    // to start_time), and a legacy "3:00 PM" was read as 3 AM.
    const _reqStart = (() => {
      const t = String(r.time || '').trim();
      if (/^\d+$/.test(t)) { const m = Number(t); return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0'); }
      return _to24h(t) || '09:00';
    })();
    const start = startTime || (dealerRequest ? _reqStart : '09:00');
    const dt = String(date || r.date || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dt)) {
      return failResp(new Error('Could not read the request date. Reject this request and ask the customer to resubmit.'));
    }
    const duration = _serviceDuration(service);
    const end = _addMinutesToTimeStr(start, duration);
    const vehicleDesc = [r.vehicle_year, r.vehicle_make, r.vehicle_model, r.vehicle_color].map((x) => String(x || '').trim()).filter(Boolean).join(' ');

    // Upsert the customer so the booking links to a registry row (best-effort).
    let customerId;
    try {
      const up = await supabase.rpc('upsert_customer', { p: { fullName: r.customer_name, phone: r.phone, email: r.email } });
      customerId = up.data && up.data.id;
    } catch (_) { /* non-fatal — booking still carries the name */ }

    // If staff approved at a different time/date than the customer requested,
    // lead the auto pre-draft with a note about the change.
    const reqDate = String(r.date || '').slice(0, 10);
    // Only a DATE move is news to the customer now — there is no time to change.
    const timeChanged = !!(reqDate && dt !== reqDate);
    let preDraftNote = '';

    // 🚨 Never on a dealership request, for two reasons — the second is the
    // one that matters.
    //
    // It leaks the time, which Albany customer copy withholds. But it is also
    // simply UNTRUE: the note says "a change from the time you originally
    // requested", and this customer requested nothing. The dealership picked
    // the slot. Telling someone their request moved when they never made one
    // reads as a mix-up with somebody else's car.
    //
    // Nothing is lost by omitting it: the welcome email states the date
    // plainly, and the party that DID request the slot — the dealership —
    // sees the outcome in their own portal.
    if (timeChanged && !dealerRequest) {
      const pd = _prettyDate(dt);
      preDraftNote = "Quick note — we've scheduled your appointment for " + (pd || dt)
        + ", a change from the day you originally requested. If that doesn't work for you, just reply and we'll find another day.";
    }

    // The daily allotment — the same limit the booking page enforces. Approving
    // past it needs a deliberate "Approve anyway" (bypassConflict); the panel is
    // owner/principal only, so that IS the CEO/COO override. The wording keeps
    // "already booked" because that is what the panel keys its bypass on.
    if (!bypassConflict) {
      try {
        const { data: sameDay } = await supabase.from('bookings')
          .select('id,status').eq('site', r.site || 'kingston').eq('date', dt);
        const live = (sameDay || []).filter((b) => !DEAD_BOOKING_STATUSES.includes(String(b.status || '')));
        if (live.length >= BOOKING_DAY_CAP) {
          return failResp(new Error('That day is already booked to the limit — ' + live.length + ' of ' + BOOKING_DAY_CAP
            + ' appointments. Approve anyway to go over it.'));
        }
      } catch (_) { /* a failed count must not block an approval */ }
    }

    // 🚨 REGISTER THE CAR, don't just describe it. The request arrives with
    // clean year / make / model / colour / size fields and this function used
    // to flatten them into `vehicleDesc` free text and throw the structure
    // away — so an online booking produced a customer with NO vehicle record.
    // The consequences show up far from here: their customer card reads "No
    // vehicles on file", the invoicer has nothing to price a size off, and the
    // walk-around capture dead-ends (which is why link-booking-vehicle had to
    // be built as a one-tap failsafe in the first place).
    //
    // Best-effort and non-fatal: a booking with a described car is still far
    // better than no booking. Reuses an existing match so re-approving, or a
    // returning customer, cannot stack duplicates.
    let vehicleId;
    if (customerId && (r.vehicle_make || r.vehicle_model)) {
      try {
        const yr = r.vehicle_year ? Number(r.vehicle_year) : null;
        const mk = String(r.vehicle_make || '').trim();
        const md = String(r.vehicle_model || '').trim();
        const { data: existing } = await supabase.from('vehicles')
          .select('id,year,make,model').eq('customer_id', customerId);
        const same = (existing || []).find((v) => String(v.make || '').trim().toLowerCase() === mk.toLowerCase()
          && String(v.model || '').trim().toLowerCase() === md.toLowerCase()
          && (yr == null || Number(v.year) === yr));
        if (same) vehicleId = same.id;
        else {
          const add = await addVehicle(customerId, {
            year: yr, make: mk, model: md, color: r.vehicle_color || '',
            // 🚨 canonVehicleSize, not the raw request value. The website
            // labels its tiers for customers ("Hatch / Mid SUV") and those
            // land verbatim on the request; no price map holds that key, so a
            // vehicle stored with it silently prices at the Sedan fallback --
            // the exact defect the 2026-08-15 pricing fix chased down.
            vin: r.vehicle_plate || '', size: canonVehicleSize(r.size || ''),
          });
          if (add && add.id) vehicleId = add.id;
        }
      } catch (_) { /* non-fatal — the booking still carries the description */ }
    }

    const created = await createBooking({
      customerId, customerName: r.customer_name, vehicleDesc, vehicleId, service,
      bay: bay || 'wash_bay', date: dt, startTime: start, endTime: end,
      // Expected drop-off: the approver's choice, else what the customer said.
      // 🚨 Never a Kingston drop-off on a DEALER job. The dealership booked a
      // slot and the car arrives when they drive it over; stamping "9–10 AM"
      // here is how every card read the Kingston window over OTTO's slot.
      // hasOwnSlot() in lib/dropoff.js is the shared rule.
      dropoff: dealerRequest ? undefined : (dropoff || r.dropoff || undefined),
      dropoffMin: dealerRequest ? null : ((dropoff || r.dropoff) === 'later' ? (dropoffMin != null ? dropoffMin : r.dropoff_min) : null),
      duration, vehicleSize: r.size || '', notes: r.notes || '', createdBy: 'request-approval',
      preDraftNote,
      // Inherit the REQUEST's location, not the approver's active one. Requests
      // from both shops land in one hub so Ryder can approve either without
      // switching, which means the toggle position at approval time is
      // unrelated to where the job actually belongs.
      // Inherit the REQUEST's dealer too, for the same reason as site: this
      // job belongs to whoever submitted it, not to whoever approved it.
      dealer: r.dealer || undefined,
      // The price the dealership was quoted AT REQUEST TIME travels with the
      // job. Re-deriving it later from today's partner rate would silently
      // re-price work that was already agreed.
      dealerPrice: r.price == null ? undefined : r.price,
      // ☣ OTTO flagged it at booking: the request price already carries the
      // fee, so record which part it is (the invoice itemizes it). They told
      // US, so it is acknowledged from the start — no notice back to them.
      ...(dealerRequest && r.biohazard ? {
        biohazard: true,
        dealerBiohazardFee: await (async () => { try { const { data } = await supabase.from('dealers').select('biohazard_fee').eq('slug', r.dealer).maybeSingle(); return data && data.biohazard_fee != null ? Number(data.biohazard_fee) : 150; } catch (_) { return 150; } })(),
      } : {}),
      site: r.site || undefined,
    });
    if (!created.success) return created;

    await supabase.from('booking_requests').update({
      status: 'approved', approved_booking_id: created.id, approved_at: new Date().toISOString(),
    }).eq('id', requestId);

    // Tell the customer they're confirmed. Lives HERE rather than in the UI so
    // every approval path sends it — the notification's Approve button and the
    // Pending requests panel alike. The customer asked online and has heard
    // nothing since, and the auto pre-appointment EMAIL is only a draft in the
    // Outbox, so without this they wait on a human. Best-effort: a text failure
    // must never undo an approval that already created the booking. sendSms
    // enforces the opt-out check + STOP footer server-side.
    let textSent = false, textError = '';
    const phone = String(r.phone || '').replace(/\D/g, '');
    if (phone) {
      const first = String(r.customer_name || '').trim().split(/\s+/)[0] || 'there';
      // 🚨 Date only for a dealership job. This text is the FIRST thing the
      // customer gets, and they are usually booked with the dealer's mechanic
      // the same day — two times in two messages is exactly the confusion
      // this rule exists to prevent.
      const when = [_prettyDate(dt), _tellCustomerTheTime(r) ? _hmPretty(start) : null]
        .filter(Boolean).join(' at ');
      // Same rule as the reminders: when the drop-off window goes on (it carries
      // the shop number), the change-it line stops repeating the number.
      const drop = dropoffWindowApplies(r) ? ' ' + DROPOFF_SMS_LINE : '';
      const body = 'Hi ' + first + ', 3C Detailing — you\'re confirmed for ' + (service || 'your detail')
        + (when ? ' on ' + when : '') + '.' + drop
        + (drop ? ' Need to change it? Just reply.' : ' Need to change it? Just reply or call 845-901-5573.')
        + ' See you then!';
      try {
        const sms = await sendSms(r.phone, composeSms(body));
        textSent = !!(sms && sms.success);
        if (!textSent) textError = (sms && sms.error) || 'Text failed';
      } catch (e) { textError = (e && e.message) || 'Text failed'; }
    } else {
      textError = 'No phone on file';
    }
    return okResp({ bookingId: created.id, date: dt, startTime: start, textSent, textError });
  } catch (e) { return failResp(e); }
}
export async function rejectBookingRequest(requestId, reason) {
  try {
    const { data: r } = await supabase.from('booking_requests')
      .select('status,customer_name,phone,date,time,service').eq('id', requestId).maybeSingle();
    if (!r) return failResp(new Error('Request not found'));
    if (r.status !== 'pending') return failResp(new Error('Request already ' + r.status));
    const { error } = await supabase.from('booking_requests').update({ status: 'rejected', rejection_reason: reason || '' }).eq('id', requestId);
    if (error) return failResp(error);
    // Hand the customer's details back so the caller can offer to text them about
    // the decline. Deliberately NOT sent from here (unlike an approval): a "we
    // can't fit you" message usually needs rewording — suggest another day,
    // explain why — so the operator reviews it before it goes out.
    return okResp({
      customer: {
        name: String(r.customer_name || ''), phone: String(r.phone || ''),
        date: String(r.date || '').slice(0, 10), time: String(r.time || ''),
        service: String(r.service || ''),
        prettyDate: _prettyDate(String(r.date || '').slice(0, 10)),
      },
    });
  } catch (e) { return failResp(e); }
}

// Neutral "get this out of my queue" — no customer email, no booking created.
// If the request was already approved (it carries a booking id, e.g. one that
// got re-surfaced into pending), restore it to 'approved' so the record stays
// accurate and it can never re-email the customer or re-add the appointment.
// Otherwise mark it 'dismissed' so it simply drops out of the pending list.
export async function dismissBookingRequest(requestId) {
  try {
    const { data: r } = await supabase.from('booking_requests').select('status, approved_booking_id').eq('id', requestId).maybeSingle();
    if (!r) return failResp(new Error('Request not found'));
    const status = r.approved_booking_id ? 'approved' : 'dismissed';
    const { error } = await supabase.from('booking_requests').update({ status }).eq('id', requestId);
    return error ? failResp(error) : okResp({ status });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// DECLINED REQUESTS — the follow-up hub (Ryder, 2026-10-29)
// "an appointment denial hub where i can reach back out to customers after
// denying appointments. hold their information and request to be put into
// another date." A rejected request keeps everything the customer typed; the
// followup_* columns (20261029120000) carry what we did about it since.
// ===========================================================================
// `statuses` picks the compartment: ['rejected'] = declined by us (default);
// ['withdrawn','dismissed','cancelled'] = pulled back by the customer or the
// dealership, or dismissed from the queue, before it was ever booked.
export async function listDeclinedBookingRequests({ days = 120, statuses = ['rejected'] } = {}) {
  try {
    const since = new Date(Date.now() - Math.max(1, Number(days) || 120) * 86400000).toISOString();
    const want = Array.isArray(statuses) && statuses.length ? statuses : ['rejected'];
    const { data, error } = await supabase.from('booking_requests').select('*')
      .in('status', want).gte('submitted_at', since).order('submitted_at', { ascending: false });
    if (error) return failResp(error);
    const requests = (data || []).map((r) => ({
      id: r.id, date: S(r.date).slice(0, 10), time: S(r.time), service: r.service,
      price: Number(r.price) || 0, category: r.category, size: r.size,
      customerName: r.customer_name, phone: r.phone, email: r.email,
      vehicleYear: r.vehicle_year, vehicleMake: r.vehicle_make, vehicleModel: r.vehicle_model,
      vehicleColor: r.vehicle_color, vehiclePlate: r.vehicle_plate, notes: r.notes,
      submittedAt: isoTs(r.submitted_at), site: r.site || 'kingston', dealer: S(r.dealer),
      status: S(r.status),
      rejectionReason: S(r.rejection_reason),
      withdrawnReason: S(r.withdrawn_reason), withdrawnAt: isoTs(r.withdrawn_at),
      followupStatus: S(r.followup_status) || 'open',
      followupNote: S(r.followup_note), followupAt: isoTs(r.followup_at), followupBy: S(r.followup_by),
      followupCount: Number(r.followup_count) || 0, followupBookingId: S(r.followup_booking_id),
    }));
    return okResp({ requests });
  } catch (e) { return failResp(e); }
}
// Move a declined request along: 'contacted' (counts each reach-out),
// 'rebooked' (optionally with the booking that came of it), 'closed', or back
// to 'open'. The note is what was said or decided, shown under the row.
export async function setBookingRequestFollowup(requestId, { status, note, bookingId, by } = {}) {
  try {
    if (!requestId) return failResp('id required');
    const st = ['open', 'contacted', 'rebooked', 'closed'].includes(String(status)) ? String(status) : 'contacted';
    const { data: cur } = await supabase.from('booking_requests').select('followup_count').eq('id', requestId).maybeSingle();
    const row = {
      followup_status: st,
      followup_at: new Date().toISOString(),
      followup_by: S(by) || null,
      followup_count: (Number(cur && cur.followup_count) || 0) + (st === 'contacted' ? 1 : 0),
    };
    if (note !== undefined) row.followup_note = S(note).trim().slice(0, 500) || null;
    if (bookingId !== undefined) row.followup_booking_id = S(bookingId) || null;
    const { data, error } = await supabase.from('booking_requests').update(row).eq('id', requestId).select('followup_status,followup_note,followup_at,followup_by,followup_count,followup_booking_id');
    if (error) return failResp(error);
    if (!data || !data.length) return failResp('Nothing saved — the request may be at a shop you cannot reach, or your session lapsed. Reload and try again.');
    const f = data[0];
    return okResp({ followup: {
      followupStatus: S(f.followup_status) || 'open', followupNote: S(f.followup_note), followupAt: isoTs(f.followup_at),
      followupBy: S(f.followup_by), followupCount: Number(f.followup_count) || 0, followupBookingId: S(f.followup_booking_id),
    } });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// GIFT CARD writes
// ===========================================================================
export async function createGiftCard(data) {
  try {
    const row = {
      id: data.id || 'GC-' + genIdSuffix(), code: data.code, customer: data.customer,
      service: data.service, value: num(data.value), issued: data.issued ? String(data.issued).slice(0, 10) : null,
      expires: data.expires ? String(data.expires).slice(0, 10) : null, status: data.status || 'open',
      notes: data.notes, phone: data.phone,
      email: data.email || null, recipient_name: data.recipientName || null,
      recipient_email: data.recipientEmail || null, gift_message: data.giftMessage || null,
    };
    const { error } = await supabase.from('gift_cards').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
export async function updateGiftCard(data) {
  try {
    const row = {};
    const set = (k, v) => { if (v !== undefined) row[k] = v; };
    set('code', data.code); set('customer', data.customer); set('service', data.service);
    if (data.value !== undefined) row.value = num(data.value);
    set('status', data.status); set('notes', data.notes); set('phone', data.phone);
    set('email', data.email); set('recipient_name', data.recipientName);
    set('recipient_email', data.recipientEmail); set('gift_message', data.giftMessage);
    if (data.issued !== undefined) row.issued = data.issued ? String(data.issued).slice(0, 10) : null;
    if (data.expires !== undefined) row.expires = data.expires ? String(data.expires).slice(0, 10) : null;
    const { error } = await supabase.from('gift_cards').update(row).eq('id', data.id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function updateGiftCardStatus(id, status) {
  try {
    const patch = { status };
    if (status === 'closed') patch.closed_at = new Date().toISOString();
    const { error } = await supabase.from('gift_cards').update(patch).eq('id', id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// --- Gift card digital delivery -------------------------------------------
// Emails the buyer (and/or gift recipient) a branded HTML copy of the card via
// the existing Resend path. On a gift (recipientEmail set), the card goes to the
// recipient and the buyer gets a receipt copy. Stamps delivered_at.
function _emlEsc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function _emlDate(d) {
  const p = String(d || '').slice(0, 10).split('-').map(Number);
  if (p.length !== 3 || !p[0]) return String(d || '');
  const m = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return m[p[1] - 1] + ' ' + p[2] + ', ' + p[0];
}
function _giftCardText(card, opts) {
  opts = opts || {};
  const amt = '$' + (Number(card.value) || 0).toFixed(2);
  const L = [];
  if (opts.buyerReceipt) {
    L.push('Thank you for your gift card purchase from 3C Detailing.');
    if (card.recipientName || card.recipientEmail) L.push('Sent to: ' + [card.recipientName, card.recipientEmail].filter(Boolean).join(' '));
  } else if (opts.forRecipient) {
    L.push((card.customer || 'Someone') + ' sent you a 3C Detailing gift card!');
  } else {
    L.push("Here's your 3C Detailing gift card. Thank you!");
  }
  L.push('', 'Amount: ' + amt, 'Code: ' + (card.code || ''));
  if (card.service) L.push('Valid toward: ' + card.service);
  if (card.expires) L.push('Expires: ' + _emlDate(card.expires));
  if (card.giftMessage && !opts.buyerReceipt) L.push('', '"' + card.giftMessage + '"');
  L.push('', 'To redeem, present this code at 3C Detailing or mention it when you book.',
    'Questions? Call or text 845-901-5573.', '', '— 3C Detailing');
  return L.join('\n');
}
function _giftCardHtml(card, opts) {
  opts = opts || {};
  const amt = '$' + (Number(card.value) || 0).toFixed(2);
  const code = _emlEsc(card.code || '');
  const buyer = _emlEsc(card.customer || '');
  const recip = _emlEsc(card.recipientName || '');
  const msg = _emlEsc(card.giftMessage || '');
  const service = _emlEsc(card.service || '');
  const expires = card.expires ? _emlEsc(_emlDate(card.expires)) : '';
  const intro = opts.buyerReceipt
    ? ('Thank you for your purchase! Here’s a copy of the gift card you sent'
        + (recip ? ' to <strong>' + recip + '</strong>' : '') + '.')
    : opts.forRecipient
      ? ((buyer ? '<strong>' + buyer + '</strong> sent you' : 'You’ve received') + ' a 3C Detailing gift card!')
      : 'Thank you! Here’s your 3C Detailing gift card.';
  const msgBlock = (msg && !opts.buyerReceipt)
    ? `<tr><td style="padding:0 28px 8px;"><div style="background:#f4f6fb;border-left:3px solid #2563eb;border-radius:6px;padding:12px 14px;color:#1f2937;font-size:14px;font-style:italic;line-height:1.5;">&ldquo;${msg}&rdquo;${buyer ? `<div style="font-style:normal;color:#6b7280;font-size:12px;margin-top:6px;">— ${buyer}</div>` : ''}</div></td></tr>`
    : '';
  const serviceRow = service
    ? `<tr><td style="padding:2px 0;color:#9ca3af;font-size:13px;">Valid toward</td><td style="padding:2px 0;color:#e5e7eb;font-size:13px;text-align:right;">${service}</td></tr>`
    : '';
  const expiresRow = expires
    ? `<tr><td style="padding:2px 0;color:#9ca3af;font-size:13px;">Expires</td><td style="padding:2px 0;color:#e5e7eb;font-size:13px;text-align:right;">${expires}</td></tr>`
    : '';
  return `<!doctype html><html><body style="margin:0;padding:0;background:#eef1f6;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#eef1f6;padding:24px 12px;"><tr><td align="center">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:520px;background:#ffffff;border-radius:14px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.06);">
  <tr><td style="padding:26px 28px 6px;">
    <div style="font-size:13px;letter-spacing:0.14em;text-transform:uppercase;color:#2563eb;font-weight:700;">3C Detailing</div>
    <p style="margin:14px 0 0;color:#374151;font-size:15px;line-height:1.5;">${intro}</p>
  </td></tr>
  <tr><td style="padding:16px 28px 6px;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#0b0b0c;border-radius:12px;">
      <tr><td style="padding:22px 24px;">
        <div style="color:#9ca3af;font-size:12px;letter-spacing:0.12em;text-transform:uppercase;">Gift Card</div>
        <div style="color:#ffffff;font-size:40px;font-weight:800;margin:6px 0 14px;">${amt}</div>
        <div style="color:#9ca3af;font-size:11px;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:4px;">Code</div>
        <div style="color:#ffffff;font-size:22px;font-weight:700;font-family:'JetBrains Mono',Menlo,Consolas,monospace;letter-spacing:0.14em;border:1px dashed #3b3b40;border-radius:8px;padding:10px 14px;display:inline-block;">${code}</div>
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:16px;">${serviceRow}${expiresRow}</table>
      </td></tr>
    </table>
  </td></tr>
  ${msgBlock}
  <tr><td style="padding:14px 28px 26px;">
    <p style="margin:0 0 4px;color:#374151;font-size:13px;line-height:1.55;">To redeem, present this code at 3C Detailing or mention it when you book your appointment.</p>
    <p style="margin:8px 0 0;color:#6b7280;font-size:13px;line-height:1.55;">Questions? Call or text <a href="tel:+18459015573" style="color:#2563eb;text-decoration:none;">845-901-5573</a>.</p>
  </td></tr>
  <tr><td style="padding:14px 28px;background:#f9fafb;border-top:1px solid #eceff3;color:#9ca3af;font-size:11px;line-height:1.5;">3C Detailing · This gift card was issued to you. Keep this email or the code safe.</td></tr>
</table>
</td></tr></table>
</body></html>`;
}
export async function sendGiftCardEmail(card) {
  try {
    const buyerEmail = String((card && card.email) || '').trim();
    const recipEmail = String((card && card.recipientEmail) || '').trim();
    const primaryTo = recipEmail || buyerEmail;
    if (!primaryTo) return failResp('No email address on file to send the gift card to.');
    const forRecipient = !!recipEmail;
    const subject = '🎁 Your 3C Detailing gift card' + (card.code ? ' — ' + card.code : '');
    const r1 = await _sendEmail({
      to: primaryTo, subject,
      body: _giftCardText(card, { forRecipient }),
      html: _giftCardHtml(card, { forRecipient }),
    });
    if (r1 && r1.success === false) return r1;
    // Gift to someone else → also send the buyer a receipt copy.
    let buyerCopy = false;
    if (recipEmail && buyerEmail && buyerEmail.toLowerCase() !== recipEmail.toLowerCase()) {
      await _sendEmail({
        to: buyerEmail,
        subject: 'Your 3C Detailing gift card purchase' + (card.code ? ' — ' + card.code : ''),
        body: _giftCardText(card, { buyerReceipt: true }),
        html: _giftCardHtml(card, { buyerReceipt: true }),
      }).catch(() => {});
      buyerCopy = true;
    }
    if (card.id) { try { await supabase.from('gift_cards').update({ delivered_at: new Date().toISOString() }).eq('id', card.id); } catch (_) {} }
    return okResp({ sentTo: primaryTo, buyerCopy });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// INVOICE writes / linkage
// ===========================================================================
// Invoice writes change the Revenue stream + the Overview tiles. The legacy GAS
// wrappers busted these caches; the Supabase versions that supersede them must
// too, or the next read serves a pre-edit copy from the TTL cache.
async function _bustRevenueCaches() {
  try {
    const leg = await import('./apiLegacy');
    ['getRevenueSummary', 'getDashboardOverview'].forEach((k) => { try { leg.bustClientCache(k); } catch (_) {} });
  } catch (_) {}
}
export async function updateInvoiceRow(data) {
  try {
    const row = {};
    const set = (k, v) => { if (v !== undefined) row[k] = v; };
    set('so_number', data.soNum ?? data.soNumber); set('customer', data.customer);
    set('vehicle', data.vehicle); set('phone', data.phone); set('service', data.service);
    if (data.subtotal !== undefined) row.subtotal = num(data.subtotal);
    if (data.tax !== undefined) row.tax = num(data.tax);
    if (data.total !== undefined) row.total = num(data.total);
    if (data.date !== undefined) row.date = data.date ? String(data.date).slice(0, 10) : null;
    // `.select('id')` so we can tell whether the update ACTUALLY applied. A stale/
    // expired JWT makes auth_role() null, so the invoices_upd RLS policy silently
    // matches 0 rows and returns NO error — which looked like a successful save
    // that never persisted (edited S.O. date reverted on refresh). On a 0-row
    // result we refresh the session once and retry; if it still writes nothing,
    // surface it. Same pattern as updateCustomer.
    let { data: updated, error } = await supabase.from('invoices').update(row).eq('id', data.id).select('id');
    if (!error && (!updated || updated.length === 0)) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      ({ data: updated, error } = await supabase.from('invoices').update(row).eq('id', data.id).select('id'));
    }
    if (error) return failResp(error);
    if (!updated || updated.length === 0) return failResp('Save didn’t go through — your session may have expired. Reopen the app and try again.');
    await _bustRevenueCaches();
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function deleteInvoiceRow(invoiceId) {
  try {
    // Supabase (the state of record) goes FIRST so its RLS check is what decides
    // whether the delete is allowed. The GAS leg below trashes the Drive PDF —
    // irreversible-ish — so it must not run for a delete that didn't land.
    //
    // Same silent-RLS-deny guard as updateInvoiceRow: a DELETE denied by
    // invoices_del removes 0 rows and returns NO error, so the row "deleted"
    // in the UI and came back on refresh. Detect the no-op, refresh the
    // session, retry once, then report a real failure.
    let { data: gone, error } = await supabase.from('invoices').delete().eq('id', invoiceId).select('id');
    if (!error && (!gone || gone.length === 0)) {
      try { await supabase.auth.refreshSession(); } catch (_) {}
      ({ data: gone, error } = await supabase.from('invoices').delete().eq('id', invoiceId).select('id'));
    }
    if (error) return failResp(error);
    if (!gone || gone.length === 0) return failResp('Delete didn’t go through — your session may have expired. Reopen the app and try again.');
    // Now mirror the delete into GAS — it removes the Revenue SHEET row and
    // TRASHES the Drive PDF (by the row's file_id). Two reasons this is required:
    //   1) The user expects the Drive file scrubbed (Apps Script owns Drive).
    //   2) If we only delete the Supabase row, the next Sync Invoices re-reads
    //      the Sheet and the invoice REAPPEARS in revenue.
    // Best-effort: Supabase-only rows (DEPOSIT-/SO- record-on-print) aren't in
    // the Sheet and have no file_id → GAS 'not found', which is fine.
    try { const leg = await import('./apiLegacy'); await leg.deleteInvoiceRow(invoiceId); } catch (_) {}
    await _bustRevenueCaches();
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function linkInvoiceToBooking(invoiceId, bookingId) {
  try {
    const { data: inv } = await supabase.from('invoices').select('total').eq('id', invoiceId).maybeSingle();
    const { error } = await supabase.from('invoices').update({ booking_id: bookingId }).eq('id', invoiceId);
    if (error) return failResp(error);
    if (inv && inv.total != null) await supabase.from('bookings').update({ invoice_total: inv.total }).eq('id', bookingId);
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function unlinkInvoiceFromBooking(invoiceId) {
  try {
    const { data: inv } = await supabase.from('invoices').select('booking_id').eq('id', invoiceId).maybeSingle();
    const { error } = await supabase.from('invoices').update({ booking_id: null }).eq('id', invoiceId);
    if (error) return failResp(error);
    if (inv && inv.booking_id) await supabase.from('bookings').update({ invoice_total: null }).eq('id', inv.booking_id);
    return okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// 3CAM — EXPENSES + VENDORS  (migrated from gas/Accounting.js; owner-only RLS
// already exists on the `expenses` table). Contract matches the GAS endpoints
// exactly so pages/Books.jsx needs no change.
// ===========================================================================
const EXPENSE_CATEGORIES = ['Labor', 'Payroll', 'Supplies', 'Vehicle/Gas', 'Rent', 'Utilities', 'Marketing', 'Insurance', 'Professional Services', 'Equipment', 'Bank Fees', 'Other'];
const mapExpense = (r) => ({
  id: S(r.id), date: dateOut(r.date), vendor: S(r.vendor), category: S(r.category),
  amount: Number(r.amount) || 0, notes: S(r.notes), source: S(r.source) || 'manual',
  bankRowId: S(r.bank_row_id), createdAt: isoTs(r.created_at), updatedAt: isoTs(r.updated_at),
});
// listExpenses({ start?, end?, category?, vendor? }) — start/end YYYY-MM-DD inclusive.
export async function listExpenses(filters) {
  try {
    const f = filters ? (typeof filters === 'string' ? JSON.parse(filters) : filters) : {};
    let q = supabase.from('expenses').select('*');
    if (f.start) q = q.gte('date', f.start);
    if (f.end) q = q.lte('date', f.end);
    if (f.category) q = q.eq('category', f.category);
    if (f.vendor) q = q.eq('vendor', f.vendor);
    const { data, error } = await q.order('date', { ascending: false });
    if (error) return failResp(error);
    const expenses = (data || []).map(mapExpense);
    return okResp({ expenses, count: expenses.length });
  } catch (e) { return failResp(e); }
}
// Distinct vendors (+ each vendor's most-recent category) for the autocomplete.
export async function listExpenseVendors() {
  try {
    const { data, error } = await supabase.from('expenses').select('vendor,category');
    if (error) return failResp(error);
    const seen = {};
    (data || []).forEach((r) => { const v = S(r.vendor).trim(); if (v && !seen[v]) seen[v] = S(r.category).trim(); });
    const vendors = Object.keys(seen).sort().map((v) => ({ name: v, defaultCategory: seen[v] }));
    return okResp({ vendors, categories: EXPENSE_CATEGORIES });
  } catch (e) { return failResp(e); }
}
export async function addExpense(data) {
  try {
    const p = (typeof data === 'string') ? JSON.parse(data) : (data || {});
    if (!p.date || !/^\d{4}-\d{2}-\d{2}$/.test(String(p.date))) return failResp('date must be YYYY-MM-DD');
    if (!p.vendor) return failResp('vendor required');
    if (!p.category) return failResp('category required');
    const amount = Number(p.amount);
    if (!Number.isFinite(amount) || amount <= 0) return failResp('amount must be a positive number');
    const id = 'EXP-' + genIdSuffix();
    const nowIso = new Date().toISOString();
    const { error } = await supabase.from('expenses').insert({
      id, date: String(p.date).slice(0, 10), vendor: String(p.vendor).trim(), category: String(p.category).trim(),
      amount, notes: String(p.notes || '').trim(), source: String(p.source || 'manual'),
      bank_row_id: String(p.bankRowId || '') || null, created_at: nowIso, updated_at: nowIso,
    });
    return error ? failResp(error) : okResp({ id });
  } catch (e) { return failResp(e); }
}
export async function updateExpense(data) {
  try {
    const p = (typeof data === 'string') ? JSON.parse(data) : (data || {});
    if (!p.id) return failResp('id required');
    const row = { updated_at: new Date().toISOString() };
    if (typeof p.vendor === 'string') row.vendor = p.vendor.trim();
    if (typeof p.category === 'string') row.category = p.category.trim();
    if (typeof p.notes === 'string') row.notes = p.notes;
    if (p.amount !== undefined && p.amount !== '') {
      const amount = Number(p.amount);
      if (Number.isFinite(amount) && amount > 0) row.amount = amount;
    }
    if (p.date) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(p.date))) return failResp('date must be YYYY-MM-DD');
      row.date = String(p.date).slice(0, 10);
    }
    const { error } = await supabase.from('expenses').update(row).eq('id', String(p.id));
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function deleteExpense(id) {
  try {
    if (!id) return failResp('id required');
    const { error } = await supabase.from('expenses').delete().eq('id', String(id));
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
// Vendor roll-up (QB-style): totals, txn counts, first/last seen, top category.
export async function listVendorsRollup(filters) {
  try {
    const f = filters ? (typeof filters === 'string' ? JSON.parse(filters) : filters) : {};
    let q = supabase.from('expenses').select('vendor,category,amount,date');
    if (f.start) q = q.gte('date', f.start);
    if (f.end) q = q.lte('date', f.end);
    if (f.category) q = q.eq('category', f.category);
    const { data, error } = await q;
    if (error) return failResp(error);
    const map = Object.create(null);
    (data || []).forEach((r) => {
      const key = S(r.vendor).trim(); if (!key) return;
      const kl = key.toLowerCase();
      const d = dateOut(r.date);
      if (!map[kl]) map[kl] = { name: key, total: 0, txnCount: 0, firstSeen: d, lastSeen: d, byCategory: Object.create(null) };
      const v = map[kl];
      const amt = Number(r.amount) || 0;
      v.total += amt; v.txnCount += 1;
      if (d && (!v.firstSeen || d < v.firstSeen)) v.firstSeen = d;
      if (d && (!v.lastSeen || d > v.lastSeen)) v.lastSeen = d;
      const cat = S(r.category) || 'Other';
      v.byCategory[cat] = (v.byCategory[cat] || 0) + amt;
    });
    const vendors = Object.keys(map).map((k) => {
      const e = map[k]; let top = '', topAmt = 0;
      Object.keys(e.byCategory).forEach((c) => { if (e.byCategory[c] > topAmt) { top = c; topAmt = e.byCategory[c]; } });
      return { name: e.name, total: e.total, txnCount: e.txnCount, firstSeen: e.firstSeen, lastSeen: e.lastSeen, primaryCategory: top, byCategory: e.byCategory };
    });
    vendors.sort((a, b) => b.total - a.total);
    return okResp({ vendors });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// 3CAM — MILEAGE  (migrated from gas/Accounting.js; owner-only RLS on `mileage`
// already exists). The deduction estimate uses the IRS standard rate; the GAS
// version allowed per-year overrides via Script Properties but none are set, so
// the constant reproduces current behavior (move to app_settings if ever needed).
// ===========================================================================
const IRS_MILEAGE_RATE_DEFAULT = 0.67;
function _irsMileageRate(_yearStr) { return IRS_MILEAGE_RATE_DEFAULT; }
const mapMileage = (r) => ({
  id: S(r.id), date: dateOut(r.date), miles: Number(r.miles) || 0,
  purpose: S(r.purpose), vehicle: S(r.vehicle), notes: S(r.notes),
  createdAt: isoTs(r.created_at), updatedAt: isoTs(r.updated_at),
});
export async function listMileage(filters) {
  try {
    const f = filters ? (typeof filters === 'string' ? JSON.parse(filters) : filters) : {};
    let q = supabase.from('mileage').select('*');
    if (f.start) q = q.gte('date', f.start);
    if (f.end) q = q.lte('date', f.end);
    const { data, error } = await q.order('date', { ascending: false });
    if (error) return failResp(error);
    const entries = (data || []).map(mapMileage);
    const perYear = {};
    entries.forEach((m) => { const yr = m.date ? m.date.slice(0, 4) : ''; if (yr) perYear[yr] = (perYear[yr] || 0) + m.miles; });
    let totalMiles = 0, deduction = 0;
    Object.keys(perYear).forEach((yr) => { totalMiles += perYear[yr]; deduction += perYear[yr] * _irsMileageRate(yr); });
    return okResp({
      entries,
      summary: { totalMiles, deduction: Math.round(deduction * 100) / 100, rate: _irsMileageRate(String(new Date().getFullYear())), perYear },
    });
  } catch (e) { return failResp(e); }
}
export async function addMileage(data) {
  try {
    const p = (typeof data === 'string') ? JSON.parse(data) : (data || {});
    if (!p.date || !/^\d{4}-\d{2}-\d{2}$/.test(String(p.date))) return failResp('date must be YYYY-MM-DD');
    const miles = Number(p.miles);
    if (!Number.isFinite(miles) || miles <= 0) return failResp('miles must be a positive number');
    const id = 'MI-' + genIdSuffix();
    const nowIso = new Date().toISOString();
    const { error } = await supabase.from('mileage').insert({
      id, date: String(p.date).slice(0, 10), miles, purpose: String(p.purpose || '').trim(),
      vehicle: String(p.vehicle || '').trim(), notes: String(p.notes || '').trim(),
      created_at: nowIso, updated_at: nowIso,
    });
    return error ? failResp(error) : okResp({ id });
  } catch (e) { return failResp(e); }
}
export async function updateMileage(data) {
  try {
    const p = (typeof data === 'string') ? JSON.parse(data) : (data || {});
    if (!p.id) return failResp('id required');
    const row = { updated_at: new Date().toISOString() };
    if (typeof p.purpose === 'string') row.purpose = p.purpose.trim();
    if (typeof p.vehicle === 'string') row.vehicle = p.vehicle.trim();
    if (typeof p.notes === 'string') row.notes = p.notes;
    if (p.miles !== undefined && p.miles !== '') { const m = Number(p.miles); if (Number.isFinite(m) && m > 0) row.miles = m; }
    if (p.date) { if (!/^\d{4}-\d{2}-\d{2}$/.test(String(p.date))) return failResp('date must be YYYY-MM-DD'); row.date = String(p.date).slice(0, 10); }
    const { error } = await supabase.from('mileage').update(row).eq('id', String(p.id));
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function deleteMileage(id) {
  try {
    if (!id) return failResp('id required');
    const { error } = await supabase.from('mileage').delete().eq('id', String(id));
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// 3CAM — EMPLOYEES + TIMECLOCK (migrated from gas/Timeclock.js; owner-only RLS
// on employees + timeclock already exists). saveTimeclock enforces one row per
// (employee, week) via find-or-insert, so the Sheet-era duplicate mess is gone;
// reads still dedupe defensively (richest row per week) in case the backfill
// carried any legacy dupes.
// ===========================================================================
const mapEmployee = (r) => ({ id: S(r.id), name: S(r.name), hourlyRate: Number(r.hourly_rate) || 0, active: r.active !== false, createdAt: isoTs(r.created_at) });
export async function listEmployees() {
  try {
    const { data, error } = await supabase.from('employees').select('*');
    if (error) return failResp(error);
    const employees = (data || []).map(mapEmployee)
      .sort((a, b) => (a.active !== b.active ? (a.active ? -1 : 1) : a.name.localeCompare(b.name)));
    return okResp({ employees });
  } catch (e) { return failResp(e); }
}
export async function addEmployee(data) {
  try {
    const p = (typeof data === 'string') ? JSON.parse(data) : (data || {});
    const name = String(p.name || '').trim();
    if (!name) return failResp('Name required');
    const id = 'EMP-' + genIdSuffix();
    const { error } = await supabase.from('employees').insert({ id, name, hourly_rate: Number(p.hourlyRate) || 0, active: true, created_at: new Date().toISOString() });
    return error ? failResp(error) : okResp({ id });
  } catch (e) { return failResp(e); }
}
export async function updateEmployee(data) {
  try {
    const p = (typeof data === 'string') ? JSON.parse(data) : (data || {});
    if (!p.id) return failResp('id required');
    const row = {};
    if (typeof p.name === 'string' && p.name.trim()) row.name = p.name.trim();
    if (p.hourlyRate !== undefined) row.hourly_rate = Number(p.hourlyRate) || 0;
    if (p.active !== undefined) row.active = !!p.active;
    const { error } = await supabase.from('employees').update(row).eq('id', String(p.id));
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function deleteEmployee(id) {
  try {
    if (!id) return failResp('id required');
    const { error } = await supabase.from('employees').delete().eq('id', String(id));
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
// Richest row per (employee_id, week_of): hours win, then a fuller entries blob,
// then recency — so an empty re-save never masks a real week.
function _tcDedupe(rows, eidFilter) {
  const byKey = {};
  (rows || []).forEach((r) => {
    const eid = S(r.employee_id);
    if (eidFilter && eid !== eidFilter) return;
    const wk = S(r.week_of).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(wk)) return;
    const hrs = Number(r.total_hours) || 0;
    const entries = (r.entries && typeof r.entries === 'object') ? r.entries : {};
    const t = r.updated_at ? new Date(r.updated_at).getTime() : 0;
    const score = (hrs > 0 ? 1e9 : 0) + JSON.stringify(entries).length;
    const key = eid + '|' + wk;
    const ex = byKey[key];
    if (!ex || score > ex._score || (score === ex._score && t >= ex._t)) byKey[key] = { employeeId: eid, weekOf: wk, totalHours: hrs, entries, _t: t, _score: score };
  });
  return Object.keys(byKey).map((k) => byKey[k]);
}
export async function getTimeclock(employeeId, weekOf) {
  try {
    const eid = String(employeeId || '').trim(), wk = String(weekOf || '').trim();
    if (!eid || !wk) return okResp({ entries: {}, totalHours: 0 });
    const { data, error } = await supabase.from('timeclock').select('*').eq('employee_id', eid).eq('week_of', wk);
    if (error) return failResp(error);
    const hit = _tcDedupe(data, eid).find((r) => r.weekOf === wk);
    return okResp(hit ? { entries: hit.entries, totalHours: hit.totalHours } : { entries: {}, totalHours: 0 });
  } catch (e) { return failResp(e); }
}
export async function saveTimeclock(data) {
  try {
    const p = (typeof data === 'string') ? JSON.parse(data) : (data || {});
    const eid = String(p.employeeId || '').trim();
    const wk = String(p.weekOf || '').trim();
    if (!eid) return failResp('employeeId required');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(wk)) return failResp('weekOf must be yyyy-MM-dd (Monday)');
    let entries = p.entries || {};
    if (typeof entries === 'string') { try { entries = JSON.parse(entries); } catch (_) { entries = {}; } }
    const totalHours = Number(p.totalHours) || 0;
    const nowIso = new Date().toISOString();
    const { data: existing } = await supabase.from('timeclock').select('id').eq('employee_id', eid).eq('week_of', wk).limit(1);
    if (existing && existing[0]) {
      const { error } = await supabase.from('timeclock').update({ entries, total_hours: totalHours, updated_at: nowIso }).eq('id', existing[0].id);
      return error ? failResp(error) : okResp({ updated: true });
    }
    const id = 'TC-' + genIdSuffix();
    const { error } = await supabase.from('timeclock').insert({ id, employee_id: eid, week_of: wk, entries, total_hours: totalHours, created_at: nowIso, updated_at: nowIso });
    return error ? failResp(error) : okResp({ id });
  } catch (e) { return failResp(e); }
}
export async function listEmployeeWeeks(employeeId) {
  try {
    const eid = String(employeeId || '').trim();
    if (!eid) return okResp({ weeks: [] });
    const { data, error } = await supabase.from('timeclock').select('*').eq('employee_id', eid);
    if (error) return failResp(error);
    const weeks = _tcDedupe(data, eid).map((r) => ({ weekOf: r.weekOf, totalHours: r.totalHours })).sort((a, b) => b.weekOf.localeCompare(a.weekOf));
    return okResp({ weeks });
  } catch (e) { return failResp(e); }
}
export async function getEmployeeArchive(employeeId) {
  try {
    const eid = String(employeeId || '').trim();
    if (!eid) return okResp({ weeks: [] });
    const { data, error } = await supabase.from('timeclock').select('*').eq('employee_id', eid);
    if (error) return failResp(error);
    const weeks = _tcDedupe(data, eid).map((r) => ({ weekOf: r.weekOf, totalHours: r.totalHours, entries: r.entries })).sort((a, b) => b.weekOf.localeCompare(a.weekOf));
    return okResp({ weeks });
  } catch (e) { return failResp(e); }
}
export async function getLaborSummary(year) {
  try {
    const yr = parseInt(year, 10) || new Date().getFullYear();
    const [emp, tc] = await Promise.all([
      supabase.from('employees').select('*'),
      supabase.from('timeclock').select('*'),
    ]);
    if (emp.error) return failResp(emp.error);
    if (tc.error) return failResp(tc.error);
    const rateById = {}, nameById = {};
    (emp.data || []).forEach((e) => { rateById[e.id] = Number(e.hourly_rate) || 0; nameById[e.id] = S(e.name); });
    const rows = _tcDedupe(tc.data, null).filter((r) => r.weekOf.slice(0, 4) === String(yr));
    const perEmp = {};
    let totalHours = 0, totalCost = 0;
    rows.forEach((r) => {
      const rate = rateById[r.employeeId] || 0;
      const cost = r.totalHours * rate;
      if (!perEmp[r.employeeId]) perEmp[r.employeeId] = { employeeId: r.employeeId, name: nameById[r.employeeId] || '(removed)', rate, hours: 0, cost: 0 };
      perEmp[r.employeeId].hours += r.totalHours;
      perEmp[r.employeeId].cost += cost;
      totalHours += r.totalHours; totalCost += cost;
    });
    const byEmployee = Object.keys(perEmp).map((k) => perEmp[k]).sort((a, b) => b.cost - a.cost);
    return okResp({ year: yr, totalHours, totalCost, byEmployee });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// 3CAM — BANK STATEMENTS + RECONCILIATION (migrated from gas/BankStatements.js;
// owner-only RLS on bank_statements + bank_transactions already exists). Files
// move from a Drive folder to a Supabase Storage bucket ('bank-statements');
// parsing moves to a Claude-Vision action (parse-bank-statement) in
// api/invoicer.js. Contract matches the GAS endpoints so Books.jsx is untouched.
// ===========================================================================
const BANK_SOURCES = ['rondout', 'capital-one-card'];
const mapBankStmt = (r) => ({
  id: S(r.id), fileName: S(r.file_name), driveFileId: S(r.drive_file_id),
  viewUrl: r.drive_file_id && !String(r.drive_file_id).includes('.') ? 'https://drive.google.com/file/d/' + r.drive_file_id + '/view' : '',
  periodStart: dateOut(r.period_start), periodEnd: dateOut(r.period_end),
  openingBalance: Number(r.opening_balance) || 0, closingBalance: Number(r.closing_balance) || 0,
  txnCount: Number(r.txn_count) || 0, status: S(r.status), parseError: S(r.parse_error),
  uploadedAt: isoTs(r.uploaded_at), parsedAt: isoTs(r.parsed_at), notes: S(r.notes),
  bankSource: S(r.bank_source) || 'rondout', expensesCreated: Number(r.expenses_created) || 0,
});
const mapBankTxn = (r) => ({
  id: S(r.id), statementId: S(r.statement_id), date: dateOut(r.date), description: S(r.description),
  amount: Number(r.amount) || 0, type: S(r.type) || 'debit', rawText: S(r.raw_text),
  suggestedCategory: S(r.suggested_cat), expenseId: S(r.expense_id), ignored: r.ignored === true,
  createdAt: isoTs(r.created_at),
});
export async function listBankStatements() {
  try {
    const { data, error } = await supabase.from('bank_statements').select('*');
    if (error) return failResp(error);
    const statements = (data || []).map(mapBankStmt)
      .sort((a, b) => String(b.periodEnd || b.uploadedAt).localeCompare(String(a.periodEnd || a.uploadedAt)));
    return okResp({ statements, count: statements.length });
  } catch (e) { return failResp(e); }
}
export async function listBankTransactions(statementId) {
  try {
    const sid = String(statementId || '');
    if (!sid) return okResp({ transactions: [], count: 0 });
    const { data, error } = await supabase.from('bank_transactions').select('*').eq('statement_id', sid).order('date');
    if (error) return failResp(error);
    const transactions = (data || []).map(mapBankTxn);
    return okResp({ transactions, count: transactions.length });
  } catch (e) { return failResp(e); }
}
// Upload the statement file to Supabase Storage + create the pending-parse row.
// Accepts the raw base64 (data-URL prefix stripped if present) to match the
// existing Books.jsx handler. onProgress is called coarsely (Storage upload
// gives no byte-progress).
export async function uploadBankStatement(fileBase64, fileName, mimeType, notes, bankSource, onProgress) {
  try {
    const mime = String(mimeType || 'application/pdf');
    const extByMime = { 'application/pdf': 'pdf', 'image/png': 'png', 'image/jpeg': 'jpg', 'image/jpg': 'jpg' };
    if (!extByMime[mime]) return failResp('Unsupported file type — PDF, PNG or JPG only.');
    const b64 = String(fileBase64 || '').replace(/^data:[^;]+;base64,/, '');
    if (!b64) return failResp('No file data.');
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    if (bytes.length > 15 * 1024 * 1024) return failResp('File exceeds the 15 MB limit.');
    if (onProgress) { try { onProgress(0.3, 1, 0.3); } catch (_) {} }
    const id = 'BSTMT-' + genIdSuffix();
    const path = id + '.' + extByMime[mime];
    const blob = new Blob([bytes], { type: mime });
    const up = await supabase.storage.from('bank-statements').upload(path, blob, { contentType: mime, upsert: false });
    if (up.error) return failResp('Upload failed: ' + up.error.message);
    if (onProgress) { try { onProgress(1, 1, 1); } catch (_) {} }
    const src = BANK_SOURCES.indexOf(String(bankSource)) >= 0 ? bankSource : 'rondout';
    const { error: insErr } = await supabase.from('bank_statements').insert({
      id, file_name: String(fileName || path), drive_file_id: path, status: 'pending-parse',
      txn_count: 0, notes: String(notes || ''), bank_source: src, uploaded_at: new Date().toISOString(), expenses_created: 0,
    });
    if (insErr) { try { await supabase.storage.from('bank-statements').remove([path]); } catch (_) {} return failResp(insErr); }
    return okResp({ id, driveFileId: path, fileName: String(fileName || path), bankSource: src });
  } catch (e) { return failResp(e); }
}
// Parse runs server-side (Claude Vision) — see api/invoicer.js parse-bank-statement.
export async function parseBankStatement(statementId) {
  try {
    const { data: sess } = await supabase.auth.getSession();
    const tok = sess && sess.session && sess.session.access_token;
    const res = await fetch('/api/invoicer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
      body: JSON.stringify({ action: 'parse-bank-statement', statementId }),
    });
    const j = await res.json().catch(() => ({}));
    return j && j.success ? { success: true, ...j } : failResp((j && j.error) || 'Parse failed');
  } catch (e) { return failResp(e); }
}
export async function convertBankTxnToExpense(txnId, category, vendor, notes) {
  try {
    if (!txnId) return failResp('txnId required');
    if (!category) return failResp('category required');
    const { data: txn, error: te } = await supabase.from('bank_transactions').select('*').eq('id', String(txnId)).maybeSingle();
    if (te) return failResp(te);
    if (!txn) return failResp('Transaction not found');
    if (txn.expense_id) return failResp('This transaction is already an expense.');
    if (txn.type !== 'debit') return failResp('Only money-out (debit) transactions become expenses.');
    const vend = String(vendor || txn.description || '').slice(0, 60).trim();
    const expId = 'EXP-' + genIdSuffix();
    const nowIso = new Date().toISOString();
    const { error: ie } = await supabase.from('expenses').insert({
      id: expId, date: dateOut(txn.date), vendor: vend, category, amount: Math.abs(Number(txn.amount) || 0),
      notes: String(notes || '').trim(), source: 'bank-pdf', bank_row_id: String(txnId), created_at: nowIso, updated_at: nowIso,
    });
    if (ie) return failResp(ie);
    await supabase.from('bank_transactions').update({ expense_id: expId }).eq('id', String(txnId));
    return okResp({ expenseId: expId });
  } catch (e) { return failResp(e); }
}
export async function setBankTxnIgnored(txnId, ignored) {
  try {
    if (!txnId) return failResp('txnId required');
    const { error } = await supabase.from('bank_transactions').update({ ignored: !!ignored }).eq('id', String(txnId));
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function deleteBankStatement(statementId) {
  try {
    const sid = String(statementId || '');
    if (!sid) return failResp('statementId required');
    const { data: txns } = await supabase.from('bank_transactions').select('id').eq('statement_id', sid);
    const txnRemoved = (txns || []).length;
    await supabase.from('bank_transactions').delete().eq('statement_id', sid);
    const { data: st } = await supabase.from('bank_statements').select('drive_file_id').eq('id', sid).maybeSingle();
    if (st && st.drive_file_id) { try { await supabase.storage.from('bank-statements').remove([st.drive_file_id]); } catch (_) {} }
    const { error } = await supabase.from('bank_statements').delete().eq('id', sid);
    return error ? failResp(error) : okResp({ txnRemoved });
  } catch (e) { return failResp(e); }
}
export async function setBankStatementSource(statementId, bankSource) {
  try {
    if (!statementId) return failResp('statementId required');
    if (BANK_SOURCES.indexOf(String(bankSource)) < 0) return failResp('invalid bankSource');
    const { error } = await supabase.from('bank_statements').update({ bank_source: bankSource }).eq('id', String(statementId));
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function getReconciliationSummary() {
  try {
    const { data, error } = await supabase.from('bank_transactions').select('type,expense_id,ignored');
    if (error) return failResp(error);
    let matched = 0, unmatched = 0, ignored = 0, credits = 0, total = 0;
    (data || []).forEach((r) => {
      total += 1;
      if (r.type !== 'debit') { credits += 1; return; }
      if (r.ignored === true) { ignored += 1; return; }
      if (r.expense_id) matched += 1; else unmatched += 1;
    });
    return okResp({ matched, unmatched, ignored, credits, total });
  } catch (e) { return failResp(e); }
}
export async function listUnmatchedBankTxns(opts) {
  try {
    const o = opts || {};
    const sinceDays = Number(o.sinceDays) > 0 ? Number(o.sinceDays) : 60;
    const since = new Date(Date.now() - sinceDays * 86400000).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const { data, error } = await supabase.from('bank_transactions').select('*')
      .eq('type', 'debit').is('expense_id', null).eq('ignored', false).gte('date', since)
      .order('date', { ascending: false });
    if (error) return failResp(error);
    let transactions = (data || []).map(mapBankTxn);
    if (o.amount !== undefined && o.amount !== '' && o.amount !== null) {
      const amt = Number(o.amount);
      if (Number.isFinite(amt)) transactions = transactions.filter((t) => Math.abs(t.amount - amt) <= 1.0);
    }
    return okResp({ transactions });
  } catch (e) { return failResp(e); }
}
export async function linkExpenseToBankTxn(expenseId, txnId) {
  try {
    if (!expenseId || !txnId) return failResp('expenseId and txnId required');
    const eid = String(expenseId), tid = String(txnId);
    const { data: exp } = await supabase.from('expenses').select('bank_row_id').eq('id', eid).maybeSingle();
    const { data: txn } = await supabase.from('bank_transactions').select('expense_id').eq('id', tid).maybeSingle();
    if (!exp) return failResp('Expense not found');
    if (!txn) return failResp('Transaction not found');
    if (exp.bank_row_id && exp.bank_row_id !== tid) return failResp('That expense is already linked to a different transaction.');
    if (txn.expense_id && txn.expense_id !== eid) return failResp('That transaction is already linked to a different expense.');
    await supabase.from('expenses').update({ bank_row_id: tid, updated_at: new Date().toISOString() }).eq('id', eid);
    await supabase.from('bank_transactions').update({ expense_id: eid }).eq('id', tid);
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function unlinkExpenseFromBankTxn(expenseId) {
  try {
    if (!expenseId) return failResp('expenseId required');
    const eid = String(expenseId);
    const { data: exp } = await supabase.from('expenses').select('bank_row_id').eq('id', eid).maybeSingle();
    const tid = exp && exp.bank_row_id ? String(exp.bank_row_id) : '';
    await supabase.from('expenses').update({ bank_row_id: null, updated_at: new Date().toISOString() }).eq('id', eid);
    if (tid) await supabase.from('bank_transactions').update({ expense_id: null }).eq('id', tid);
    return okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// MARKETING (migrated from gas/Marketing.js; owner-only RLS on `marketing`).
// The agent chat routes to /api/ai (assistant:'marketing'), which builds its
// context from the same Supabase table.
// ===========================================================================
const MARKET_TARGETS = {
  region: 'Hudson Valley / Capital Region, NY',
  counties: [
    { name: 'Ulster County', seat: 'Kingston', population: 183000 },
    { name: 'Greene County', seat: 'Catskill', population: 50000 },
    { name: 'Dutchess County', seat: 'Poughkeepsie', population: 295000 },
  ],
  combinedPopulation: 510000, driveRadiusMin: 40, squareMiles: 3450,
};
const mapMarketing = (r) => ({
  id: S(r.id), category: S(r.category), slug: S(r.slug),
  avgCpc: Number(r.avg_cpc) || 0, monthlySearches: Number(r.monthly_searches) || 0,
  keywords: Array.isArray(r.keywords) ? r.keywords : [], competitors: Array.isArray(r.competitors) ? r.competitors : [],
  lowBudget: Number(r.low_budget) || 0, medBudget: Number(r.med_budget) || 0, highBudget: Number(r.high_budget) || 0,
  industryCpc: Number(r.industry_cpc) || 0, industryCtr: Number(r.industry_ctr) || 0.08, industryCvr: Number(r.industry_cvr) || 0.10,
  notes: S(r.notes), createdAt: isoTs(r.created_at), updatedAt: isoTs(r.updated_at),
});
export async function getMarketingData() {
  try {
    const { data, error } = await supabase.from('marketing').select('*');
    if (error) return failResp(error);
    const categories = (data || []).map(mapMarketing).sort((a, b) => a.category.localeCompare(b.category));
    return okResp({ market: MARKET_TARGETS, categories });
  } catch (e) { return failResp(e); }
}
export async function saveMarketingCategory(data) {
  try {
    const p = (typeof data === 'string') ? JSON.parse(data) : (data || {});
    const id = p.id || ('mkt-' + genIdSuffix());
    const slug = p.slug || String(p.category || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const nowIso = new Date().toISOString();
    const row = {
      id, category: String(p.category || ''), slug,
      avg_cpc: Number(p.avgCpc) || 0, monthly_searches: Number(p.monthlySearches) || 0,
      keywords: Array.isArray(p.keywords) ? p.keywords : [], competitors: Array.isArray(p.competitors) ? p.competitors : [],
      low_budget: Number(p.lowBudget) || 0, med_budget: Number(p.medBudget) || 0, high_budget: Number(p.highBudget) || 0,
      industry_cpc: Number(p.industryCpc) || 0, industry_ctr: Number(p.industryCtr) || 0.08, industry_cvr: Number(p.industryCvr) || 0.10,
      notes: String(p.notes || ''), updated_at: nowIso,
    };
    if (!p.id) row.created_at = nowIso; // preserve created_at on update
    const { error } = await supabase.from('marketing').upsert(row, { onConflict: 'id' });
    return error ? failResp(error) : okResp({ id });
  } catch (e) { return failResp(e); }
}
export async function deleteMarketingCategory(id) {
  try {
    if (!id) return failResp('id required');
    const { error } = await supabase.from('marketing').delete().eq('id', String(id));
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}
export async function askMarketingAgent(question, history) { return _askAI('marketing', { question, history }); }

// ===========================================================================
// ENGINEER / CEO TOOLS (migrated from gas/Engineer.js). Leaderboard/heatmap/
// retro are DERIVED from existing Supabase tables (invoices/vehicles/bookings);
// scratchpads + maintenance + last-bug-fix move to app_settings. No backfill.
// force-logout-all is now a no-op — it cleared GAS session tokens, which no
// longer exist (auth is Supabase).
// ===========================================================================
async function _currentRole() {
  try { const { data } = await supabase.auth.getUser(); return (data && data.user && data.user.app_metadata && data.user.app_metadata.role) || 'unknown'; }
  catch (_) { return 'unknown'; }
}
export async function getEngineerData() {
  try {
    const role = await _currentRole();
    const roleSlug = String(role).toLowerCase().replace(/[^a-z0-9_]/g, '') || 'unknown';
    const [inv, veh, bk] = await Promise.all([
      supabase.from('invoices').select('customer,total,so_number'),
      selectAllRows(() => supabase.from('vehicles').select('make,model').order('id')),
      supabase.from('bookings').select('date,service'),
    ]);
    // Leaderboard — top 10 customers by revenue (distinct S.O.# = a visit).
    const totals = {};
    (inv.data || []).forEach((r) => {
      const name = S(r.customer).trim(); if (!name) return;
      const k = name.toLowerCase();
      if (!totals[k]) totals[k] = { name, total: 0, visits: 0, _so: {} };
      totals[k].total += Number(r.total) || 0;
      const so = S(r.so_number) || ('#' + Object.keys(totals[k]._so).length);
      if (!totals[k]._so[so]) { totals[k]._so[so] = 1; totals[k].visits += 1; }
    });
    const leaderboard = Object.keys(totals).map((k) => ({ name: totals[k].name, total: totals[k].total, visits: totals[k].visits })).sort((a, b) => b.total - a.total).slice(0, 10);
    // Vehicle heatmap — top 15 make+model combos.
    const combos = {};
    (veh.data || []).forEach((v) => { const make = S(v.make).trim(); if (!make) return; const key = (make + ' ' + S(v.model)).trim(); combos[key] = (combos[key] || 0) + 1; });
    const vehicleHeatmap = Object.keys(combos).map((k) => ({ name: k, count: combos[k] })).sort((a, b) => b.count - a.count).slice(0, 15);
    // Retro stats.
    const now = new Date(); const tm = now.getMonth(), ty = now.getFullYear();
    let bookingsThisMonth = 0, totalEver = 0; const dow = [0, 0, 0, 0, 0, 0, 0]; const svcCount = {};
    (bk.data || []).forEach((b) => {
      totalEver += 1;
      if (!b.date) return;
      const d = new Date(String(b.date).slice(0, 10) + 'T12:00:00'); if (isNaN(d.getTime())) return;
      if (d.getMonth() === tm && d.getFullYear() === ty) bookingsThisMonth += 1;
      dow[d.getDay()] += 1;
      const svc = S(b.service).split(',')[0].trim(); if (svc) svcCount[svc] = (svcCount[svc] || 0) + 1;
    });
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    let bestIdx = 0; for (let j = 1; j < 7; j++) if (dow[j] > dow[bestIdx]) bestIdx = j;
    let bestSvc = '', bestCount = 0; Object.keys(svcCount).forEach((s) => { if (svcCount[s] > bestCount) { bestCount = svcCount[s]; bestSvc = s; } });
    const retroStats = { bookingsThisMonth, bestDayOfWeek: dayNames[bestIdx], mostBookedService: bestSvc || '—', totalEver };
    // app_settings values.
    const scratchpad = String((await _getSetting('ENGINEER_SCRATCHPAD', '')) || '');
    const privateScratchpad = String((await _getSetting('ENGINEER_SCRATCHPAD_PRIVATE_' + roleSlug, '')) || '');
    const lastBugFix = await _getSetting('LAST_BUG_FIX_DATE', '');
    let daysSinceLastBugFix = null;
    if (lastBugFix) { const d = new Date(lastBugFix); if (!isNaN(d.getTime())) daysSinceLastBugFix = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000)); }
    let maintenance = await _getSetting('MAINTENANCE', { enabled: false, message: '', until: '' });
    if (maintenance && maintenance.enabled && maintenance.until) {
      const d = new Date(maintenance.until);
      if (!isNaN(d.getTime()) && d.getTime() < Date.now()) { maintenance = { enabled: false, message: '', until: '' }; await _setSetting('MAINTENANCE', maintenance); }
    }
    return okResp({ leaderboard, vehicleHeatmap, retroStats, daysSinceLastBugFix, scratchpad, privateScratchpad, maintenance });
  } catch (e) { return failResp(e); }
}
export async function engineerSaveScratchpad(content) {
  try { await _setSetting('ENGINEER_SCRATCHPAD', String(content || '')); return okResp({}); } catch (e) { return failResp(e); }
}
export async function engineerSavePrivateScratchpad(content) {
  try { const slug = String(await _currentRole()).toLowerCase().replace(/[^a-z0-9_]/g, '') || 'unknown'; await _setSetting('ENGINEER_SCRATCHPAD_PRIVATE_' + slug, String(content || '')); return okResp({}); } catch (e) { return failResp(e); }
}
export async function engineerMarkBugFixed() {
  try { await _setSetting('LAST_BUG_FIX_DATE', new Date().toISOString()); return okResp({}); } catch (e) { return failResp(e); }
}
export async function engineerSetMaintenanceMode(enabled, message, durationMinutes) {
  try {
    let maintenance;
    if (enabled) {
      const mins = parseInt(durationMinutes, 10);
      maintenance = { enabled: true, message: String(message || 'Brief maintenance — back shortly.'), until: (mins && mins > 0) ? new Date(Date.now() + mins * 60000).toISOString() : '' };
    } else { maintenance = { enabled: false, message: '', until: '' }; }
    await _setSetting('MAINTENANCE', maintenance);
    return okResp({ maintenance });
  } catch (e) { return failResp(e); }
}
export async function engineerForceLogoutAll() { return okResp({ cleared: 0 }); }
export async function engineerDeleteHeatmapCombo(combo) {
  try {
    const target = String(combo || '').trim().toLowerCase();
    if (!target) return failResp('No combo supplied');
    const { data } = await selectAllRows(() => supabase.from('vehicles').select('id,make,model').order('id'));
    const ids = (data || []).filter((v) => ((S(v.make) + ' ' + S(v.model)).trim().toLowerCase()) === target).map((v) => v.id);
    if (ids.length) { const { error } = await supabase.from('vehicles').delete().in('id', ids); if (error) return failResp(error); }
    _bustPhoneMap();
    return okResp({ removed: ids.length });
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// NOTIFICATIONS dismiss / VIP writes
// ===========================================================================
// Clear a booking request's notification prompt for EVERY role. Called whenever a
// request gets settled from any surface — the bell / Overview buttons AND the
// Pending requests panel's Approve / Reject / Dismiss. Without this, settling a
// request in the panel left its alert sitting in the bell and at the top of
// Overview still offering Approve/Deny for something already handled.
// Matches on meta->>requestId, so alerts raised before that field existed aren't
// found (clear those with the × ). No trash snapshot, unlike dismissNotification:
// this is automatic bookkeeping rather than a user dismissal.
export async function clearBookingRequestNotifications(requestId) {
  try {
    const rid = String(requestId || '');
    if (!rid) return okResp({ cleared: 0 });
    const { data, error } = await supabase.from('notifications')
      .select('id,dismissed_by').eq('meta->>requestId', rid);
    if (error) return failResp(error);
    let cleared = 0;
    for (const row of (data || [])) {
      const cur = String(row.dismissed_by || '').split(',').map((s) => s.trim()).filter(Boolean);
      ['owner', 'principal'].forEach((r) => { if (!cur.includes(r)) cur.push(r); });
      const up = await supabase.from('notifications').update({ dismissed_by: cur.join(',') }).eq('id', row.id);
      if (!up.error) cleared++;
    }
    return okResp({ cleared });
  } catch (e) { return failResp(e); }
}

export async function dismissNotification(notifId, role) {
  try {
    const { data } = await supabase.from('notifications').select('dismissed_by, message').eq('id', notifId).maybeSingle();
    const cur = String((data && data.dismissed_by) || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (!cur.includes(role)) cur.push(role);
    const { error } = await supabase.from('notifications').update({ dismissed_by: cur.join(',') }).eq('id', notifId);
    if (error) return failResp(error);
    // Snapshot a restore token so a dismissed notification is recoverable from
    // the trash bin for 24h. Notifications aren't hard-deleted (dismissal is
    // per-role), so restore just pulls the role back out of dismissed_by.
    await _toTrash('notification', notifId, { id: notifId, role }, (data && data.message) || 'Notification', role);
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function deleteVipNote(noteId) {
  try {
    const { data: row } = await supabase.from('vip_notes').select('*').eq('id', noteId).maybeSingle();
    const { error } = await supabase.from('vip_notes').delete().eq('id', noteId);
    if (error) return failResp(error);
    if (row) await _toTrash('vip_note', noteId, row, (row.customer_name ? row.customer_name + ' — ' : '') + String(row.note || '').replace(/\s+/g, ' ').slice(0, 60), null);
    return okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// TRASH BIN — 24h recovery window for light items (notifications, tasks,
// checklist items, inbox messages). A delete routes the row here instead of
// vanishing (UI deletes without a confirm); restore re-inserts it. Rows past
// expires_at are purged lazily on every trash read, so no cron is required.
// ===========================================================================
const TRASH_TYPE_TABLE = { task: 'tasks', checklist_task: 'checklist_tasks', vip_note: 'vip_notes', email_draft: 'email_drafts', pool_task: 'pool_tasks' };
async function _toTrash(entityType, entityId, payload, label, deletedBy) {
  // Best-effort: a trash-write failure must never block the actual delete.
  try {
    await supabase.from('trash_bin').insert({
      id: 'TR-' + genIdSuffix(), entity_type: entityType, entity_id: String(entityId),
      payload, label: label || '', deleted_by: deletedBy || null,
    });
  } catch (_) {}
}
async function _purgeExpiredTrash() {
  try { await supabase.from('trash_bin').delete().lt('expires_at', new Date().toISOString()); } catch (_) {}
}
export async function getTrash() {
  try {
    await _purgeExpiredTrash();
    const { data, error } = await supabase.from('trash_bin').select('*').order('deleted_at', { ascending: false });
    if (error) return failResp(error);
    const items = (data || []).map((r) => ({
      id: r.id, type: r.entity_type, entityId: r.entity_id, label: r.label || '(item)',
      deletedAt: isoTs(r.deleted_at), expiresAt: isoTs(r.expires_at), deletedBy: r.deleted_by || '',
    }));
    return okResp({ items });
  } catch (e) { return failResp(e); }
}
export async function restoreTrashItem(trashId) {
  try {
    const { data: row } = await supabase.from('trash_bin').select('*').eq('id', trashId).maybeSingle();
    if (!row) return failResp(new Error('Trash item not found (it may have expired)'));
    if (row.entity_type === 'notification') {
      const role = (row.payload && row.payload.role) || row.deleted_by;
      const { data: n } = await supabase.from('notifications').select('dismissed_by').eq('id', row.entity_id).maybeSingle();
      if (n) {
        const cur = String(n.dismissed_by || '').split(',').map((s) => s.trim()).filter(Boolean).filter((x) => x !== role);
        const u = await supabase.from('notifications').update({ dismissed_by: cur.join(',') }).eq('id', row.entity_id);
        if (u.error) return failResp(u.error);
      }
    } else {
      const table = TRASH_TYPE_TABLE[row.entity_type];
      if (!table) return failResp(new Error('Unknown trash type: ' + row.entity_type));
      const ins = await supabase.from(table).insert(row.payload);
      if (ins.error) return failResp(ins.error);
      // A restored pool task is back on somebody's day, so their digest for it
      // has to list it again — deletePoolTask cleared it on the way out.
      if (row.entity_type === 'pool_task' && row.payload) {
        await _armPoolDay(row.payload.assignee_id, row.payload.task_date);
      }
    }
    await supabase.from('trash_bin').delete().eq('id', trashId);
    return okResp({});
  } catch (e) { return failResp(e); }
}
export async function purgeTrashItem(trashId) {
  try { const { error } = await supabase.from('trash_bin').delete().eq('id', trashId); return error ? failResp(error) : okResp({}); }
  catch (e) { return failResp(e); }
}
export async function emptyTrash() {
  try { const { error } = await supabase.from('trash_bin').delete().gte('deleted_at', '1900-01-01'); return error ? failResp(error) : okResp({}); }
  catch (e) { return failResp(e); }
}
export async function setVipChecklistVisible(bookingId, visible) {
  try {
    const { error } = await supabase.from('bookings').update({ vip_checklist_visible: !!visible }).eq('id', bookingId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// ===========================================================================
// AI assistants — Vercel /api/ai (Anthropic + Supabase context). Returns the
// legacy { success, response: <encodeURIComponent text> } shape.
// ===========================================================================
async function _askAI(assistant, extra) {
  try {
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    const res = await fetch('/api/ai', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
      body: JSON.stringify({ assistant, ...extra }),
    });
    return await res.json();
  } catch (e) { return failResp(e); }
}
export async function askOwnerAI(question, history) { return _askAI('owner', { question, history }); }
export async function askPrincipalAI(question, history) { return _askAI('principal', { question, history }); }
export async function askDetailerAI(vehicle, question, history) {
  return _askAI('detailer', { question, history, vehicleYear: vehicle && vehicle.year, vehicleMake: vehicle && vehicle.make, vehicleModel: vehicle && vehicle.model });
}

// ===========================================================================
// MESSAGE BLASTS (message_blasts + message_blast_recipients)
// One message to a filtered slice of the customer base — Outbox → Blast.
// See migration 20260818000000.
//
// The SEND itself is a client-side loop (BlastPanel), the same shape the
// win-back mass-text has used since it shipped: one recipient at a time,
// throttled, through the existing sendSms / send-email functions so opt-out
// checks, the compliance footer and the JWT gate all still apply. Nothing here
// sends anything; this file is the record of what was sent.
// ===========================================================================
const mapBlast = (b) => ({
  id: b.id, title: S(b.title), channel: b.channel || 'sms', brief: S(b.brief),
  smsBody: S(b.sms_body), emailSubject: S(b.email_subject), emailBody: S(b.email_body),
  audience: b.audience || {}, status: b.status || 'draft',
  sentCount: Number(b.sent_count) || 0, failedCount: Number(b.failed_count) || 0,
  skippedCount: Number(b.skipped_count) || 0,
  createdAt: b.created_at, createdBy: S(b.created_by), sentAt: b.sent_at,
});

export async function listBlasts(limit) {
  try {
    const { data, error } = await supabase.from('message_blasts')
      .select('*').order('created_at', { ascending: false }).limit(Number(limit) || 25);
    if (error) return failResp(error);
    return okResp({ blasts: (data || []).map(mapBlast) });
  } catch (e) { return failResp(e); }
}

// Turn the operator's rough note into a sendable SMS + email.
//
// The model is asked for JSON; this is where that promise is checked. A blast
// goes to the whole customer list, so "the AI returned something odd" has to
// surface as a refusal the operator sees — never as a half-parsed body that
// gets sent to 170 people. Hence: fenced-JSON tolerance, then a hard fail.
export async function generateBlastCopy(brief, opts) {
  try {
    const note = String(brief || '').trim();
    if (!note) return failResp('Write a short note about what you want to say first.');
    const o = opts || {};
    const question = [
      'Audience: ' + (o.audienceLabel || 'the whole customer base') + ' (' + (o.audienceCount || 0) + ' people).',
      // Context for tone and length only — the reply must carry BOTH channels
      // regardless, since the operator can switch after reading the copy.
      'Channel they plan to send on (write both anyway): ' + (o.channel === 'both' ? 'text and email' : o.channel === 'email' ? 'email' : 'text'),
      '', 'What I want to say:', note,
    ].join('\n');
    const r = await _askAI('blast', { question });
    if (!r || r.success === false) return failResp((r && r.error) || 'AI unavailable');
    const parsed = parseBlastReply(r.response);
    if (!parsed) return failResp('The AI did not return a usable message — try rewording your note.');
    if (!parsed.sms && !parsed.emailBody) return failResp('The AI came back empty — try rewording your note.');
    return okResp(parsed);
  } catch (e) { return failResp(e); }
}

// Pull {sms, emailSubject, emailBody} out of whatever the model actually sent.
//
// Exported so it can be tested directly. It is asked for bare JSON and usually
// obliges, but "usually" is not good enough when the output is about to be sent
// to the entire customer list: the tolerated shapes below (a ```json fence, a
// sentence of preamble) are the ones models reach for anyway, and ANYTHING else
// returns null so the caller refuses out loud instead of sending a fragment.
export function parseBlastReply(response) {
  let text = '';
  // The /api/ai contract encodes its reply; a non-encoded one must still parse.
  try { text = decodeURIComponent(response || ''); } catch (_) { text = String(response || ''); }
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fenced) text = fenced[1];
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start < 0 || end <= start) return null;
  let parsed;
  try { parsed = JSON.parse(text.slice(start, end + 1)); } catch (_) { return null; }
  if (!parsed || typeof parsed !== 'object') return null;
  const str = (v) => (typeof v === 'string' ? v.trim() : '');
  return { sms: str(parsed.sms), emailSubject: str(parsed.emailSubject), emailBody: str(parsed.emailBody) };
}

// Create the blast row up front, BEFORE a single message goes out, so the
// recipient ledger has something to hang off from the very first send. A blast
// that dies halfway is then still a readable record rather than nothing.
export async function saveBlast(blast) {
  try {
    const b = blast || {};
    const row = {
      title: String(b.title || '').trim() || 'Untitled blast',
      channel: ['sms', 'email', 'both'].includes(b.channel) ? b.channel : 'sms',
      brief: String(b.brief || ''),
      sms_body: String(b.smsBody || ''),
      email_subject: String(b.emailSubject || ''),
      email_body: String(b.emailBody || ''),
      audience: b.audience || {},
      status: ['draft', 'sending', 'sent', 'cancelled'].includes(b.status) ? b.status : 'draft',
    };
    if (b.id) {
      const { error } = await supabase.from('message_blasts').update(row).eq('id', b.id);
      return error ? failResp(error) : okResp({ id: b.id });
    }
    row.id = 'BLST-' + genIdSuffix();
    row.created_by = b.by ? String(b.by) : null;
    const { error } = await supabase.from('message_blasts').insert(row);
    return error ? failResp(error) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}

// Record what happened to one recipient, immediately after it happened.
//
// Written one at a time rather than batched at the end on purpose: the whole
// point of the ledger is to survive the tab being closed mid-blast, and a batch
// that only lands at the end is exactly the case it fails to cover.
//
// ⚠️ Bulk-insert gotcha (CLAUDE.md): this is a SINGLE-object insert, so a
// missing `site` key falls through to the column default. Do not convert it to
// an array insert without attaching `site` explicitly.
export async function recordBlastSend(blastId, row) {
  try {
    if (!blastId || !row || !row.customerId) return failResp('Missing blast or customer');
    const r = {
      blast_id: blastId,
      customer_id: String(row.customerId),
      channel: row.channel === 'email' ? 'email' : 'sms',
      status: ['sent', 'failed', 'skipped'].includes(row.status) ? row.status : 'failed',
      reason: row.reason ? String(row.reason).slice(0, 300) : null,
      to_addr: row.to ? String(row.to).slice(0, 200) : null,
    };
    // onConflict: a resumed blast re-walking the list must not throw on people
    // it already reached — it should quietly leave the original record alone.
    const { error } = await supabase.from('message_blast_recipients')
      .upsert(r, { onConflict: 'blast_id,customer_id,channel', ignoreDuplicates: true });
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// Who this blast already reached, so a resume skips them. Returns a Set of
// "<customerId>|<channel>" keys — the same shape the send loop tests against.
export async function blastAlreadySent(blastId) {
  try {
    if (!blastId) return okResp({ keys: [] });
    // 1,028 rows on the first real blast — past the cap already. A short read
    // here would re-contact everyone it failed to see.
    const { data, error } = await selectAllRows(() => supabase.from('message_blast_recipients')
      .select('customer_id,channel,status').eq('blast_id', blastId).eq('status', 'sent').order('customer_id'));
    if (error) return failResp(error);
    return okResp({ keys: (data || []).map((r) => r.customer_id + '|' + r.channel) });
  } catch (e) { return failResp(e); }
}

// Who has already been reached by a set of EARLIER blasts.
//
// Distinct from blastAlreadySent, which dedupes a blast against ITSELF while
// it runs. This answers the other question: "do not contact anyone who got
// the last one." A new blast has its own ledger, so nothing in the resume
// machinery would otherwise skip them — the whole 201 would be mailed again.
//
// Keyed customerId|channel, the same shape as the ledger, so an email blast
// excludes people who were EMAILED before and not people who were texted.
export async function blastReachedKeys(blastIds) {
  try {
    const ids = (Array.isArray(blastIds) ? blastIds : [blastIds]).filter(Boolean);
    if (!ids.length) return okResp({ keys: [] });
    const { data, error } = await selectAllRows(() => supabase.from('message_blast_recipients')
      .select('customer_id,channel,status').in('blast_id', ids).eq('status', 'sent').order('customer_id'));
    if (error) return failResp(error);
    return okResp({ keys: (data || []).map((r) => r.customer_id + '|' + r.channel) });
  } catch (e) { return failResp(e); }
}

// True totals for a blast, straight from the ledger.
//
// A resumed run only knows about the recipients IT walked, so closing a blast
// out with the loop's own counters would report the resumed slice as if it were
// the whole thing — a blast that sent 1000 across two runs would be filed as
// having sent the last 300. The ledger is the only place that knows the total.
export async function blastLedgerCounts(blastId) {
  try {
    if (!blastId) return okResp({ sent: 0, failed: 0 });
    const { data, error } = await selectAllRows(() => supabase.from('message_blast_recipients')
      .select('status,customer_id').eq('blast_id', blastId).order('customer_id'));
    if (error) return failResp(error);
    const rows = data || [];
    return okResp({
      sent: rows.filter((r) => r.status === 'sent').length,
      failed: rows.filter((r) => r.status === 'failed').length,
    });
  } catch (e) { return failResp(e); }
}

// A blast the loop never got to close out — the tab was shut, the browser slept,
// the machine died. Surfaced on load so it can be resumed instead of restarted,
// which is the difference between finishing 300 sends and re-sending 700.
export async function unfinishedBlast() {
  try {
    const { data, error } = await supabase.from('message_blasts')
      .select('*').eq('status', 'sending').order('created_at', { ascending: false }).limit(1);
    if (error) return failResp(error);
    const row = (data || [])[0];
    if (!row) return okResp({ blast: null });
    const counts = await blastLedgerCounts(row.id);
    return okResp({ blast: { ...mapBlast(row), sentCount: counts.sent || 0, failedCount: counts.failed || 0 } });
  } catch (e) { return failResp(e); }
}

export async function finishBlast(blastId, counts) {
  try {
    if (!blastId) return failResp('Missing id');
    const c = counts || {};
    const { error } = await supabase.from('message_blasts').update({
      status: 'sent',
      sent_count: Number(c.sent) || 0,
      failed_count: Number(c.failed) || 0,
      skipped_count: Number(c.skipped) || 0,
      sent_at: new Date().toISOString(),
    }).eq('id', blastId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

export async function deleteBlast(blastId) {
  try {
    if (!blastId) return failResp('Missing id');
    const { error } = await supabase.from('message_blasts').delete().eq('id', blastId);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

// Send one blast email. Thin wrapper over the same Resend function the Outbox
// drafts use (JWT-gated, owner/principal) — no draftId, since a blast email has
// no email_drafts row to mark sent; the recipient ledger is its record.
export async function sendBlastEmail(to, subject, body, unsubToken, partner) {
  // The token is what marks this send as MARKETING: api/send-email.js appends
  // the unsubscribe footer and the List-Unsubscribe headers only when one is
  // present, so every transactional send stays byte-identical.
  return _sendEmail({ to, subject, body, unsubToken, marketing: true, partner });
}

// ===========================================================================
// BLAST PRESETS (blast_presets)
// Reusable blast templates — Outbox → Blast. See migration 20260818120000.
//
// A preset is a DRAFT source, never a send shortcut: loading one fills the
// composer and the panel re-scans it for dates, percentages and amounts that
// expire. Nothing here sends anything.
// ===========================================================================
const mapBlastPreset = (p) => ({
  id: p.id, name: S(p.name), channel: p.channel || 'sms', brief: S(p.brief),
  smsBody: S(p.sms_body), emailSubject: S(p.email_subject), emailBody: S(p.email_body),
  audience: p.audience || {}, createdAt: p.created_at, createdBy: S(p.created_by),
  lastUsedAt: p.last_used_at,
});

// Most-recently-used first, then newest — the preset you reached for last week
// is the one you want at the top of the picker.
export async function listBlastPresets() {
  try {
    const { data, error } = await supabase.from('blast_presets')
      .select('*')
      .order('last_used_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false });
    if (error) return failResp(error);
    return okResp({ presets: (data || []).map(mapBlastPreset) });
  } catch (e) { return failResp(e); }
}

// Create, or overwrite by id. Saving over an existing preset is how you fix a
// stale discount at the source instead of re-editing it on every reuse.
export async function saveBlastPreset(preset) {
  try {
    const p = preset || {};
    const name = String(p.name || '').trim();
    if (!name) return failResp('Give the preset a name');
    if (!String(p.smsBody || '').trim() && !String(p.emailBody || '').trim()) {
      return failResp('Write the message before saving it as a preset.');
    }
    const row = {
      name,
      channel: ['sms', 'email', 'both'].includes(p.channel) ? p.channel : 'sms',
      brief: String(p.brief || ''),
      sms_body: String(p.smsBody || ''),
      email_subject: String(p.emailSubject || ''),
      email_body: String(p.emailBody || ''),
      audience: p.audience || {},
    };
    if (p.id) {
      const { error } = await supabase.from('blast_presets').update(row).eq('id', p.id);
      return error ? failResp(_presetErr(error)) : okResp({ id: p.id });
    }
    row.id = 'BPRE-' + genIdSuffix();
    row.created_by = p.by ? String(p.by) : null;
    const { error } = await supabase.from('blast_presets').insert(row);
    return error ? failResp(_presetErr(error)) : okResp({ id: row.id });
  } catch (e) { return failResp(e); }
}
function _presetErr(error) {
  const m = String((error && error.message) || '');
  if (m.includes('blast_presets_name_uniq')) return 'A preset with that name already exists — pick another name, or load that one and re-save it.';
  return error;
}

// Stamped when a preset is LOADED, not when a blast is sent: the ordering this
// feeds is "what do I reach for", and a preset you opened and decided against
// is still one you reached for.
export async function touchBlastPreset(id) {
  try {
    if (!id) return failResp('Missing id');
    const { error } = await supabase.from('blast_presets')
      .update({ last_used_at: new Date().toISOString() }).eq('id', id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}

export async function deleteBlastPreset(id) {
  try {
    if (!id) return failResp('Missing id');
    const { error } = await supabase.from('blast_presets').delete().eq('id', id);
    return error ? failResp(error) : okResp({});
  } catch (e) { return failResp(e); }
}



// ===========================================================================
// ID generation (matches legacy 'PREFIX-XXXXXXXXXXX')
// ===========================================================================
function genIdSuffix() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let s = '';
  for (let i = 0; i < 11; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
  return s;
}

// ===========================================================================
// TODO — remaining endpoints to migrate before cutover (still on /exec today):
//   getDashboardOverview, getBayConfig, weekly/annual goals,
//   deposits/balance (request/mark/email), email-draft generate/send/delete,
//   recurring bookings + expenses, 3CAM (expenses/mileage/employees/timeclock/
//   bank*/reconciliation), marketing, engineer*, gift-card create/update,
//   booking requests (list/approve/reject), invoice link/unlink/delete/update,
//   syncInvoices/syncCustomers (retire), AI endpoints, invoice counter
//   (claim/peek_invoice_num RPCs), searchInvoicePdfs, vehicle media.
// Public/anon pages (booking submit, VIP portal, deposit/balance pay) need
// SECURITY DEFINER RPCs — separate work.
// ===========================================================================

// ===========================================================================
// PROACTIVE CARE — condition checks at check-in + the outreach they earn
// (20261104120000). Rules live in lib/care.js; this is the data door.
// ===========================================================================
function mapConditionCheck(r) {
  const n = (v) => (v == null || v === '' ? null : Number(v));
  return {
    id: r.id, vehicleId: S(r.vehicle_id), customerId: S(r.customer_id), bookingId: S(r.booking_id), site: S(r.site),
    checkedAt: isoTs(r.checked_at), checkedBy: S(r.checked_by),
    odometer: n(r.odometer),
    // Both spellings on purpose: the rules iterate the column names.
    tire_lf: n(r.tire_lf), tire_rf: n(r.tire_rf), tire_lr: n(r.tire_lr), tire_rr: n(r.tire_rr),
    tireLf: n(r.tire_lf), tireRf: n(r.tire_rf), tireLr: n(r.tire_lr), tireRr: n(r.tire_rr),
    battery: S(r.battery) || null, wipers: S(r.wipers) || null, brakesMm: n(r.brakes_mm),
    coatingProduct: S(r.coating_product) || null, coatingAppliedOn: r.coating_applied_on ? String(r.coating_applied_on).slice(0, 10) : null,
    notes: S(r.notes),
  };
}
// Save one check. Any field left undefined/'' is stored NULL — a blank tire
// means "didn't measure", never 0. When an odometer is given it also becomes
// the vehicle's latest reading through the existing RPC (which refuses a
// backwards or implausible number; that refusal is reported, not swallowed).
export async function saveConditionCheck(d) {
  try {
    const vehicleId = S(d.vehicleId).trim();
    if (!vehicleId) return failResp('Pick a vehicle first.');
    const n = (v) => (v === '' || v == null || !Number.isFinite(Number(v)) ? null : Number(v));
    const row = {
      id: 'VC-' + genIdSuffix(), vehicle_id: vehicleId, customer_id: S(d.customerId) || null, booking_id: S(d.bookingId) || null,
      checked_by: S(d.by) || null, odometer: n(d.odometer) == null ? null : Math.round(n(d.odometer)),
      tire_lf: n(d.tireLf), tire_rf: n(d.tireRf), tire_lr: n(d.tireLr), tire_rr: n(d.tireRr),
      battery: d.battery || null, wipers: d.wipers || null, brakes_mm: n(d.brakesMm),
      coating_product: S(d.coatingProduct).trim() || null, coating_applied_on: d.coatingAppliedOn || null,
      notes: S(d.notes).trim() || null,
    };
    if (d.site) row.site = d.site;
    const { error } = await supabase.from('vehicle_condition_checks').insert(row);
    if (error) return failResp(error);
    let odometerWarning = '';
    if (row.odometer != null) {
      const r = await supabase.rpc('set_vehicle_service_facts', { p_vehicle_id: vehicleId, p_odometer: row.odometer, p_by: row.checked_by || null });
      if (r.error) odometerWarning = r.error.message || 'odometer not updated on the vehicle';
    }
    return okResp({ id: row.id, odometerWarning });
  } catch (e) { return failResp(e); }
}
export async function listConditionChecks(opts) {
  try {
    const o = opts || {};
    let q = supabase.from('vehicle_condition_checks').select('*').order('checked_at', { ascending: false });
    if (o.vehicleId) q = q.eq('vehicle_id', o.vehicleId);
    if (o.customerId) q = q.eq('customer_id', o.customerId);
    q = q.limit(o.limit || 500);
    const { data, error } = await q;
    if (error) return failResp(error);
    return okResp({ checks: (data || []).map(mapConditionCheck) });
  } catch (e) { return failResp(e); }
}
// Everything the rules need in one call: every check this shop can see, the
// cars and people behind them, and those customers' finished appointments.
// Scoped to cars that HAVE a check — a car we never looked at has nothing
// specific to say, and the brief forbids a message without a car-specific fact.
export async function listCareFleet() {
  try {
    const cc = await selectAllRows(() => supabase.from('vehicle_condition_checks').select('*').order('checked_at', { ascending: false }));
    if (cc.error) return failResp(cc.error);
    const checks = (cc.data || []).map(mapConditionCheck);
    const vehIds = [...new Set(checks.map((c) => c.vehicleId).filter(Boolean))];
    const vehicles = [];
    for (let i = 0; i < vehIds.length; i += 200) {
      const r = await supabase.from('vehicles').select('*').in('id', vehIds.slice(i, i + 200));
      (r.data || []).forEach((v) => vehicles.push({ ...mapVehicle(v), customerId: S(v.customer_id) }));
    }
    const custIds = [...new Set(vehicles.map((v) => v.customerId).concat(checks.map((c) => c.customerId)).filter(Boolean))];
    const customers = [];
    const bookings = [];
    for (let i = 0; i < custIds.length; i += 200) {
      const ids = custIds.slice(i, i + 200);
      const [c, b] = await Promise.all([
        supabase.from('customers').select('id,first_name,last_name,full_name,phone,opt_out,red_flag').in('id', ids),
        supabase.from('bookings').select('id,customer_id,vehicle_id,date,service,status').in('customer_id', ids).in('status', ['complete', 'delivered']).order('date', { ascending: false }),
      ]);
      (c.data || []).forEach((r) => customers.push({ id: r.id, firstName: S(r.first_name), lastName: S(r.last_name), fullName: S(r.full_name) || [r.first_name, r.last_name].filter(Boolean).join(' '), phone: S(r.phone), optOut: !!r.opt_out, redFlag: !!r.red_flag }));
      (b.data || []).forEach((r) => bookings.push({ id: r.id, customerId: S(r.customer_id), vehicleId: S(r.vehicle_id), date: String(r.date || '').slice(0, 10), service: S(r.service), status: S(r.status) }));
    }
    return okResp({ checks, vehicles, customers, bookings });
  } catch (e) { return failResp(e); }
}
function mapCareOutreach(r) {
  return {
    id: r.id, vehicleId: S(r.vehicle_id), customerId: S(r.customer_id), site: S(r.site), ruleKey: S(r.rule_key),
    reason: S(r.reason), message: S(r.message), status: S(r.status), snoozedUntil: isoTs(r.snoozed_until),
    sentAt: isoTs(r.sent_at), sentBy: S(r.sent_by), dismissedAt: isoTs(r.dismissed_at), bookingId: S(r.booking_id),
    createdAt: isoTs(r.created_at), updatedAt: isoTs(r.updated_at), meta: (r.meta && typeof r.meta === 'object') ? r.meta : {},
  };
}
export async function listCareOutreach() {
  try {
    const r = await selectAllRows(() => supabase.from('care_outreach').select('*').order('created_at', { ascending: false }));
    if (r.error) return failResp(r.error);
    return okResp({ rows: (r.data || []).map(mapCareOutreach) });
  } catch (e) { return failResp(e); }
}
// Insert new suggestions. Skips any (vehicle, rule) that is already open and
// any row whose dedupe key (meta.dedupe — "once per season", "once per coating
// age") was ever sent or dismissed. A unique-index race is swallowed.
export async function addCareSuggestions(rows, existing) {
  try {
    const open = new Set((existing || []).filter((x) => ['suggested', 'snoozed'].includes(x.status)).map((x) => x.vehicleId + '|' + x.ruleKey));
    const seenDedupe = new Set((existing || []).map((x) => x.meta && x.meta.dedupe).filter(Boolean));
    let created = 0, skipped = 0;
    for (const s of rows || []) {
      if (open.has(s.vehicleId + '|' + s.ruleKey) || (s.dedupe && seenDedupe.has(s.dedupe))) { skipped += 1; continue; }
      const row = {
        id: 'CO-' + genIdSuffix(), vehicle_id: s.vehicleId, customer_id: s.customerId || null, rule_key: s.ruleKey,
        reason: s.reason, message: s.message, status: 'suggested', meta: { dedupe: s.dedupe || null, facts: s.facts || null, season: s.season || null },
      };
      if (s.site) row.site = s.site;
      const { error } = await supabase.from('care_outreach').insert(row);
      if (error) { if (/duplicate|unique/i.test(error.message || '')) { skipped += 1; continue; } return failResp(error); }
      open.add(s.vehicleId + '|' + s.ruleKey); if (s.dedupe) seenDedupe.add(s.dedupe);
      created += 1;
    }
    return okResp({ created, skipped });
  } catch (e) { return failResp(e); }
}
export async function updateCareOutreach(id, patch) {
  try {
    const row = { updated_at: new Date().toISOString() };
    const p = patch || {};
    if (p.status) row.status = p.status;
    if (p.message !== undefined) row.message = p.message;
    if (p.snoozedUntil !== undefined) row.snoozed_until = p.snoozedUntil || null;
    if (p.sentAt !== undefined) row.sent_at = p.sentAt || null;
    if (p.sentBy !== undefined) row.sent_by = p.sentBy || null;
    if (p.dismissedAt !== undefined) row.dismissed_at = p.dismissedAt || null;
    if (p.bookingId !== undefined) row.booking_id = p.bookingId || null;
    const { data, error } = await supabase.from('care_outreach').update(row).eq('id', id).select('id');
    if (error) return failResp(error);
    if (!data || !data.length) return failResp('Nothing saved — the row was not found or you are not allowed to change it.');
    return okResp({ id });
  } catch (e) { return failResp(e); }
}
export async function getCareConfig() {
  try { const cfg = await _getSetting('CARE_RULES', null); return okResp({ config: (cfg && typeof cfg === 'object') ? cfg : {} }); }
  catch (e) { return failResp(e); }
}
export async function saveCareConfig(config) {
  try {
    const { error } = await _setSetting('CARE_RULES', config || {});
    if (error) return failResp(error);
    return okResp({});
  } catch (e) { return failResp(e); }
}
// When each customer was last texted by ANY pipeline (reminders: appointment
// reminders, ready texts, staff-sent reminders) — so the Care card can say
// "last texted 4 days ago" and the sender can judge fatigue beyond the
// outreach-only cooldown. Best-effort.
export async function careLastTexted(customerIds) {
  try {
    const ids = [...new Set((customerIds || []).filter(Boolean))];
    const out = {};
    for (let i = 0; i < ids.length; i += 200) {
      const r = await supabase.from('reminders').select('customer_id,last_fired_at').eq('channel', 'sms').eq('status', 'sent').in('customer_id', ids.slice(i, i + 200)).not('last_fired_at', 'is', null);
      (r.data || []).forEach((x) => { const t = isoTs(x.last_fired_at); if (!out[x.customer_id] || t > out[x.customer_id]) out[x.customer_id] = t; });
    }
    return okResp({ lastTexted: out });
  } catch (e) { return failResp(e); }
}
